/**
 * Manager Worker — forked child process that polls the API for agent config
 * changes and local file drift. Communicates with the watchdog parent via IPC.
 *
 * This file is a standalone entry point built by tsup so `fork()` can target it.
 */

import { createHash } from 'node:crypto';
import { readFileSync, writeFileSync, mkdirSync, existsSync, rmSync, readdirSync, statSync, unlinkSync, copyFileSync } from 'node:fs';
import { execFileSync as syncExecFile } from 'node:child_process';
import { join, dirname } from 'node:path';
import { homedir } from 'node:os';
import { fileURLToPath } from 'node:url';
import {
  extractFrontmatter,
  resolveChannels,
  getFramework,
  DEFAULT_FRAMEWORK,
  parseDeliveryTarget,
  isParseError,
  resolveDmTarget,
  isResolveError,
  appendDmFooter,
  wrapScheduledTaskPrompt,
  classifyOutput,
  SUPPRESS_SENTINEL,
  type PriorRun,
  type FrameworkAdapter,
  type CharterFrontmatter,
  type ToolsFrontmatter,
  type ChannelId,
  type ChannelPolicy,
  type OrgChannelPolicy,
  type DeploymentTarget,
  type ProvisionInput,
  type DeliveryTarget,
  type ResolverAgent,
  type ResolverPerson,
  KANBAN_CHECK_COMMAND,
  formatRunMarker,
  classifyActor,
  resolveAvatarEnvUrl,
  MAX_AVATAR_ENV_URL_BYTES,
  FLAGS_SCHEMA_VERSION,
  type FlagValue,
  coerceOnboardingState,
  isOnboardingArea,
  laneTagFragment,
  resolveAgentTimezone,
  isUnsetTimezone,
} from '@augmented/core';
import { provision } from '@augmented/core/provisioning/provisioner.js';
import { extractCommandNotFound } from '@augmented/core/provisioning/hook-env.js';
import { getIntegration } from '@augmented/core/integrations/registry.js';
import type { ConnectivityTestOverride, ToolkitSourceType } from '@augmented/core/integrations';
import type { IntegrationAuthType, RemoteMcpSpec } from '@augmented/core';
import {
  claudeCodeUpgradeMarkerPath,
  stampClaudeCodeUpgradeMarker,
  claudeCodeUpgradeThrottled,
} from './claude-code-upgrade-throttle.js';
import { reapOrphanChannelMcps, parsePsRows } from './orphan-channel-mcp-reaper.js';
import { decideMcpDriftAction, decideMcpRestartOnDrift, managedMcpStructureHash, channelSecretValueHash } from './mcp-config-drift.js';
import { decidePostRestartVerification, shouldRemediateUnverifiedRestart, type PendingRestartVerification } from './mcp-restart-verify.js';
import { CHANNEL_SECRET_ENV_KEYS } from '@augmented/core/provisioning/env-integrations-file.js';
import { computeIntegrationsHash } from './integration-hash.js';
import { withResolvedRemoteMcp } from './host-integration-mapping.js';
import {
  diffEnvIntegrations,
  findMcpServersUsingVars,
  reapStaleMcpChildren,
} from './stale-mcp-reaper.js';
// ENG-5053: presence check for declared MCP children — restarts the
// session when a declared mcpServers entry has no live child under the
// agent's claude (e.g. xero-mcp-server crashed and Claude Code's
// transport didn't respawn it). See lib/mcp-presence-reaper.ts.
import {
  reapMissingMcpSessions,
  clearPresenceReaperState,
  clearPresenceReaperStateForKeys,
  givenUpMcpServerKeys,
  findMissingMcpServers,
} from './mcp-presence-reaper.js';
import { decideChannelRestart, launchableChannelIds } from './channel-restart-decision.js';
import { resolveChannelLaunchFlags } from './channel-launch-flags.js';
import { decideSenderPolicyRestart } from './sender-policy-restart-decision.js';
import {
  decideMsTeamsBehaviourRestart,
  extractMsTeamsBehaviourSubset,
} from './msteams-behaviour-restart.js';
import {
  decideSlackBehaviourRestart,
  extractSlackBehaviourSubset,
} from './slack-behaviour-restart.js';
// ENG-6533 (folds ENG-6527): drive a mid-flow self-onboarding agent by injecting
// a one-shot "run onboarding_get and proceed" directive into its live session.
import {
  decideOnboardingDrive,
  buildOnboardingDirective,
  onboardingNudgeReachedSession,
  readOnboardingDriveMarker,
  writeOnboardingDriveMarker,
  clearOnboardingDriveMarker,
} from './onboarding-drive.js';
// ENG-5932 (Reaper Slice 1): criticality classification + local persistent
// channel-quarantine store. Quarantine a permanently-dead OPTIONAL channel
// (e.g. an orphaned msteams config) instead of restarting the whole session
// for it forever — without ever touching ESSENTIAL control-plane channels.
import {
  ChannelQuarantineStore,
  classifyChannelCriticality,
  defaultQuarantinePath,
} from './channel-quarantine.js';
import { RestartBreaker, reaperRestartBreakerReason, type RestartReason, type TripState } from './restart-breaker.js';
import {
  McpFlapDampener,
  membershipSignature,
  readEnvNumber as readFlapEnvNumber,
  DEFAULT_FLAP_BREAKER_COOLDOWN_MS,
  FLAP_CHANNEL_INTEGRATIONS,
  FLAP_CHANNEL_MANAGED_MCP,
  isManagedMcpServerKey,
} from './mcp-flap-dampener.js';
import {
  decideAutoResume,
  hydrateAutoResumeMarkers,
  readAutoResumeConfig,
  type AutoResumeRecord,
} from './auto-resume.js';
// ENG-6383 (ENG-6375 Slice C): health-gated auto-resume. Replaces the blind
// ENG-6088 quiet-timer with decideResumeReconcile when AGT_RESUME_RECONCILER_ENABLED
// is on (the blind path stays the flag-off fallback). The pure decision +
// input-assembly helpers live in their own modules so this hot-file edit is a
// thin call into tested code.
import {
  decideResumeReconcile,
  readResumeReconcilerConfig,
} from './resume-reconciler.js';
import {
  deriveMcpPresent,
  parseResumeHealthResponse,
} from './resume-reconciler-inputs.js';
import { shouldRespawnForModelChange } from './model-change-respawn.js';
// ENG-5641: host-side connectivity probing.
import { runConnectivityProbes } from './connectivity-probe-runner.js';
// ENG-6392: per-agent down→ok diff feeding the auto-resume close-the-loop send.
import { DependencyRecoveryLedger } from './dependency-recovery-ledger.js';
import { parseEnvIntegrations } from './mcp-env-probe.js';
import { safeWriteJsonAtomic } from '@augmented/core/provisioning/mcp-config-guards.js';
import {
  decideSelfUpdateCoalesce,
  formatCoalesceDeferLogLine,
  isAgtVersionSmokeOk,
  readLastSelfUpdateAppliedMs,
  resolveCoalesceWindowMs,
  stampLastSelfUpdateApplied,
} from './self-update-coalesce.js';
import {
  decideMaintenanceWindowGate,
  isUrgentUpgrade,
  decideRestartGate,
  isGateableRestartReason,
  isWindowExemptRestartReason,
  decideForcedUpdate,
  shouldConsumeForcedUpdate,
  type SelfUpdateOutcome,
} from './update-window-gate.js';
import { formatWindowDeferLogLine } from './maintenance-window.js';
import { atomicWriteFileSync } from './atomic-write.js';
import { HostFlagStore, defaultFlagsCachePath } from './feature-flags-host.js';
import {
  addSpawn as addClaudePidSpawn,
  decideReaperActions as decideClaudeReaperActions,
  formatDrainShutdownLine as formatDrainLine,
  formatReaperBootLine as formatReaperLine,
  readPidFile as readClaudePidFile,
  removeSpawn as removeClaudePidSpawn,
  writePidFile as writeClaudePidFile,
} from './claude-pid-tracker.js';
import {
  directChatMaxAgeMs,
  isDirectChatMessageExpired,
  directChatDoorbellPath,
} from './direct-chat-delivery.js';
import {
  buildProbeEnv,
  buildConnectivityProbeDeps,
  deriveMcpServerKey,
} from './connectivity-probe-context.js';
import { runSessionToolBindProbes } from './session-tool-bind-runner.js';
import { gatherSessionToolBindProbe } from './session-tool-bind-probe-host.js';

// ENG-5794: sentinels around the side-effect-managed Integrations section
// in CLAUDE.md. See `buildIntegrationsSection` for the producer side and
// the diff strip below at the call site for the consumer rationale.
import {
  provisionStopHook,
  provisionIsolationHook,
  provisionOrientHook,
  provisionAutoKanbanProgressHook,
  provisionChannelProgressHook,
  provisionSessionStateHook,
  estimateActiveTasksTokens,
  INTEGRATIONS_SECTION_START,
  INTEGRATIONS_SECTION_END,
} from '@augmented/core/provisioning/frameworks/claudecode/index.js';

/**
 * Escape a literal string for use inside a `RegExp` — the sentinel
 * constants contain `<`/`-` etc. that aren't regex-special today, but
 * pinning the escape makes the diff strip robust if the sentinel shape
 * ever changes.
 */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

import { api, getHostId, exchangeApiKey, getCachedClaudeAuthMode, ApiError, setConfigHash } from './api-client.js';
import { collectEnvGates } from '@augmented/core/host-config';
import {
  ArtifactStreamScanner,
  nodeArtifactFs,
  artifactsDirFor,
} from './artifact-stream.js';
import { maybeReportUsageBanner } from './usage-banner-monitor.js';
import { maybeReportTokenUsage } from './token-usage-monitor.js';
import { maybeReconcileWorkflowRunTokens } from './workflow-run-reconciler.js';
import {
  maybeEvaluateConversations,
  meteredScorerSuppressed,
  selectEvalBackend,
  DEFAULT_CLAUDE_EVAL_MODEL,
  DEFAULT_LOCAL_EVAL_URL,
  DEFAULT_LOCAL_EVAL_MODEL,
  type ConversationEvalBackend,
} from './conversation-evaluator.js';
import { maybeExtractMemories } from './memory-extractor.js';
import { maybeReportActivityCache } from './activity-cache-monitor.js';
import { classifyPollError, currentPollBackoffMs } from './poll-backoff.js';
import { getApiKey, requireHost } from './config.js';
import { sanitizeMcpJson } from './mcp-sanitize.js';
import { renderIntegrationSkillContent } from './integration-context-render.js';
import {
  buildIntegrationBundle,
  bundleFingerprint,
  groupSkillsByIntegration,
  type IntegrationSkillInput,
} from './integration-skill-layout.js';
import { detectClaudeAuth } from './claude-auth-detect.js';
import { canonicalJson } from './canonical-json.js';
import { computeChannelConfigHash } from './channel-config-hash.js';
import {
  loadChannelHashCache as loadChannelHashCacheFromDisk,
  saveChannelHashCache as saveChannelHashCacheToDisk,
} from './channel-hash-cache.js';
import { SUPERVISOR_RESTART_EXIT_CODE } from '../commands/manager.js';
import { sweepChannelProcesses, killAgentChannelProcesses } from './channel-sweep.js';
import { checkChannelInputs, creditWatchdogGiveUpCount, takeWatchdogGiveUpCount } from './channel-input-watchdog.js';
import { hintProbability, pickHintVariant, shouldIncludeHint } from './delivery-hint.js';
import { withScheduleLinkFooter } from './delivery-schedule-link.js';
// ADR-0011 PR-A: shared per-agent state, migrated out of module scope so
// the manager subsystems can lift in the stacked follow-up PRs without
// having to import ambient `const Map` references from this file.
import { agentState, clearAgentState } from './manager/agent-state.js';
import { planGlobalSkillSync } from './manager/global-skills.js';
import {
  log,
  redactForDiskLog,
  sha256,
  hashFile,
  execFilePromise,
  execFilePromiseLong,
  ChildProcessError,
} from './manager/runtime.js';
import { resolveModelChain } from './manager/model.js';
import { applyClaudeAuthToEnv } from './manager/claude-auth.js';
import {
  STANDUP_TEMPLATES,
  TASK_UPDATE_TEMPLATES,
  PLAN_TEMPLATES,
  KANBAN_WORK_TEMPLATES,
  BOARD_INJECT_TEMPLATES,
  isKanbanHybridEnabled,
  isKanbanHybridDryRun,
  isHybridActionable,
  isSlashCommand,
  parseStandupSummary,
  parsePlanItems,
  parseKanbanUpdates,
  sanitizeBoardItem,
  getBuiltInSkillContent,
  formatBoardForPrompt,
} from './manager/kanban/parsers.js';
import type { BoardItem } from './manager/kanban/parsers.js';
import { enqueueKanbanNotice } from './manager/kanban/notify.js';
import {
  agentChannelTokens,
  getAlertSlackWebhook,
  setAlertSlackWebhook,
} from './manager/channels/state.js';
import { sendSlackWebhookMessage } from './manager/channels/slack.js';
import { telegramApiCall } from './manager/channels/telegram.js';
import { cacheAgentDeliveryMetadata } from './manager/delivery/state.js';
import {
  reportDeliveryStatus,
  buildDoneCardNotification,
  sendTaskNotification,
} from './manager/delivery/notifications.js';
import { deliverScheduledTaskOutput } from './manager/delivery/scheduled-output.js';
import {
  buildSchedulerTaskInput,
  startRun,
  finishRun,
  syncAndCheckClaudeScheduler,
  fireScheduledTaskViaKanban,
  reconcileScheduledRuns,
  deliverScheduledCardResult,
  isScheduledCardTracked,
  closeScheduledRunsForCode,
  registerClaudeSpawn,
  unregisterClaudeSpawn,
  claudePidFilePath,
  claudeSchedulerStates,
  inFlightClaudeTasks,
  claudeTaskConcurrency,
  MAX_CLAUDE_CONCURRENCY,
  scheduledRunsByCode,
  inFlightClaudePids,
} from './manager/scheduler.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WorkerConfig {
  intervalMs: number;
  configDir: string;
}

interface AcpSessionSummary {
  sessionId: string;
  agentCommand: string;
  sessionName?: string;
  queueState: string;
  turnCount: number;
  startedAt: string;
}

export interface AgentState {
  agentId: string;
  codeName: string;
  status: string;
  charterVersion: string;
  toolsVersion: string;
  secretsHash: string | null;
  lastRefreshAt: string | null;
  lastProvisionAt: string | null;
  lastDriftCheckAt: string | null;
  lastSecretsProvisionAt: string | null;
  // ENG-6932: gateway is OpenClaw/NemoClaw-only and those frameworks are
  // removed; Claude Code has no gateway process so these are never set by the
  // manager. Kept optional (not removed) because sibling CLI surfaces
  // (commands/manager.ts, commands/manager-watch.tsx, lib/watchdog.ts) and the
  // persisted manager-state.json still read them; they render as "no gateway".
  gatewayPort?: number | null;
  gatewayPid?: number | null;
  gatewayRunning?: boolean;
  acpSessions: AcpSessionSummary[];
  // ENG-4947: latest `restart_requested_at` (from /host/agents) that this
  // manager has already acted on. Persists across manager restarts so a
  // dashboard-issued restart fires exactly once even if we crash between
  // poll and respawn.
  lastRestartProcessedAt?: string | null;
}

interface ManagerState {
  pid: number;
  startedAt: string;
  lastPollAt: string | null;
  pollCount: number;
  errorCount: number;
  agents: AgentState[];
  // ENG-5441: serialised circuit-breaker trips keyed by code_name. Only
  // tripped breakers are persisted — the in-window event log is per-
  // worker and rebuilds from scratch on manager restart. A previously-
  // tripped agent stays tripped across a manager restart so an operator
  // can rely on the trip surviving a `systemctl restart agt-manager`.
  circuitBreakerTrips?: Record<string, TripState>;
  // ENG-6088: spent auto-resume credits keyed by code_name. Persisted so
  // a manager restart can't hand a re-tripped agent a fresh credit (the
  // exact loophole that would turn "resume once" into a resume loop).
  circuitBreakerAutoResumes?: Record<string, AutoResumeRecord>;
  // ENG-6692: the last hosts.update_requested_at this manager has CONSUMED (ran
  // its one-shot "Update CLI now" self-update for). Host-level twin of
  // AgentState.lastRestartProcessedAt — persisted so the forced update fires
  // exactly once per distinct timestamp even across a manager restart (the
  // self-update itself restarts the manager, so this MUST survive it). Echoed
  // back on the heartbeat as update_request_processed_at so the API clears the
  // column.
  lastUpdateRequestProcessedAt?: string | null;
}

/**
 * ENG-5008: stamp `lastRestartProcessedAt` on the fresh AgentState rows
 * built this cycle, carrying forward any prior ack from `state.agents`
 * when no new restart signal arrived this poll.
 *
 * Pre-ENG-5008 only `restartAcks` (fresh signals) populated the field, so
 * every cycle where a previously-acked agent had no new signal dropped
 * the ack — and the very next cycle saw `prev?.lastRestartProcessedAt =
 * null`, the timestamp comparison failed, and the manager re-fired the
 * kill. That's the loop that pinned vigil/vera/stirling for hours on
 * 2026-05-12.
 *
 * Exported for unit testing — caller wires it into the poll loop after
 * `processAgent` has filled `agentStates`.
 */
export function applyRestartAcks(args: {
  agentStates: AgentState[];
  priorAgents: AgentState[];
  restartAcks: Map<string, string>;
}): boolean {
  const { agentStates, priorAgents, restartAcks } = args;
  if (agentStates.length === 0) return false;

  const priorAcks = new Map<string, string>();
  for (const prev of priorAgents) {
    if (prev.lastRestartProcessedAt) {
      priorAcks.set(prev.agentId, prev.lastRestartProcessedAt);
    }
  }
  // CodeRabbit (PR #953): return whether anything actually changed in
  // this cycle's agentStates rows so the caller can force a crash-safe
  // immediate write on EITHER a fresh ack OR a carry-forward repair.
  // Pre-fix, the immediate write was keyed off `restartAcks.size > 0`,
  // which missed the case where a prior write had failed silently and
  // this cycle is restoring the ack only via carry-forward.
  let changed = false;
  for (const ack of agentStates) {
    const freshAck = restartAcks.get(ack.agentId);
    const carryForward = priorAcks.get(ack.agentId);
    // Fresh ack from this cycle wins (caller only adds to restartAcks
    // when the requested timestamp is strictly newer than any prior).
    // When no fresh ack arrived, preserve the prior ack so the
    // already-acked restart doesn't re-fire next poll.
    if (freshAck) {
      if (ack.lastRestartProcessedAt !== freshAck) changed = true;
      ack.lastRestartProcessedAt = freshAck;
    } else if (carryForward) {
      if (ack.lastRestartProcessedAt !== carryForward) changed = true;
      ack.lastRestartProcessedAt = carryForward;
    }
  }
  return changed;
}

// Event types (formerly IPC messages, now just internal events)
type ManagerEvent =
  | { type: 'ready' }
  | { type: 'state-update'; state: ManagerState }
  | { type: 'provisioned'; agentId: string; codeName: string }
  | { type: 'drift-detected'; agentId: string; codeName: string; files: string[] }
  | { type: 'error'; message: string }
  | { type: 'shutdown' };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Channel-process zombie sweep (ENG-4453). Throttled to avoid shelling out
// to `ps eww` every poll. Default 5 min, overridable via env for tuning or
// stress tests. AGT_CHANNEL_SWEEP_DRY_RUN=1 logs kill targets without sending
// SIGTERM — operators should flip this on first and verify the targets before
// letting the sweep act.
const CHANNEL_SWEEP_INTERVAL_MS = (() => {
  const raw = parseInt(process.env['AGT_CHANNEL_SWEEP_INTERVAL_MS'] ?? '', 10);
  if (!Number.isFinite(raw)) return 5 * 60 * 1000;
  // Clamp overrides up to the 30s floor rather than falling back to default
  // — an operator who sets =10000 clearly intends a short interval, not the
  // five-minute silent fallback.
  return Math.max(raw, 30_000);
})();
const CHANNEL_SWEEP_DRY_RUN = process.env['AGT_CHANNEL_SWEEP_DRY_RUN'] === '1';
let lastChannelSweepAt = 0;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let config: WorkerConfig | null = null;
let running = false;
let pollTimer: ReturnType<typeof setTimeout> | null = null;

// ENG-7273: the fast, independent liveness heartbeat timer. The poll cycle
// stamps liveness only once per iteration, so a busy host's long cycle (or the
// multi-minute re-provision after a restart) can starve last_seen_at past the
// webapp's 2-minute offline threshold and false-flag every agent offline. This
// setInterval posts a minimal /host/heartbeat on its own ~30s cadence, fully
// decoupled from the heavy cycle. `livenessInFlight` guards against a slow POST
// stacking on the next tick.
let livenessTimer: ReturnType<typeof setInterval> | null = null;
let livenessInFlight = false;
const LIVENESS_HEARTBEAT_INTERVAL_MS = 30_000;

// ENG-4659: when persistent-session reports a session unhealthy, the
// pane log tail is included in the operator-facing log line. Keep the
// inline preview short — operators reading the log don't need a full
// 20-line dump on every restart, just enough to identify the failure
// pattern. The full tail is still on disk at ~/.augmented/<codeName>/pane.log.
const PANE_TAIL_PREVIEW_LINES = 5;
/**
 * ENG-4923: extract `multi_agent.telegram_peers[]` from a CHARTER's raw
 * frontmatter so the manager can pass the resolved roster as the
 * TELEGRAM_PEERS env var on the per-agent MCP child. The shape on
 * disk is `{ code_name, bot_id }`; we add an empty `agent_id` field
 * because the classifier's parsePeersEnv requires it (downstream
 * runtime — ENG-4904 — can resolve agent_id from the API when
 * building the peer preamble).
 *
 * Lenient: any parse / shape error returns []. The CHARTER lint
 * rule from ENG-4901 already validates the shape at write time;
 * a runtime parse failure here just means the classifier sees no
 * declared peers, which falls through to its existing
 * `unknown_peer` drop path. Better than crashing the manager tick.
 */
/**
 * ENG-4935 / ENG-4929 §4.0: gate-path metadata for each CHARTER-declared
 * peer. The classifier in packages/mcp admits a peer message only when
 * the peer resolves AND `gate_path` is non-null; a null gate path
 * surfaces in telemetry as `cross_team_grant_missing` (cross-org pair
 * without a live grant, or an org in `consent_required` mode with a
 * peer that has neither same-team membership nor a grant).
 *
 *  - `same_team`               — peer's bot_id is on this team's roster.
 *  - `intra_org_unrestricted`  — peer is foreign-team, but the org's
 *                                cross_team_peer_intra_org toggle is
 *                                `unrestricted`, so no grant required.
 *  - `grant:<grant_id>`        — peer is foreign-team and a live grant
 *                                authorises it. Validated against the
 *                                inbound grants snapshot.
 *  - `null`                    — gate missing: cross-org without grant
 *                                or charter references a stale grant.
 *                                Charter still lists it (so the UI can
 *                                tell the user) but classifier drops.
 */
type PeerGatePath = 'same_team' | 'intra_org_unrestricted' | `grant:${string}` | null;

export interface CharterPeerWithGate {
  code_name: string;
  bot_id: number;
  agent_id: string;
  gate_path: PeerGatePath;
}

/**
 * ENG-4970 / ENG-4974: Slack mirror of CharterPeerWithGate. The runtime
 * Slack classifier (ENG-4936) expects entries keyed on `bot_user_id`
 * (Slack `U…` identity), so the manager extractor produces this shape.
 */
export interface CharterSlackPeerWithGate {
  code_name: string;
  bot_user_id: string;
  agent_id: string;
  gate_path: PeerGatePath;
}

export interface CharterPeerGateContext {
  /** Bot_ids of same-team agents (from /host/refresh team_peer_bot_ids). */
  teamPeerBotIds?: ReadonlyArray<number>;
  /**
   * ENG-4970 / ENG-4973: Slack `U…` user_ids of same-team agents (from
   * /host/refresh team_peer_slack_user_ids). Parallel to teamPeerBotIds.
   */
  teamPeerSlackUserIds?: ReadonlyArray<string>;
  /**
   * Inbound grants snapshot (from /host/refresh cross_team_grants.inbound).
   * `granted_agent_bot_id` is denormalised so we can match a grant by the
   * bot_id the CHARTER cites without a second lookup. ENG-4973 adds the
   * parallel `granted_agent_slack_user_id` so the Slack extractor can
   * match the same way.
   */
  crossTeamGrants?: ReadonlyArray<{
    grant_id: string;
    granted_agent_bot_id: number | null;
    /** ENG-4970 / ENG-4973: Slack `U…` user_id of granted_agent_id. */
    granted_agent_slack_user_id?: string | null;
    revoked_at: string | null;
    expires_at: string | null;
  }>;
  /** Org-level toggle from organization.cross_team_peer_intra_org. */
  crossTeamPeerIntraOrg?: 'unrestricted' | 'consent_required';
  /** Time source for expiry comparison. Defaults to new Date(). */
  now?: () => Date;
}

/**
 * ENG-4923 / ENG-4935: extract `multi_agent.telegram_peers[]` from a
 * CHARTER's raw frontmatter and resolve a gate_path per peer.
 *
 * Lenient parsing — any shape error returns []. The CHARTER lint rules
 * (ENG-4901 + ENG-4938) already validate at write time; a runtime parse
 * failure here just means the classifier sees no declared peers, which
 * falls through to its existing `unknown_peer` drop path.
 *
 * When `gateContext` is omitted (e.g. older /host/refresh response that
 * predates ENG-4935), every peer's gate_path is set to `same_team` —
 * which preserves the pre-ENG-4935 behaviour where every declared peer
 * was admitted unconditionally.
 */
export function extractCharterTelegramPeers(
  rawContent: string,
  gateContext?: CharterPeerGateContext,
): CharterPeerWithGate[] {
  if (!rawContent || rawContent.length === 0) return [];
  try {
    const parsed = extractFrontmatter(rawContent);
    const frontmatter = parsed.frontmatter as
      | { multi_agent?: { telegram_peers?: Array<{ code_name?: unknown; bot_id?: unknown; cross_team_grant_id?: unknown }> } }
      | null;
    const peers = frontmatter?.multi_agent?.telegram_peers;
    if (!peers || !Array.isArray(peers)) return [];
    const teamBotIds = new Set(gateContext?.teamPeerBotIds ?? []);
    const grants = gateContext?.crossTeamGrants ?? [];
    const intraOrg = gateContext?.crossTeamPeerIntraOrg ?? 'unrestricted';
    const now = (gateContext?.now ?? (() => new Date()))();
    const out: CharterPeerWithGate[] = [];
    for (const p of peers) {
      if (
        !p ||
        typeof p !== 'object' ||
        typeof p.code_name !== 'string' ||
        typeof p.bot_id !== 'number' ||
        !Number.isInteger(p.bot_id) ||
        p.bot_id <= 0
      ) {
        continue;
      }
      const grantId =
        typeof p.cross_team_grant_id === 'string' && p.cross_team_grant_id.length > 0
          ? p.cross_team_grant_id
          : null;
      let gate: PeerGatePath = null;

      if (gateContext === undefined) {
        // Backwards-compat: no gate context = pre-ENG-4935 manager; admit
        // every declared peer as same-team to preserve the old contract.
        gate = 'same_team';
      } else if (teamBotIds.has(p.bot_id)) {
        // CodeRabbit on PR #868: same-team wins over a CHARTER-cited
        // grant_id. Otherwise a stale cross_team_grant_id (left over from
        // when the peer was on another team) would downgrade a current
        // same-team peer to `null` on revoke/expiry. Team membership is
        // the strongest signal — trust it.
        gate = 'same_team';
      } else if (grantId) {
        // Charter cites a cross-team grant — must match a live grant in the
        // inbound snapshot, and the grant must authorise the bot_id the
        // charter declared. Lint covers this at write time; the runtime
        // re-checks because the grant may have been revoked since.
        const grant = grants.find((g) => g.grant_id === grantId);
        if (
          grant &&
          !grant.revoked_at &&
          (!grant.expires_at || new Date(grant.expires_at) > now) &&
          grant.granted_agent_bot_id === p.bot_id
        ) {
          gate = `grant:${grantId}` as const;
        } else {
          gate = null;
        }
      } else if (intraOrg === 'unrestricted') {
        // Foreign-team peer without a grant — admitted only because the
        // org is in unrestricted mode. Flipping the toggle to
        // consent_required will reduce this to `null` and the classifier
        // will start dropping with `cross_team_grant_missing`.
        gate = 'intra_org_unrestricted';
      } else {
        gate = null;
      }
      out.push({ code_name: p.code_name, bot_id: p.bot_id, agent_id: '', gate_path: gate });
    }
    return out;
  } catch {
    return [];
  }
}

/**
 * ENG-4970 / ENG-4974: Slack parallel of `extractCharterTelegramPeers`.
 * Reads `multi_agent.slack_peers[]` from a CHARTER's frontmatter and
 * resolves a gate_path per peer using the same four-state model
 * (same_team / intra_org_unrestricted / grant:<id> / null).
 *
 * Slack-specific differences from the Telegram extractor:
 *   - Keyed on `bot_user_id` (string `U…`) instead of `bot_id` (integer)
 *   - Same-team detection uses `teamPeerSlackUserIds` from /host/refresh
 *   - Grant match compares `granted_agent_slack_user_id` (ENG-4973)
 *
 * Backwards-compat: when `gateContext` is omitted, every peer is admitted
 * as `same_team` — matches the Telegram extractor's pre-ENG-4935
 * behaviour for managers that haven't picked up the new refresh fields.
 */
export function extractCharterSlackPeers(
  rawContent: string,
  gateContext?: CharterPeerGateContext,
): CharterSlackPeerWithGate[] {
  if (!rawContent || rawContent.length === 0) return [];
  try {
    const parsed = extractFrontmatter(rawContent);
    const frontmatter = parsed.frontmatter as
      | {
          multi_agent?: {
            slack_peers?: Array<{
              code_name?: unknown;
              bot_user_id?: unknown;
              cross_team_grant_id?: unknown;
            }>;
          };
        }
      | null;
    const peers = frontmatter?.multi_agent?.slack_peers;
    if (!peers || !Array.isArray(peers)) return [];
    const teamUserIds = new Set(gateContext?.teamPeerSlackUserIds ?? []);
    const grants = gateContext?.crossTeamGrants ?? [];
    const intraOrg = gateContext?.crossTeamPeerIntraOrg ?? 'unrestricted';
    const now = (gateContext?.now ?? (() => new Date()))();
    const out: CharterSlackPeerWithGate[] = [];
    for (const p of peers) {
      if (
        !p ||
        typeof p !== 'object' ||
        typeof p.code_name !== 'string' ||
        typeof p.bot_user_id !== 'string' ||
        p.bot_user_id.length === 0
      ) {
        continue;
      }
      const grantId =
        typeof p.cross_team_grant_id === 'string' && p.cross_team_grant_id.length > 0
          ? p.cross_team_grant_id
          : null;
      let gate: PeerGatePath = null;

      if (gateContext === undefined) {
        gate = 'same_team';
      } else if (teamUserIds.has(p.bot_user_id)) {
        // Same-team wins over a stale CHARTER grant_id — same precedence
        // rule as the Telegram extractor (CodeRabbit on PR #868).
        gate = 'same_team';
      } else if (grantId) {
        const grant = grants.find((g) => g.grant_id === grantId);
        if (
          grant &&
          !grant.revoked_at &&
          (!grant.expires_at || new Date(grant.expires_at) > now) &&
          (grant.granted_agent_slack_user_id ?? null) === p.bot_user_id
        ) {
          gate = `grant:${grantId}` as const;
        } else {
          gate = null;
        }
      } else if (intraOrg === 'unrestricted') {
        gate = 'intra_org_unrestricted';
      } else {
        gate = null;
      }
      out.push({
        code_name: p.code_name,
        bot_user_id: p.bot_user_id,
        agent_id: '',
        gate_path: gate,
      });
    }
    return out;
  } catch {
    return [];
  }
}

function truncateForLog(s: string): string {
  const lines = s.split('\n').filter((l) => l.length > 0);
  return lines.slice(-PANE_TAIL_PREVIEW_LINES).map((l) => `    | ${l}`).join('\n');
}
// CodeRabbit (PR #618): only embed the raw pane tail in manager.log
// when the failure signature is one we recognise as a safe-to-quote
// Claude error string. For anything else (including 'unknown' tails)
// log only the sha256 prefix and point operators at the on-disk file.
// Add new signatures here only after auditing what Claude actually
// prints for that failure mode.
const KNOWN_SAFE_TAIL_SIGNATURES = new Set<string>(['session_id_in_use']);

/**
 * ENG-5090: gate the revoked-agent cleanup block in processAgent so it
 * only runs on the first tick after the status flips to `revoked`.
 * Extracted as an exported pure function so it has a regression-pin
 * test without dragging in the full processAgent surface (matches the
 * extract-and-test pattern in extractCharterTelegramPeers etc.).
 *
 * @param previousKnownStatus the value of `agentState.knownStatuses.get(agent.agent_id)`
 *   captured at the top of the current processAgent call — `undefined`
 *   means the manager has never seen this agent before this tick.
 * @returns true when the caller should skip the cleanup side effects
 *   (gateway stop, tmux kill, fs cleanup, cache purge) because they
 *   already ran on a prior tick.
 */
export function shouldSkipRevokedCleanup(previousKnownStatus: string | undefined): boolean {
  return previousKnownStatus === 'revoked';
}

/**
 * ENG-5090: residual check that runs after `shouldSkipRevokedCleanup`
 * says "yes, we already cleaned this up on a prior tick". A couple of
 * the cleanup ops in processAgent's revoked branch — `stopGatewayIfRunning`
 * (manager-worker.ts:2055) and `cleanupAgentFiles` (manager-worker.ts:8272)
 * — wrap their inner work in try/catch and log on failure rather than
 * rethrowing. That means a failure on the first revoked pass can still
 * reach `agentState.knownStatuses.set('revoked')` and trip the short-circuit on
 * subsequent ticks while real residuals (running gateway, allocated
 * port, surviving provision dir) remain.
 *
 * Treat the obvious residuals as a signal to fall back into the full
 * cleanup path. The check is cheap: an `isGatewayRunning` adapter call
 * (no-op `false` for adapters without a gateway, e.g. ClaudeCode), an
 * in-memory JSON read for the port file, and a single `existsSync` for
 * the provision dir.
 *
 * `gatewayRunning` is essential because the revoked branch calls
 * `freePort` and `cleanupAgentFiles` *after* `stopGatewayIfRunning`,
 * and those steps succeed even when the stop failed silently. Without
 * the live-gateway check the port and dir residuals are both wiped to
 * false on the next tick and the short-circuit would leave a zombie
 * gateway running forever — the exact bug CodeRabbit on PR #1054 caught.
 */
export function hasRevokedResiduals(state: {
  gatewayRunning: boolean;
  portAllocated: boolean;
  provisionDirExists: boolean;
}): boolean {
  return state.gatewayRunning || state.portAllocated || state.provisionDirExists;
}

// Track last-known versions + hashes per agent to detect changes.
// ADR-0011 PR-A: moved into `agentState` (./manager/agent-state.ts).

// ENG-4807 / CodeRabbit on PR #773: coalesce per-agent session-restart
// timers so two hot-reload triggers in the same tick (e.g. channel set
// changed AND MCP toolkit changed) don't schedule two stops back-to-back.
// Without this, the first stop kills the original session and the second
// stop fires a few seconds later — by which time the persistent-session
// healthcheck has already respawned, and the second stop kills the
// freshly-respawned session.
// ENG-6491: track the timer AND the reason it was scheduled with, so the
// coalescer can refuse to DOWNGRADE a window-exempt 'channel-set-change' restart
// to a window-gated 'hot-reload-mcp' one. A channel ADD also writes .mcp.json +
// channel secrets, so the same-poll drift watchers schedule a generic hot-reload
// restart that coalesces over the channel-set timer — without this, the exempt
// reason would be silently lost and the add would wait for the maintenance window
// after all (the exact coalescing seen in valai/gccm manager.log, ENG-6486).
interface PendingRestart {
  timer: ReturnType<typeof setTimeout>;
  reason: string;
  breakerReason: RestartReason;
  // ENG-6491: fire-time side effect (channel-add DM-notice marker write).
  beforeStop?: () => void;
}
const pendingSessionRestarts = new Map<string, PendingRestart>();

// ENG-6174: per-agent record of a hot-reload-MCP restart whose respawn we are
// still trying to VERIFY. Populated when a hot-reload restart's stop fires (see
// scheduleSessionRestart) and consumed each poll by verifyPendingRestarts(),
// which confirms the session respawned healthy — or, if it never does, surfaces
// a bounded WARN→ERROR escalation instead of the pre-fix silent fire-and-forget.
// Keyed by code_name. Cleared on verification success, on escalation, and on any
// non-hot-reload teardown (cancelPendingSessionRestart).
const pendingRestartVerifications = new Map<string, PendingRestartVerification>();

// ENG-5441: per-manager circuit breaker for runaway session restart loops
// (today's Scout incident: day-rollover repeated every ~55s for hours).
// Trip state is mirrored into manager-state.json — see saveBreakerState /
// hydrateBreakerFromState below — so a manager restart can't accidentally
// clear a tripped breaker and revive the loop the operator stopped.
const restartBreaker = new RestartBreaker();
// Tracks which trips we've already told the API about, so the same trip
// doesn't POST /host/circuit-breaker/trip on every poll.
const reportedTrips = new Map<string, number>();

// ENG-6123: detects when an agent's managed desired-config set is OSCILLATING
// (flipping back and forth between membership states many times in a window)
// rather than changing once. While a channel flaps, the manager suppresses the
// integration-update / mcp-update notice injection and the session restart —
// the two per-flip token burns that took valai to ~1,550 opus sessions in 4
// days when two Composio integrations' `status` flapped active↔configured every
// ~60s. The `.mcp.json` itself still converges (file writes are cheap); only
// the opus-facing notice + reload are held until the set settles.
const mcpFlapDampener = new McpFlapDampener();

// ENG-7239: cool-off for the breaker exemption below. A 'managed-mcp-churn'
// restart fired while the agent's managed-MCP set is flapping, or within this
// window after it last flapped, restarts the session but does NOT count toward
// the ENG-5441 circuit breaker, so managed-MCP churn can't auto-pause the whole
// agent (Kylie/dig, 2026-06-29). Same posture as ENG-5547's single-dead-MCP
// reaper exemption.
const FLAP_BREAKER_COOLDOWN_MS = readFlapEnvNumber(
  'AGT_MCP_FLAP_BREAKER_COOLDOWN_MS',
  DEFAULT_FLAP_BREAKER_COOLDOWN_MS,
);

// ENG-6123: report a config-churn restart event to the API so the
// agent-heartbeat-monitor cron can aggregate per-agent counts into the
// `McpConfigChurnRestartsHourly` CloudWatch metric + `config-churn-flapping`
// alarm — the missing detection twin of the reaper-flapping alarm (which only
// counts DYING MCPs, never CHURNING membership, so valai ran 4 days unpaged).
// Fire-and-forget: a missed POST only under-counts the metric; suppression
// still works locally off the dampener's in-memory state.
function recordConfigChurnEvent(
  agentId: string,
  codeName: string,
  channel: string,
  signature: string,
): void {
  api.post('/host/mcp-config-churn', {
    agent_id: agentId,
    code_name: codeName,
    channel,
    signature,
  }).catch((err) => {
    log(
      `[mcp-flap-dampener] failed to record config-churn event for '${codeName}' (${channel}) (ENG-6123): ${(err as Error).message} — local suppression still active; CloudWatch metric will under-count`,
    );
  });
}

// ENG-6184 (epic ENG-6181): surface a single mcp-presence-reaper action (a
// declared-MCP-server restart, or an optional-channel quarantine) to the
// control plane, which opens a `reaper_action` alert via POST /host/reaper-event
// → alert-pager. Pre-fix a single kill was invisible — an operator learned of
// reaper activity only if restarts crossed the per-hour reaper-flapping
// CloudWatch alarm (ENG-5286).
//
// Scoped DELIBERATELY to the mcp-presence-reaper: its actions mean "a declared
// MCP died / a channel is permanently dead", a genuine incident. The
// stale-mcp-reaper (credential-rotation respawn) and orphan-channel-mcp-reaper
// (post-session-exit cleanup) are routine maintenance and intentionally do NOT
// alert — paging them would reintroduce the very noise epic ENG-6181 removes.
//
// Fire-and-forget: the reaper's correctness must not depend on this, and the
// endpoint dedupes per (agent, reaper) so a flapping reaper refreshes one open
// alert (the sweeper closes it a quiet window after the flapping stops) rather
// than storming pages.
async function reportReaperEvent(
  agentId: string,
  reaper: string,
  action: 'restart' | 'quarantine' | 'kill',
  reason: string,
): Promise<void> {
  try {
    await api.post('/host/reaper-event', {
      agent_id: agentId,
      reaper,
      action,
      reason,
    });
  } catch (err) {
    log(
      `[${reaper}] failed to report reaper-event (ENG-6184): ${(err as Error).message} — local reap still proceeded; the alert may be missed`,
    );
  }
}

function recordRestartForBreaker(codeName: string, reason: RestartReason): void {
  // ENG-7239: a 'managed-mcp-churn' restart (a .mcp.json drift whose changed
  // server keys are all managed-MCP/brokered toolkits) that fires while the
  // agent's managed-MCP set is flapping, or within the cool-off after it last
  // flapped, restarts the session but is NOT counted toward the breaker. The
  // dampener already holds most flap-driven restarts (ENG-6123), but its eager
  // single-poll recovery (ENG-6512) can briefly clear `isFlapping`
  // mid-oscillation, letting one flip slip through; counting it tripped the
  // breaker and auto-paused Kylie (org dig) unreachable on every channel.
  // Scoped to the 'managed-mcp-churn' reason (CodeRabbit PR #2870) so a
  // co-occurring channel-credential rotation or channel-set drift, which shares
  // the cool-off but is a different cause, still counts toward the breaker.
  // Mirrors ENG-5547's single-dead-MCP reaper exemption: restart, don't count.
  if (
    reason === 'managed-mcp-churn' &&
    mcpFlapDampener.flappedWithin(codeName, FLAP_CHANNEL_MANAGED_MCP, FLAP_BREAKER_COOLDOWN_MS)
  ) {
    log(
      `[mcp-flap-dampener] '${codeName}' managed-MCP churn restart NOT counted toward circuit breaker, ` +
      `managed-MCP set flapped within ${Math.round(FLAP_BREAKER_COOLDOWN_MS / 1000)}s (ENG-7239)`,
    );
    return;
  }
  const result = restartBreaker.record(codeName, reason);
  if (!result.tripped || !result.trip) return;
  const trip = result.trip;
  log(
    `[persistent-session-decision] agent=${codeName} decision=circuit-tripped ` +
    `detail=${JSON.stringify(trip.statusMessage)} (ENG-5441)`,
  );
  // Fire-and-forget — a missed API call leaves the trip in `reportedTrips`
  // unset, so `maybeReportCurrentTrip` will retry on the next poll.
  // The breaker keeps gating spawns locally regardless.
  maybeReportCurrentTrip(codeName);
}

async function reportCircuitTripped(codeName: string, trip: TripState): Promise<void> {
  // Skip the API call if we've already reported this exact trip (same
  // trippedAt timestamp). Without this, every subsequent poll would
  // re-POST until the operator clears, spamming the audit log.
  if (reportedTrips.get(codeName) === trip.trippedAt) return;
  // Look up agent_id from the local cache (populated by processAgent on
  // every poll). Cold-start manager that hydrated a trip but hasn't
  // observed the agent in /host/agents yet will hit this — leave
  // `reportedTrips` untouched so `maybeReportCurrentTrip` retries once
  // the agent_id lands. CodeRabbit on PR #1302.
  const agentId = agentState.codeNameToAgentId.get(codeName);
  if (!agentId) throw new Error(`no agent_id known yet for '${codeName}' — will retry next poll`);
  // CodeRabbit on PR #1302: do NOT mark `reportedTrips` until the POST
  // succeeds. Pre-fix, a transient API failure (5xx, JWT expiry race)
  // would stamp the timestamp anyway and silently swallow the trip
  // forever; the breaker would still gate spawns locally but the
  // dashboard / status_message would stay stale.
  await api.post('/host/circuit-breaker/trip', {
    agent_id: agentId,
    status_message: trip.statusMessage,
    tripped_at: new Date(trip.trippedAt).toISOString(),
    events: trip.eventsAtTrip.map((e) => ({ reason: e.reason, at: new Date(e.at).toISOString() })),
  });
  reportedTrips.set(codeName, trip.trippedAt);
}

// Idempotent helper — call after `agentState.codeNameToAgentId` is populated for an
// agent to retry a pending trip report. Used both on the first trip and
// on the first poll after a manager restart (where the trip was
// hydrated from manager-state.json before any /host/agents call).
function maybeReportCurrentTrip(codeName: string): void {
  const trip = restartBreaker.getTrip(codeName);
  if (!trip) return;
  if (reportedTrips.get(codeName) === trip.trippedAt) return;
  reportCircuitTripped(codeName, trip).catch((err) => {
    log(`[circuit-breaker] Failed to report trip for '${codeName}' to API: ${(err as Error).message}`);
  });
}

// ENG-6088: bounded auto-resume. One credit per trip cycle — markers are
// persisted into manager-state.json (circuitBreakerAutoResumes) so a
// manager restart can't hand a re-tripped agent a fresh credit. Cleared on
// operator resume (deliberate human intent re-arms the credit) or by the
// AGT_AUTO_RESUME_BACKOFF_RESET_MS TTL inside decideAutoResume.
let autoResumeMarkers = new Map<string, AutoResumeRecord>();
// POSTs in flight (or acked but not yet observed as active): stops a 5s
// poll tick from duplicating the request mid-round-trip. NOT the
// self-vs-operator discriminator — that's marker freshness (see the
// paused→active watcher), which survives manager restarts where this
// in-memory set does not.
const autoResumeInFlight = new Set<string>();
// How long after issuing an auto-resume a paused→active transition is
// attributed to US rather than an operator. Sound because after OUR
// resume the agent is active — an operator resume can only follow a
// LATER re-pause (re-trip quiet-period timescale ≫ this window).
const AUTO_RESUME_SELF_WINDOW_MS = 120_000;
// Per-(codeName, trippedAt) one-shot logging for skip reasons, so a
// 5s poll loop doesn't write 'not-quiet-yet' to manager.log 360×/hour.
const autoResumeLoggedSkips = new Map<string, string>();
// Trips the server answered `not-eligible` for (operator/cost pause owns
// the agent). Keyed `${codeName}:${trippedAt}` so the stand-down dies with
// the trip — a NEW trip gets a fresh evaluation. Without this tombstone a
// not-eligible answer would re-POST every poll tick.
const autoResumeStandDowns = new Set<string>();
// ENG-6392: which integrations the manager's connectivity probe saw go down and
// later read ok again — diffed into `recovered_dependencies` on auto-resume so
// the server can fire the ENG-6384 "X is back" confirmation. Populated by the
// connectivity-probe round; consumed + cleared when a trip clears.
const dependencyRecoveryLedger = new DependencyRecoveryLedger();
// ENG-6392: unstable latches whose manager-DM report (POST /host/circuit-breaker/
// unstable-latch) has NOT yet landed a response. Keyed `${codeName}:${trippedAt}`.
// A latch-unstable stands the trip down (no "next trip" to carry a retry), and
// the agent stays paused — so a transient POST failure would otherwise lose the
// manager notification permanently. We keep the key here until a POST succeeds
// (the endpoint is idempotent per (agent, trip)) and re-attempt on each poll.
const unstableLatchUnreported = new Set<string>();
// Guards against stacking a second report POST while one is in flight (the poll
// fires every few seconds; a slow POST must not fan out into duplicates).
const unstableLatchReportInFlight = new Set<string>();

// ENG-6146: code_names the manager has halted because their scope is killed
// (the API overlaid status:'paused' + a kill_switch marker). Used to announce a
// resume notice when the switch clears (paused → active transition). Added in
// the paused branch, drained on resume.
const killPausedCodeNames = new Set<string>();

// ENG-6316: agents reliably announce "I'm back online" after these
// manager-driven session restarts — on `--resume` they're responding to the
// "your manager will restart your session shortly" notice injected just before
// the restart. Left to themselves they prefix that announcement with a 🟢
// green-light emoji. Standardize it at the exact message the agent reacts to:
// open the announcement with a 👋 wave instead. Appended to every pre-restart
// notice below (all 'system'-typed, all immediately followed by a restart).
export const BACK_ONLINE_GREETING_GUIDANCE =
  ' When you reconnect, if you tell anyone you are back, start that message with a 👋 wave emoji and do not use a 🟢 green-light emoji.';

function maybeAutoResume(agent: { agent_id: string; code_name: string }): void {
  const codeName = agent.code_name;
  // ENG-6383/ENG-6395: when the `resume-reconciler` flag is on it SUPERSEDES the
  // blind ENG-6088 quiet-timer below. Resolved via the host flag store
  // (env override `AGT_RESUME_RECONCILER_ENABLED` → heartbeat → default off), so
  // the canary bake flips per-host centrally. Fire-and-forget (maybeResumeReconcile
  // owns its own in-flight guard + fail-safe holds); the blind path stays the
  // flag-off fallback so a bad rollout reverts by flipping the flag.
  if (hostFlagStore().getBoolean('resume-reconciler')) {
    void maybeResumeReconcile(agent);
    return;
  }
  // In-flight guard. Without it, a second poll tick during the POST
  // round-trip would duplicate the request (harmless server-side — the
  // duplicate lands `not-eligible` — but it would tombstone the trip and
  // log a misleading stand-down).
  if (autoResumeInFlight.has(codeName)) return;
  const trip = restartBreaker.getTrip(codeName);
  if (trip && autoResumeStandDowns.has(`${codeName}:${trip.trippedAt}`)) return;
  const decision = decideAutoResume({
    trip,
    marker: autoResumeMarkers.get(codeName),
    config: readAutoResumeConfig(),
    now: Date.now(),
  });

  if (decision.action === 'skip') {
    // 'disabled' and 'no-trip' are steady-state — never log. The two
    // interesting reasons log once per (trip, reason) transition.
    if ((decision.reason === 'already-auto-resumed' || decision.reason === 'not-quiet-yet') && trip) {
      const key = `${codeName}:${trip.trippedAt}`;
      if (autoResumeLoggedSkips.get(key) !== decision.reason) {
        autoResumeLoggedSkips.set(key, decision.reason);
        const detail = decision.reason === 'not-quiet-yet'
          ? ` (~${Math.ceil((decision.remainingMs ?? 0) / 60_000)}min remaining)`
          : ' — credit spent for this cycle; operator resume re-arms it';
        log(`[auto-resume] agent=${codeName} decision=skip reason=${decision.reason}${detail} (ENG-6088)`);
      }
    }
    return;
  }

  // Issue the resume. Fire-and-forget like reportCircuitTripped — a failed
  // POST leaves no marker, so the next poll retries. The server's RPC only
  // flips status='paused' AND paused_reason='circuit_breaker' rows, so a
  // race with an operator action degrades to a `resumed:false` no-op.
  const trippedAt = trip!.trippedAt;
  log(
    `[auto-resume] agent=${codeName} decision=resume quiet=${Math.round(decision.quietMs / 60_000)}min ` +
    `trippedAt=${new Date(trippedAt).toISOString()} (ENG-6088)`,
  );
  autoResumeInFlight.add(codeName);
  api.post<{ ok: boolean; resumed?: boolean; reason?: string }>('/host/circuit-breaker/auto-resume', {
    agent_id: agent.agent_id,
    quiet_ms: decision.quietMs,
    tripped_at: new Date(trippedAt).toISOString(),
  }).then((res) => {
    autoResumeInFlight.delete(codeName);
    if (res.resumed) {
      // Spend the credit and clear the local trip so the spawn gate opens
      // on the next poll. reportedTrips is cleared so a NEW trip after
      // this resume posts to the API again.
      autoResumeMarkers.set(codeName, { trippedAt, autoResumedAt: Date.now() });
      restartBreaker.clear(codeName);
      reportedTrips.delete(codeName);
      // ENG-6392: the trip cleared — reset the recovery diff so it doesn't
      // linger into the next cycle (this blind path sends no
      // recovered_dependencies, but the ledger must still not leak across trips).
      dependencyRecoveryLedger.clear(codeName);
      // Persist the spent credit NOW rather than waiting for the next
      // poll's end-of-cycle snapshot — a worker exit in that window would
      // drop the marker and hand a re-tripped agent a fresh credit after
      // restart, breaking the bounded-resume guarantee (CodeRabbit on
      // this PR).
      state = {
        ...state,
        circuitBreakerTrips: restartBreaker.serialize(),
        circuitBreakerAutoResumes: Object.fromEntries(autoResumeMarkers.entries()),
      };
      send({ type: 'state-update', state });
      log(`[auto-resume] agent=${codeName} resumed — re-trip within backoff window will stay paused (ENG-6088)`);
    } else {
      // not-eligible: an operator/cost pause owns this agent, or someone
      // already resumed it. Stand down for THIS trip — the tombstone stops
      // the per-poll retry; the status watcher reconciles the rest. Keep
      // the local trip; if the agent is genuinely active again the
      // paused→active transition clears it (and the tombstone key dies
      // with the trip).
      autoResumeStandDowns.add(`${codeName}:${trippedAt}`);
      log(`[auto-resume] agent=${codeName} not applied (reason=${res.reason ?? 'unknown'}) — standing down for this trip (ENG-6088)`);
    }
  }).catch((err) => {
    autoResumeInFlight.delete(codeName);
    log(`[auto-resume] agent=${codeName} POST failed (will retry next poll): ${(err as Error).message}`);
  });
}

// ENG-6383: log a reconciler HOLD at most once per (trip, reason) transition —
// a hold is steady-state (every 5s poll until the dependency recovers), so an
// unguarded log would write 720×/hour. Reuses the ENG-6088 skip-log map (the
// blind and reconciler paths are mutually exclusive by flag).
//
// `reason` is the STABLE dedupe key — it must NOT carry transient detail (a
// varying error message, a climbing cycles-short count), or the same HOLD state
// would re-log every poll and defeat the guard (CodeRabbit on this PR). Put any
// changing diagnostic in `detail`: it rides the log body but never the key.
function logResumeReconcileHoldOnce(
  codeName: string,
  trippedAt: number,
  reason: string,
  detail?: string,
): void {
  const key = `${codeName}:${trippedAt}`;
  if (autoResumeLoggedSkips.get(key) !== reason) {
    autoResumeLoggedSkips.set(key, reason);
    log(`[resume-reconciler] agent=${codeName} decision=hold reason=${reason}${detail ? ` (${detail})` : ''} (ENG-6383)`);
  }
}

// ENG-6383 (ENG-6375 Slice C): the health-gated auto-resume path. Replaces the
// blind ENG-6088 quiet-timer (maybeAutoResume) with decideResumeReconcile —
// a tripped, paused agent is cleared ONLY when its dependency has genuinely
// recovered (status active + MCP present + connectivity ok >= minHealthyCycles).
//
// FAIL-SAFE by construction: a failed/garbled health fetch, or any unexpected
// error, resolves to a HOLD (retry next poll) — NEVER a resume. The reconciler
// exists because a blind resume re-creates the crash-loop, so ambiguity holds.
//
// Binding invariants (ENG-6333 council; mirror decideResumeReconcile's): the
// restart counter is NEVER reset (we only read the trip + marker; only
// restartBreaker.clear() on a confirmed resume drops the trip, leaving the
// spent-credit marker as the one-per-window cap); a resumed→re-tripped agent
// LATCHES to mandatory-manual rather than oscillating.
// ENG-6392: best-effort, retry-until-landed report of an unstable latch to the
// host (which DMs the manager). The endpoint is idempotent on (agent, trip), so
// re-POSTing until one call returns is safe; we clear the unreported marker on
// ANY response (success or a 4xx/5xx the server chose to return) and only retry
// when the POST itself rejected (network/transport). The in-flight guard stops
// the poll loop from stacking duplicate POSTs. The latch's correctness (agent
// stays paused) never depends on this landing.
function reportUnstableLatch(
  agent: { agent_id: string; code_name: string },
  trippedAt: number,
  standDownKey: string,
): void {
  if (unstableLatchReportInFlight.has(standDownKey)) return;
  unstableLatchReportInFlight.add(standDownKey);
  void api
    .post('/host/circuit-breaker/unstable-latch', {
      agent_id: agent.agent_id,
      tripped_at: new Date(trippedAt).toISOString(),
    })
    .then(() => {
      // The host received and answered (sent / dark / already-latched) — the
      // notification is no longer our responsibility to retry.
      unstableLatchUnreported.delete(standDownKey);
    })
    .catch((err) => {
      // Transport-level failure only — leave the key so the next poll retries.
      log(
        `[resume-reconciler] agent=${agent.code_name} unstable-latch report failed (will retry next poll): ${(err as Error).message} (ENG-6392)`,
      );
    })
    .finally(() => {
      unstableLatchReportInFlight.delete(standDownKey);
    });
}

async function maybeResumeReconcile(agent: { agent_id: string; code_name: string }): Promise<void> {
  const codeName = agent.code_name;
  // In-flight guard FIRST — the async health GET now precedes the decision, so
  // a later poll tick must not re-enter mid-fetch and duplicate the work.
  if (autoResumeInFlight.has(codeName)) return;
  const trip = restartBreaker.getTrip(codeName);
  if (!trip) return; // no local trip → nothing to reconcile (and no GET).
  const standDownKey = `${codeName}:${trip.trippedAt}`;
  // Already latched-unstable or answered not-eligible for THIS trip → stop
  // re-evaluating every poll (a new trip gets a fresh trippedAt → fresh key).
  if (autoResumeStandDowns.has(standDownKey)) {
    // ENG-6392: the trip is decided, but if we latched unstable and the manager
    // report hasn't landed yet, keep retrying it here — the stood-down agent
    // stays paused, so this early-return is the only place a retry can happen.
    if (unstableLatchUnreported.has(standDownKey)) {
      reportUnstableLatch(agent, trip.trippedAt, standDownKey);
    }
    return;
  }

  autoResumeInFlight.add(codeName);
  try {
    // Numeric knobs (minHealthyCycles, backoffResetMs) stay env-tunable; the
    // enable gate is the registry flag (env override → heartbeat → default), so
    // decideResumeReconcile's own `disabled` check agrees with the call-site gate.
    const config = { ...readResumeReconcilerConfig(), enabled: hostFlagStore().getBoolean('resume-reconciler') };
    const trippedAt = trip.trippedAt;

    // Server-owned health (ENG-6382). Fail SAFE: a thrown/timed-out GET or a
    // malformed body is treated as "not healthy yet" (hold), never as healthy.
    // We deliberately do NOT fall back to the blind decideAutoResume here — a
    // second resume door would defeat the one-per-window cap.
    let serverHealth: ReturnType<typeof parseResumeHealthResponse> = null;
    try {
      const raw = await api.get(
        `/host/circuit-breaker/resume-health?agent_id=${encodeURIComponent(agent.agent_id)}`,
      );
      serverHealth = parseResumeHealthResponse(raw);
    } catch (err) {
      logResumeReconcileHoldOnce(codeName, trippedAt, 'health-fetch-failed', (err as Error).message.slice(0, 80));
      return;
    }
    if (!serverHealth) {
      logResumeReconcileHoldOnce(codeName, trippedAt, 'health-unparseable');
      return;
    }

    // Manager-owned mcpPresent — the one signal the server CANNOT see
    // (.mcp.json desired state never leaves the host). Whole-agent: every
    // declared MCP server is present (none given up by the presence-reaper).
    const declaredKeys = projectMcpKeys(codeName, psGetProjectDir(codeName));
    const mcpPresent = deriveMcpPresent(declaredKeys, givenUpMcpServerKeys(codeName));

    const decision = decideResumeReconcile({
      trip,
      marker: autoResumeMarkers.get(codeName),
      health: { ...serverHealth, mcpPresent },
      config,
      now: Date.now(),
    });

    if (decision.action === 'hold') {
      // Stable dedupe key = which precondition is failing (so a status→mcp→
      // connectivity progression still logs each transition once). The climbing
      // cyclesShort count rides `detail`, NOT the key — else it'd re-log per poll.
      const reason = decision.precondition
        ? `not-healthy-yet:${decision.precondition}`
        : decision.reason;
      const detail = decision.cyclesShort ? `${decision.cyclesShort}-short` : undefined;
      logResumeReconcileHoldOnce(codeName, trippedAt, reason, detail);
      return;
    }

    if (decision.action === 'latch-unstable') {
      // The agent auto-resumed once, then re-tripped within the backoff window
      // — UNSTABLE, not down. We refuse to chase the oscillation: stop
      // auto-resuming and latch to mandatory-manual. We do NOT clear the trip
      // and NOT reset the counter — the agent stays paused, and the re-trip's
      // own /host/circuit-breaker/trip report keeps the operator-visible
      // circuit-breaker alert OPEN. ENG-6392: we ALSO report the latch to the
      // host so it DMs the manager a distinct worded 'unstable' message ("X keeps
      // falling over after I bring it back — take a look"), separate from the
      // dependency 'down' alert.
      autoResumeStandDowns.add(standDownKey);
      const key = `${codeName}:${trippedAt}`;
      if (autoResumeLoggedSkips.get(key) !== 'latch-unstable') {
        autoResumeLoggedSkips.set(key, 'latch-unstable');
        log(
          `[resume-reconciler] agent=${codeName} decision=latch-unstable — auto-resumed once then re-tripped ` +
          `within the backoff window; latching to mandatory-manual (NOT resuming; restart counter untouched; ` +
          `agent stays paused with its circuit-breaker alert open) (ENG-6383)`,
        );
      }
      // Mark the manager report as owed and attempt it. Tracked OUTSIDE the
      // log-once guard so a transient POST failure is retried on later polls
      // (via the stand-down branch above) until it lands — a stood-down agent
      // has no "next trip" to carry a lost notification.
      unstableLatchUnreported.add(standDownKey);
      reportUnstableLatch(agent, trippedAt, standDownKey);
      return;
    }

    // decision.action === 'resume' — the genuine-health gate passed.
    log(
      `[resume-reconciler] agent=${codeName} decision=resume healthyCycles=${decision.healthyCycles} ` +
      `trippedAt=${new Date(trippedAt).toISOString()} (ENG-6383)`,
    );
    // ENG-6392: the whole-agent gate can't name WHICH dependency recovered, so
    // we diff the manager's own per-integration probe ledger: every dep we saw
    // go `down` and later read `ok`. The resume-health gate already proved all
    // deps read active, so these are the genuine recoveries; the server confirms
    // only the ones with an OPEN incident and skips the rest, so a stale or
    // already-resolved entry is harmless. Omitted from the body when empty.
    const recoveredDependencies = dependencyRecoveryLedger.collectRecovered(codeName);
    const res = await api.post<{ ok: boolean; resumed?: boolean; reason?: string }>(
      '/host/circuit-breaker/auto-resume',
      {
        agent_id: agent.agent_id,
        tripped_at: new Date(trippedAt).toISOString(),
        ...(recoveredDependencies.length > 0
          ? { recovered_dependencies: recoveredDependencies }
          : {}),
      },
    );
    if (res.resumed) {
      // Spend the credit + drop the local trip so the spawn gate reopens. The
      // marker (the one-per-window cap) is persisted NOW — a worker exit before
      // the next end-of-cycle snapshot would otherwise hand a re-tripped agent
      // a fresh credit after restart (mirrors the ENG-6088 path).
      autoResumeMarkers.set(codeName, { trippedAt, autoResumedAt: Date.now() });
      restartBreaker.clear(codeName);
      reportedTrips.delete(codeName);
      // ENG-6392: the diff has been reported (above) — reset it so the next
      // trip cycle starts from a clean slate and recovered/deleted deps don't
      // linger across cycles.
      dependencyRecoveryLedger.clear(codeName);
      state = {
        ...state,
        circuitBreakerTrips: restartBreaker.serialize(),
        circuitBreakerAutoResumes: Object.fromEntries(autoResumeMarkers.entries()),
      };
      send({ type: 'state-update', state });
      log(`[resume-reconciler] agent=${codeName} resumed — a re-trip within the backoff window will latch unstable (ENG-6383)`);
    } else {
      // not-eligible: an operator/cost pause owns the agent, or someone already
      // resumed it. Stand down for THIS trip; the status watcher reconciles.
      autoResumeStandDowns.add(standDownKey);
      log(`[resume-reconciler] agent=${codeName} not applied (reason=${res.reason ?? 'unknown'}) — standing down for this trip (ENG-6383)`);
    }
  } catch (err) {
    // Any unexpected error is a fail-safe hold — the next poll retries.
    log(`[resume-reconciler] agent=${codeName} reconcile failed (will retry next poll): ${(err as Error).message}`);
  } finally {
    autoResumeInFlight.delete(codeName);
  }
}


function scheduleSessionRestart(
  codeName: string,
  delayMs: number,
  reason: string,
  // ENG-5441: typed restart reason for the circuit breaker. Defaults to
  // 'hot-reload-mcp' (the .mcp.json drift / integration-change class). The
  // channel-set-change caller now passes its reason explicitly (ENG-6491) so the
  // window gate can exempt it; the parameter stays explicit so a future caller
  // can't silently bypass the breaker by omitting it.
  breakerReason: RestartReason = 'hot-reload-mcp',
  // ENG-6491: optional side effect run EXACTLY ONCE, at the moment the restart
  // actually fires (immediately before stopPersistentSession) — not at schedule
  // time. Used to stamp the channel-add DM-notice marker with the real stop time
  // so a long idle-defer can't leave it stale. Preserved across defer re-arms and
  // coalescing so the deferred restart still runs it.
  beforeStop?: () => void,
): void {
  const existing = pendingSessionRestarts.get(codeName);
  if (existing) {
    // ENG-6491: don't let a generic restart downgrade an already-scheduled
    // window-exempt one. A channel ADD schedules 'channel-set-change' (exempt),
    // then the same poll's .mcp.json / channel-secret drift watchers schedule a
    // generic 'hot-reload-mcp' that coalesces over it. Keep the exempt restart as
    // scheduled — and KEEP ITS TIMER (don't clear + reschedule), so a drift call
    // with delayMs=0 can't make the add fire immediately and skip the notice delay.
    const keepExistingWindowExempt =
      isWindowExemptRestartReason(existing.breakerReason) &&
      !isWindowExemptRestartReason(breakerReason);
    if (keepExistingWindowExempt) {
      log(
        `[hot-reload] Coalesced restart for '${codeName}': keeping the already-scheduled window-exempt '${existing.reason}' (${existing.breakerReason}); ignoring lower-priority '${reason}' (${breakerReason}) (ENG-6491)`,
      );
      return;
    }
    clearTimeout(existing.timer);
    log(`[hot-reload] Coalesced restart for '${codeName}': replacing pending timer with ${reason}`);
  }
  const timer = setTimeout(() => {
    pendingSessionRestarts.delete(codeName);
    // ENG-5963: hold the restart while outside the maintenance window or the
    // agent is mid-turn (defer-until-idle). Re-arm a re-check rather than
    // restarting now; the marker delete below is skipped on defer so the
    // baseline stays intact. (The delete above already cleared any pending
    // timer, so this re-arm logs no "coalesced" line.)
    const gate = restartGateFor(codeName, breakerReason);
    if (gate !== 'bypass' && gate !== 'proceed') {
      // ENG-6123: a held restart re-checks every 60s; logging each re-check
      // floods manager.log (valai sat deferred for ~50 min = ~50 identical
      // lines). Log on the first defer of a cycle, then at most every
      // DEFER_LOG_THROTTLE_MS, so the held state is still visible without the
      // per-minute spam. The throttle clears once the restart proceeds/cancels.
      const lastLogged = deferLogThrottle.get(codeName);
      if (lastLogged === undefined || Date.now() - lastLogged >= DEFER_LOG_THROTTLE_MS) {
        log(
          `[maintenance-window] Deferring '${reason}' restart for '${codeName}' (${gate}) — re-checking every ${RESTART_DEFER_RECHECK_MS / 1000}s until the window opens`,
        );
        deferLogThrottle.set(codeName, Date.now());
      }
      scheduleSessionRestart(codeName, RESTART_DEFER_RECHECK_MS, reason, breakerReason, beforeStop);
      return;
    }
    deferLogThrottle.delete(codeName);
    // ENG-6203 (CodeRabbit): re-evaluate the breaker at FIRE time for a
    // bind-remediation restart. shouldRemediateUnverifiedRestart checked the
    // breaker when this restart was SCHEDULED, but a bind-remediation fires after
    // a jitter delay during which another restart for this agent can trip the
    // breaker. Stopping now would leave the session down — a tripped breaker
    // blocks the respawn at ensurePersistentSession — which is the exact "stop a
    // session we can't bring back" hazard the schedule-time guard exists to
    // prevent. Abort instead; the session keeps running its current (degraded)
    // tools and the failure stays visible. Scoped to bind-remediation: other
    // restart classes intend to count toward / trip the breaker, and pre-empting
    // their stop would change long-standing behaviour.
    if (breakerReason === 'bind-remediation' && restartBreaker.isTripped(codeName)) {
      log(
        `[bind-remediation] '${codeName}' breaker tripped before the re-respawn fired — ` +
        `aborting to avoid stopping a session the breaker won't let respawn (ENG-6203)`,
      );
      return;
    }
    // ENG-6491: run the fire-time side effect (channel-add DM-notice marker write)
    // immediately before the stop, so the marker timestamp reflects the real
    // restart moment. Best-effort — never let it block the restart.
    if (beforeStop) {
      try {
        beforeStop();
      } catch (err) {
        log(`[hot-reload] beforeStop hook threw for '${codeName}' (non-fatal): ${(err as Error).message}`);
      }
    }
    stopPersistentSession(codeName, log);
    // ENG-4897: drop the hash baseline when the session actually stops.
    // Without this, the next poll compares the fresh session against the
    // dead one's hash and schedules another spurious restart. Covers the
    // existing hot-reload-restart callers (channel-set change, new MCP
    // servers) that already pre-date the drift check.
    runningMcpHashes.delete(codeName);
    // ENG-5441: record into the breaker AFTER the actual stop fires so
    // coalesced restarts (two triggers in the same tick collapsed into
    // one timer) only count as one restart event — matching what the
    // operator sees in manager.log.
    recordRestartForBreaker(codeName, breakerReason);
    log(`[hot-reload] Session stopped for '${codeName}' — will respawn with ${reason}`);
    // ENG-6174: arm respawn verification for hot-reload-MCP restarts (the class
    // that exists to bind newly-connected MCP tools). The stop has fired; from
    // here the manager must confirm the respawn actually lands healthy rather
    // than assume it. Preserve any existing attempt count so a re-armed window
    // (verifyPendingRestarts) keeps escalating toward the ERROR rather than
    // resetting to attempt 0 each time. Other restart classes (crash, wedge,
    // auth-rotation) have their own watchers and don't need this binding check.
    // ENG-6203: a 'bind-remediation' re-respawn is itself verified, so a session
    // that still won't bind loops back to `unverified final` — and is capped by
    // the breaker (which this restart counts toward) rather than looping forever.
    // ENG-6491: 'channel-set-change' also connects a new MCP (the channel
    // listener) that must bind, so it needs the same respawn verification as the
    // generic hot-reload-mcp class it used to be tagged as.
    if (
      breakerReason === 'hot-reload-mcp' ||
      breakerReason === 'managed-mcp-churn' ||
      breakerReason === 'bind-remediation' ||
      breakerReason === 'channel-set-change'
    ) {
      const prior = pendingRestartVerifications.get(codeName);
      pendingRestartVerifications.set(codeName, {
        firedAt: Date.now(),
        attempts: prior?.attempts ?? 0,
      });
    }
  }, delayMs);
  // Don't keep the manager alive solely on a pending restart timer.
  timer.unref?.();
  pendingSessionRestarts.set(codeName, { timer, reason, breakerReason, beforeStop });
}

// CodeRabbit on PR #773 (round 2): cancel any pending hot-reload restart
// timer for an agent so a non-hot-reload teardown path (pause / revoke /
// unassign / auth-rotation stop / day-rollover / shutdown / restart-flags)
// doesn't get followed seconds later by a stale timer firing and killing
// the freshly respawned replacement. Idempotent: safe to call when no
// timer is pending. Call this BEFORE every direct stopPersistentSession
// call in this file (the single in-timer call is already self-cleaning
// via the pendingSessionRestarts.delete above).
function cancelPendingSessionRestart(codeName: string): void {
  // ENG-6174: a non-hot-reload teardown (pause / revoke / unassign / shutdown)
  // means the session is intentionally NOT coming back — drop any armed respawn
  // verification so verifyPendingRestarts doesn't later flag a "restart failed"
  // ERROR for an agent we deliberately stopped.
  pendingRestartVerifications.delete(codeName);
  const existing = pendingSessionRestarts.get(codeName);
  if (!existing) return;
  clearTimeout(existing.timer);
  pendingSessionRestarts.delete(codeName);
  deferLogThrottle.delete(codeName); // ENG-6123: reset the defer-log throttle for the next cycle
  log(`[hot-reload] Cancelled pending restart timer for '${codeName}' (another teardown path is handling it)`);
}

// ENG-6203: opt-in self-heal. When a hot-reload restart reaches `unverified
// final` (never respawned healthy / never re-bound its MCP tools), force ONE
// gated re-respawn instead of only logging ERROR. Default OFF — flip per-host
// for canary before fleet-wide.
function bindRemediationEnabled(): boolean {
  const v = (process.env['AGT_BIND_REMEDIATION_ENABLED'] ?? '').trim().toLowerCase();
  return v === 'true' || v === '1';
}

// ENG-6203: spread the remediation re-respawn over a small random window so a
// fleet-wide trigger (e.g. a kill-switch flip that stalls many restarts at once)
// can't synchronise every agent's respawn into one thundering-herd tick.
const BIND_REMEDIATION_JITTER_MS = 30_000;

// ENG-6174: each poll, confirm that hot-reload-MCP restarts actually respawned a
// HEALTHY session. Pre-fix the manager stopped the session and assumed success;
// a stalled respawn (the canonical case: one that raced the kill-switch test)
// left the agent on its pre-restart tool set with nothing surfaced — the console
// said "Connected" while the live session never bound the tools. This turns that
// silence into a bounded WARN→ERROR escalation.
//
// The verification decision itself stays OBSERVATIONAL (decidePostRestartVerification
// never forces a respawn — fighting whatever legitimately holds a session down
// would re-create the exact thrashing harm). ENG-6203 layers a SEPARATE, opt-in
// remediation gate on top: only at the TERMINAL failure, and only when the
// breaker is closed, it initiates ONE re-respawn through the same gated
// scheduleSessionRestart path (maintenance-window + breaker honored) — initiating
// a normal gated restart from a settled state, never bypassing a gate.
function verifyPendingRestarts(now: number): void {
  if (pendingRestartVerifications.size === 0) return;
  for (const [codeName, pending] of pendingRestartVerifications) {
    // Probe health FIRST: isSessionHealthy() hydrates a not-yet-tracked tmux
    // session into the session Map with startedAt=now — which is exactly the
    // case when a respawn is first observed on this poll. Reading startedAt
    // before the probe would see null and delay verification by a full poll
    // (CodeRabbit on ENG-6174).
    const healthy = isSessionHealthy(codeName);
    const session = getSessionState(codeName);
    const startedAt = session?.startedAt ?? null;
    // currentSessionId is the spawn identity — null for a zombie rehydrated by
    // the health probe after a failed kill, non-null for a genuine respawn. This
    // is what stops a lingering pre-restart session from being mistaken for a
    // successful respawn (CodeRabbit on ENG-6174).
    const respawnId = session?.currentSessionId ?? null;
    const outcome = decidePostRestartVerification(pending, startedAt, healthy, respawnId, now);
    switch (outcome.kind) {
      case 'verified':
        pendingRestartVerifications.delete(codeName);
        log(`[restart-verify] '${codeName}' respawned healthy after MCP change — tools bound (ENG-6174)`);
        break;
      case 'waiting':
        // Still inside the grace window — keep watching silently.
        break;
      case 'unverified':
        if (outcome.final) {
          // Bounded: emit ONE ERROR then stop watching so a genuinely-stuck
          // agent produces a single greppable line, not a per-poll flood.
          pendingRestartVerifications.delete(codeName);
          log(
            `[restart-verify] ERROR '${codeName}' did NOT respawn healthy after ${outcome.attempt} attempts ` +
            `following an MCP change — the live session may be stuck on its pre-restart tools. ` +
            `Check session health / manager.log; a manual session restart may be required (ENG-6174)`,
          );
          // ENG-6203: close the loop — force ONE gated re-respawn so the
          // ensure-pass re-reads .mcp.json and binds the tools. Gated on the
          // breaker NOT being tripped (scheduleSessionRestart stops before the
          // breaker records, and a tripped breaker blocks the respawn — so
          // remediating into a tripped breaker would stop a session it can't
          // bring back). The remediation restart counts toward the breaker and
          // is itself verified, so a session that still won't bind degrades to
          // this same visible ERROR after a bounded number of attempts, never an
          // infinite loop. Jittered so a fleet-wide stall doesn't synchronise
          // respawns across this host's agents.
          if (
            shouldRemediateUnverifiedRestart(outcome, {
              enabled: bindRemediationEnabled(),
              breakerTripped: restartBreaker.isTripped(codeName),
            })
          ) {
            const jitterMs = Math.floor(Math.random() * BIND_REMEDIATION_JITTER_MS);
            log(
              `[bind-remediation] '${codeName}' restart never bound MCP tools — forcing one gated ` +
              `re-respawn in ${Math.round(jitterMs / 1000)}s (breaker-capped, ENG-6203)`,
            );
            scheduleSessionRestart(
              codeName,
              jitterMs,
              'bind-remediation: prior restart left MCP tools unbound',
              'bind-remediation',
            );
          }
        } else {
          // Re-arm a fresh window, carrying the bumped attempt count forward.
          pendingRestartVerifications.set(codeName, { firedAt: now, attempts: outcome.attempt });
          log(
            `[restart-verify] WARN '${codeName}' not yet respawned healthy after MCP change ` +
            `(attempt ${outcome.attempt}) — still watching (ENG-6174)`,
          );
        }
        break;
    }
  }
}

// ENG-5963 (-4): how often to re-evaluate a restart that the maintenance-window
// / defer-until-idle gate is holding, until the gate opens.
const RESTART_DEFER_RECHECK_MS = 60_000;

// ENG-6123: throttle for the per-re-check "Deferring … restart" log line so a
// long-held restart doesn't emit one line every RESTART_DEFER_RECHECK_MS. Keyed
// by codeName → last-logged epoch ms; cleared when the restart proceeds or is
// cancelled so the next defer cycle logs fresh.
const DEFER_LOG_THROTTLE_MS = 600_000;
const deferLogThrottle = new Map<string, number>();

// ENG-5963 (-4): last time the manager handled an inbound message for an agent
// (direct-chat — the manager-visible inbound path; Slack/Telegram are delivered
// by MCP children and surface via pane.log freshness instead). Feeds the
// defer-until-idle gate so a restart never lands right as a message arrives,
// before the agent has produced any pane output yet.
const lastInboundMs = new Map<string, number>();

// ENG-6153: per-agent streak of consecutive poll cycles that looked like a
// wedge (inbound waiting + pane not advancing). Reset to 0 the moment a cycle
// no longer qualifies; once it reaches the configured minCycles the manager
// force-fresh respawns the session. Keyed by code_name.
const consecutiveWedgeCycles = new Map<string, number>();

// ENG-6294: agents whose suppressed-wedge episode has already been logged.
// A parent idle-awaiting a `run_in_background` sub-agent matches the wedge
// signature exactly (quiet pane + stale main transcript + queued inbound), so
// the detector exempts it — and logs ONE line per episode here rather than one
// per poll cycle. Cleared when the exemption stops applying (task finished /
// inbound drained / agent left the persistent set).
const wedgeExemptionLogged = new Set<string>();

// ENG-6171: per-(agent,card) consecutive wedge-respawn accounting. When a card
// is in_progress across `threshold` consecutive wedge-respawns it's flagged a
// suspected poison task and the hybrid inject stops re-nudging it (it falls to
// the 30-min stale-auto-fail instead), breaking the respawn loop — while the
// agent's other actionable cards keep being nudged. Outer key = code_name,
// inner key = card id. See lib/wedge-poison-card.ts.
const wedgeRestartsByCard = new Map<string, Map<string, CardWedgeState>>();

/** Stamp an inbound message for `codeName` (called from the direct-chat path). */
function noteInbound(codeName: string): void {
  lastInboundMs.set(codeName, Date.now());
}

/** Age (seconds) since the last manager-seen inbound, or null if none seen. */
function inboundAgeSecondsFor(codeName: string): number | null {
  const ts = lastInboundMs.get(codeName);
  if (ts === undefined) return null;
  return Math.max(0, Math.floor((Date.now() - ts) / 1000));
}

/**
 * Age (seconds) of the agent's pane.log — our primary idle signal. Claude
 * writes a line on every output (including the in-place progress heartbeats),
 * so a fresh pane.log means the agent is mid-turn / recently active.
 *
 * Returns null ONLY when the pane.log genuinely doesn't exist yet (ENOENT —
 * a never-spawned agent, nothing to interrupt). Any OTHER stat failure
 * (transient I/O, permissions) returns 0 — "treat as just-active" — so a read
 * blip fails safe toward NOT interrupting rather than toward a restart.
 */
function paneLogAgeSecondsFor(codeName: string): number | null {
  try {
    const mtimeMs = statSync(paneLogPath(codeName)).mtimeMs;
    return Math.max(0, Math.floor((Date.now() - mtimeMs) / 1000));
  } catch (err) {
    if ((err as NodeJS.ErrnoException)?.code === 'ENOENT') return null;
    return 0;
  }
}

/**
 * ENG-6264: age (seconds) of the running session's transcript JSONL — the
 * primary "is the model actually producing tokens" signal (ENG-6238). Returns
 * null when there's no live session id or the file can't be stat'd, in which
 * case the gate falls back to pane.log age. Pure mtime read; mirrors
 * paneLogAgeSecondsFor + the wedge detector's transcript signal.
 */
function transcriptAgeSecondsFor(codeName: string): number | null {
  const sessionId = getSessionState(codeName)?.currentSessionId ?? null;
  return transcriptActivityAgeSeconds(ccGetProjectDir(codeName), sessionId, new Date());
}

/**
 * ENG-5963: resolve the restart gate for a (codeName, reason). Returns
 * 'bypass' for reasons that aren't gateable (deprovision / dashboard / auth
 * recovery / day-rollover / channel-restart-flag) so the caller restarts
 * immediately as before; otherwise the window + defer-until-idle decision
 * (transcript/pane AND inbound both quiet) against the cached window.
 *
 * ENG-6264: `reason` is the GATE reason, which may differ from the breaker-count
 * reason — the single-MCP reaper restart is gateable ('mcp-presence-reaper')
 * even though it does not count against the breaker (see
 * stopPersistentSessionAndForgetMcpBaseline).
 */
function restartGateFor(
  codeName: string,
  reason: RestartReason | undefined,
): 'bypass' | 'proceed' | 'defer-window' | 'defer-idle' {
  if (!isGateableRestartReason(reason)) return 'bypass';
  // ENG-6695: interpret the host-level maintenance window in THIS agent's
  // resolved timezone (agent override → team → the cached window's host/org
  // -effective tz), so a disruptive restart defers to the OPERATOR's local
  // off-peak, not the host default. When the agent has no override and no team
  // timezone, resolveAgentTimezone returns the cached window tz unchanged —
  // byte-for-byte today's behaviour.
  const tzInputs = agentRestartTimezoneInputs.get(codeName);
  const perAgentWindow = cachedMaintenanceWindow
    ? {
        ...cachedMaintenanceWindow,
        timezone: resolveAgentTimezone(
          tzInputs?.agentTimezone ?? null,
          tzInputs?.teamTimezone ?? null,
          cachedMaintenanceWindow.timezone,
        ),
      }
    : cachedMaintenanceWindow;
  return decideRestartGate({
    window: perAgentWindow,
    // ENG-6491: channel adds (channel-set-change) skip the off-peak window but
    // still defer-until-idle — they come online as soon as the agent is quiet.
    windowExempt: isWindowExemptRestartReason(reason),
    paneLogAgeSeconds: paneLogAgeSecondsFor(codeName),
    transcriptAgeSeconds: transcriptAgeSecondsFor(codeName),
    inboundAgeSeconds: inboundAgeSecondsFor(codeName),
    now: new Date(),
  });
}

/**
 * ENG-6692: is ANY agent on this host mid-turn / recently active? The forced
 * "Update CLI now" self-update bypasses the maintenance window + throttle +
 * coalesce, but must still never interrupt a live conversation — the manager
 * restart it triggers tears down every agent session on the host. So it's
 * host-level: busy if a SINGLE agent is busy. Reuses decideRestartGate's idle
 * axes (transcript/pane progress AND inbound quiet) with `window: null` so only
 * the defer-until-idle gate applies (no window check).
 *
 * `env: {}` is passed deliberately: AGT_MAINTENANCE_WINDOW_DISABLE short-circuits
 * decideRestartGate to 'proceed', but ENG-6692 (AC: "defer-until-idle is still
 * respected") bypasses only the WINDOW, never the idle-safety gate — so a host in
 * incident-override mode still won't have a live customer turn interrupted by the
 * forced update. The empty env makes the idle check ignore that flag.
 */
function isHostBusyForForcedUpdate(): boolean {
  const now = new Date();
  for (const agent of state.agents) {
    const decision = decideRestartGate({
      window: null,
      paneLogAgeSeconds: paneLogAgeSecondsFor(agent.codeName),
      transcriptAgeSeconds: transcriptAgeSecondsFor(agent.codeName),
      inboundAgeSeconds: inboundAgeSecondsFor(agent.codeName),
      now,
      env: {},
    });
    if (decision === 'defer-idle') return true;
  }
  return false;
}
// ADR-0011 PR-A: agentState.writtenHashes + agentState.knownSecretsHashes moved into `agentState`.

// ENG-4897: per-agent hash of the project `.mcp.json` that the running
// claude session was launched with. The existing stale-MCP reaper
// (ENG-4832) only watches env-var-keyed integration changes; structural
// changes to .mcp.json (new server entries, removed entries, inline env
// changes for tokens that aren't ${VAR}-substituted) slip past it.
//
// Sterling/agt-demo (2026-05-09): operator added Telegram, the manager
// wrote the new .mcp.json, but the running claude session — launched
// with --strict-mcp-config — had only the pre-Telegram view. Its
// `--dangerously-load-development-channels server:telegram` flag then
// errored "no MCP server configured with that name" until manual
// `tmux kill-session` triggered a respawn.
//
// On each poll we hash the project .mcp.json and compare to the value
// recorded for this agent. If they differ, schedule a session restart
// (the existing hot-reload coalescer takes care of the rest). On first
// observation post-manager-start we adopt the current hash as the
// optimistic baseline — worst case is one missed drift event after a
// manager restart, the next change is caught.
const runningMcpHashes = new Map<string, string>();

// ENG-5285: parallel map of the SET of declared mcpServers keys for the
// running session. Used by the drift handler to compute a precise diff
// (added/removed keys) so we only reset presence-reaper give-up state
// for keys whose membership actually changed — not for keys whose value
// rotated (e.g. composio refreshing a signed proxy URL every ~20 min,
// which was the root cause of Stirling flapping 450× over six days
// post-ENG-5279).
const runningMcpServerKeys = new Map<string, Set<string>>();

// ENG-6062: per-agent value hash of the channel-owned secrets in the running
// session's project `.env.integrations` (TELEGRAM_BOT_TOKEN, SLACK_*_TOKEN,
// MSTEAMS_CLIENT_SECRET). Post-Track-D these are `${VAR}` templates in
// `.mcp.json`, so a token rotation moves neither the `.mcp.json` content hash
// (runningMcpHashes) nor the structure hash — this is the only signal that a
// channel credential changed and the session must respawn to pick it up.
// Same optimistic-baseline semantics as runningMcpHashes.
const runningChannelSecretHashes = new Map<string, string>();

function projectMcpHash(_codeName: string, projectDir: string): string | null {
  try {
    // ENG-5071: hash the canonical (key-sorted) JSON, not the raw bytes.
    // Pre-fix this hashed `readFileSync(...)` directly, so two writers
    // producing logically-equivalent files with different key insertion
    // order would trip drift detection every poll — Scout flapped for
    // hours because writeMcpServer emitted `{ url, headers, command,
    // args }` in one order and sanitizeMcpJson rewrote the same logical
    // entry in another. Canonicalising means only real structural
    // changes (new server, removed server, changed values) move the
    // hash; pure shape/order churn no longer fires a restart. Belt-and-
    // braces alongside the sanitize-headers fix in the same PR — the
    // sanitizer was producing semantically-different content for
    // Granola, but byte-level hashing would still bite the next time
    // any writer pair disagreed on key order.
    const raw = readFileSync(join(projectDir, '.mcp.json'), 'utf-8');
    return createHash('sha256').update(canonicalJson(JSON.parse(raw))).digest('hex');
  } catch {
    return null;
  }
}

/**
 * ENG-5285: read the project `.mcp.json` and return the set of declared
 * `mcpServers` keys. Returns null when the file is missing or unparseable
 * (caller treats that as "no information" — falls back to the wholesale
 * give-up-state clear).
 */
function projectMcpKeys(_codeName: string, projectDir: string): Set<string> | null {
  try {
    const raw = readFileSync(join(projectDir, '.mcp.json'), 'utf-8');
    const parsed = JSON.parse(raw);
    const servers = (parsed as { mcpServers?: Record<string, unknown> }).mcpServers;
    if (!servers || typeof servers !== 'object') return new Set();
    return new Set(Object.keys(servers));
  } catch {
    return null;
  }
}

// ENG-6441: `readMcpHttpServerConfig`, `deriveMcpServerKey`, and the
// probeEnv/probeDeps assembly were moved to ./connectivity-probe-context.js so
// the on-demand `agt integration probe` command shares the exact same probe
// context as this periodic loop (no drift). They're imported above.

// ENG-5641: run the rolling host-side connectivity probes for one agent and
// report results to the control plane. Flag-gated by the caller
// (AGT_CONNECTIVITY_PROBE_ENABLED) — dormant until enabled + host-validated.
// ENG-6139: managed (Composio) toolkits ARE probed here now — the agent's wired
// MCP server carries the x-api-key + user_id, so the host verifies the
// connected-account binding (the signal tools/list misses). Non-fatal: throws
// are caught by the caller.
async function runAgentConnectivityProbes(
  agent: { agent_id: string; code_name: string },
  integrations: Array<{
    id: string;
    definition_id: string;
    scope: string;
    auth_type: string;
    credentials: Record<string, unknown>;
    source_type?: string | null;
    last_connectivity_check_at?: string | null;
    connectivity_test?: ConnectivityTestOverride | null;
  }>,
  projectDir: string,
): Promise<void> {
  if (integrations.length === 0) return;

  // ENG-6441: the probeEnv overlay + the four-leg probeDeps are built by the
  // shared factory so the on-demand `agt integration probe` command runs the
  // IDENTICAL probe context as this loop.
  const probeEnv = buildProbeEnv(projectDir);
  const probeDeps = buildConnectivityProbeDeps(projectDir, probeEnv);

  const intervalSec = Number(process.env.AGT_CONNECTIVITY_PROBE_INTERVAL_SECONDS) || 3600;
  const result = await runConnectivityProbes(
    integrations.map((i) => {
      const sourceType = (i.source_type ?? null) as ToolkitSourceType | null;
      return {
        id: i.id,
        definition_id: i.definition_id,
        scope: i.scope as 'agent' | 'team' | 'organization',
        auth_type: i.auth_type as IntegrationAuthType,
        source_type: sourceType,
        credentials: i.credentials,
        last_connectivity_check_at: i.last_connectivity_check_at ?? null,
        // ENG-5665 / ENG-6396: for any integration whose probe is an MCP
        // `tools/list` against the agent's wired server (managed/Composio,
        // mcp_server, or a remote-MCP OAuth provider like granola), derive the
        // .mcp.json server key. The executor looks this up in .mcp.json; an
        // unresolvable key degrades to transient_error, never a false 'down'.
        mcp_server_key: deriveMcpServerKey({
          definitionId: i.definition_id,
          sourceType,
          authType: i.auth_type as IntegrationAuthType,
          connectivityTest: (i.connectivity_test ?? null) as ConnectivityTestOverride | null,
        }),
        // ENG-6242: forward the prescribed connectivity-test tool to the executor.
        connectivity_test: i.connectivity_test ?? null,
      };
    }),
    { probeDeps, intervalMs: intervalSec * 1000 },
  );

  if (result.reports.length > 0) {
    await api.post('/host/integration-connectivity', {
      agent_id: agent.agent_id,
      results: result.reports,
    });
    // ENG-6392: mirror this round's verdicts into the recovery ledger so a
    // later auto-resume can confirm which integrations came back. Reports carry
    // `integration_id`; the incident label is `definition_id`, so resolve it
    // from the probed set (a report whose id we can't map is dropped).
    const labelById = new Map(integrations.map((i) => [i.id, i.definition_id]));
    dependencyRecoveryLedger.record(
      agent.code_name,
      result.reports.flatMap((r) => {
        const label = labelById.get(r.integration_id);
        return label ? [{ kind: 'integration' as const, label, status: r.status }] : [];
      }),
    );
    log(
      `Connectivity probe for '${agent.code_name}': probed=${result.probed} reported=${result.reports.length} due=${result.due}`,
    );
  }
}

/**
 * ENG-7053 / ENG-7220 - rolling host-side session-tool-bind probe. For each
 * installed integration, decides whether its MCP tools are actually BOUND in the
 * running agent session (a backing stdio child is alive, or a remote MCP answers
 * `tools/list`) and reports a `bound|missing|unreachable|unknown` verdict to
 * `/host/session-tool-bind`. Reuses the presence-reaper's stdio-liveness check
 * and the connectivity-probe context's HTTP `tools/list` handshake, so the
 * verdict composes signals the manager already computes. Best-effort: a failure
 * here never fails the poll (the caller wraps it). Flag-gated by
 * `session-tool-probe`; dormant until armed.
 */
async function runAgentSessionToolBindProbes(
  agent: { agent_id: string; code_name: string },
  integrations: Array<{
    id: string;
    definition_id: string;
    scope: string;
    auth_type: string;
    source_type?: string | null;
    last_session_tool_bind_at?: string | null;
    connectivity_test?: ConnectivityTestOverride | null;
  }>,
  projectDir: string,
): Promise<void> {
  if (integrations.length === 0) return;

  // ENG-7255: the host-side gather+probe lives in a shared lib so the `agt` CLI
  // one-shot ("Probe now") can reuse it. ENG-7263: the manager supplies the MCP
  // server keys the running session loaded at spawn (persisted in
  // direct-chat-session.json, keyed on agent_id) - the same source the standalone
  // command reads - to gate HTTP `bound`. The rolling ~1h cadence does the
  // due-selection.
  const intervalSec = Number(process.env.AGT_CONNECTIVITY_PROBE_INTERVAL_SECONDS) || 3600;
  const sessionLoadedServerKeys = new Set(
    readDirectChatSessionState(agent.agent_id)?.mcpServerKeys ?? [],
  );
  const result = await gatherSessionToolBindProbe(agent, integrations, projectDir, {
    sessionLoadedServerKeys,
    intervalMs: intervalSec * 1000,
  });

  if (result && result.reports.length > 0) {
    await api.post('/host/session-tool-bind', {
      agent_id: agent.agent_id,
      results: result.reports,
    });
    log(
      `Session-tool-bind probe for '${agent.code_name}': probed=${result.probed} reported=${result.reports.length} due=${result.due}`,
    );
  }
}

/**
 * ENG-4897 follow-up (CodeRabbit on PR #829): drop the `runningMcpHashes`
 * entry whenever a session is stopped for ANY reason (auth rotation,
 * day rollover, pause, terminate, hot-reload restart). Without this,
 * the post-`ensurePersistentSession()` drift check on the NEXT poll
 * compares the freshly-respawned session against the dead session's
 * hash, immediately scheduling a spurious second restart.
 *
 * `cancelPendingSessionRestart` is called before every direct
 * `stopPersistentSession` (see its docstring), but the cancel doesn't
 * clear the hash. Call this wrapper instead — it bundles the cancel +
 * the hash drop, so every stop path stays consistent.
 */
function stopPersistentSessionAndForgetMcpBaseline(
  codeName: string,
  // ENG-5441: every call site that represents a *restart loop trigger*
  // (day-rollover, auth-tuple rotation, reaper-driven restart, hot-reload,
  // channel-set change) passes its reason so the breaker can count it.
  // De-provisioning paths (agent paused / revoked / removed from host /
  // operator-initiated dashboard restart) pass undefined — those are
  // intentional and shouldn't accumulate against the breaker.
  breakerReason?: RestartReason,
  // ENG-6264: the reason that drives the defer-until-idle GATE, which may differ
  // from the breaker-count reason above. The single-MCP reaper restart must
  // still defer-until-idle (gateable) yet NOT count against the breaker
  // (breakerReason=undefined). Defaults to breakerReason so every other caller
  // is unchanged — only the reaper passes a distinct gate reason.
  gateReason: RestartReason | undefined = breakerReason,
): void {
  // ENG-5963: gate non-urgent restart-causing maintenance to the maintenance
  // window + defer-until-idle. Bypass reasons (deprovision/dashboard/auth/
  // day-rollover) resolve to 'bypass' and tear down immediately as before. A
  // deferred restart returns early WITHOUT clearing the running-state baselines
  // (runningMcpHashes/Keys) or recording the breaker, so the underlying trigger
  // (drift / changed channel set / dead MCP) is simply re-detected on the next
  // poll and the gate re-evaluated — a natural retry, no extra timer needed.
  const gate = restartGateFor(codeName, gateReason);
  if (gate !== 'bypass' && gate !== 'proceed') {
    log(`[maintenance-window] Deferring '${gateReason}' restart for '${codeName}' (${gate})`);
    return;
  }
  cancelPendingSessionRestart(codeName);
  stopPersistentSession(codeName, log);
  runningMcpHashes.delete(codeName);
  runningMcpServerKeys.delete(codeName);
  runningChannelSecretHashes.delete(codeName); // ENG-6062: re-baseline on next start
  // ENG-5565: a session teardown (pause / revoke / remove / restart trigger)
  // interrupts any in-flight injected kanban run — close it so it doesn't hang
  // open. 'cancelled' since the work was interrupted, not completed.
  closeInjectedRunIfOpen(codeName, 'cancelled', `session stopped (${breakerReason ?? 'deprovision'})`);
  // ENG-5599: likewise close any in-flight scheduled-task runs routed in-session
  // so an interrupted firing doesn't hang open and mis-attribute later turns.
  closeScheduledRunsForCode(codeName, 'cancelled', `session stopped (${breakerReason ?? 'deprovision'})`);
  if (breakerReason) {
    recordRestartForBreaker(codeName, breakerReason);
  }

  // ENG-6399: report system-initiated restarts to audit_log (POST
  // /host/restart-event), best-effort and fire-and-forget. Reached only after
  // an ACTUAL teardown — a maintenance-gated/deferred call returned early
  // above, so a deferred restart is never logged as one. Emitted only for
  // `breakerReason` restarts: deprovision paths (pause/revoke/remove) pass
  // breakerReason=undefined and are not restarts, and the operator dashboard
  // restart is audited server-side at its API route. The two flag-driven
  // reasons are SKIPPED here because restart-handler already reports them with
  // a richer free-text reason (the agent's own words for `agent-requested`),
  // so emitting here too would double-log.
  if (breakerReason && breakerReason !== 'agent-requested' && breakerReason !== 'channel-restart-flag') {
    const restartAgentId = agentState.codeNameToAgentId.get(codeName);
    if (restartAgentId) {
      void api
        .post('/host/restart-event', {
          agent_id: restartAgentId,
          source: breakerReason,
          reason: breakerReason,
        })
        .catch((err) => log(`[restart-event] report failed for '${codeName}': ${(err as Error).message}`));
    }
  }
}

function checkMcpConfigDriftAndScheduleRestart(codeName: string, projectDir: string): void {
  const currentHash = projectMcpHash(codeName, projectDir);
  const action = decideMcpDriftAction(currentHash, runningMcpHashes.get(codeName));
  switch (action.kind) {
    case 'no-config':
    case 'no-drift':
      return;
    case 'baseline': {
      runningMcpHashes.set(codeName, action.hash);
      // ENG-5285: also capture the declared mcpServers key set so the
      // next drift event can diff added/removed keys without re-reading
      // the previous file (which is already overwritten by then).
      //
      // ENG-5285 follow-up (CodeRabbit on PR #1178): preserve the
      // "unknown" signal from projectMcpKeys — a transient read/parse
      // race should NOT poison the baseline with an empty set, which
      // would make the next drift see every current key as "newly
      // added" and reset the cap on every MCP. Treat null as "no
      // baseline" — the drift handler falls back to the wholesale
      // clear, which is the safe choice when we don't know what the
      // previous declared set was.
      const baselineKeys = projectMcpKeys(codeName, projectDir);
      if (baselineKeys) {
        runningMcpServerKeys.set(codeName, baselineKeys);
      } else {
        runningMcpServerKeys.delete(codeName);
      }
      return;
    }
    case 'drift': {
      log(
        `[hot-reload] .mcp.json content changed for '${codeName}' ` +
        `(${action.previous.slice(0, 12)} → ${action.current.slice(0, 12)}) (ENG-4897)`,
      );
      // ENG-5285: clear presence-reaper give-up state ONLY for keys
      // whose membership in the declared set actually changed — i.e.
      // keys that were added or removed. Keys present in both before
      // and after (typical for value-only drift, e.g. composio rotating
      // a signed proxy URL every ~20 min) keep their cap accumulated.
      // Pre-fix this wholesale-cleared the agent's reaper state on
      // every URL rotation, which was why Stirling flapped 450× over
      // six days despite ENG-5279's restart cap — the cap was reset
      // before it could trip.
      // ENG-5285 follow-up (CodeRabbit on PR #1178): preserve the
      // "unknown" signal from projectMcpKeys. Coercing either side to
      // an empty set would either poison the comparison (every current
      // key looks "newly added") or hide the drift (both empty looks
      // like "no membership change"). When either side is unknown,
      // fall back to the wholesale clear — the safe behaviour we had
      // pre-ENG-5285, used here as the fallback rather than the
      // default.
      const previousKeys = runningMcpServerKeys.get(codeName);
      const currentKeys = projectMcpKeys(codeName, projectDir);
      // ENG-5537: only a *membership* change (server added/removed) warrants
      // restarting the running session. A value-only drift — the same set of
      // servers with a rotated url/header value, e.g. composio re-signing
      // `mcp_url` every poll — leaves the running session's established MCP
      // connections intact, and a future respawn reads the freshly-written
      // file anyway. Pre-fix this scheduled a restart on EVERY drift, so a
      // flapping signed url tripped the ENG-5441 breaker and auto-paused the
      // agent within minutes. When key membership is unknown we keep the
      // pre-ENG-5537 safe default (treat as a restart-worthy change).
      const decision = decideMcpRestartOnDrift(previousKeys, currentKeys);
      // ENG-5932: 0-restart-on-removal twin for the .mcp.json drift watcher.
      // When the only membership change is the REMOVAL of quarantined
      // channel(s) — the quarantine drop rewrote .mcp.json without the dead
      // channel — adopt the new baseline but DON'T bounce the live session
      // (bouncing to "notice" a dead channel left would SIGINT the healthy
      // siblings, the exact harm we're removing). Only when membership is known.
      let quarantineOnlyDrift = false;
      if (decision.restart && !decision.membershipUnknown && currentKeys) {
        const quarantined = channelQuarantineStore().getQuarantinedKeys(codeName);
        const added = decision.addedOrRemoved.filter((k) => currentKeys.has(k));
        const removed = decision.addedOrRemoved.filter((k) => !currentKeys.has(k));
        quarantineOnlyDrift =
          added.length === 0 && removed.length > 0 && removed.every((k) => quarantined.has(k));
      }

      // ENG-6123: if this agent's managed-MCP set is currently flapping, hold
      // the drift-driven restart the same way a value-only drift does — adopt
      // the new on-disk baseline but DON'T bounce the session. This is the
      // drift-watcher twin of the convergence-loop suppression: without it the
      // restart burn just moves from the '.mcp.json content change' scheduler to
      // here every poll while the desired set oscillates (valai, ENG-6123).
      const flapSuppressed =
        decision.restart &&
        !quarantineOnlyDrift &&
        mcpFlapDampener.isFlapping(codeName, FLAP_CHANNEL_MANAGED_MCP);

      if (decision.membershipUnknown) {
        clearPresenceReaperState(codeName);
        log(
          `[hot-reload] .mcp.json key membership unavailable for '${codeName}' — clearing full presence-reaper state and restarting (ENG-5285/ENG-5537)`,
        );
      } else if (quarantineOnlyDrift) {
        log(
          `[channel-quarantine] .mcp.json drift for '${codeName}' is quarantined-channel removal only [${decision.addedOrRemoved.join(', ')}] — adopting new baseline WITHOUT restart (0-restart-on-removal, ENG-5932)`,
        );
      } else if (flapSuppressed) {
        log(
          `[mcp-flap-dampener] .mcp.json membership drift for '${codeName}' [${decision.addedOrRemoved.join(', ')}] held — managed-MCP set is flapping, adopting baseline WITHOUT restart until it settles (ENG-6123, ENG-6512)`,
        );
      } else if (decision.restart) {
        clearPresenceReaperStateForKeys(codeName, new Set(decision.addedOrRemoved));
        log(
          `[hot-reload] .mcp.json membership changed for '${codeName}': ` +
          `${decision.addedOrRemoved.length} key(s) added/removed [${decision.addedOrRemoved.join(', ')}] — reaper state cleared for those keys only (ENG-5285)`,
        );
      } else {
        log(
          `[hot-reload] .mcp.json value-only drift for '${codeName}' — preserving presence-reaper state and NOT restarting (ENG-5285/ENG-5537)`,
        );
      }

      if (decision.restart && !quarantineOnlyDrift && !flapSuppressed) {
        // ENG-7239 (CodeRabbit PR #2870): tag the restart 'managed-mcp-churn'
        // only when EVERY changed .mcp.json key is a managed-MCP/brokered
        // toolkit server, so the breaker carve-out for a flapping managed-MCP set
        // can't also swallow a co-occurring channel-listener or credential
        // restart. A mixed, channel-only, or membership-unknown drift stays
        // 'hot-reload-mcp' and always counts toward the breaker.
        const driftBreakerReason: RestartReason =
          decision.addedOrRemoved.length > 0 && decision.addedOrRemoved.every(isManagedMcpServerKey)
            ? 'managed-mcp-churn'
            : 'hot-reload-mcp';
        scheduleSessionRestart(codeName, 0, '.mcp.json content change (ENG-4897)', driftBreakerReason);
        // Drop the entries — the next poll after respawn captures the
        // new hash + key set as baseline. Keeping the old values would
        // re-fire every poll until the respawn timer triggers.
        runningMcpHashes.delete(codeName);
        runningMcpServerKeys.delete(codeName);
      } else if (flapSuppressed) {
        // ENG-6123: deliberately do NOT adopt the new baseline while flapping.
        // Keeping the running session's launch-time baseline stale means that
        // once the set finally settles (dampener recovers → flapSuppressed
        // false), this same drift is still pending and fires exactly ONE
        // restart to land the session on the settled config — instead of the
        // session being silently stranded on its stale tool view. The per-poll
        // re-detection until then is a cheap log line, no opus involvement.
      } else {
        // ENG-5537: value-only drift — adopt the new content as the baseline
        // so we don't re-log/re-evaluate every poll, but leave the running
        // session untouched. currentKeys is non-null here (membershipUnknown
        // would have set decision.restart=true), so the key set stays in sync.
        runningMcpHashes.set(codeName, action.current);
        if (currentKeys) runningMcpServerKeys.set(codeName, currentKeys);
      }
      return;
    }
  }
}

/**
 * ENG-6062: value hash of the channel-owned secrets in the running session's
 * project `.env.integrations`. Returns null when the file is missing /
 * unreadable or carries none of the channel keys (agent has no channels).
 */
function projectChannelSecretHash(projectDir: string): string | null {
  try {
    const entries = parseEnvIntegrations(
      readFileSync(join(projectDir, '.env.integrations'), 'utf-8'),
    );
    return channelSecretValueHash(entries, CHANNEL_SECRET_ENV_KEYS);
  } catch {
    return null;
  }
}

/**
 * ENG-6062: schedule a respawn when a channel token in `.env.integrations`
 * changed since the running session launched. Unlike the `.mcp.json` drift
 * check, a change here is ALWAYS restart-worthy — these keys only move on a
 * deliberate rotation, and the live channel MCP holds the old (now revoked)
 * token in its spawn env until respawned. Reuses the optimistic-baseline
 * primitive and the shared `scheduleSessionRestart` coalescer, so it inherits
 * the maintenance-window deferral and ENG-5441 restart-breaker protection.
 */
function checkChannelSecretDriftAndScheduleRestart(codeName: string, projectDir: string): void {
  const currentHash = projectChannelSecretHash(projectDir);
  const action = decideMcpDriftAction(currentHash, runningChannelSecretHashes.get(codeName));
  switch (action.kind) {
    case 'no-config':
    case 'no-drift':
      return;
    case 'baseline':
      runningChannelSecretHashes.set(codeName, action.hash);
      return;
    case 'drift':
      log(
        `[hot-reload] channel credential rotated for '${codeName}' ` +
        `(${action.previous} → ${action.current}) — respawning to load the new token (ENG-6062)`,
      );
      scheduleSessionRestart(codeName, 0, 'channel credential rotation (ENG-6062)');
      // Drop the baseline — the next poll after respawn re-captures the new
      // hash, so we don't re-fire every poll until the respawn timer runs.
      runningChannelSecretHashes.delete(codeName);
      return;
  }
}
// ADR-0011 PR-A: the per-agent hash caches that were declared here moved
// into `agentState` (./manager/agent-state.ts). Persistence semantics are
// unchanged: `loadChannelHashCache()` / `saveChannelHashCache()` below still
// own the on-disk mirror of `agentState.knownChannelConfigHashes` (ENG-4712).
// Original ENG- annotations preserved at the use sites:
//   - agentState.knownManagedMcpHashes       ENG-4481 (self-heal toolkits without cred change)
//   - agentState.knownManagedMcpStructure    ENG-5537 (structure hash gates session restart;
//                                 prevents URL-rotation flap on Composio)
// ENG-5292: reverse mapping from (agent_id, serverId) to toolkit_id.
// The serverId is the .mcp.json key — the lowercased-non-alnum-to-
// underscore form of toolkit_id that writeMcpServer uses. The reaper's
// onGiveUp callback consults this so it can tell the API which toolkit
// to mark unhealthy.
//
// CodeRabbit on PR #1184: scoped per-agent (composite key) — NOT keyed
// by serverId alone. Two different agents can declare the same
// toolkit (same serverId), and stale entries from a now-removed agent
// must not misattribute a give-up on another agent's MCP to the wrong
// toolkit_id. The composite key also lets us prune per-agent on agent
// removal without a full scan.
const managedToolkitIdByAgentAndServerId = new Map<string, string>();

function managedToolkitMapKey(agentId: string, serverId: string): string {
  return `${agentId}\x00${serverId}`;
}
// Track last-seen cron run timestamps per job to avoid re-processing
const lastCronRunTs = new Map<string, number>();
// Track last work_trigger_at per agent to avoid duplicate triggers
const lastWorkTriggerAt = new Map<string, number>();
// Track which in_progress items we've already alerted as stale
const alertedStaleItems = new Set<string>();
// ENG-5408: separate set for the structured stuck-task watchdog — we want
// one log line per stuck row per occurrence so CloudWatch metric filters
// can count them, but we still don't want to spam every poll tick for
// the same row. Cleared in lockstep with alertedStaleItems below.
const stuckKanbanLogged = new Set<string>();
// Track agents with known API key errors (avoid spamming the API)
const apiKeyStatusCache = new Map<string, boolean>();
// ENG-5408: threshold env var, falls back to 30 min. The existing
// auto-fail path (around manager-worker.ts:5042) and the new structured
// stuck-task watchdog log line both honour this — operators can tighten
// or loosen the budget without a code change.
const STALE_TASK_THRESHOLD_MS = (() => {
  const env = process.env['AUGMENTED_STUCK_TASK_THRESHOLD_MINUTES'];
  const minutes = env ? Number.parseInt(env, 10) : NaN;
  if (Number.isFinite(minutes) && minutes > 0) return minutes * 60 * 1000;
  return 30 * 60 * 1000;
})();

// Track alerted job IDs to avoid duplicate alerts within a session
const alertedJobs = new Set<string>();
// Map cron job name (aug:template:uuid) → human-readable task info
const taskDisplayInfo = new Map<string, { taskName: string; schedule: string; agentDisplayName: string }>();
// ADR-0011 PR-A: agentState.agentDisplayNames + agentState.codeNameToAgentId moved into `agentState`
// (./manager/agent-state.ts). Use `agentState.agentDisplayNames`,
// `agentState.codeNameToAgentId`.
// ADR-0011 PR-4: agentChannelTokens (code_name → channel bot tokens) and its
// test-only seeder __setAgentChannelTokensForTest moved into
// ./manager/channels/state.ts (imported above).

// Per-cycle channel tracking — populated by processAgent, reconciled after all agents
const activeChannels = new Map<string, Set<string>>(); // channelId -> Set<codeName>

// Cumulative state for the parent
let state: ManagerState = {
  pid: process.pid,
  startedAt: new Date().toISOString(),
  lastPollAt: null,
  pollCount: 0,
  errorCount: 0,
  agents: [],
};

// Cache registered agents per framework per poll cycle
const registeredAgentsCache = new Map<string, Set<string>>();

// Per-agent framework ID cache (populated from API response and refresh data)
const agentFrameworkCache = new Map<string, string>();

// ENG-5901 PR 3: agents whose on-disk secret storage has been migrated this
// manager run (literal-hoist is idempotent; the gate just avoids re-reading
// .mcp.json every poll tick for every agent).
const migratedSecretStorage = new Set<string>();

// Track which framework binaries we've already checked/installed this session
const frameworkBinaryChecked = new Set<string>();

// Claude Code auth status — set during ensureFrameworkBinary, checked before launching sessions
let agentRuntimeAuthenticated = false;

// ENG-6068: outcome of the last channels managed-settings assertion
// (ensureClaudeManagedSettings). Anything but 'ok' means Claude Code may be
// silently dropping ALL channel inbound on this host — reported in the host
// heartbeat so the control plane can alert instead of relying on an operator
// reading manager.log.
let lastManagedSettingsStatus: ManagedSettingsStatus | null = null;

/** Resolve the framework adapter for a given agent, using the cache or defaulting to claude-code (ENG-6919). */
function resolveAgentFramework(codeName: string): FrameworkAdapter {
  const frameworkId = agentFrameworkCache.get(codeName) ?? DEFAULT_FRAMEWORK;
  return getFramework(frameworkId);
}

/**
 * Clear all per-agent caches when an agent is unassigned, revoked, or needs
 * a full reprovision. Keeps the cleanup in one place instead of scattering
 * .delete() calls for every new Map we add.
 *
 * ADR-0011 PR-A: the agentState-owned Maps are cleared via `clearAgentState`
 * in ./manager/agent-state.ts. Subsystem-owned caches still live in this
 * file and will lift with their owning module in the stacked follow-up PRs.
 */
function clearAgentCaches(agentId: string, codeName: string): void {
  const channelCacheMutated = clearAgentState(agentId, codeName);

  // ADR-0011 PR-4 / CR #2145: agentChannelTokens now lives in the shared
  // channels/state.ts singleton, so teardown must purge it here too — otherwise
  // a revoked/handed-off/unassigned agent's Slack/Telegram bot tokens stay
  // resident for the rest of the process. All clearAgentCaches callers are
  // teardown paths (host-handoff, decommission, realtime unassign), each having
  // already killed the agent's channel processes, so this never strands an
  // active delivery.
  agentChannelTokens.delete(codeName);

  // Subsystem-owned caches (move with their subsystem in PR-B onwards):
  agentFrameworkCache.delete(codeName);
  kanbanBoardCache.delete(codeName);
  lastHarvestAt.delete(codeName);
  claudeSchedulerStates.delete(codeName);
  claudeTaskConcurrency.delete(codeName);
  // CodeRabbit on PR #1318: drop the per-agent dedupe so a re-enrolled
  // agent's first banner observation isn't suppressed by the previous
  // generation's cached count.
  lastMcpFailedBannerCount.delete(codeName);

  // Memory sync caches
  memoryFileHashes.delete(agentId);
  lastDownloadHash.delete(agentId);
  lastLocalFileHash.delete(agentId);

  // Compound-keyed (`${codeName}:suffix`) subsystem caches
  for (const key of taskDisplayInfo.keys()) {
    if (key.startsWith(`${codeName}:`)) taskDisplayInfo.delete(key);
  }

  // ENG-6123: drop flap-dampener history + defer-log throttle so a re-enrolled
  // agent starts with a clean flap window.
  mcpFlapDampener.forget(codeName);
  deferLogThrottle.delete(codeName);

  if (channelCacheMutated) saveChannelHashCache();
}

// Cached framework version (detected once at startup, refreshed periodically)
let cachedFrameworkVersion: string | null = null;
// ENG-5963: the host's resolved maintenance window, delivered on each heartbeat
// response (host-runtime.ts). Cached module-level (mirrors cachedFrameworkVersion)
// so the self-update + Claude Code update gates can consult it without an extra
// fetch. null until the first heartbeat returns — the gate fails open in that
// window so a host that hasn't heard from the API isn't frozen out of updates.
let cachedMaintenanceWindow: { start: string; end: string; timezone: string } | null = null;
// ENG-6695: per-agent timezone inputs captured on each /host/refresh, so the
// per-agent restart gate can interpret the (host-level) maintenance window in
// the operator's zone. Resolution precedence agent → team → host/org-effective
// window tz; when both agent and team are unset this reproduces the cached
// window timezone exactly (today's behaviour).
const agentRestartTimezoneInputs = new Map<string, { agentTimezone: string | null; teamTimezone: string | null }>();
let lastVersionCheckAt = 0;
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000; // Re-check every 5 minutes

// ENG-5399: Tier 1 responsiveness probe — pane.log freshness reported
// per agent on a slow cadence. Throttled here because pollCycle runs
// every 10s but we only need this once every few minutes (the existing
// SyntheticReplyAgeSeconds cron runs every 15 min as the deeper check).
let lastResponsivenessProbeAt = 0;

// agt CLI version, injected by tsup at build time. Reported on heartbeat
// so the host page can surface "which agt is actually running here?".
// In dev (tsx), the constant isn't defined — fall back to 'dev'.
declare const __CLI_VERSION__: string;
const agtCliVersion = typeof __CLI_VERSION__ !== 'undefined' ? __CLI_VERSION__ : 'dev';

// ---------------------------------------------------------------------------
// Framework binary install / upgrade
// ---------------------------------------------------------------------------

/**
 * Ensure a Homebrew formula is installed. Used for framework dependencies
 * like tmux that are required but not part of the framework itself.
 * Returns true if the binary is available after the check.
 */
async function ensureBrewDependency(binary: string, formula: string): Promise<boolean> {
  if (frameworkBinaryChecked.has(`dep:${binary}`)) return true;

  const { execFileSync } = await import('node:child_process');

  try {
    execFileSync('which', [binary], { timeout: 5_000 });
    frameworkBinaryChecked.add(`dep:${binary}`);
    return true;
  } catch {
    // Binary not found — try to install
  }

  let brewPath: string;
  try {
    brewPath = execFileSync('which', ['brew'], { timeout: 5_000 }).toString().trim();
  } catch {
    log(`${binary} not found and Homebrew not available — install manually: brew install ${formula}`);
    frameworkBinaryChecked.add(`dep:${binary}`);
    return false;
  }

  log(`${binary} not found — installing via Homebrew...`);
  try {
    execFileSync(brewPath, ['install', formula], { timeout: 120_000, stdio: 'pipe' });
  } catch (err) {
    log(`Failed to install ${formula}: ${(err as Error).message}`);
    frameworkBinaryChecked.add(`dep:${binary}`);
    return false;
  }

  // Prepend brew's bin dir to PATH so the post-install verification (and any
  // downstream subprocesses) can find the binary. Brew installs to a custom
  // prefix that isn't on the manager's PATH when spawned by cloud-init.
  const brewBinDir = dirname(brewPath);
  if (!process.env.PATH?.split(':').includes(brewBinDir)) {
    process.env.PATH = `${brewBinDir}:${process.env.PATH ?? ''}`;
  }

  try {
    execFileSync('which', [binary], { timeout: 5_000 });
    log(`${binary} installed successfully`);
    frameworkBinaryChecked.add(`dep:${binary}`);
    return true;
  } catch {
    log(`${formula} install completed but ${binary} not found on PATH`);
    frameworkBinaryChecked.add(`dep:${binary}`);
    return false;
  }
}

/**
 * Ensure the CLI binary for a framework is available. Currently only handles
 * `claude-code` via Homebrew. Called once per session when we first see an
 * agent using that framework.
 */
/**
 * Resolve the brew binary. `which brew` fails when PATH doesn't include the
 * Homebrew bin dir (common when the manager is spawned by cloud-init with a
 * minimal env). Fall back to the canonical Linuxbrew install location.
 */
function resolveBrewPath(execFileSync: typeof import('node:child_process').execFileSync): string | null {
  try {
    const out = execFileSync('which', ['brew'], { timeout: 5_000 }).toString().trim();
    if (out) return out;
  } catch {
    // fall through to absolute path check
  }
  // Canonical install locations across the three platforms we run on.
  // Linuxbrew first (the AL2023 hosts the manager runs on most often),
  // then Apple-silicon macOS (`/opt/homebrew`), then Intel macOS
  // (`/usr/local`). Without the macOS entries the launchd-spawned
  // manager bails out of self-update on Macs whose sparse PATH doesn't
  // include the brew bin dir.
  const fallbacks = [
    '/home/linuxbrew/.linuxbrew/bin/brew',
    '/opt/homebrew/bin/brew',
    '/usr/local/bin/brew',
  ];
  for (const path of fallbacks) {
    if (existsSync(path)) return path;
  }
  return null;
}

/**
 * Detect whether the `claude` binary is installed on this host. Mirrors
 * resolveBrewPath's three-platform-fallback shape: Linuxbrew (EC2 default),
 * Apple-silicon macOS, Intel macOS — then `which claude` as a last resort
 * for unusual install locations.
 *
 * Pre-CodeRabbit fix (PR #1226 review): only the Linuxbrew path was
 * checked before falling back to `which`, which fails on macOS launchd
 * with a sparse PATH. The manager would then treat Claude as missing,
 * stamp the upgrade marker, and skip the upgrade — silently never
 * refreshing Claude Code on dev macs.
 */
function claudeBinaryInstalled(
  execFileSync: typeof import('node:child_process').execFileSync,
): boolean {
  const canonical = [
    '/home/linuxbrew/.linuxbrew/bin/claude',
    '/opt/homebrew/bin/claude',
    '/usr/local/bin/claude',
  ];
  if (canonical.some((path) => existsSync(path))) return true;
  try {
    execFileSync('which', ['claude'], { timeout: 5_000 });
    return true;
  } catch {
    return false;
  }
}

/**
 * ENG-4421: resolve a toolkit → install its CLI if missing.
 *
 * Plugins declare `required_toolkits: [...]`. The manager unions these across
 * an agent's installed plugins and calls this per toolkit. Idempotent: a hit
 * on `command -v <binary>` short-circuits; the in-memory guard makes repeat
 * polls cheap.
 *
 * Installer strategies are declared in the catalog (`IntegrationCliTool.installer`):
 *   - 'npm'    : `npm install -g <package>`
 *   - 'brew'   : `brew install <package>` (proxied via ec2-user when running as root)
 *   - 'script' : runs `cli_tool.script` verbatim. The catalog is the trust boundary —
 *                never take the script from plugin-side data.
 *   - 'manual' : skip — logged as a hint for the operator.
 *
 * All failures are non-fatal: the manager logs and moves on so a missing CLI
 * doesn't block the rest of the agent's provisioning.
 *
 * Caching semantics:
 *   - `toolkitCliEnsured` caches *permanent* outcomes: success, 'manual'
 *     installer (operator's responsibility), or catalog authoring errors
 *     (missing package/script). Those states won't change poll-to-poll.
 *   - `toolkitCliRetryAfter` caches *transient* failures (network, brew
 *     refused root, npm 500, post-install verify fail) with a 5-min
 *     cooldown. This lets a retry recover automatically instead of
 *     requiring a manager restart.
 */
const toolkitCliEnsured = new Set<string>();
const toolkitCliRetryAfter = new Map<string, number>();
const toolkitCliFailureCount = new Map<string, number>();
const TOOLKIT_INSTALL_RETRY_MS = 5 * 60_000;
// After this many consecutive failures, mark the toolkit permanently
// failed for the lifetime of the manager process. Stops the log spam
// when an upstream package is broken (e.g. linear-cli's musl fallback
// doesn't exist) or the host is missing some prereq we can't auto-
// install. Operator restarts the manager to retry.
const TOOLKIT_INSTALL_MAX_FAILURES = 3;

function recordToolkitFailure(toolkitSlug: string, reason: string): void {
  const count = (toolkitCliFailureCount.get(toolkitSlug) ?? 0) + 1;
  toolkitCliFailureCount.set(toolkitSlug, count);
  if (count >= TOOLKIT_INSTALL_MAX_FAILURES) {
    log(`[toolkit-install] ${toolkitSlug}: ${reason} (giving up after ${count} attempts — restart the manager to retry)`);
    toolkitCliEnsured.add(toolkitSlug);
    toolkitCliRetryAfter.delete(toolkitSlug);
  } else {
    log(`[toolkit-install] ${toolkitSlug}: ${reason} (attempt ${count}/${TOOLKIT_INSTALL_MAX_FAILURES}, retrying in ${TOOLKIT_INSTALL_RETRY_MS / 60_000}m)`);
    toolkitCliRetryAfter.set(toolkitSlug, Date.now() + TOOLKIT_INSTALL_RETRY_MS);
  }
}

async function ensureToolkitCli(toolkitSlug: string): Promise<void> {
  if (toolkitCliEnsured.has(toolkitSlug)) return;
  const retryAfter = toolkitCliRetryAfter.get(toolkitSlug) ?? 0;
  if (retryAfter > Date.now()) return;

  const integration = getIntegration(toolkitSlug);
  if (!integration?.cli_tool) {
    // Unknown toolkit or no CLI declared — permanent, won't change between polls.
    toolkitCliEnsured.add(toolkitSlug);
    return;
  }

  const { binary, installer, package: pkg, script } = integration.cli_tool;
  const resolvedInstaller = installer ?? 'manual';

  const { execFileSync, execSync } = await import('node:child_process');

  // Fast path: already on PATH.
  try {
    execFileSync('which', [binary], { timeout: 5_000, stdio: 'pipe' });
    toolkitCliEnsured.add(toolkitSlug);
    toolkitCliRetryAfter.delete(toolkitSlug);
    toolkitCliFailureCount.delete(toolkitSlug);
    return;
  } catch { /* not on PATH — attempt install */ }

  if (resolvedInstaller === 'manual') {
    log(`[toolkit-install] ${toolkitSlug}: binary '${binary}' missing, installer=manual — operator must install`);
    // Not going to change without operator action; mark permanent to avoid log spam.
    toolkitCliEnsured.add(toolkitSlug);
    return;
  }

  let brewBinDir: string | null = null;
  try {
    if (resolvedInstaller === 'npm') {
      if (!pkg) {
        log(`[toolkit-install] ${toolkitSlug}: installer=npm but no package declared`);
        // Catalog authoring mistake — permanent, retrying won't fix it.
        toolkitCliEnsured.add(toolkitSlug);
        return;
      }
      log(`[toolkit-install] ${toolkitSlug}: installing via npm (${pkg})…`);
      execFileSync('npm', ['install', '-g', pkg], { timeout: 180_000, stdio: 'pipe' });
    } else if (resolvedInstaller === 'brew') {
      if (!pkg) {
        log(`[toolkit-install] ${toolkitSlug}: installer=brew but no package declared`);
        toolkitCliEnsured.add(toolkitSlug);
        return;
      }
      const brewPath = resolveBrewPath(execFileSync);
      if (!brewPath) {
        log(`[toolkit-install] ${toolkitSlug}: installer=brew but Homebrew not available — install manually: brew install ${pkg}`);
        // Env-level problem — unlikely to self-heal; mark permanent.
        toolkitCliEnsured.add(toolkitSlug);
        return;
      }
      brewBinDir = dirname(brewPath);
      const isRoot = typeof process.getuid === 'function' && process.getuid() === 0;
      log(`[toolkit-install] ${toolkitSlug}: installing via brew (${pkg})…`);
      if (isRoot) {
        // cwd: '/tmp' — brew refuses if the cwd isn't readable to
        // ec2-user; the manager's cwd is /root (mode 0700) by default.
        execFileSync('sudo', ['-u', 'ec2-user', '-H', brewPath, 'install', pkg], { timeout: 180_000, stdio: 'pipe', cwd: '/tmp' });
      } else {
        execFileSync(brewPath, ['install', pkg], { timeout: 180_000, stdio: 'pipe' });
      }
    } else if (resolvedInstaller === 'script') {
      if (!script) {
        log(`[toolkit-install] ${toolkitSlug}: installer=script but no script declared`);
        toolkitCliEnsured.add(toolkitSlug);
        return;
      }
      log(`[toolkit-install] ${toolkitSlug}: running declared install script…`);
      // The script comes from the catalog (source-controlled) — never from
      // runtime plugin data. execSync is safe here because the catalog is
      // the trust boundary.
      execSync(script, { timeout: 180_000, stdio: 'pipe' });
    }
  } catch (err) {
    const msg = (err as Error).message.slice(0, 200);
    recordToolkitFailure(toolkitSlug, `installer=${resolvedInstaller} failed — ${msg}`);
    return;
  }

  // Prepend brew's bin dir to PATH so the post-install `which` + any downstream
  // spawns can actually find the new binary. Brew installs to a custom prefix
  // (e.g. /home/linuxbrew/.linuxbrew/bin) that isn't on the manager's PATH when
  // spawned by cloud-init with a minimal env.
  if (brewBinDir && !process.env.PATH?.split(':').includes(brewBinDir)) {
    process.env.PATH = `${brewBinDir}:${process.env.PATH ?? ''}`;
  }

  // Verify the binary landed on PATH after install.
  try {
    execFileSync('which', [binary], { timeout: 5_000, stdio: 'pipe' });
    log(`[toolkit-install] ${toolkitSlug}: installed — ${binary} now on PATH`);
    toolkitCliEnsured.add(toolkitSlug);
    toolkitCliRetryAfter.delete(toolkitSlug);
    toolkitCliFailureCount.delete(toolkitSlug);
  } catch {
    recordToolkitFailure(toolkitSlug, `installer=${resolvedInstaller} completed but ${binary} still not on PATH`);
  }
}

/**
 * Run a command and capture stdout, without blocking the Node event loop.
 *
 * `execFileSync` blocks the entire process until the child returns, which on
 * a slow `brew upgrade --cask claude-code` can be 30–120s. Every other
 * manager poll task — HTTP, WS heartbeats, agent processing — stalls for
 * that whole window. Switching to `spawn` + a Promise wrapper keeps the
 * event loop alive while brew runs.
 */
function runAsync(
  cmd: string,
  args: string[],
  opts: { timeout: number; cwd?: string },
): Promise<{ code: number; stdout: string; stderr: string }> {
  return new Promise((resolve, reject) => {
    import('node:child_process').then(({ spawn }) => {
      const child = spawn(cmd, args, { stdio: ['ignore', 'pipe', 'pipe'], cwd: opts.cwd });
      let stdout = '';
      let stderr = '';
      // 'close' only fires after stdio streams drain, which can hang
      // indefinitely if the child ignores SIGTERM. Settle the promise the
      // moment the deadline hits so callers don't block past `opts.timeout`,
      // then escalate to SIGKILL as a backstop.
      let settled = false;
      const timer = setTimeout(() => {
        child.kill('SIGTERM');
        if (settled) return;
        settled = true;
        reject(new Error(`${cmd} ${args.join(' ')} timed out after ${opts.timeout}ms`));
        setTimeout(() => { try { child.kill('SIGKILL'); } catch { /* already gone */ } }, 5_000).unref();
      }, opts.timeout);

      child.stdout?.on('data', (b) => { stdout += b.toString(); });
      child.stderr?.on('data', (b) => { stderr += b.toString(); });
      child.on('error', (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      });
      child.on('close', (code) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve({ code: code ?? -1, stdout, stderr });
      });
    }).catch(reject);
  });
}

/**
 * Path to Claude Code's *managed*-settings file. macOS reads it from
 * /Library/Application Support/ClaudeCode/, every other platform (Linux EC2
 * hosts) from /etc/claude-code/. This is the enterprise-controlled policy
 * file — distinct from user settings.json, which the channels gate ignores.
 */
export function claudeManagedSettingsPath(): string {
  return process.platform === 'darwin'
    ? '/Library/Application Support/ClaudeCode/managed-settings.json'
    : '/etc/claude-code/managed-settings.json';
}

/**
 * ENG-5786: Claude Code 2.1.158+ gates the channels feature behind a managed
 * setting. Unless `channelsEnabled: true` is present in the managed-settings
 * file, CC blocks `--dangerously-load-development-channels` (how the manager
 * pushes telegram / direct-chat / slack inbound into the agent session) and
 * SILENTLY DROPS all inbound — no error, the agent just goes quiet on every
 * channel. Host provisioning historically never wrote this file, so any host
 * that auto-upgraded to 2.1.158+ on subscription/Team auth went dark.
 *
 * Ensure the file exists with channelsEnabled:true, *merging* (never clobbering)
 * any other keys. Runs once per manager process for claude-code hosts, so it
 * self-heals existing hosts (fleet backfill) and re-asserts after a CC upgrade
 * or host re-provision. Idempotent: only writes when the value is missing/wrong.
 * Best-effort — a write failure (e.g. non-root without perms on macOS
 * /Library) is logged, not fatal.
 *
 * ENG-6068: returns the assertion outcome so the heartbeat can report it —
 * 'invalid_json' and 'write_failed' both mean channels may be silently
 * blocked, and the control plane alerts on them instead of the failure
 * living only in manager.log.
 */
export type ManagedSettingsStatus = 'ok' | 'invalid_json' | 'write_failed';

export function ensureClaudeManagedSettings(path: string = claudeManagedSettingsPath()): ManagedSettingsStatus {
  try {
    let settings: Record<string, unknown> = {};
    if (existsSync(path)) {
      const raw = readFileSync(path, 'utf-8').trim();
      if (raw) {
        let parsed: unknown;
        try {
          parsed = JSON.parse(raw);
        } catch (err) {
          // Don't blindly overwrite a managed-settings file we can't parse —
          // an operator may have hand-authored other policy there.
          log(`[managed-settings] ${path} is not valid JSON (${(err as Error).message}) — leaving as-is; Claude Code channels may be blocked`);
          return 'invalid_json';
        }
        // Valid JSON but a non-object (null, array, number, string) can't carry
        // channelsEnabled — and dereferencing it would throw. Treat it like an
        // empty object: we overwrite with a well-formed managed-settings file.
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          settings = parsed as Record<string, unknown>;
        }
      }
    }
    if (settings.channelsEnabled === true) return 'ok'; // already correct — no write
    settings.channelsEnabled = true;
    mkdirSync(dirname(path), { recursive: true });
    writeFileSync(path, `${JSON.stringify(settings, null, 2)}\n`);
    log(`[managed-settings] set channelsEnabled:true in ${path} (ENG-5786 — unblocks Claude Code channels)`);
    return 'ok';
  } catch (err) {
    log(`[managed-settings] could not ensure ${path}: ${(err as Error).message} — Claude Code channels may be blocked; set channelsEnabled:true manually`);
    return 'write_failed';
  }
}

async function ensureFrameworkBinary(frameworkId: string): Promise<void> {
  if (frameworkId !== 'claude-code') return;
  if (frameworkBinaryChecked.has(frameworkId)) return;
  frameworkBinaryChecked.add(frameworkId);

  // ENG-5786: assert the channels managed-setting before anything else. Runs
  // regardless of how (or whether) the binary gets installed below, so hosts
  // that already have Claude Code — including ones that silently auto-upgraded
  // to 2.1.158+ — get backfilled on the next manager start.
  lastManagedSettingsStatus = ensureClaudeManagedSettings();

  const { execFileSync } = await import('node:child_process');

  const brewPath = resolveBrewPath(execFileSync);
  if (!brewPath) {
    log('Homebrew not found (no `brew` on PATH, no /home/linuxbrew/.linuxbrew/bin/brew). Cannot auto-install Claude Code. Install manually: https://claude.ai/download');
    return;
  }
  log(`Using brew at ${brewPath}`);

  // Brew refuses to run as root. When the manager is spawned by the EC2
  // bootstrap (user-data runs as root), proxy the brew call through ec2-user.
  // Otherwise invoke brew directly.
  //
  // The cwd matters: brew refuses to run if the cwd isn't readable to the
  // user it'll run as. The manager's cwd is typically /root (mode 0700)
  // when launched by cloud-init, which ec2-user can't read — so the
  // sudo'd brew bails out with "current working directory must be
  // readable to ec2-user". /tmp is world-readable and has no other
  // semantic meaning to brew.
  const isRoot = typeof process.getuid === 'function' && process.getuid() === 0;
  const runBrew = (args: string[], opts: { timeout: number }) => {
    if (isRoot) {
      return runAsync('sudo', ['-u', 'ec2-user', '-H', brewPath, ...args], { ...opts, cwd: '/tmp' });
    }
    return runAsync(brewPath, args, opts);
  };

  const claudeExists = claudeBinaryInstalled(execFileSync);

  if (!claudeExists) {
    log(`Claude Code binary not found — installing via Homebrew${isRoot ? ' (as ec2-user via sudo)' : ''}...`);
    try {
      const r = await runBrew(['install', '--cask', 'claude-code'], { timeout: 120_000 });
      if (r.code !== 0) {
        log(`Claude Code install failed (exit ${r.code}): ${r.stderr.trim() || r.stdout.trim()}`);
        return;
      }
    } catch (err) {
      log(`Claude Code install failed: ${(err as Error).message}`);
      return;
    }

    // Prepend brew's bin dir to PATH so subsequent `which claude` /
    // resolveClaudeBinary calls find the new binary on minimal-PATH hosts
    // (e.g. EC2 cloud-init root env). Mirrors ensureBrewDependency().
    const brewBinDir = dirname(brewPath);
    if (!process.env.PATH?.split(':').includes(brewBinDir)) {
      process.env.PATH = `${brewBinDir}:${process.env.PATH ?? ''}`;
    }

    // Verify
    if (existsSync('/home/linuxbrew/.linuxbrew/bin/claude')) {
      log('Claude Code installed successfully');
    } else {
      log('Claude Code install completed but binary not found at expected path — check brew logs');
    }

    // ENG-5357: fresh install is by definition the latest version — stamp
    // the upgrade marker so the next poll's maybeUpgradeClaudeCode() won't
    // immediately fire a redundant `brew upgrade`. Same disk-persisted
    // throttle described next to maybeUpgradeClaudeCode().
    stampClaudeCodeUpgradeMarker();
  }
  // ENG-5357: upgrade for existing installs is no longer kicked off here.
  // The in-memory `frameworkBinaryChecked` guard above is process-scope,
  // so on alyve-host-1 a restart storm fired N concurrent `brew upgrade
  // --cask claude-code` jobs and pegged the host until it wedged. Upgrade
  // now lives in maybeUpgradeClaudeCode() which is called from pollCycle
  // and throttled via an on-disk 24h marker — restarts can no longer
  // re-trigger it.

  // Check if Claude Code is authenticated. The binary that's already on
  // disk works regardless of upgrade state.
  agentRuntimeAuthenticated = await checkClaudeAuth();
}

// ---------------------------------------------------------------------------
// Claude Code background upgrade (ENG-5357)
// ---------------------------------------------------------------------------
//
// Called from pollCycle on every tick. Returns immediately when the disk
// marker is fresh — only the once-per-day actual `brew upgrade` does any
// real work. This is the same disk-marker pattern as the agt CLI self-
// update path (checkAndUpdateCli); the window is just longer because
// Claude Code releases on a much slower cadence than the agt CLI.
//
// The marker is stamped BEFORE the fire-and-forget upgrade kicks off so
// concurrent poll cycles inside the 30-120s upgrade window cannot double-
// trigger. Without that pre-stamp, every poll tick during a slow upgrade
// would see "no marker yet" and queue another brew job behind the lock —
// the same restart-loop pathology this issue was created to fix.

let claudeCodeUpgradeInFlight = false;

// The marker-throttle primitives live in a tiny dependency-free module so
// their unit test (ENG-6418) doesn't have to evaluate this whole file's
// import graph. Imported for internal use (maybeUpgradeClaudeCode below)
// and re-exported to keep the public surface unchanged for any caller that
// imports them from manager-worker.js.
export {
  claudeCodeUpgradeMarkerPath,
  stampClaudeCodeUpgradeMarker,
  claudeCodeUpgradeThrottled,
};

export async function maybeUpgradeClaudeCode(): Promise<void> {
  if (claudeCodeUpgradeInFlight) return;
  if (claudeCodeUpgradeThrottled()) return;

  // ENG-5963: gate this legacy `brew upgrade --cask claude-code` path to the
  // maintenance window too — the upgraded binary applies on the next session
  // start, so it's restart-adjacent like the agt-cli self-update. Deferring
  // BEFORE the throttle stamp below means an out-of-window poll retries on the
  // next in-window cycle instead of burning the ~24h throttle on a no-op.
  if (decideMaintenanceWindowGate({ window: cachedMaintenanceWindow, now: new Date() }) === 'defer') {
    return;
  }

  // Stamp BEFORE kicking off the upgrade so concurrent polls inside the
  // upgrade window see "throttled" and bail. Without this, every poll
  // tick during the 30-120s brew run would queue another job. The
  // in-flight flag is a second belt for the rare case where two polls
  // race past the disk read in the same event-loop tick.
  claudeCodeUpgradeInFlight = true;
  stampClaudeCodeUpgradeMarker();

  const { execFileSync } = await import('node:child_process');
  const brewPath = resolveBrewPath(execFileSync);
  if (!brewPath) {
    claudeCodeUpgradeInFlight = false;
    return;
  }

  // Only upgrade if the binary already exists; install lives in
  // ensureFrameworkBinary's hot path (it's a hard prerequisite, not a
  // refresh, and shouldn't be deferred to the poll loop).
  if (!claudeBinaryInstalled(execFileSync)) {
    claudeCodeUpgradeInFlight = false;
    return;
  }

  const isRoot = typeof process.getuid === 'function' && process.getuid() === 0;
  const runBrew = (args: string[], opts: { timeout: number }) => {
    if (isRoot) {
      return runAsync('sudo', ['-u', 'ec2-user', '-H', brewPath, ...args], { ...opts, cwd: '/tmp' });
    }
    return runAsync(brewPath, args, opts);
  };

  log(`Checking for Claude Code updates in background${isRoot ? ' (as ec2-user via sudo)' : ''}...`);
  runBrew(['upgrade', '--cask', 'claude-code'], { timeout: 120_000 })
    .then((r) => {
      const combined = `${r.stdout}\n${r.stderr}`;
      if (r.code === 0) {
        if (combined.includes('already installed') || combined.includes('up-to-date')) {
          log('Claude Code is already up to date');
        } else {
          log('Claude Code upgraded successfully (will apply on next session start)');
        }
      } else if (combined.includes('already installed') || combined.includes('up-to-date') || combined.includes('not upgraded')) {
        log('Claude Code is already up to date');
      } else {
        log(`Claude Code upgrade failed (exit ${r.code}): ${r.stderr.trim() || r.stdout.trim()}`);
      }
    })
    .catch((err) => log(`Claude Code upgrade failed: ${(err as Error).message}`))
    .finally(() => { claudeCodeUpgradeInFlight = false; });
}

// ---------------------------------------------------------------------------
// CLI self-update via Homebrew (throttled to once every 5 minutes)
// ---------------------------------------------------------------------------

const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// ENG-5862 — path of the coalesce-gate marker. Distinct from
// `.last-update-check` (which throttles the cheap version probe): this one
// records the last time we actually applied an upgrade and triggered a
// restart, so the brew + npm paths can both refuse to restart again within
// the coalesce window. See self-update-coalesce.ts for the rationale.
function selfUpdateAppliedMarkerPath(): string {
  return join(homedir(), '.augmented', '.last-self-update-applied');
}
// ENG-4485: only log "up to date" once per manager process so the periodic
// check doesn't spam the log every 5 minutes. Errors, "update available",
// and "upgraded to X" always log.
let selfUpdateUpToDateLogged = false;
// ENG-4488: set after a successful `brew upgrade` so the poll loop exits
// cleanly and a supervisor can re-spawn onto the new binary. Without this,
// the running process keeps executing from the old Cellar path whose lazy-
// imported hashed chunks brew has already removed — the first dynamic
// import after the swap throws `Cannot find module`.
let restartAfterUpgrade = false;
let pendingUpgradeVersion: string | null = null;
// In-flight lock for the self-update path. pollCycle() kicks
// checkAndUpdateCli() off fire-and-forget, so without this a slow
// `brew update` or `npm install -g` lets later polls re-enter and
// launch a second updater concurrently. brew/npm both lock-file
// internally and would error on the collision; worse, both could
// flip restartAfterUpgrade and double-schedule a manager respawn.
let selfUpdateInFlight = false;
// ENG-6692: `force` is the admin "Update CLI now" one-shot. It skips the
// `.last-update-check` throttle here and (threaded down) bypasses the
// maintenance-window + coalesce gates in the brew/npm paths — the operator
// explicitly asked for an immediate upgrade, like the `urgent` dist-tag. It does
// NOT bypass the defer-until-idle gate: the caller (the heartbeat consume block)
// only invokes this once the host is idle, so a live turn is never interrupted.
interface CheckAndUpdateCliOpts {
  /** Forced "Update CLI now" (ENG-6692): bypass throttle + window + coalesce. */
  force?: boolean;
}

async function checkAndUpdateCli(opts?: CheckAndUpdateCliOpts): Promise<SelfUpdateOutcome> {
  const force = opts?.force ?? false;
  // Already running: don't double-kick. 'failed' (not 'noop') so a forced caller
  // that somehow races past its own in-flight guard retries rather than burns
  // its one-shot for an update this call didn't run.
  if (selfUpdateInFlight) return 'failed';
  selfUpdateInFlight = true;
  try {
    const cliPath = process.argv[1] ?? '';
    const isDevMode = cliPath.includes('/src/') || cliPath.includes('tsx');
    if (isDevMode) return 'noop';

    // Distinguish brew formula installs from npm-global installs. A bare
    // `/home/linuxbrew/` or `/opt/homebrew/` prefix isn't enough — Linuxbrew's
    // hosted Node lays npm-global packages under `.../lib/node_modules/...`,
    // so we match on the canonical formula path `/Cellar/<formula>/`.
    let resolvedPath = cliPath;
    try {
      const { realpathSync } = await import('node:fs');
      resolvedPath = realpathSync(cliPath);
    } catch { /* fall back to argv[1] verbatim */ }
    const isBrewFormula = /\/Cellar\/[^/]+\//.test(resolvedPath);
    const isNpmGlobal = !isBrewFormula && resolvedPath.includes('node_modules');
    if (!isBrewFormula && !isNpmGlobal) return 'noop'; // Unknown install method — can't self-update; don't leave the one-shot stuck

    // Throttle: check last update time from disk. ENG-6692: the forced
    // "Update CLI now" path skips the throttle so an operator click isn't
    // swallowed by a recent cheap-probe stamp.
    const { readFileSync: readF, writeFileSync: writeF } = await import('node:fs');
    const markerPath = join(homedir(), '.augmented', '.last-update-check');
    if (!force) {
      try {
        const lastCheck = parseInt(readF(markerPath, 'utf-8').trim(), 10);
        if (Date.now() - lastCheck < UPDATE_CHECK_INTERVAL_MS) return 'noop';
      } catch { /* no marker yet — first run */ }
    }

    // Stamp the marker BEFORE running the heavy updater so a long
    // brew/npm run doesn't let the same poll cycle re-enter past the
    // throttle as soon as the lock releases. Update again at the end
    // so the next throttle window measures from completion.
    try {
      writeF(markerPath, String(Date.now()));
    } catch { /* non-fatal */ }

    const outcome = isBrewFormula
      ? await checkAndUpdateCliViaBrew(force)
      : await checkAndUpdateCliViaNpm(force);

    // Update marker regardless of outcome — failures here shouldn't trigger
    // a tight retry loop on every poll cycle.
    try {
      writeF(markerPath, String(Date.now()));
    } catch { /* non-fatal */ }
    return outcome;
  } finally {
    selfUpdateInFlight = false;
  }
}

async function checkAndUpdateCliViaBrew(force = false): Promise<SelfUpdateOutcome> {
  const { execFileSync } = await import('node:child_process');
  // Use the shared resolveBrewPath helper rather than a bare `which brew`.
  // The manager runs with cloud-init's minimal env on EC2 (and a similarly
  // sparse PATH under launchd on macOS), so `which brew` returns nothing
  // even on hosts where the CLI clearly lives in a Cellar — without this,
  // self-update on those hosts silently no-ops.
  const brewPath = resolveBrewPath(execFileSync);
  if (!brewPath) return 'noop'; // No Homebrew on PATH or canonical install dirs.

  // ENG-6771: a failed tap-metadata refresh makes a subsequent "no outdated"
  // result UNRELIABLE (brew outdated reads the stale local cache), so a forced
  // check must not consume its one-shot on that false no-op — track freshness
  // and return 'failed' (retry) rather than 'noop' (consume) when stale.
  let metadataFresh = true;
  // Refresh tap metadata first. `brew outdated` consults the LOCAL formula
  // cache, so without this it will happily report no updates even when the
  // tap has a newer version on GitHub. Hitting this was the reason self-
  // updates silently stopped working for hours after a version bump.
  try {
    execFileSync(brewPath, ['update', '--quiet'], { timeout: 60_000, stdio: 'pipe' });
  } catch (err) {
    metadataFresh = false;
    log(`[self-update] brew update failed (continuing with stale cache): ${(err as Error).message}`);
  }

  try {
    const outdated = execFileSync(brewPath, ['outdated', '--json=v2'], {
      timeout: 30_000,
      encoding: 'utf-8',
    });
    const data = JSON.parse(outdated) as { formulae?: Array<{ name: string; installed_versions: string[]; current_version: string }> };
    const agtOutdated = data.formulae?.find((f) => f.name === 'agt' || f.name === 'integrity-labs/tap/agt');

    if (agtOutdated) {
      const installed = agtOutdated.installed_versions?.[0] ?? 'unknown';
      const latest = agtOutdated.current_version ?? 'unknown';

      // ENG-5963: gate the restart-causing upgrade to the maintenance window
      // before the coalesce gate. (The urgent npm dist-tag escape hatch is
      // npm-only — brew hosts have no equivalent moving target — so brew relies
      // on the window + coalesce. AGT_MAINTENANCE_WINDOW_DISABLE bypasses.)
      // ENG-6692: the forced "Update CLI now" path bypasses the window.
      if (!force && decideMaintenanceWindowGate({ window: cachedMaintenanceWindow, now: new Date() }) === 'defer') {
        log(
          `[self-update] agt CLI ${installed} → ${latest} (brew) deferred — ` +
            formatWindowDeferLogLine(cachedMaintenanceWindow ?? {}, new Date()),
        );
        return 'noop';
      }

      // ENG-5862 — gate the restart-causing upgrade behind the coalesce window
      // shared with the npm path. Defer logs but never executes the upgrade so
      // we don't burn into the marker that another caller is racing us for.
      // ENG-6692: the forced "Update CLI now" path bypasses coalesce too.
      const coalesceWindowMs = resolveCoalesceWindowMs();
      const coalesce = decideSelfUpdateCoalesce({
        windowMs: coalesceWindowMs,
        lastAppliedMs: readLastSelfUpdateAppliedMs(selfUpdateAppliedMarkerPath()),
        now: Date.now(),
      });
      if (!force && !coalesce.proceed) {
        log(formatCoalesceDeferLogLine({
          installed, latest, channelLabel: 'brew',
          remainingMs: coalesce.remainingMs, windowMs: coalesceWindowMs,
        }));
        return 'noop';
      }

      log(`[self-update] agt CLI update available: ${installed} → ${latest}. Upgrading via brew${force ? ' (forced — Update CLI now)' : ''}...`);

      try {
        execFileSync(brewPath, ['upgrade', 'integrity-labs/tap/agt'], {
          timeout: 120_000,
          stdio: 'pipe',
        });
        log(`[self-update] agt CLI upgraded to ${latest}. Scheduling manager restart so the new binary takes effect.`);
        stampLastSelfUpdateApplied(selfUpdateAppliedMarkerPath());
        restartAfterUpgrade = true;
        pendingUpgradeVersion = latest;
        return 'updated';
      } catch (err) {
        log(`[self-update] brew upgrade failed: ${(err as Error).message}`);
        return 'failed';
      }
    } else if (!selfUpdateUpToDateLogged) {
      log(`[self-update] agt CLI is up to date (brew, ${agtCliVersion})`);
      selfUpdateUpToDateLogged = true;
    }
    // Not outdated. On fresh metadata this is a genuine no-op (consume the
    // one-shot). On STALE metadata (the `brew update` above failed) the
    // "no outdated" verdict is unreliable, so report 'failed' — a forced check
    // then retries next heartbeat instead of burning the request on a result we
    // couldn't confirm. (ENG-6771; CodeRabbit PR #2447.)
    return metadataFresh ? 'noop' : 'failed';
  } catch (err) {
    log(`[self-update] brew outdated failed: ${(err as Error).message}`);
    return 'failed';
  }
}

/**
 * npm-global install upgrade path — used on AWS EC2 hosts (the bootstrap
 * runs `npm install -g @integrity-labs/agt-cli`). Queries the npm registry
 * directly (the source of truth that the API also reads from), compares
 * against the build-time `__CLI_VERSION__`, and re-runs `npm install -g`
 * when behind. Requires `npm` on PATH; on AL2023 hosts the manager runs as
 * ec2-user with passwordless sudo so we shell out via `sudo -n` to write
 * into /usr/lib/node_modules (owned by root).
 */
async function checkAndUpdateCliViaNpm(force = false): Promise<SelfUpdateOutcome> {
  // Skip the comparison entirely in dev where __CLI_VERSION__ wasn't injected.
  if (agtCliVersion === 'dev') return 'noop';

  // Release channel — `latest` (prod default) or a gated channel like `test`
  // (set on the test environment's hosts via AGT_CLI_RELEASE_CHANNEL). The
  // channel is the npm dist-tag we track: prod hosts follow `latest`,
  // test hosts follow `test`, same registry. Unset ⇒ `latest`, so prod
  // behaviour is unchanged.
  const channel = process.env.AGT_CLI_RELEASE_CHANNEL || 'latest';

  // Query the npm registry's channel dist-tag. Direct fetch — same source
  // the /cli/version API endpoint reads from — so the manager doesn't need
  // any extra wiring beyond outbound HTTPS. AbortSignal.timeout keeps a
  // hung registry from blocking the poll loop.
  let latest: string;
  try {
    const res = await fetch(
      `https://registry.npmjs.org/@integrity-labs/agt-cli/${encodeURIComponent(channel)}`,
      {
        signal: AbortSignal.timeout(10_000),
        headers: { Accept: 'application/json' },
      },
    );
    if (!res.ok) {
      log(`[self-update] npm registry returned ${res.status} (channel=${channel})`);
      return 'failed';
    }
    const body = (await res.json()) as { version?: string };
    if (!body.version) {
      log(`[self-update] npm registry response missing version field`);
      return 'failed';
    }
    latest = body.version;
  } catch (err) {
    log(`[self-update] npm registry fetch failed: ${(err as Error).message}`);
    return 'failed';
  }

  // ENG-5963: urgent escape hatch — a version published to the `urgent` npm
  // dist-tag installs immediately, bypassing BOTH the maintenance window and
  // the coalesce gate, and overriding the channel pin entirely (a fleet-wide
  // hotfix lands on `test`-pinned hosts too). Checked before the channel
  // shouldUpdate logic so an urgent fix lands even when the channel is current.
  const urgentTarget = await fetchUrgentDistTagVersion();
  // Prerelease-safe comparison: install the urgent target when it differs from
  // what's installed AND isn't a numeric-tuple downgrade. `isNewerSemver`
  // alone parses prerelease tags (e.g. 0.27.6-test.2) into NaN and would treat
  // a canary/test host as "not newer", silently skipping an urgent hotfix —
  // exactly the hosts the escape hatch is for. `isOlderSemverTuple` strips the
  // prerelease suffix before comparing, so a hotfix still lands there.
  if (
    isUrgentUpgrade({
      urgentTarget,
      installed: agtCliVersion,
      isNewer: (installed, candidate) =>
        candidate !== installed && !isOlderSemverTuple(installed, candidate),
    })
  ) {
    log(
      `[self-update] URGENT agt CLI hotfix ${agtCliVersion} → ${urgentTarget} ` +
        `(overrides channel=${channel} + maintenance window). Upgrading via npm...`,
    );
    return await installAgtCliViaNpm(urgentTarget!, 'urgent');
  }

  // `latest` channel: never move backwards — only upgrade to a strictly
  // newer semver (prod-safe; ignores prerelease tags entirely). A gated
  // channel is a moving pointer to a specific build, so track it exactly:
  // install whenever the tag points somewhere other than what we run,
  // including prereleases that isNewerSemver can't compare.
  //
  // ENG-5785: even on a gated channel, refuse to *downgrade*. The silent
  // revert this guards against: a host is pinned to `test` (canary),
  // someone manually upgrades to a newer stable via `npm i -g @latest`,
  // then the next poll cycle finds `test` still pointing at an older
  // prerelease and reinstalls it on top. Symptom: agents look upgraded
  // but bounce back to the old version. Today (2026-05-31) this blocked
  // the ENG-5779 / ENG-5781 fixes from landing on agt-aws-1 until the
  // canary pin was manually removed.
  let shouldUpdate: boolean;
  if (channel === 'latest') {
    shouldUpdate = shouldUpdateOnLatest(agtCliVersion, latest);
  } else if (latest === agtCliVersion) {
    shouldUpdate = false;
  } else if (isOlderSemverTuple(agtCliVersion, latest)) {
    // Channel target is strictly older than what we run. Refuse and warn.
    log(
      `[self-update] refusing to downgrade: installed ${agtCliVersion} is newer than ` +
        `channel=${channel} target ${latest}. Bump the channel tag forward to resume tracking.`,
    );
    shouldUpdate = false;
  } else {
    shouldUpdate = true;
  }
  if (!shouldUpdate) {
    if (!selfUpdateUpToDateLogged) {
      log(`[self-update] agt CLI is up to date (npm, channel=${channel}, ${agtCliVersion})`);
      selfUpdateUpToDateLogged = true;
    }
    // Already on the latest in-channel build: a legitimate no-op. A forced
    // request is consumed here (matches the dialog's "already on the latest CLI
    // → no-op"), so it doesn't retry forever.
    return 'noop';
  }

  // ENG-5963: gate the (non-urgent) restart-causing upgrade to the maintenance
  // window before the coalesce gate. Outside the window we defer to the next
  // in-window poll; AGT_MAINTENANCE_WINDOW_DISABLE bypasses for incidents.
  // ENG-6692: the forced "Update CLI now" path bypasses the window.
  if (!force && decideMaintenanceWindowGate({ window: cachedMaintenanceWindow, now: new Date() }) === 'defer') {
    log(
      `[self-update] agt CLI ${agtCliVersion} → ${latest} (channel=${channel}) deferred — ` +
        formatWindowDeferLogLine(cachedMaintenanceWindow ?? {}, new Date()),
    );
    // Window defer only happens for non-forced checks (force bypasses above);
    // the return value is ignored by non-forced callers.
    return 'noop';
  }

  // ENG-5862 — same coalesce gate as the brew path. Multiple patch bumps that
  // arrive within the window collapse into one restart at the next poll cycle
  // after the window expires (the registry will return the latest tag then).
  // ENG-6692: the forced "Update CLI now" path bypasses coalesce too.
  const coalesceWindowMs = resolveCoalesceWindowMs();
  const coalesce = decideSelfUpdateCoalesce({
    windowMs: coalesceWindowMs,
    lastAppliedMs: readLastSelfUpdateAppliedMs(selfUpdateAppliedMarkerPath()),
    now: Date.now(),
  });
  if (!force && !coalesce.proceed) {
    log(formatCoalesceDeferLogLine({
      installed: agtCliVersion, latest, channelLabel: `npm, channel=${channel}`,
      remainingMs: coalesce.remainingMs, windowMs: coalesceWindowMs,
    }));
    return 'noop';
  }

  log(`[self-update] agt CLI update available: ${agtCliVersion} → ${latest} (channel=${channel}). Upgrading via npm...`);
  return await installAgtCliViaNpm(latest, `channel=${channel}`);
}

/**
 * ENG-5963: fetch the version the `urgent` npm dist-tag points at, or null when
 * the tag isn't published (the 404 common case) or the registry is unreachable.
 * Best-effort — a failure here just means "no urgent hotfix", and never blocks
 * the normal channel path.
 */
async function fetchUrgentDistTagVersion(): Promise<string | null> {
  try {
    const res = await fetch('https://registry.npmjs.org/@integrity-labs/agt-cli/urgent', {
      signal: AbortSignal.timeout(10_000),
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { version?: string };
    return body.version ?? null;
  } catch {
    return null;
  }
}

/**
 * Install a specific agt-cli version globally via npm and schedule the manager
 * restart. Shared by the channel path and the urgent escape hatch; `reasonLabel`
 * only colours the log line. Stamps the coalesce marker so a same-window race
 * collapses to one restart.
 *
 * /usr/lib/node_modules is root-owned on EC2; the manager runs as ec2-user.
 * `sudo -n` non-interactively elevates (NOPASSWD on AL2023) and fails fast
 * rather than hang on a TTY prompt; when already root we skip sudo entirely.
 */
async function installAgtCliViaNpm(version: string, reasonLabel: string): Promise<SelfUpdateOutcome> {
  const { execFileSync } = await import('node:child_process');
  const isRoot = typeof process.getuid === 'function' && process.getuid() === 0;
  const cmd = isRoot ? 'npm' : 'sudo';
  const installArgs = [
    'install',
    '-g',
    `@integrity-labs/agt-cli@${version}`,
    '--registry=https://registry.npmjs.org',
  ];
  const args = isRoot ? installArgs : ['-n', 'npm', ...installArgs];
  try {
    execFileSync(cmd, args, { timeout: 180_000, stdio: 'pipe' });
  } catch (err) {
    log(`[self-update] npm upgrade failed: ${(err as Error).message}`);
    return 'failed';
  }

  // ENG-6698: post-install smoke check. `npm i -g` removes the old package
  // before writing the new one, so an install interrupted mid-flight can leave
  // `agt` a dangling symlink even after the spawn returns. Restarting into a
  // dangling binary is what took the whole fleet dark (203/EXEC crashloop ->
  // systemd `failed`). Verify the freshly-installed binary actually runs and
  // reports the new version BEFORE scheduling the restart; if it doesn't, keep
  // the current (still-working) in-memory process running and let the next
  // cycle retry. The systemd ExecStartPre repair is the backstop for the case
  // where this manager is itself killed mid-install and never reaches here.
  let smokeOk = false;
  try {
    const out = execFileSync('agt', ['--version'], { timeout: 15_000, stdio: 'pipe' }).toString();
    smokeOk = isAgtVersionSmokeOk(out, version);
    if (!smokeOk) {
      log(
        `[self-update] smoke check failed: \`agt --version\` did not report ${version} after install (got: ${out.trim().slice(0, 80)}). NOT restarting.`,
      );
    }
  } catch (err) {
    log(
      `[self-update] smoke check failed: \`agt --version\` would not run after install (${(err as Error).message}). The binary may be dangling; NOT restarting. systemd ExecStartPre will repair on the next start if needed.`,
    );
  }
  if (!smokeOk) return 'failed';

  log(
    `[self-update] agt CLI upgraded to ${version} (${reasonLabel}). Scheduling manager restart so the new binary takes effect.`,
  );
  stampLastSelfUpdateApplied(selfUpdateAppliedMarkerPath());
  restartAfterUpgrade = true;
  pendingUpgradeVersion = version;
  return 'updated';
}

/**
 * ENG-6353: true when a version string carries a prerelease suffix
 * (e.g. `0.27.150-test.17`). `isNewerSemver` NaN-parses the prerelease patch
 * component and reports "not newer" for EVERY candidate, so a host left on a
 * `-test.x` build (an unpinned canary) would never auto-upgrade to a higher
 * stable `latest`. The self-update path detects prereleases and falls back to
 * tuple comparison (`isOlderSemverTuple`) instead. Build metadata after `+` is
 * not a prerelease, so it's stripped before the `-` check.
 */
export function isPrereleaseVersion(v: string): boolean {
  return v.replace(/^v/, '').split('+')[0]!.includes('-');
}

/**
 * ENG-6353: the `latest`-channel self-update decision.
 *
 * - Stable installed → upgrade only to a strictly-newer semver
 *   (`isNewerSemver`) — unchanged prod behaviour.
 * - Prerelease installed (e.g. `0.27.150-test.17`, left behind when a canary
 *   host is unpinned) → `isNewerSemver` NaN-parses the prerelease patch and
 *   reports "not newer" for EVERY stable `latest`, stranding the host on the
 *   prerelease forever (the trap that forced a manual force-install on
 *   agt-aws-1 during the ENG-6300 unpin). Compare by numeric tuple instead:
 *   upgrade unless `latest` is a strictly-OLDER tuple — preserving the
 *   never-downgrade guarantee while letting a higher stable land.
 */
export function shouldUpdateOnLatest(installed: string, latest: string): boolean {
  if (isPrereleaseVersion(installed)) {
    return latest !== installed && !isOlderSemverTuple(installed, latest);
  }
  return isNewerSemver(installed, latest);
}

/** Compare two semver strings; returns true when `remote` is strictly newer. */
function isNewerSemver(local: string, remote: string): boolean {
  const parse = (v: string) => v.replace(/^v/, '').split('.').map(Number);
  const l = parse(local);
  const r = parse(remote);
  for (let i = 0; i < 3; i++) {
    const lv = l[i] ?? 0;
    const rv = r[i] ?? 0;
    if (rv !== lv) return rv > lv;
  }
  return false;
}

/**
 * Compare the major.minor.patch tuple of two semver strings, ignoring
 * prerelease/build suffixes; returns true when `remote`'s tuple is
 * strictly older than `local`'s. Used by the channel-aware self-update
 * guard (ENG-5785) to refuse a downgrade from a manually-upgraded
 * stable back to an older channel-pinned prerelease.
 *
 * Note: a same-tuple/different-prerelease pair (e.g. local=`0.27.6`,
 * remote=`0.27.6-test.2`) returns false — the channel still moves the
 * host onto its target. The guard only blocks strictly-older tuples.
 */
export function isOlderSemverTuple(local: string, remote: string): boolean {
  const parse = (v: string) =>
    v
      .replace(/^v/, '')
      .split(/[-+]/)[0]!
      .split('.')
      .map((p) => Number(p));
  const l = parse(local);
  const r = parse(remote);
  for (let i = 0; i < 3; i++) {
    const lv = l[i] ?? 0;
    const rv = r[i] ?? 0;
    if (rv !== lv) return rv < lv;
  }
  return false;
}

/**
 * Check Claude Code auth and return true if logged in.
 *
 * Uses the same file/keychain probe as `detectClaudeAuth` (our heartbeat
 * reporter) instead of shelling out to `claude auth status`. On a root-owned
 * manager that's spawned by cloud-init, `claude` isn't on PATH and even if
 * it were, running it as root would look at `/root/.claude/` — but the
 * operator logs in as `ssm-user` or `ec2-user`, so the creds live under
 * their home. `detectClaudeAuth` probes every user home in `/home` when
 * running as root on Linux, so it finds subscription creds regardless of
 * which user completed `claude /login`.
 */
async function checkClaudeAuth(): Promise<boolean> {
  try {
    const report = await detectClaudeAuth();
    if (!report) {
      log('⚠️  Claude Code is not authenticated. Run `claude /login` (or set `ANTHROPIC_API_KEY`) on the host, then the manager will pick it up on the next cycle.');
      return false;
    }
    if (report.status === 'expired') {
      log(`⚠️  Claude Code auth expired (${report.mode}). Re-run \`claude /login\` to refresh.`);
      return false;
    }
    if (report.status === 'expiring_soon') {
      log(`[auth] Claude Code token expiring soon (expires_at=${report.expires_at}) — still valid, proceeding`);
    }
    return true;
  } catch (err) {
    log(`⚠️  Claude Code auth probe failed: ${(err as Error).message}`);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Conversation success evaluation — isolated Haiku spawn
//
// Runs `claude -p --model <haiku>` with the agent's auth applied but WITHOUT
// the agent's CLAUDE.md system prompt, MCP servers, or tools. This is an
// objective evaluator, not the agent persona — and keeping the transcript
// host-side (it only reaches Anthropic via the agent's own auth, never the agt
// API) is the whole point of scoring on the manager. Returns stdout text.
// ---------------------------------------------------------------------------

let evalEmptyMcpConfigPath: string | null = null;
function ensureEvalEmptyMcpConfig(): string {
  if (evalEmptyMcpConfigPath && existsSync(evalEmptyMcpConfigPath)) return evalEmptyMcpConfigPath;
  const dir = join(homedir(), '.augmented');
  try { mkdirSync(dir, { recursive: true }); } catch { /* best-effort */ }
  const p = join(dir, '.eval-empty-mcp.json');
  writeFileSync(p, JSON.stringify({ mcpServers: {} }));
  evalEmptyMcpConfigPath = p;
  return p;
}

async function runEvalClaude(prompt: string, model: string): Promise<string> {
  const childEnv: Record<string, string | undefined> = { ...process.env };
  await applyClaudeAuthToEnv(childEnv, 'conversation-eval');
  const emptyMcp = ensureEvalEmptyMcpConfig();
  const args = [
    '-p', prompt,
    '--model', model,
    '--output-format', 'text',
    // Isolation: only the empty MCP config, ignore project/user config + no tools.
    '--mcp-config', emptyMcp,
    '--strict-mcp-config',
    '--permission-mode', 'auto',
    '--allowedTools', '',
  ];
  const { stdout } = await execFilePromiseLong(resolveClaudeBinary(), args, {
    cwd: homedir(),
    timeout: 120_000,
    stdin: 'ignore',
    env: childEnv,
    onSpawn: (pid) => registerClaudeSpawn({ pid, started_at: Date.now(), kind: 'conv-eval' }),
    onExit: (pid) => unregisterClaudeSpawn(pid),
  });
  return stdout;
}

// Pluggable conversation-eval scoring backend:
//   AGT_CONV_EVAL_BACKEND=claude-p (default) - Haiku via `claude -p`, reusing the
//     agent's auth; transcript reaches Anthropic (the boundary the agent already
//     crosses), nothing else. A heavy subprocess, but free under the agent's Max
//     subscription.
//   AGT_CONV_EVAL_BACKEND=anthropic-api - Haiku via a direct Anthropic Messages
//     API fetch (no subprocess). Same Anthropic boundary, but metered per-token.
//     Auth: AGT_CONV_EVAL_ANTHROPIC_API_KEY (falls back to ANTHROPIC_API_KEY);
//     missing key fails CLOSED (eval disabled) rather than reverting to claude-p,
//     so the cost/auth boundary never shifts by accident.
//   AGT_CONV_EVAL_BACKEND=local - an OpenAI-compatible endpoint on the host
//     (Ollama / vLLM / llama.cpp / LM Studio) serving e.g. Gemma 4 12B; the
//     transcript never leaves the host at all. Tunables:
//       AGT_CONV_EVAL_LOCAL_URL     (default http://localhost:11434/v1/chat/completions)
//       AGT_CONV_EVAL_LOCAL_MODEL   (default gemma4:12b)
//       AGT_CONV_EVAL_LOCAL_API_KEY (optional bearer, for vLLM etc.)
//   AGT_CONV_EVAL_CLAUDE_MODEL overrides the Haiku model id for both Claude paths.
// The backend KIND is the 'conversation-eval-backend' registry flag (ADR-0022),
// so AGT_CONV_EVAL_BACKEND is now the operator override on that flag rather than
// a standalone gate, and the fleet default flips centrally (no restart). The
// other AGT_CONV_EVAL_* knobs above stay plain env. Selection logic lives in
// selectEvalBackend (conversation-evaluator.ts) so it's pure / unit-testable;
// this wrapper reads the flag + env and rebuilds only when they change.
// ENG-6200 / ADR-0022: host-side memory extraction is now the 'memory-extraction'
// registry feature flag, resolved env override → heartbeat → compiled default.
// Ships dark (default false). The legacy AGT_MEMORY_EXTRACTION_ENABLED env var
// stays the highest-precedence operator override during migration, so any host
// already setting it keeps working; a central DB flip now ALSO reaches a running
// manager within one heartbeat (~5m) without a restart. Reuses the
// conversation-eval backend (same on-host privacy boundary).
function memoryExtractionEnabled(): boolean {
  return hostFlagStore().getBoolean('memory-extraction');
}

// ENG-7235: on an OpenRouter-metered host the idle, no-deliverable scorers
// (conversation eval + memory extraction) are real per-token cost - and the
// default `claude-p` backend additionally can't authenticate there (no Max
// OAuth) so it would retry-loop "Not logged in" every poll. Suppress the metered
// backends; the free, host-private `local` backend is the explicit opt-in to
// keep scoring on a metered host. Decided per poll off the cached host auth mode
// (host-level, populated by the early exchange) + the resolved backend flag.
function conversationScorerSuppressed(): boolean {
  return meteredScorerSuppressed(
    getCachedClaudeAuthMode(),
    hostFlagStore().getString('conversation-eval-backend'),
  );
}

let lastMeteredScorerLogMs = 0;
function logMeteredScorerSuppressed(): void {
  const nowMs = Date.now();
  // Throttle: this runs per-agent per-poll; one line / 30 min is enough to make
  // the cost-skip observable without flooding manager.log.
  if (nowMs - lastMeteredScorerLogMs < 30 * 60_000) return;
  lastMeteredScorerLogMs = nowMs;
  const kind = hostFlagStore().getString('conversation-eval-backend');
  log(
    `[conversation-eval] host is OpenRouter-metered (backend='${kind}') - skipping conversation eval + memory extraction to avoid per-token cost for a no-deliverable scorer (ENG-7235). Set conversation-eval-backend=local to run the free host-local scorer instead.`,
  );
}

let conversationEvalBackend: ConversationEvalBackend | null = null;
let conversationEvalBackendSig: string | null = null;
function resolveConversationEvalBackend(): ConversationEvalBackend {
  // Backend kind is the 'conversation-eval-backend' enum flag (ENG-6581), read
  // through the host flag store so it carries full ADR-0022 precedence:
  // env override (AGT_CONV_EVAL_BACKEND) → central heartbeat flag/overrides →
  // compiled default ('claude-p'). Reading it per-call (the store reads its
  // cached map, cheap) means a central flip reaches a running manager within one
  // heartbeat with no restart - we just rebuild the backend when the resolved
  // inputs actually change (signature below), so steady state stays memoised.
  const kind = hostFlagStore().getString('conversation-eval-backend');
  const claudeModel = process.env['AGT_CONV_EVAL_CLAUDE_MODEL']?.trim() || DEFAULT_CLAUDE_EVAL_MODEL; // feature-gate-allow: model tunable, not a gate (matches the _MODE substring of _MODEL)
  const anthropicApiKey =
    process.env['AGT_CONV_EVAL_ANTHROPIC_API_KEY']?.trim() ||
    process.env['ANTHROPIC_API_KEY']?.trim() ||
    '';
  const local = {
    url: process.env['AGT_CONV_EVAL_LOCAL_URL']?.trim() || DEFAULT_LOCAL_EVAL_URL,
    model: process.env['AGT_CONV_EVAL_LOCAL_MODEL']?.trim() || DEFAULT_LOCAL_EVAL_MODEL, // feature-gate-allow: model tunable, not a gate (matches the _MODE substring of _MODEL)
    apiKey: process.env['AGT_CONV_EVAL_LOCAL_API_KEY']?.trim() || undefined,
  };
  const sig = `${kind}|${claudeModel}|${anthropicApiKey ? 'key' : 'nokey'}|${local.url}|${local.model}|${local.apiKey ? 'lkey' : 'nolkey'}`;
  if (conversationEvalBackend && conversationEvalBackendSig === sig) return conversationEvalBackend;
  // selectEvalBackend is non-throwing by design (fail-closed via a backend whose
  // run() rejects, contained by the try/catch in maybeEvaluateConversations /
  // maybeExtractMemories). Wrap it anyway so a future change that made backend
  // construction throw could only ever disable eval, never bubble out of the
  // synchronous resolve and abort the rest of processAgent's refresh/session work.
  try {
    conversationEvalBackend = selectEvalBackend({
      kind,
      claudeModel,
      anthropicApiKey,
      local,
      // The manager owns the spawn helpers; selectEvalBackend stays transport-free.
      claudePRunner: runEvalClaude,
      log,
    });
  } catch (err) {
    const message = (err as Error).message;
    log(`[conversation-eval] backend unavailable, eval disabled: ${message}`);
    conversationEvalBackend = {
      model: `${kind || 'default'}:unavailable`,
      run: async () => {
        throw new Error(message);
      },
    };
  }
  conversationEvalBackendSig = sig;
  return conversationEvalBackend;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// State file lives under the manager's configDir so a non-default dir
// doesn't split manager artifacts across locations.
function getStateFile(): string {
  return join(config?.configDir ?? join(process.env['HOME'] ?? '/tmp', '.augmented'), 'manager-state.json');
}

// ENG-4712: see channel-hash-cache.ts for the rationale. We keep the
// load/save call sites inline here so every cache mutation pairs with a
// persist; the module holds the file format + IO and stays trivially
// testable.
function channelHashCacheDir(): string {
  return config?.configDir ?? join(process.env['HOME'] ?? '/tmp', '.augmented');
}

function loadChannelHashCache(): void {
  loadChannelHashCacheFromDisk(agentState.knownChannelConfigHashes, channelHashCacheDir());
}

function saveChannelHashCache(): void {
  saveChannelHashCacheToDisk(agentState.knownChannelConfigHashes, channelHashCacheDir());
}

// ENG-5932: lazily-constructed singleton quarantine store, rooted at the
// manager's configDir so the marker survives manager restarts AND per-agent
// reprovisions (the persistence is what actually kills the flap — see
// channel-quarantine.ts). Lazy because `config` isn't populated at module load.
let _channelQuarantineStore: ChannelQuarantineStore | null = null;
function channelQuarantineStore(): ChannelQuarantineStore {
  if (!_channelQuarantineStore) {
    const dir = config?.configDir ?? join(process.env['HOME'] ?? '/tmp', '.augmented');
    _channelQuarantineStore = new ChannelQuarantineStore(defaultQuarantinePath(dir));
  }
  return _channelQuarantineStore;
}

// ENG-6251: lazily-constructed singleton host flag store, rooted at the
// manager's configDir so the last-known-good `flags-cache.json` survives
// manager restarts. Lazy because `config` isn't populated at module load.
// Consumers read flags through this (env -> heartbeat -> compiled default); the
// heartbeat merge below keeps it fresh so DB flips take effect within one poll.
let _hostFlagStore: HostFlagStore | null = null;
function hostFlagStore(): HostFlagStore {
  if (!_hostFlagStore) {
    const dir = config?.configDir ?? join(process.env['HOME'] ?? '/tmp', '.augmented');
    _hostFlagStore = new HostFlagStore({ cachePath: defaultFlagsCachePath(dir), log });
  }
  return _hostFlagStore;
}

/**
 * ENG-5932 rollout control, flag-managed since ENG-6252. Resolves through the
 * host flag store (`channel-quarantine-mode`, enum off|shadow|enforce):
 *   env var (AGT_CHANNEL_QUARANTINE_MODE, operator override)
 *     -> heartbeat value (set centrally via the admin surface, no restart)
 *     -> compiled default ('shadow' — observe-only, see the registry comment).
 * A central flip to 'enforce' now takes effect within one heartbeat cycle with
 * no `systemctl restart`; the env var still wins as the operator escape hatch.
 *   'off'     — disable the quarantine logic entirely (legacy reaper behaviour).
 *   'enforce' — quarantine dead optional channels (drop from .mcp.json).
 *   'shadow'  — compute + log "would quarantine X" without acting (the default).
 */
function channelQuarantineMode(): 'off' | 'shadow' | 'enforce' {
  return hostFlagStore().getString('channel-quarantine-mode') as 'off' | 'shadow' | 'enforce';
}

// ENG-5932: dwell floor (ms) an optional channel must sit continuously missing
// — beyond the restart budget — before it's quarantine-eligible. Council-locked
// at 180s. Overridable for ops via AGT_CHANNEL_QUARANTINE_DWELL_MS.
function channelQuarantineDwellMs(): number {
  const raw = parseInt(process.env['AGT_CHANNEL_QUARANTINE_DWELL_MS'] ?? '', 10);
  return Number.isFinite(raw) && raw >= 0 ? raw : 180_000;
}

// ENG-5932: small set-difference helper (no side effects on either input).
function setWithout(source: Set<string>, remove: Set<string>): Set<string> {
  if (remove.size === 0) return new Set(source);
  const out = new Set<string>();
  for (const v of source) if (!remove.has(v)) out.add(v);
  return out;
}

function send(msg: ManagerEvent): void {
  // Write state updates to file for `agt manager status` to read
  if (msg.type === 'state-update') {
    try {
      // ENG-5865 — atomicWriteFileSync (write-tmp + fsync + rename) so concurrent
      // readers (synthetic probe, `agt status`) never see a torn JSON write.
      atomicWriteFileSync(getStateFile(), JSON.stringify(msg.state, null, 2));
    } catch { /* non-fatal */ }
  }
  // Log notable events
  if (msg.type === 'provisioned') {
    log(`Provisioned ${msg.codeName}`);
  } else if (msg.type === 'drift-detected') {
    log(`Drift detected: ${msg.codeName} (${msg.files?.join(', ')})`);
  } else if (msg.type === 'error') {
    log(`Error: ${msg.message}`);
  }
}

// ---------------------------------------------------------------------------
// Skills index — refresh the "## Available Skills" section in CLAUDE.md by
// scanning the agent's .claude/skills/ directory and parsing frontmatter.
// ---------------------------------------------------------------------------

const SKILLS_INDEX_START = '<!-- AGT:SKILLS_INDEX_START -->';
const SKILLS_INDEX_END = '<!-- AGT:SKILLS_INDEX_END -->';

function sanitizeSkillsIndexText(value: string): string {
  return value
    .replaceAll(SKILLS_INDEX_START, '')
    .replaceAll(SKILLS_INDEX_END, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/\r?\n+/g, ' ')
    .trim();
}

function parseSkillFrontmatter(content: string): { name?: string; description?: string } {
  if (!content.startsWith('---')) return {};
  const end = content.indexOf('\n---', 3);
  if (end === -1) return {};
  const block = content.slice(3, end);
  const out: { name?: string; description?: string } = {};
  for (const line of block.split('\n')) {
    const m = line.match(/^(name|description)\s*:\s*(.*)$/);
    if (m && m[1] && m[2] !== undefined) out[m[1] as 'name' | 'description'] = m[2].trim().replace(/^["']|["']$/g, '');
  }
  return out;
}

async function refreshSkillsIndexInClaudeMd(configDir: string, codeName: string, log: (msg: string) => void): Promise<void> {
  const { readdirSync, readFileSync: rfs, existsSync: ex, writeFileSync } = await import('node:fs');
  const skillsDir = join(configDir, codeName, 'project', '.claude', 'skills');
  const claudeMdPath = join(configDir, codeName, 'project', 'CLAUDE.md');
  if (!ex(skillsDir) || !ex(claudeMdPath)) return;

  const entries: { id: string; name: string; description: string }[] = [];
  for (const dir of readdirSync(skillsDir).sort()) {
    const skillFile = join(skillsDir, dir, 'SKILL.md');
    if (!ex(skillFile)) continue;
    try {
      const { name, description } = parseSkillFrontmatter(rfs(skillFile, 'utf-8'));
      entries.push({
        id: dir,
        name: sanitizeSkillsIndexText(name ?? dir),
        description: sanitizeSkillsIndexText(description ?? '(no description)'),
      });
    } catch { /* skip unreadable */ }
  }

  const body = entries.length
    ? entries.map((e) => `- **${e.name}** — ${e.description}`).join('\n')
    : '_(no skills installed)_';
  const section = `${SKILLS_INDEX_START}
## Available Skills

The following skills are installed in \`.claude/skills/\`. Claude Code auto-activates them when relevant — you don't need to read them manually, but you should know they exist.

${body}

## Updating Integrations (ENG-4341)

Integration skills under \`.claude/skills/integration-*/SKILL.md\` are **read-only** and managed by the platform. They are derived from the integration's database row plus any per-agent context overrides, and are re-rendered every time the manager polls or a context change is broadcast over Supabase Realtime.

**Never edit \`.claude/skills/integration-*/SKILL.md\` files directly.** If you do, your edit will be silently overwritten on the next manager refresh, AND it won't propagate to other agents using the same integration.

To change an integration's behavior (add a rule, update a default, tell the integration not to do something), call the **\`integration_improve\`** MCP tool with the user's request. The tool calls the platform API, which uses an LLM to translate the request into a structured update of the integration's typed context fields and/or freeform overrides text. You'll get a diff back to show the user; on confirmation, call the tool again with \`auto_apply: true\` to apply.

Examples of when to use \`integration_improve\`:
- *"Update the Coding integration so we always use trunk instead of main"*
- *"Add a rule to the Coding integration: never merge directly to main, always open a PR"*
- *"Tell the Knowledge Base integration not to return results below 70% relevance"*
- *"The Slack integration should post incidents to #oncall, not #general"*
${SKILLS_INDEX_END}`;

  const current = rfs(claudeMdPath, 'utf-8');
  let next: string;
  if (current.includes(SKILLS_INDEX_START) && current.includes(SKILLS_INDEX_END)) {
    next = current.replace(new RegExp(`${SKILLS_INDEX_START}[\\s\\S]*?${SKILLS_INDEX_END}`), section);
  } else {
    next = current.trimEnd() + '\n\n' + section + '\n';
  }

  if (next !== current) {
    writeFileSync(claudeMdPath, next, 'utf-8');
    log(`Refreshed skills index in CLAUDE.md for '${codeName}' (${entries.length} skills)`);
  }
}

// ---------------------------------------------------------------------------
// Poll cycle
// ---------------------------------------------------------------------------

// ENG-5041: in-process backoff for /host/exchange and poll-cycle failures.
// Pre-fix the manager would `process.exit(1)` on any "exchange failed" or
// "401" error and rely on the systemd/launchd watchdog to restart it,
// which during a sustained API outage (e.g. the S3 SDK prod-down on
// 2026-05-12 that returned 500 for ~30 min) caused a restart storm and
// blackholed the agent for the duration. Now we stay running, log each
// attempt, and double the delay up to a 60s cap. A successful cycle
// resets the counter so we go straight back to the normal interval.
// Math + classifier extracted to ./poll-backoff.ts so they're testable
// without dragging in this whole module's import graph.
let consecutivePollFailures = 0;

async function pollCycle(): Promise<void> {
  if (!config) return;

  // ENG-4488: short-circuit every entrypoint into pollCycle — not just the
  // normal scheduleNext timer. triggerEarlyPoll() (fired by Realtime
  // events, webapp-direct-chat, etc.) queues
  // pollCycle() with zero delay, which could otherwise sneak one more
  // cycle in on the old Cellar path after restartAfterUpgrade flipped.
  if (restartAfterUpgrade) return;

  // ENG-4568: process /restart flag files written by channel MCPs. Run
  // before the rest of the cycle so a kill takes effect immediately and
  // the ensure-session pass below respawns the killed agent in the same
  // poll. Errors are logged inside the handler — never let a flag take
  // down the whole cycle.
  try {
    await processRestartFlags({
      log,
      resolveFramework: (codeName) => agentFrameworkCache.get(codeName) ?? null,
      stopSession: (codeName, flag) => {
        // CodeRabbit on PR #773 (round 2): cancel any pending hot-reload
        // restart timer so it can't fire after this teardown and kill
        // the replacement session.
        // ENG-5441: restart-flags are written by channel MCPs (slack/
        // telegram/direct-chat) when they hit an unrecoverable error
        // they can't fix in-place. If a single channel keeps writing
        // restart flags, it counts against the breaker — same protection
        // as a hot-reload loop.
        //
        // ENG-5963: this is RECOVERY (the channel is broken until restarted),
        // so it uses the non-gateable 'channel-restart-flag' reason — it must
        // NOT be held behind the maintenance window like the config/drift/reaper
        // restarts, or a broken Slack/Telegram/direct-chat would stay down until
        // 01:00. Still counts toward the breaker.
        //
        // ENG-6229: the in-session agent's self-restart (source 'agent', via
        // the request_restart MCP tool after a human approved over its ask_user
        // HITL) records as 'agent-requested'. Same RECOVERY semantics — restarts
        // immediately, not maintenance-gated, because the agent only asks when
        // it's actively stuck mid-conversation — and still counts toward the
        // breaker so a structurally-confused agent that keeps asking trips.
        const breakerReason: RestartReason =
          flag.source === 'agent' ? 'agent-requested' : 'channel-restart-flag';
        stopPersistentSessionAndForgetMcpBaseline(codeName, breakerReason);
        agentState.persistentSessionAgents.delete(codeName);
        claudeAuthTupleBySession.delete(codeName);
      },
      getTelegramTokens: (codeName) => {
        const t = agentChannelTokens.get(codeName);
        if (!t?.telegram) return null;
        return { token: t.telegram, allowedChats: t.telegramAllowedChats };
      },
      sendTelegram: (botToken, method, body) => telegramApiCall(botToken, method, body),
      getSlackToken: (codeName) => agentChannelTokens.get(codeName)?.slack ?? null,
      sendSlack: async (botToken, body) => {
        // Match the timeout pattern other chat.postMessage call sites in
        // this file already use (lines ~5543, ~5762) — without this, a
        // hung Slack API would block pollCycle and stall every agent's
        // ensure-session pass behind the restart ack (CodeRabbit feedback).
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 5_000);
        try {
          const res = await fetch('https://slack.com/api/chat.postMessage', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json; charset=utf-8',
              Authorization: `Bearer ${botToken}`,
            },
            body: JSON.stringify(body),
            signal: controller.signal,
          });
          const data = (await res.json()) as { ok: boolean; error?: string };
          return data;
        } catch (err) {
          const isAbort = (err as Error).name === 'AbortError';
          return { ok: false, error: isAbort ? 'timeout' : (err as Error).message };
        } finally {
          clearTimeout(timer);
        }
      },
      // ENG-6399: report the restart to audit_log via POST /host/restart-event.
      // Maps the flag's transport `source` onto the canonical audit source: the
      // in-session self-restart (request_restart, ENG-6229) is 'agent-self-request';
      // a channel /restart (slack/telegram) is 'channel-command'. Best-effort —
      // restart-handler calls this fire-and-forget so a reporting failure never
      // blocks the restart.
      reportRestartEvent: async (flag) => {
        const agentId = agentState.codeNameToAgentId.get(flag.codeName);
        if (!agentId) {
          log(`[restart-handler] no agent_id for '${flag.codeName}' — skipping restart-event report`);
          return;
        }
        // Map the flag's transport source onto the canonical audit source. The
        // in-session self-restart (request_restart, ENG-6229) is the only
        // 'agent' source; a channel /restart writes 'telegram'/'slack'; a flag
        // with a missing/garbled source falls back to 'unknown'
        // (restart-flags.ts) — keep that distinct rather than mislabelling it a
        // channel command (CodeRabbit on #2134).
        const source =
          flag.source === 'agent'
            ? 'agent-self-request'
            : flag.source === 'unknown'
              ? 'unknown'
              : 'channel-command';
        // Truly fire-and-forget: don't await (a reporting failure must never
        // surface as a processRestartFlags throw) and swallow rejections
        // locally. The async wrapper still resolves a Promise so the caller's
        // optional `.catch` stays valid.
        void api
          .post('/host/restart-event', {
            agent_id: agentId,
            source,
            ...(flag.reason ? { reason: flag.reason } : {}),
          })
          .catch((err) => log(`[restart-event] report failed for '${flag.codeName}': ${(err as Error).message}`));
      },
    });
  } catch (err) {
    log(`[restart-handler] processRestartFlags threw: ${(err as Error).message}`);
  }

  // ENG-4485: Kick off the CLI self-update check on every poll. The function
  // throttles the actual `brew outdated` call via an on-disk marker
  // (`.last-update-check`) + `UPDATE_CHECK_INTERVAL_MS`, so calling it each
  // poll is cheap — most invocations return immediately without shelling out.
  // Fire-and-forget: we don't want poll latency coupled to brew.
  checkAndUpdateCli().catch((err) => log(`[self-update] Check failed: ${(err as Error).message}`));

  // ENG-5357: same fire-and-forget shape for the Claude Code framework
  // binary. Throttled to once per 24h via an on-disk marker so a restart
  // storm (e.g. transient API 5xx during a deploy) cannot fire N
  // concurrent `brew upgrade --cask claude-code` jobs the way it could
  // pre-fix — that pathology pegged alyve-host-1 until reboot.
  maybeUpgradeClaudeCode().catch((err) => log(`[claude-code-upgrade] Check failed: ${(err as Error).message}`));

  try {
    // Clear per-cycle caches
    registeredAgentsCache.clear();

    // 1. Discover assigned agents
    const hostId = await getHostId();
    if (!hostId) {
      send({ type: 'error', message: 'Could not resolve host ID from API key' });
      return;
    }

    // 1b. Detect framework version (cached, refreshed every 5 min)
    const now = Date.now();
    if (now - lastVersionCheckAt > VERSION_CHECK_INTERVAL_MS) {
      try {
        // Use the first known agent's framework, or the default framework
        const firstAgent = state.agents[0];
        const versionAdapter = firstAgent ? resolveAgentFramework(firstAgent.codeName) : getFramework(DEFAULT_FRAMEWORK);
        if (versionAdapter.getVersion) {
          cachedFrameworkVersion = await versionAdapter.getVersion();
        }
      } catch {
        // Non-fatal — version detection is supplementary
      }
      lastVersionCheckAt = now;
    }

    // 1c. Heartbeat — update last_seen_at, framework_version, host security, diagnostics, and hostname
    try {
      const { detectHostSecurity } = await import('./host-security.js');
      const { collectDiagnostics } = await import('./persistent-session.js');

      // Collect diagnostics for all known persistent session agents
      const diagCodeNames = [...agentState.persistentSessionAgents];
      const agentDiagnostics = diagCodeNames.length > 0 ? collectDiagnostics(diagCodeNames) : undefined;

      // Detect Tailscale hostname for remote SSH attach
      let tailscaleHostname: string | undefined;
      try {
        const { execSync: es } = await import('node:child_process');
        const tsJson = es('tailscale status --self --json 2>/dev/null', {
          encoding: 'utf-8', timeout: 3000,
        }).trim();
        const ts = JSON.parse(tsJson);
        tailscaleHostname = ts.Self?.DNSName?.replace(/\.$/, '') || undefined;
      } catch {
        // Tailscale not installed or not connected — fall back to hostname
        try {
          const { execSync: es } = await import('node:child_process');
          tailscaleHostname = es('hostname', { encoding: 'utf-8', timeout: 1000 }).trim();
        } catch { /* non-fatal */ }
      }

      // Get OS username for SSH command
      let osUsername: string | undefined;
      try {
        const { userInfo } = await import('node:os');
        osUsername = userInfo().username;
      } catch { /* non-fatal */ }

      // Claude Code auth freshness — console uses this to show a re-pair prompt
      // before the OAuth token expires silently. Returns null if no auth is
      // detectable (e.g. a fresh host with no Claude Code login yet).
      let claudeAuth: Awaited<ReturnType<typeof detectClaudeAuth>> = null;
      try {
        claudeAuth = await detectClaudeAuth();
      } catch (err) {
        // Per coding guidelines: default to hash-only/redacted logging in prod.
        // Auth detection errors can surface credential file paths — hash
        // instead of logging raw.
        const errText = err instanceof Error ? err.message : String(err);
        const errId = createHash('sha256').update(errText).digest('hex').slice(0, 12);
        log(`Claude auth detection failed (error_id=${errId})`);
      }

      // ENG-6068: re-assert the channels managed-setting each heartbeat on
      // claude-code hosts. Idempotent (no write when already correct), so
      // this self-heals drift (operator edits, CC reinstalls) without a
      // manager restart and keeps the reported status fresh.
      // CodeRabbit: gate on *current* agents, not the ever-growing
      // frameworkBinaryChecked set, and clear the cached status when no
      // claude-code agent remains — otherwise a host whose last claude-code
      // agent was removed keeps reporting (and alerting on) a file that no
      // longer gates anything.
      const hostHasClaudeCode = state.agents.some(
        (a) => agentFrameworkCache.get(a.codeName) === 'claude-code',
      );
      if (hostHasClaudeCode) {
        lastManagedSettingsStatus = ensureClaudeManagedSettings();
      } else {
        lastManagedSettingsStatus = null;
      }

      const hbResp = (await api.post('/host/heartbeat', {
        host_id: hostId,
        framework_version: cachedFrameworkVersion ?? undefined,
        agt_version: agtCliVersion,
        host_security: detectHostSecurity() ?? undefined,
        agent_runtime_authenticated: agentRuntimeAuthenticated,
        agent_diagnostics: agentDiagnostics,
        hostname: tailscaleHostname,
        os_username: osUsername,
        claude_auth: claudeAuth ?? undefined,
        // ENG-5614: inbound-loop liveness. The actual phoenix socket state
        // behind the direct-chat Realtime subscription. When this is false
        // but the manager keeps heartbeating, inbound is being served by the
        // polling fallback (healRealtimeSocketDivergence forces it). Surfaced
        // so a sustained socket-down window is observable rather than hidden
        // behind a fresh heartbeat.
        realtime_socket_connected: isRealtimeSocketConnected(),
        // ENG-6068: outcome of the channels managed-settings assertion.
        // 'invalid_json' / 'write_failed' mean CC may be silently dropping
        // ALL channel inbound — the API opens a host-scoped alert on it.
        // Omitted (undefined) on hosts that haven't run the assertion
        // (non-claude-code frameworks).
        channels_setting_status: lastManagedSettingsStatus ?? undefined,
        // ENG-6251: capability advertisement — the flags schema version this
        // CLI compiled with, so the admin UI can show flip reach across a
        // mixed-CLI fleet (an older CLI sends an older fingerprint).
        flags_schema_version: FLAGS_SCHEMA_VERSION,
        // ENG-6412: the host's allowlisted, non-secret env gates. The API
        // combines these with the org's server-resolved flags + versions into a
        // host_config_snapshots row (on change) so bugs correlate with config.
        // Allowlist-only by construction — no raw env / secrets ever sent.
        env_gates: collectEnvGates(process.env),
        // ENG-6692: ack the last consumed "Update CLI now" timestamp so the API
        // clears hosts.update_requested_at. Echoes our persisted dedup marker;
        // null until we've ever consumed one.
        update_request_processed_at: state.lastUpdateRequestProcessedAt ?? null,
      })) as
        | {
            desired_claude_code_version?: string;
            maintenance_window?: { start: string; end: string; timezone: string };
            feature_flags?: Record<string, FlagValue>;
            flags_schema_version?: string;
            // ENG-6412: authoritative content hash of the host's current config,
            // computed API-side. Cached for the X-Config-Hash request header.
            config_hash?: string;
            // ENG-6692: one-shot "Update CLI now" signal (null = none pending).
            update_requested_at?: string | null;
          }
        | undefined;

      // ENG-5963: cache the resolved maintenance window for the update gates.
      // Keep the last-known value if a heartbeat omits it (older API) so we
      // don't flap to fail-open mid-session.
      if (hbResp?.maintenance_window) {
        cachedMaintenanceWindow = hbResp.maintenance_window;
      }

      // ENG-6692: consume a one-shot "Update CLI now" request. The admin button
      // stamps hosts.update_requested_at; the heartbeat re-delivers it every
      // cycle until we ack-clear it. We run ONE window-bypassing self-update per
      // distinct timestamp — dedup on state.lastUpdateRequestProcessedAt, which
      // is persisted so the fire survives the manager restart the update itself
      // triggers (exactly the AgentState.lastRestartProcessedAt pattern, but
      // host-level). We STILL defer until the host is idle: bypassing the window
      // must not mean interrupting a live turn.
      const requestedUpdateAt = hbResp?.update_requested_at ?? null;
      const forcedUpdate = decideForcedUpdate({
        requestedAt: requestedUpdateAt,
        lastProcessedAt: state.lastUpdateRequestProcessedAt ?? null,
        hostBusy: isHostBusyForForcedUpdate(),
      });
      if (forcedUpdate === 'defer-idle') {
        log(
          `[self-update] "Update CLI now" requested at ${requestedUpdateAt} — deferring: an agent is mid-turn / recently active. Retrying next poll.`,
        );
      } else if (forcedUpdate === 'consume') {
        if (selfUpdateInFlight) {
          // A self-update is already running. A forced kick would no-op on its
          // in-flight guard and the request would be LOST (we'd have advanced
          // the dedup marker for an update that never ran). Defer WITHOUT
          // consuming: the column stays set, the marker doesn't move, and the
          // next heartbeat retries once this update settles (or after the
          // restart it may trigger, where the host is already converging).
          log(
            `[self-update] "Update CLI now" requested at ${requestedUpdateAt} — deferring: a self-update is already in flight. Retrying next poll.`,
          );
        } else {
          // ENG-6771: consume the one-shot based on the OUTCOME, not optimistically.
          // The old code advanced the dedup marker BEFORE kicking the updater, so a
          // pre-install failure (a transient npm registry timeout, a non-200, an
          // install/smoke error) burned the request silently — the next heartbeat
          // acked it, the API cleared hosts.update_requested_at, and nothing retried
          // while the webapp had already shown success. Now we only consume on
          // 'updated' (install committed; restart scheduled) or 'noop' (already on
          // the latest in-channel build); a 'failed' attempt leaves the request
          // pending so the next idle heartbeat retries.
          //
          // Restart-survival: on 'updated', installAgtCliViaNpm only SETS
          // restartAfterUpgrade (the loop restarts later), so the .then() below
          // persists the marker durably before that restart. If a restart still
          // races ahead, the post-restart manager re-runs this forced check, finds
          // itself already-current ('noop'), and consumes then — no double install.
          log(
            `[self-update] "Update CLI now" requested at ${requestedUpdateAt} — running window-bypassing self-update now (host idle).`,
          );
          const prevProcessedAt = state.lastUpdateRequestProcessedAt ?? null;
          void checkAndUpdateCli({ force: true })
            .then((outcome) => {
              if (!shouldConsumeForcedUpdate(outcome)) {
                log(
                  `[self-update] "Update CLI now" did not complete (no install ran) — leaving the request pending; the next idle heartbeat retries.`,
                );
                return;
              }
              // 'updated' or 'noop' — consume the one-shot durably.
              state.lastUpdateRequestProcessedAt = requestedUpdateAt;
              try {
                atomicWriteFileSync(getStateFile(), JSON.stringify(state, null, 2));
              } catch (err) {
                state.lastUpdateRequestProcessedAt = prevProcessedAt;
                log(
                  `[self-update] failed to persist update-request ack; retrying next heartbeat: ${(err as Error).message}`,
                );
              }
            })
            .catch((err) => {
              // Threw before committing to an install → treat as failed → don't
              // consume → retry next heartbeat.
              log(
                `[self-update] forced check failed: ${(err as Error).message} — leaving the request pending for retry.`,
              );
            });
        }
      }

      // ENG-6251: merge the pre-evaluated flag map from this heartbeat into the
      // host flag store — logs per-flag transitions + env-masking warns and
      // refreshes the on-disk last-known-good cache. A DB flip lands here within
      // one poll cycle, no restart. An older API that omits the field is a
      // no-op, so we keep the last-known map rather than flapping to defaults.
      if (hbResp?.feature_flags) {
        hostFlagStore().applyHeartbeat(hbResp.feature_flags, hbResp.flags_schema_version);
      }

      // ENG-6412: cache the API-computed config_hash so subsequent host-runtime
      // requests carry X-Config-Hash (audit_log config correlation). Clear it
      // when a heartbeat omits it (older API, or a transient eval miss) rather
      // than preserving a possibly-stale hash — a NULL config_hash on an audit
      // row is an honest "unknown", whereas a stale one would mis-correlate the
      // row to the wrong snapshot. Self-heals on the next heartbeat that returns
      // one. setConfigHash treats null/empty as "clear".
      setConfigHash(hbResp?.config_hash ?? null);

      // ENG-5672: idle-gated Claude Code auto-update. Host-level — runs once
      // per poll alongside the heartbeat, using the server-pinned desired
      // version from the heartbeat response. The updater is internally
      // throttled (~6h), idle-gated (defers while a `claude --name agt-*`
      // agent is live), and log-and-continue, so it never disrupts the poll.
      //
      // ENG-5963: also gate the binary upgrade to the maintenance window — it's
      // restart-adjacent (the next session picks up the new binary). Outside
      // the window we skip the updater entirely; it runs on the next in-window
      // poll. Urgent CLI hotfixes are handled in the agt-cli self-update path,
      // not here (Claude Code has no `urgent` dist-tag of ours).
      try {
        if (decideMaintenanceWindowGate({ window: cachedMaintenanceWindow, now: new Date() }) === 'proceed') {
          const { maybeUpdateClaudeCode } = await import('./claude-code-updater.js');
          await maybeUpdateClaudeCode({
            desiredVersion: hbResp?.desired_claude_code_version ?? 'latest',
            currentVersion: cachedFrameworkVersion,
            log,
          });
        }
      } catch (err) {
        log(`Claude Code updater error: ${(err as Error).message}`);
      }
    } catch (err) {
      // ENG-6412: a failed heartbeat means config-hash freshness is unknown —
      // clear the cache so later requests don't carry a stale X-Config-Hash and
      // mis-correlate audit rows. Self-heals on the next successful heartbeat.
      setConfigHash(null);
      log(`Heartbeat failed: ${(err as Error).message}`);
    }

    // 1d. ENG-5399 Tier 1 responsiveness probe — pane.log freshness per
    // agent. Reports `PaneActivityAgeSeconds` on a slow cadence (default
    // 5 min via AUGMENTED_RESPONSIVENESS_INTERVAL_MS). Complements the
    // existing 15-min agent-synthetic-probe cron with a faster, zero-
    // token canary that catches silent agents before the 35-min
    // SyntheticReplyAgeSeconds alarm window.
    try {
      const {
        collectResponsivenessProbes,
        getResponsivenessIntervalMs,
      } = await import('./responsiveness-probe.js');
      const probeIntervalMs = getResponsivenessIntervalMs();
      if (now - lastResponsivenessProbeAt > probeIntervalMs) {
        const probeCodeNames = [...agentState.persistentSessionAgents];
        if (probeCodeNames.length > 0) {
          // ENG-6017: attach the watchdog's give-up counter (events since
          // the last probe) so "input typed but unsubmittable" reaches
          // CloudWatch as InputStuckGiveUps within one probe cycle. The
          // collect call itself also adds pending_inbound_oldest_age_seconds
          // when marker files exist.
          // ENG-6037: the take is destructive, so remember what each probe
          // drained and re-credit it if the POST fails — a transient API
          // blip must not permanently lose the counts.
          const drainedGiveUps = new Map<string, number>();
          const probes = collectResponsivenessProbes(probeCodeNames).map((p) => {
            const giveUps = takeWatchdogGiveUpCount(p.code_name);
            if (giveUps > 0) drainedGiveUps.set(p.code_name, giveUps);
            return { ...p, input_stuck_give_ups: giveUps };
          });
          if (probes.length > 0) {
            // Fire-and-forget — never block pollCycle on CloudWatch
            // latency, and a transient API error here mustn't pause
            // the rest of the cycle.
            void api
              .post('/host/responsiveness-probe', { host_id: hostId, probes })
              .catch((err: unknown) => {
                for (const [codeName, count] of drainedGiveUps) {
                  creditWatchdogGiveUpCount(codeName, count);
                }
                log(`[responsiveness-probe] post failed (give-up counts re-credited): ${(err as Error).message}`);
              });
          }
        }
        lastResponsivenessProbeAt = now;
      }
    } catch (err) {
      log(`[responsiveness-probe] collection failed: ${(err as Error).message}`);
    }

    // 1d-2. ENG-6153 — wedged-but-alive detection. A session whose claude
    // process is alive but isn't draining inbound (frozen turn / hung Stop
    // hook) reads HEALTHY to isSessionHealthy, and a manual restart just
    // `--resume`s the same stuck transcript and re-wedges (the sherlock
    // incident, 2026-06-08). Detect it from the cheap pane+inbound signals the
    // responsiveness probe already scans and force a FRESH respawn (rotated
    // UUID, no --resume; old transcript preserved). Shadow-first — does nothing
    // until AGT_WEDGE_RESTART_MODE is shadow/enforce. See lib/wedge-detection.ts.
    try {
      const wedgeConfig = resolveWedgeConfig();
      if (wedgeConfig.mode !== 'off') {
        const {
          collectResponsivenessProbes,
          livePendingInboundOldestAgeSeconds,
          parkPendingInbound,
        } = await import('./responsiveness-probe.js');
        // ENG-6238: transcript growth = "the model is actually producing
        // tokens", the signal that distinguishes a legit long turn from a
        // frozen/hung one and spares it from the hard-cap respawn.
        // (ENG-6264: transcriptActivityAgeSeconds is now a static import — the
        // restart gate shares the same signal.)
        const { getProjectDir: wedgeProjectDir } = await import('./claude-scheduler.js');
        const wedgeNow = new Date();
        const liveAgents = agentState.persistentSessionAgents;
        // CR #1901: prune streaks for agents that have left the persistent set
        // so a departed-then-returned agent never inherits a stale wedge streak.
        for (const tracked of consecutiveWedgeCycles.keys()) {
          if (!liveAgents.has(tracked)) consecutiveWedgeCycles.delete(tracked);
        }
        for (const tracked of wedgeExemptionLogged) {
          if (!liveAgents.has(tracked)) wedgeExemptionLogged.delete(tracked);
        }
        // ENG-6171: same hygiene for the poison-card accounting — a departed
        // agent must not carry its suspected-poison suppression into a later
        // re-add.
        for (const tracked of wedgeRestartsByCard.keys()) {
          if (!liveAgents.has(tracked)) wedgeRestartsByCard.delete(tracked);
        }
        const poisonConfig = resolvePoisonCardConfig();
        for (const probe of collectResponsivenessProbes([...liveAgents])) {
          const codeName = probe.code_name;
          // A wedge is by definition ALIVE-but-stuck. A crashed/absent session
          // (isSessionHealthy=false) is owned by the normal unhealthy-recovery
          // path (which resumes today's transcript) — the wedge path must not
          // cold-start it via rotateSessionForWedge. (CR #1901)
          if (!isSessionHealthy(codeName)) {
            consecutiveWedgeCycles.delete(codeName);
            wedgeExemptionLogged.delete(codeName);
            continue;
          }
          // ENG-6160: count only LIVE inbound for the wedge signal — markers
          // older than the current session start (leftovers from a prior
          // session) or flagged undeliverable must not register as "this
          // session isn't draining". Without this, an orphan marker survives a
          // fresh respawn and the detector re-fires forever on a healthy idle
          // agent (the sherlock enforce loop, 2026-06-08). The alarm-facing
          // probe.pending_inbound_oldest_age_seconds is deliberately untouched.
          const wedgeSession = getSessionState(codeName);
          const sessionStartMs = wedgeSession?.startedAt ?? null;
          // ENG-6238: age of the live session's transcript JSONL. null when the
          // session id / file can't be resolved → isSessionProducing falls back
          // to pane age (pre-6238 behaviour) rather than treating it as frozen.
          const transcriptAge = transcriptActivityAgeSeconds(
            wedgeProjectDir(codeName),
            wedgeSession?.currentSessionId ?? null,
            wedgeNow,
          );
          // ENG-6294: a parent that dispatched a `run_in_background` sub-agent
          // and ended its turn is healthy, not wedged — the worker's own
          // transcript (<session>/subagents/*.jsonl) keeps growing while the
          // main one goes quiet. Counted as "producing" by the detector.
          const subagentAge = subagentActivityAgeSeconds(
            wedgeProjectDir(codeName),
            wedgeSession?.currentSessionId ?? null,
            wedgeNow,
          );
          const signals = {
            paneActivityAgeSeconds: probe.pane_activity_age_seconds,
            transcriptActivityAgeSeconds: transcriptAge,
            subagentActivityAgeSeconds: subagentAge,
            pendingInboundOldestAgeSeconds: livePendingInboundOldestAgeSeconds(
              codeName,
              sessionStartMs,
              wedgeNow,
            ),
          };
          if (!isWedgeCandidateCycle(signals, wedgeConfig)) {
            consecutiveWedgeCycles.delete(codeName);
            // ENG-6294: if the ONLY thing sparing this session is the in-flight
            // background task, say so — once per episode — so a suppressed
            // wedge is visible in manager.log instead of silently resetting.
            const exemption = wedgeExemptionReason(signals, wedgeConfig);
            if (exemption !== null) {
              if (!wedgeExemptionLogged.has(codeName)) {
                wedgeExemptionLogged.add(codeName);
                log(
                  `[wedge] exempt agent=${codeName} reason=${exemption} ` +
                    `subagentAge=${subagentAge}s ` +
                    `inboundAge=${signals.pendingInboundOldestAgeSeconds}s ` +
                    `transcriptAge=${transcriptAge === null ? 'n/a' : `${transcriptAge}s`}`,
                );
              }
            } else {
              wedgeExemptionLogged.delete(codeName); // episode over — re-arm the log
            }
            continue;
          }
          wedgeExemptionLogged.delete(codeName);
          const streak = (consecutiveWedgeCycles.get(codeName) ?? 0) + 1;
          consecutiveWedgeCycles.set(codeName, streak);
          if (decideWedgeRestart({ ...signals, consecutiveWedgeCycles: streak }, wedgeConfig) !== 'wedged') {
            continue; // streak hasn't reached minCycles yet
          }
          const transcriptAgeLabel = transcriptAge === null ? 'n/a' : `${transcriptAge}s`;
          const detail =
            `agent=${codeName} paneAge=${signals.paneActivityAgeSeconds}s ` +
            `transcriptAge=${transcriptAgeLabel} ` +
            `inboundAge=${signals.pendingInboundOldestAgeSeconds}s cycles=${streak}`;
          if (wedgeConfig.mode === 'shadow') {
            // One line per wedge episode (logged the cycle the streak first
            // crosses minCycles) — not every poll, to avoid spamming.
            if (streak === wedgeConfig.minCycles) {
              log(`[wedge] SHADOW would force-fresh respawn ${detail}`);
            }
            continue;
          }
          // enforce: rotate today's UUID so the next spawn cold-starts fresh,
          // then tear down the wedged session; the ensure-session pass below
          // respawns onto the fresh (transcript-less) UUID.
          try {
            const newId = rotateSessionForWedge(codeName);
            try {
              const { execSync: es } = await import('node:child_process');
              es(`tmux kill-session -t agt-${codeName} 2>/dev/null`, { stdio: 'ignore' });
            } catch {
              /* no session to kill — the respawn will start a fresh one */
            }
            stopPersistentSessionAndForgetMcpBaseline(codeName);
            consecutiveWedgeCycles.delete(codeName);
            // ENG-6289 (supersedes the ENG-6160 blanket dead-letter): PARK the
            // wedged session's undrained pending-inbound markers — leave them in
            // the live dirs so the fresh session's orient hook surfaces them and
            // ENG-5969 replay (when enabled) re-pushes them. The ENG-6160
            // pre-session exclusion keeps untouched parked markers out of the
            // fresh session's wedge signal, so the loop still can't re-form.
            // Only `undeliverable: true` markers (already ⏳-noticed, never
            // drainable) are dead-lettered to `-stale`.
            const { parked, deadLettered } = parkPendingInbound(codeName, wedgeNow);
            const parkNote = parked > 0 ? `, ${parked} inbound parked for fresh session` : '';
            const deadNote = deadLettered > 0 ? `, ${deadLettered} undeliverable inbound dead-lettered` : '';
            log(
              `[wedge] forced fresh respawn ${detail} → new session ${newId} ` +
                `(transcript preserved${parkNote}${deadNote})`,
            );
            // ENG-6265: record the respawn (+ dead-lettered count) with the API
            // so the agent-heartbeat-monitor cron rolls it into the
            // WedgeRespawnsHourly / DeadLetteredInboundHourly metrics + alarms.
            // Without this the wedge path is invisible to CloudWatch — an agent
            // can thrash and drop messages while every other signal stays green.
            // Fire-and-forget: the respawn's correctness must not depend on it.
            const wedgeAgentId = agentState.codeNameToAgentId.get(codeName);
            if (wedgeAgentId) {
              api
                .post('/host/wedge-respawn', {
                  agent_id: wedgeAgentId,
                  code_name: codeName,
                  // dead_lettered_count keeps its "messages actually dropped"
                  // alarm semantics (DeadLetteredInboundHourly fires on the
                  // first one); parked messages are recoverable, reported
                  // separately. Older APIs ignore the unknown field.
                  dead_lettered_count: deadLettered,
                  parked_count: parked,
                  pane_age_seconds: signals.paneActivityAgeSeconds,
                  inbound_age_seconds: signals.pendingInboundOldestAgeSeconds,
                  transcript_age_seconds: transcriptAge,
                })
                .catch((err) => {
                  log(
                    `[wedge] failed to record respawn event for '${codeName}' (ENG-6265): ${(err as Error).message} — respawn proceeded; CloudWatch metric will under-count this event`,
                  );
                });
            }
            // ENG-6171: attribute this respawn to whatever the agent had
            // in_progress when it wedged (last-known board snapshot — the
            // cache the hybrid inject populated; absent ⇒ unattributable, a
            // no-op). A card in_progress across `threshold` consecutive
            // wedge-respawns is a suspected poison task; the hybrid inject
            // then stops re-nudging it (see maybeInjectKanbanCheck).
            const inProgressCardIds = (kanbanBoardCache.get(codeName) ?? [])
              .filter((item) => item.status === 'in_progress' && isHybridActionable(item))
              .map((item) => item.id);
            const cardStates = wedgeRestartsByCard.get(codeName) ?? new Map<string, CardWedgeState>();
            const { next, newlyPoisoned } = recordWedgeForCards(
              cardStates,
              inProgressCardIds,
              wedgeNow.getTime(),
              poisonConfig,
            );
            wedgeRestartsByCard.set(codeName, next);
            for (const cardId of newlyPoisoned) {
              log(
                `[wedge] poison-card suspected agent=${codeName} card=${cardId} ` +
                  `consecutiveWedgeRespawns=${poisonConfig.threshold} — suppressing re-nudge ` +
                  `(falls to stale-auto-fail). See ENG-6171.`,
              );
            }
          } catch (err) {
            log(`[wedge] force-fresh respawn failed for ${codeName}: ${(err as Error).message}`);
          }
        }
      }
    } catch (err) {
      log(`[wedge] detection failed: ${(err as Error).message}`);
    }

    // 1e. ENG-5459 — scrape "N MCP servers failed · /mcp" from each
    // active agent's pane.log and POST to the API so the webapp can
    // surface a "needs attention" signal alongside the existing
    // needsReconnect indicator. Catches the broken-but-process-alive
    // case mcp-presence-reaper can't see (Composio MCP whose URL
    // 401s on every tool call — process up, API dead).
    //
    // Fire-and-forget per agent. Only POSTs when the count differs
    // from the last observation (cheap dedupe — most ticks the count
    // is stable). Skipped entirely for agents with no pane.log.
    try {
      const { scrapeMcpFailedBannerCount } = await import('./pane-mcp-banner-scraper.js');
      const observations: Array<{ agent_id: string; count: number }> = [];
      // CodeRabbit on PR #1318: commit dedupe-cache only after POST
      // succeeds, otherwise a transient API failure permanently
      // suppresses future POSTs of the same value. Build a parallel
      // (codeName, count) list while we walk agents so the success
      // handler can apply the cache without a reverse lookup.
      const pendingCacheCommits: Array<{ codeName: string; count: number }> = [];
      for (const codeName of agentState.persistentSessionAgents) {
        const agentId = agentState.codeNameToAgentId.get(codeName);
        if (!agentId) continue;
        // 40 lines is enough to catch the banner — it renders on
        // every frame, so the last few KB always contain it.
        const tail = readPaneLogTail(codeName, 40);
        if (!tail) continue;
        const observed = scrapeMcpFailedBannerCount(tail);
        // Null = no banner detected this tick. Treat as 0 — Claude
        // Code omits the banner when all MCPs are healthy. Persisting
        // 0 also lets the alarm transition back to OK on recovery.
        const count = observed ?? 0;
        const last = lastMcpFailedBannerCount.get(codeName);
        if (last === count) continue; // dedupe — no change since last poll
        observations.push({ agent_id: agentId, count });
        pendingCacheCommits.push({ codeName, count });
      }
      if (observations.length > 0) {
        // CodeRabbit on PR #1318 round 4: only commit the cache for
        // observations the API actually accepted. The endpoint
        // returns `{ results: [{ agent_id, ok, error? }] }` — a row
        // that failed (e.g. host not assigned anymore) should be
        // retried on the next tick, not silently suppressed.
        void api
          .post<{ results?: Array<{ agent_id: string; ok: boolean }> }>(
            '/host/mcp-banner-observation',
            { observations },
          )
          .then((resp) => {
            const accepted = new Set(
              (resp?.results ?? [])
                .filter((r) => r.ok)
                .map((r) => r.agent_id),
            );
            for (const { codeName, count } of pendingCacheCommits) {
              const agentId = agentState.codeNameToAgentId.get(codeName);
              if (agentId && accepted.has(agentId)) {
                lastMcpFailedBannerCount.set(codeName, count);
              }
            }
          })
          .catch((err: unknown) => {
            log(`[mcp-banner] post failed: ${(err as Error).message}`);
          });
      }
    } catch (err) {
      log(`[mcp-banner] collection failed: ${(err as Error).message}`);
    }

    const data = await api.post<{
      agents: Array<{
        agent_id: string;
        code_name: string;
        display_name: string;
        status: string;
        environment: string;
        framework?: string;
        // ENG-4947: dashboard-issued restart signal. The manager kills+
        // respawns the agent's tmux session when this advances past
        // AgentState.lastRestartProcessedAt.
        restart_requested_at?: string | null;
        // ENG-6146: kill switch. When set, the API has overlaid status:'paused'
        // for this agent because its scope (global/org/team) is killed. The
        // manager halts it via the normal paused path and uses this marker to
        // log the reason + announce resume when the switch clears.
        kill_switch?: { scope: string | null; reason: string | null; source: string | null } | null;
      }>;
    }>('/host/agents', { host_id: hostId });

    const agents = data.agents ?? [];

    // ENG-4947: act on dashboard-issued restart signals before the normal
    // refresh pass. Killing the tmux session here means processAgent below
    // sees the agent as needing a fresh respawn rather than a no-op refresh.
    // We track the acks in a per-cycle Map and apply them to the
    // AgentState rows produced by processAgent below, so the timestamp
    // survives even when the agent's first appearance on this host
    // already carries a pending signal (no `prev` state to mutate).
    const restartAcks = new Map<string, string>();
    // ENG-7011: restart requests whose server-side `restart_requested_at` we must
    // clear this cycle. Covers both the requests we action below AND ones already
    // serviced on a prior poll whose clear never landed — so the standing stuck-
    // restart backlog auto-heals without ever re-restarting a healthy agent.
    const restartClears: Array<{ agentId: string; requestedAt: string }> = [];
    for (const agent of agents) {
      const requested = agent.restart_requested_at ?? null;
      if (!requested) continue;
      // ENG-7335: the fast-restart lane (restart doorbell) is actively servicing
      // this agent right now - let it own the kill + ack this pass so the two
      // paths can't double-kill/double-respawn. If the lane fails it won't ack,
      // so a later poll (lane no longer in-flight) services it normally.
      if (restartInFlight.has(agent.agent_id)) continue;
      const prev = state.agents.find((a) => a.agentId === agent.agent_id);
      const lastProcessed = prev?.lastRestartProcessedAt ?? null;
      // Same timestamp (or older) → already serviced; do NOT restart again. We
      // still queue the clear below: the request is still set server-side, which
      // means our earlier ack never landed (or predates this manager process).
      const alreadyServiced =
        lastProcessed != null && Date.parse(lastProcessed) >= Date.parse(requested);
      if (!alreadyServiced) {
        log(`[restart] Dashboard requested restart for '${agent.code_name}' at ${requested}`);
        try {
          const { execSync: es } = await import('node:child_process');
          es(`tmux kill-session -t agt-${agent.code_name} 2>/dev/null`, { stdio: 'ignore' });
        } catch {
          /* no session to kill — the respawn below will start a fresh one */
        }
        stopPersistentSessionAndForgetMcpBaseline(agent.code_name);
        restartAcks.set(agent.agent_id, requested);
      }
      // ENG-7011: report the request as serviced so the API clears
      // host_agents.restart_requested_at. Without this the dashboard shows the
      // restart "stuck" forever and the restart-monitor cron pages a false
      // `agent_restart_stuck` ~15 min later.
      restartClears.push({ agentId: agent.agent_id, requestedAt: requested });
    }

    // ENG-7011: fire the clears best-effort (fire-and-forget, like /restart-event).
    // Race-safe server-side: the clear only nulls the column when its stored value
    // still equals `requestedAt`, so a newer click is preserved. A failed clear just
    // retries next poll — the `alreadyServiced` guard keeps the agent from being
    // re-restarted in the meantime.
    for (const clear of restartClears) {
      void api
        .post('/host/restart-ack', {
          host_id: hostId,
          agent_id: clear.agentId,
          restart_requested_at: clear.requestedAt,
        })
        .catch((err) =>
          log(`[restart] clear failed for agent_id=${clear.agentId}: ${(err as Error).message}`),
        );
    }

    // Ensure framework binaries are installed/upgraded for any new frameworks
    const frameworksThisCycle = new Set(agents.map((a) => a.framework).filter(Boolean));
    for (const fw of frameworksThisCycle) {
      await ensureFrameworkBinary(fw!);
    }

    // Track which channels have at least one active agent per profile
    activeChannels.clear();

    // Update state agents list
    const agentStates: AgentState[] = [];

    for (const agent of agents) {
      // ENG-7335: the fast-restart lane is mid-respawn for this agent - do NOT let
      // processAgent observe the transiently-killed session and respawn it a
      // second time, nor overwrite the lane's lastRestartProcessedAt when
      // agentStates replaces state.agents at end of cycle. Carry the existing
      // state row forward; the next poll (lane done) processes it normally. If
      // there's no prior row (agent brand-new this cycle) we fall through - the
      // lane's per-agent lock + startPersistentSession's "already running" no-op
      // still guard against a double spawn.
      if (restartInFlight.has(agent.agent_id)) {
        const existing = state.agents.find((a) => a.agentId === agent.agent_id);
        if (existing) {
          agentStates.push(existing);
          continue;
        }
      }
      try {
        await processAgent(agent, agentStates);
      } catch (err) {
        log(`Error processing agent '${agent.code_name}': ${(err as Error).message}`);
        // Preserve existing state for this agent on error
        const existing = state.agents.find((a) => a.agentId === agent.agent_id);
        if (existing) {
          agentStates.push(existing);
        } else {
          agentStates.push({
            agentId: agent.agent_id,
            codeName: agent.code_name,
            status: agent.status,
            charterVersion: '',
            toolsVersion: '',
            secretsHash: null,
            lastRefreshAt: null,
            lastProvisionAt: null,
            lastDriftCheckAt: null,
            lastSecretsProvisionAt: null,
            acpSessions: [],
          });
        }
      }
    }

    // ENG-5445: forward Claude Code's host-wide activity counts from
    // `~/.claude/stats-cache.json` to /host/activity-observations. Once
    // per cycle (not per-agent) — the cache is host-scoped, every
    // session on the machine contributes to the same numbers. The
    // monitor handles its own rate-limiting and the API endpoint
    // upserts on (host_id, date), so calling here every cycle is
    // safe. Fire-and-forget — observability writes must not slow the
    // poll cycle.
    void maybeReportActivityCache({ api, log });

    // ENG-4947: stamp lastRestartProcessedAt on the AgentState rows produced
    // above so the next poll doesn't replay the same restart. Done after
    // processAgent so the timestamp applies even when this was the agent's
    // first appearance on the host (no prior state to mutate up-front).
    // Persist to disk IMMEDIATELY (don't wait for end of cycle) so a crash
    // between here and the cycle's end-of-cycle state-update can't replay
    // the restart on the next manager boot.
    //
    // ENG-5008: carry forward `lastRestartProcessedAt` from `state.agents`
    // (the prior cycle's state) for every agent in this cycle's
    // agentStates, regardless of whether `restartAcks` has a fresh
    // timestamp this poll. `processAgent` rebuilds AgentState from scratch
    // on every successful path (line 4416+), so without this copy-forward
    // the field gets dropped any cycle where a previously-acked agent
    // doesn't have a brand-new restart signal — and the very next cycle
    // sees `prev?.lastRestartProcessedAt = null`, the timestamp comparison
    // fails, and the manager re-fires the kill. That's the loop that
    // pinned vigil/vera/stirling for hours on 2026-05-12.
    // CodeRabbit (PR #953): key the immediate write off whether any
    // ack state actually changed this cycle, not just fresh signals.
    // A carry-forward repair after a prior write-failure should also
    // persist immediately to close the same crash-replay window.
    const restartAckStateChanged = applyRestartAcks({
      agentStates,
      priorAgents: state.agents,
      restartAcks,
    });
    if (restartAckStateChanged) {
      try {
        const ackedState: ManagerState = { ...state, agents: agentStates };
        // ENG-5865 — atomic write so the synthetic probe never reads a torn
        // restart-ack state mid-write (this site fires off the hot path).
        atomicWriteFileSync(getStateFile(), JSON.stringify(ackedState, null, 2));
      } catch (err) {
        // Non-fatal — the end-of-cycle state-update will retry the write.
        // Worst case: a crash between here and end-of-cycle replays the
        // restart on next boot, which is the pre-fix behaviour anyway.
        log(`[restart] failed to persist ack immediately: ${(err as Error).message}`);
      }
    }

    // ENG-7328: when we serviced a restart this cycle, processAgent above already
    // killed the old session and respawned a fresh one, stamping a new
    // session.startedAt. But the /host/heartbeat that carries agent_diagnostics
    // fired EARLIER in this same cycle (before the kill + respawn), so the fresh
    // startedAt would not reach the API until the NEXT poll - a one-poll-interval
    // lag. That lag is what makes the dashboard restart-progress UI sit on
    // "respawning session" and eventually time out instead of confirming "back
    // online" (the poll keys on diagnostics.startedAt advancing past
    // restart_requested_at). Flush an out-of-band, diagnostics-only heartbeat for
    // just the restarted agents so their new startedAt lands within seconds.
    // Fire-and-forget and best-effort: a failed flush simply self-heals on the
    // next poll's regular heartbeat.
    if (restartAcks.size > 0) {
      const restartedCodeNames = agents
        .filter((a) => restartAcks.has(a.agent_id))
        .map((a) => a.code_name);
      if (restartedCodeNames.length > 0) {
        void (async () => {
          try {
            const { collectDiagnostics } = await import('./persistent-session.js');
            const freshDiagnostics = collectDiagnostics(restartedCodeNames);
            await api.post('/host/heartbeat', {
              host_id: hostId,
              agent_diagnostics: freshDiagnostics,
            });
          } catch (err) {
            log(`[restart] post-respawn diagnostics flush failed: ${(err as Error).message}`);
          }
        })();
      }
    }

    // Reconcile channel enabled state per profile.
    // Each agent has its own profile config, so enable/disable within that profile.
    try {
      for (const [channelId, codeNames] of activeChannels) {
        for (const codeName of codeNames) {
          const adapter = resolveAgentFramework(codeName);
          if (adapter.setChannelEnabled) {
            adapter.setChannelEnabled(channelId, true, codeName);
          }
        }
      }
    } catch { /* non-fatal */ }

    // Detect unassigned/deleted agents (were in state but not in current list)
    const currentIds = new Set(agents.map((a) => a.agent_id));
    for (const prev of state.agents) {
      if (!currentIds.has(prev.agentId)) {
        log(`Agent '${prev.codeName}' removed from host (deleted or unassigned)`);
        const adapter = resolveAgentFramework(prev.codeName);
        // Stop the persistent tmux session first — that takes the Claude Code
        // process and most of its MCP children with it. The explicit kill-session
        // mirrors the revoked branch and covers sessions started by a previous
        // manager run that this manager's `sessions` Map doesn't track.
        stopPersistentSessionAndForgetMcpBaseline(prev.codeName);
        try {
          const { execSync: es } = await import('node:child_process');
          es(`tmux kill-session -t agt-${prev.codeName} 2>/dev/null`, { stdio: 'ignore' });
        } catch { /* no session to kill */ }
        // ENG: tear down slack/telegram/direct-chat MCPs explicitly. Channel
        // children sometimes reparent to init when their Claude Code parent
        // exits, and the periodic channel-sweep won't touch them after this
        // point because their codeName is no longer in the manager's active
        // set. Without this, a Telegram poller for the de-provisioned agent
        // can keep running and conflict with whichever new host now owns it.
        killAgentChannelProcesses(prev.codeName, { log });
        const agentDir = join(adapter.getAgentDir(prev.codeName), 'provision');
        await cleanupAgentFiles(prev.codeName, agentDir);
        clearAgentCaches(prev.agentId, prev.codeName);
      }
    }

    // ENG-4581: poll for pending Claude Code OAuth pairing sessions across
    // all agents on this host and dispatch each one. Best-effort —
    // failures are logged and the next refresh will retry.
    try {
      await processClaudePairSessions(agents.map((a) => ({
        agentId: a.agent_id,
        codeName: a.code_name,
      })));
    } catch (err) {
      log(`[claude-pair] poll failed: ${(err as Error).message}`);
    }

    // Channel MCP zombie sweep (ENG-4453) — every CHANNEL_SWEEP_INTERVAL_MS,
    // kill surplus per-agent channel MCP processes (orphans reparented to
    // init after a parent Claude Code died). ENG-4434 added signal handlers
    // that *should* prevent this, but on hosts we've still seen channel
    // zombies accumulate; this is the belt-and-braces net.
    if (Date.now() - lastChannelSweepAt >= CHANNEL_SWEEP_INTERVAL_MS) {
      lastChannelSweepAt = Date.now();
      // ENG-6670: EXCLUDE Docker-isolated agents from the host sweep. It anchors
      // channel procs by PPID-chain to the agent's HOST tmux pane pids; an
      // isolated agent's channels run inside its container (chain to container
      // PID-1 claude), so the host sweep mis-classifies them as not-in-live-chain
      // and SIGTERMs LIVE channels -> presence-reaper restarts -> flap (measured
      // on agt-aws-1). There are no HOST orphans to sweep for an isolated agent:
      // its channels exist only in the `--rm` container (in-container liveness is
      // already owned by the Docker-aware presence-reaper), so skipping is correct.
      const agentCodeNames = new Set(
        agentStates.map((a) => a.codeName).filter((c) => isolationMode(c) !== 'docker'),
      );
      sweepChannelProcesses({
        agentCodeNames,
        dryRun: CHANNEL_SWEEP_DRY_RUN,
        log,
      }).catch((err) => {
        log(`[channel-sweep] sweep error: ${(err as Error).message}`);
      });
    }

    // ENG-4705: channel input watchdog. Detect channel-server-injected
    // messages that landed in the Claude Code TUI input buffer but never
    // got their auto-submit Enter (the bug: text sits as `❯ <message>` and
    // never becomes a user turn). Fire Enter for any input that's been
    // unchanged for >5s on a session with no human attached. Cheap — one
    // tmux capture-pane per claude-code agent per poll.
    {
      const claudeCodeAgents = agentStates
        .filter((a) => a.status === 'active')
        .filter((a) => (agentFrameworkCache.get(a.codeName) ?? DEFAULT_FRAMEWORK) === 'claude-code')
        .map((a) => a.codeName);
      // Always call checkChannelInputs — when the list is empty it still
      // performs the per-cycle state cleanup pass for agents that have
      // been paused/revoked since last check.
      checkChannelInputs(claudeCodeAgents, {
        capturePane: (codeName) => {
          try {
            return syncExecFile('tmux', ['capture-pane', '-t', `agt-${codeName}`, '-p'], {
              stdio: ['ignore', 'pipe', 'ignore'],
              timeout: 2_000,
            }).toString();
          } catch {
            return null;
          }
        },
        isClientAttached: (codeName) => {
          try {
            const out = syncExecFile('tmux', ['list-clients', '-t', `agt-${codeName}`], {
              stdio: ['ignore', 'pipe', 'ignore'],
              timeout: 2_000,
            }).toString();
            return out.trim().length > 0;
          } catch {
            // Fail closed: if we can't tell whether a human is attached,
            // assume they are and skip the watchdog action. Better to
            // miss a stuck buffer than to fire Enter while someone is
            // typing.
            return true;
          }
        },
        sendEnter: (codeName) => {
          try {
            syncExecFile('tmux', ['send-keys', '-t', `agt-${codeName}`, 'Enter'], {
              stdio: 'ignore',
              timeout: 2_000,
            });
          } catch {
            // Best-effort watchdog action — never fail the poll cycle on
            // a tmux hiccup. Next cycle will retry.
          }
        },
        // ENG-6017: dialog-dismissal keys — one send-keys call per key so a
        // multi-key sequence can't be wrapped into a single bracketed paste
        // (see defaultArmSender in persistent-session.ts). The inter-key
        // delay is a blocking sleep; dialog sequences are at most two keys
        // with a 300ms gap, well within poll-cycle tolerances.
        sendKeys: (codeName, keys, interKeyDelayMs) => {
          try {
            for (let i = 0; i < keys.length; i++) {
              if (i > 0 && interKeyDelayMs > 0) {
                const view = new Int32Array(new SharedArrayBuffer(4));
                Atomics.wait(view, 0, 0, interKeyDelayMs);
              }
              syncExecFile('tmux', ['send-keys', '-t', `agt-${codeName}`, keys[i]!], {
                stdio: 'ignore',
                timeout: 2_000,
              });
            }
          } catch {
            // Best-effort — the dialog will still be there next cycle.
          }
        },
        log,
        now: () => Date.now(),
        // ENG-6058: persist the give-up for the agent's channel servers —
        // their periodic sweeps turn it into a throttled user-facing
        // "please resend" notice for every conversation with an undrained
        // pending-inbound marker. Best-effort: a write failure is logged by
        // the watchdog, never fails the poll cycle.
        signalGiveUp: (codeName) => {
          const dir = join(homedir(), '.augmented', codeName);
          if (!existsSync(dir)) return;
          // CR on PR #1824: atomic (tmp + fsync + rename) — a torn JSON
          // parses as "no signal" downstream and would silently suppress
          // the user-facing notice.
          atomicWriteFileSync(
            join(dir, 'watchdog-give-up.json'),
            JSON.stringify({ gave_up_at: new Date().toISOString() }),
          );
        },
      }, {
        // ENG-6055: heal-keystroke escalation. 'disturb' (default) retries
        // with x→BSpace→Enter after the first bare Enter fails — a bare
        // retry repeats the exact keystroke the stuck TUI state eats
        // (kylie 2026-06-05). AGT_INPUT_HEAL_MODE=bare reverts to the
        // pre-ENG-6055 behaviour if a future CC/tmux pairing misbehaves.
        healMode: process.env.AGT_INPUT_HEAL_MODE === 'bare' ? 'bare' : 'disturb',
      });
    }

    // ENG-5614: reconcile the realtime socket vs the channel-subscription
    // flag BEFORE deciding whether to poll. A half-open socket leaves the
    // flag stale-true, which would otherwise skip the poll fallback and
    // silently drop inbound direct-chat. On divergence this forces the
    // flag false (and a reconnect next tick) so the poll fallback runs now.
    healRealtimeSocketDivergence();

    // Direct chat: use Realtime if connected, fall back to polling
    if (!isRealtimeConnected()) {
      pollDirectChatMessages(agentStates).catch((err) => {
        log(`Direct chat poll error: ${(err as Error).message}`);
      });
    }

    // Start Realtime subscription if not yet started and we have Supabase config
    ensureRealtimeStarted(agentStates);
    ensureRealtimeDriftStarted(agentStates);
    ensureRealtimeAssignStarted(agentStates);
    ensureRealtimeConfigStarted(agentStates);
    ensureRealtimeKanbanStarted(agentStates);
    ensureRealtimeIntegrationContextStarted(agentStates);

    // Spawn due recurring kanban templates
    try {
      const spawnData = await api.post<{ spawned: number }>('/host/kanban/recurring/spawn');
      if (spawnData.spawned > 0) {
        log(`Spawned ${spawnData.spawned} recurring kanban item(s)`);
      }
    } catch { /* non-fatal */ }

    state = {
      ...state,
      lastPollAt: new Date().toISOString(),
      pollCount: state.pollCount + 1,
      agents: agentStates,
      // ENG-5441: serialise trip state on every poll so manager restarts
      // never silently clear a tripped breaker. Cheap — only tripped
      // entries are stored, and the typical state is zero entries.
      circuitBreakerTrips: restartBreaker.serialize(),
      // ENG-6088: spent auto-resume credits survive manager restarts too.
      circuitBreakerAutoResumes: Object.fromEntries(autoResumeMarkers.entries()),
    };

    // ENG-5041: cycle completed — clear the backoff counter so the next
    // tick fires at the normal interval. Logging only on the transition
    // (n>0 → 0) so a healthy host doesn't spam recovery messages.
    if (consecutivePollFailures > 0) {
      log(`[poll-backoff] recovered after ${consecutivePollFailures} failure(s), resuming normal interval`);
      consecutivePollFailures = 0;
    }
    // ENG-6174: after the cycle's session work, verify any hot-reload-MCP
    // restarts actually respawned healthy — surfacing a stalled restart instead
    // of the pre-fix silent fire-and-forget.
    verifyPendingRestarts(Date.now());
    send({ type: 'state-update', state });
  } catch (err) {
    state.errorCount++;
    const message = (err as Error).message;
    log(`Poll error: ${message}`);
    send({ type: 'error', message });

    // ENG-5041: stay running and back off in-process instead of exiting
    // for the watchdog. Auth and 5xx failures are usually transient (key
    // rotation, API restart, prod outage) and the watchdog restart loop
    // was both slow and noisy — see the 2026-05-12 prod-down where the
    // S3 SDK regression made every /host/exchange return 500 for ~30
    // min and agt-aws-1 thrashed the whole time.
    consecutivePollFailures += 1;
    // CR (CodeRabbit on PR #982): log the *actual* scheduled delay (which
    // scheduleNext computes as max(intervalMs, backoffMs)) rather than the
    // raw backoff value, so the log line agrees with what really happens.
    // Use `_ms` suffix instead of seconds-with-s so downstream parsers
    // (Loki, awk, etc.) don't have to strip units.
    const scheduledDelayMs = Math.max(
      config?.intervalMs ?? 0,
      currentPollBackoffMs(consecutivePollFailures),
    );
    const errorClass = classifyPollError(message);
    log(`[poll-backoff] attempt=${consecutivePollFailures} class=${errorClass} next_retry_ms=${scheduledDelayMs}`);
  }
}

async function getOrCacheRegisteredAgents(adapter: FrameworkAdapter, profile?: string): Promise<Set<string>> {
  const cacheKey = profile ? `${adapter.id}:${profile}` : adapter.id;
  let cached = registeredAgentsCache.get(cacheKey);
  if (!cached) {
    cached = await adapter.getRegisteredAgents(profile);
    registeredAgentsCache.set(cacheKey, cached);
  }
  return cached;
}

async function processAgent(
  agent: { agent_id: string; code_name: string; display_name: string; status: string; environment: string; framework?: string; kill_switch?: { scope: string | null; reason: string | null; source: string | null } | null },
  agentStates: AgentState[],
): Promise<void> {
  if (!config) return;

  // ENG-5080: the per-agent banner used to fire here unconditionally —
  // one line per agent per poll-cycle (10s default), so an idle host with
  // three agents wrote ~26k pure-decoration lines per day to CloudWatch.
  // Every nested log() in this function carries `'${agent.code_name}'`,
  // so removing the banner doesn't cost any forensic context. Keep the
  // map writes; they're cheap and other call sites depend on them.
  //
  // The status-branches below (draft / paused / revoked) similarly used
  // to log unconditionally; they now edge-trigger on `agentState.knownStatuses`,
  // matching the existing "status changed" pattern further down.
  const previousKnownStatus = agentState.knownStatuses.get(agent.agent_id);
  agentState.agentDisplayNames.set(agent.code_name, agent.display_name);
  agentState.codeNameToAgentId.set(agent.code_name, agent.agent_id);
  // ENG-5441 + CodeRabbit on PR #1302: if a trip was hydrated from
  // `manager-state.json` (manager restart with a pre-existing trip),
  // `agentState.codeNameToAgentId` wasn't populated when the breaker's record path
  // first tried to report. Retry now that we know the agent_id.
  // Idempotent — `reportedTrips` short-circuits already-reported trips.
  maybeReportCurrentTrip(agent.code_name);

  // Cache the framework from the agent list response (refreshAgentConfig may override with more specific data)
  if (agent.framework) {
    agentFrameworkCache.set(agent.code_name, agent.framework);
  }

  // ENG-5389: scrape the Claude Code "X% of your weekly limit" banner from
  // pane.log and forward observations to the API. Claude Code only — the
  // banner doesn't exist in other framework runtimes. The monitor handles
  // its own rate-limiting and dedupes against an in-memory last value, so
  // calling it every cycle for every active CC agent is safe. Fire-and-
  // forget — observability writes must not slow the poll cycle.
  if (agent.framework === 'claude-code' && agent.status === 'active') {
    void maybeReportUsageBanner({
      api,
      codeName: agent.code_name,
      agentId: agent.agent_id,
      log,
    });

    // ENG-5516: read per-agent token usage from the session transcripts and
    // upsert it via /host/token-usage so usage is comparable across agents.
    // Same fire-and-forget contract as the banner monitor: self-rate-limited,
    // self-deduped, errors swallowed — must not slow the poll cycle.
    void maybeReportTokenUsage({
      api,
      codeName: agent.code_name,
      agentId: agent.agent_id,
      log,
    });

    // ENG-6314: reconcile accurate workflow-run token totals from the same
    // transcripts (summed over each run's window). Same fire-and-forget
    // contract: self-rate-limited (5-min/agent), errors swallowed.
    void maybeReconcileWorkflowRunTokens({
      api,
      codeName: agent.code_name,
      agentId: agent.agent_id,
      log,
    });

    // ENG-7235: metering-aware cost gate for the two idle, no-deliverable model
    // scorers below. On an OpenRouter-metered host both would be per-token spend
    // (and claude-p would retry-loop "Not logged in"), so they're suppressed
    // unless the operator picks the free host-local backend. Resolved once and
    // applied to both eval + extraction.
    const scorerSuppressed = conversationScorerSuppressed();
    if (scorerSuppressed) {
      logMeteredScorerSuppressed();
    }

    // Score completed end-user conversations for "did the agent help" with a
    // pluggable host-side backend (Haiku via `claude -p` by default, or a fully
    // local model such as Gemma 4 — see resolveConversationEvalBackend). The raw
    // transcript stays on the host; only the verdict crosses the wire. Same
    // fire-and-forget contract: self-throttled (5-min/agent), bounded
    // spawns/cycle, errors swallowed.
    if (!scorerSuppressed) {
      void maybeEvaluateConversations({
        api,
        backend: resolveConversationEvalBackend(),
        codeName: agent.code_name,
        agentId: agent.agent_id,
        log,
      });
    }

    // ENG-6200: extract durable memories from completed conversations
    // host-side (the replacement for the dead `sessions`-fed dreaming cron).
    // Same privacy boundary + pluggable backend as conversation eval — the raw
    // transcript stays on the host, only candidate memories cross the wire.
    // Gated by the 'memory-extraction' registry flag (ADR-0022), ships dark.
    // Arm it centrally from the admin Feature Flags page (reaches a running
    // manager within one heartbeat) or per-host via AGT_MEMORY_EXTRACTION_ENABLED=true.
    if (memoryExtractionEnabled() && !scorerSuppressed) {
      void maybeExtractMemories({
        api,
        backend: resolveConversationEvalBackend(),
        codeName: agent.code_name,
        agentId: agent.agent_id,
        log,
      });
    }
  }

  const now = new Date().toISOString();
  const adapter = resolveAgentFramework(agent.code_name);
  // ENG-4418: agentDir resolves through the adapter so manager + framework
  // share a single provision tree. Initial value uses the cached framework
  // from /host/agents; it is RE-resolved once /host/refresh returns the
  // authoritative framework below (see `agentDir = join(frameworkAdapter...)`
  // after agentFrameworkCache.set). Without the re-resolution, a stale
  // cache would trigger claudecodeAdapter.getAgentDir's migration on the
  // wrong framework's tree.
  let agentDir = join(adapter.getAgentDir(agent.code_name), 'provision');

  // 1a. Status-aware branching
  if (agent.status === 'draft' || agent.status === 'paused') {
    if (previousKnownStatus !== agent.status) {
      log(`Agent '${agent.code_name}' is ${agent.status}, skipping provisioning`);
    }
    // ENG-6146: kill switch. The API overlays status:'paused' + a kill_switch
    // marker when this agent's scope is killed. Halt is handled by the normal
    // paused path below; here we just record WHY (host-side, edge-triggered so
    // the runbook can see it without 10s spam) and remember it so the
    // paused→active resume below can announce the clear.
    if (agent.kill_switch) {
      killPausedCodeNames.add(agent.code_name);
      if (previousKnownStatus !== agent.status) {
        log(
          `[kill-switch] '${agent.code_name}' halted by ${agent.kill_switch.scope ?? 'unknown'} kill switch` +
            (agent.kill_switch.reason ? ` (reason: ${agent.kill_switch.reason})` : '') +
            ` source=${agent.kill_switch.source ?? 'unknown'}`,
        );
      }
    } else if (killPausedCodeNames.delete(agent.code_name)) {
      // CR #1897: the kill switch cleared but the agent is still paused for
      // another reason (operator pause, cost guardrail, circuit breaker). Drop
      // the kill-paused marker so a later operator paused→active resume doesn't
      // emit a misleading "kill switch cleared" notice.
      log(`[kill-switch] '${agent.code_name}' kill switch cleared while still ${agent.status} (no resume — paused for another reason)`);
    }
    // Kill persistent tmux session if it exists (prevents paused agents responding on channels)
    // Must use tmux kill-session directly — the sessions Map doesn't track sessions from previous manager runs
    stopPersistentSessionAndForgetMcpBaseline(agent.code_name);
    try {
      const { execSync: es } = await import('node:child_process');
      es(`tmux kill-session -t agt-${agent.code_name} 2>/dev/null`, { stdio: 'ignore' });
      log(`Killed tmux session for paused agent '${agent.code_name}'`);
    } catch { /* no session to kill */ }
    // ENG-6088: a circuit-breaker-paused agent gets ONE auto-resume after
    // the quiet period. No-op unless AGT_AUTO_RESUME_ENABLED and this
    // manager holds a trip for the agent (operator/cost pauses have no
    // local trip → 'no-trip' skip; the server-side RPC guard is the
    // backstop for any disagreement).
    // Skip auto-resume for kill-switch-paused agents — the API overlay
    // re-pauses them every poll regardless of DB status, so an auto-resume flip
    // would be pointless churn. Only operator/cost/breaker pauses auto-resume.
    if (agent.status === 'paused' && !agent.kill_switch) {
      maybeAutoResume(agent);
    }
    agentStates.push({
      agentId: agent.agent_id,
      codeName: agent.code_name,
      status: agent.status,
      charterVersion: '',
      toolsVersion: '',
      secretsHash: null,
      lastRefreshAt: now,
      lastProvisionAt: null,
      lastDriftCheckAt: null,
      lastSecretsProvisionAt: null,
      acpSessions: [],
    });
    // ENG-5080: needed for the edge-triggered log above. The branch
    // returns before the canonical agentState.knownStatuses.set near the end of
    // processAgent, so we'd otherwise log "is draft, skipping" every
    // 10s for the lifetime of the draft.
    agentState.knownStatuses.set(agent.agent_id, agent.status);
    return;
  }

  if (agent.status === 'revoked') {
    // ENG-5090: short-circuit on the second-and-later tick. All cleanup
    // below is idempotent — tmux session already dead, files already
    // removed, caches already cleared — so
    // re-running it every 10s for the lifetime of a revoked agent is
    // pure waste (child-process spawn for tmux, fs walks, Map churn).
    // ENG-5080 silenced the log line; this short-circuits the work.
    //
    // Re-enrolment safety: if the agent ever flips off `revoked` (e.g.
    // an operator un-revokes — rare but possible), the canonical
    // `status changed: prev → curr` branch a few lines below picks up
    // the transition because we still keep `agentState.knownStatuses` accurate.
    if (shouldSkipRevokedCleanup(previousKnownStatus)) {
      // CodeRabbit on PR #1052: cleanupAgentFiles swallows internal errors
      // and logs them, so a failed first-pass cleanup can still set
      // agentState.knownStatuses['revoked'] and silently skip retries. If the
      // provision dir is still on disk, fall through to retry instead of
      // short-circuiting.
      const residuals = {
        gatewayRunning: false,
        portAllocated: false,
        provisionDirExists: existsSync(agentDir),
      };
      if (!hasRevokedResiduals(residuals)) {
        agentStates.push({
          agentId: agent.agent_id,
          codeName: agent.code_name,
          status: agent.status,
          charterVersion: '',
          toolsVersion: '',
          secretsHash: null,
          lastRefreshAt: now,
          lastProvisionAt: null,
          lastDriftCheckAt: null,
          lastSecretsProvisionAt: null,
          acpSessions: [],
        });
        return;
      }
      // Residuals detected — note it once (this branch only triggers
      // when the first-pass cleanup partially failed, which is rare)
      // and fall through to the full cleanup block below for retry.
      log(
        `[revoked] residuals for '${agent.code_name}': ` +
          `dir=${residuals.provisionDirExists} ` +
          `— retrying cleanup`,
      );
    }

    log(`Agent '${agent.code_name}' is revoked, cleaning up`);
    stopPersistentSessionAndForgetMcpBaseline(agent.code_name);
    try { const { execSync: es } = await import('node:child_process'); es(`tmux kill-session -t agt-${agent.code_name} 2>/dev/null`, { stdio: 'ignore' }); } catch { /* no session */ }
    killAgentChannelProcesses(agent.code_name, { log });
    await cleanupAgentFiles(agent.code_name, agentDir);
    clearAgentCaches(agent.agent_id, agent.code_name);
    agentState.knownStatuses.set(agent.agent_id, agent.status);
    agentStates.push({
      agentId: agent.agent_id,
      codeName: agent.code_name,
      status: agent.status,
      charterVersion: '',
      toolsVersion: '',
      secretsHash: null,
      lastRefreshAt: now,
      lastProvisionAt: null,
      lastDriftCheckAt: null,
      lastSecretsProvisionAt: null,
      acpSessions: [],
    });
    return;
  }

  // 1b. Detect status change → force reprovision and channel re-evaluation
  const previousStatus = agentState.knownStatuses.get(agent.agent_id);
  if (previousStatus && previousStatus !== agent.status) {
    log(`Agent '${agent.code_name}' status changed: ${previousStatus} → ${agent.status}`);
    agentState.knownVersions.delete(agent.agent_id); // Force reprovision
    // ENG-5441: paused → active means the operator cleared the trip
    // (either through the webapp resume or `agt agent resume`). Drop the
    // breaker state so the agent can spawn on this tick. We don't clear
    // on every status-change because draft/revoked transitions don't
    // imply operator intent to recover from a trip.
    if (previousStatus === 'paused' && agent.status === 'active' && restartBreaker.isTripped(agent.code_name)) {
      restartBreaker.clear(agent.code_name);
      reportedTrips.delete(agent.code_name);
      log(`[circuit-breaker] Cleared trip for '${agent.code_name}' on operator resume (paused → active)`);
    }
    // ENG-6088: distinguish OUR auto-resume from an operator's. An
    // operator resume is deliberate human intent — it re-arms the
    // auto-resume credit by clearing the marker. Our own resume must NOT
    // (the marker is exactly what bounds re-trips to one unattended
    // resume). Discriminator: marker freshness. A transition observed
    // within AUTO_RESUME_SELF_WINDOW_MS of the marker's autoResumedAt is
    // ours (post-resume the agent is active, so an operator resume can
    // only follow a later re-pause — a quiet-period timescale away).
    // Marker-based rather than an in-memory flag so it survives a manager
    // restart mid-transition (CodeRabbit on this PR).
    if (previousStatus === 'paused' && agent.status === 'active') {
      const marker = autoResumeMarkers.get(agent.code_name);
      const isSelfResume = marker !== undefined && Date.now() - marker.autoResumedAt < AUTO_RESUME_SELF_WINDOW_MS;
      if (!isSelfResume && autoResumeMarkers.delete(agent.code_name)) {
        // Persist the re-arm NOW, mirroring the spend path — a worker exit
        // before the end-of-cycle write would rehydrate the stale marker
        // and make the next trip look already-auto-resumed until the
        // backoff TTL expires (CodeRabbit on this PR).
        state = {
          ...state,
          circuitBreakerAutoResumes: Object.fromEntries(autoResumeMarkers.entries()),
        };
        send({ type: 'state-update', state });
        log(`[auto-resume] Cleared auto-resume marker for '${agent.code_name}' on operator resume — credit re-armed (ENG-6088)`);
      }
    }
    // ENG-6146: kill-switch resume. If this agent was halted by a kill switch
    // that has now cleared (the manager's last-seen status was the API's
    // overlaid 'paused'; the underlying DB status was 'active' throughout), it
    // re-spawns on this tick like any paused→active. Announce the resume on its
    // channels, mirroring the restart-notice hygiene. Best-effort, edge-once
    // (drained from the set so it fires only on the clearing transition).
    if (
      previousStatus === 'paused' &&
      agent.status === 'active' &&
      killPausedCodeNames.delete(agent.code_name)
    ) {
      log(`[kill-switch] '${agent.code_name}' kill switch cleared — resuming session`);
      void injectMessage(
        agent.code_name,
        'system',
        'The kill switch that halted you has been cleared. Your session is resuming.',
        { task_name: 'kill-switch-resume' },
        log,
      ).catch(() => false);
    }
    // Invalidate channel config hashes so credentials/bindings/enabled state get rewritten
    let channelCacheMutated = false;
    for (const key of agentState.knownChannelConfigHashes.keys()) {
      if (key.startsWith(`${agent.agent_id}:`)) {
        agentState.knownChannelConfigHashes.delete(key);
        channelCacheMutated = true;
      }
    }
    if (channelCacheMutated) saveChannelHashCache();
  }
  agentState.knownStatuses.set(agent.agent_id, agent.status);

  // 2. Refresh — get latest charter/tools from API
  let refreshData: {
    agent: Record<string, unknown>;
    charter: { raw_content: string; version: string } | null;
    tools: { raw_content: string; version: string } | null;
    channel_configs: Record<string, { config: unknown; status: string }> | null;
    team_channel_policy: {
      team_id: string;
      allowed_channels: string[];
      denied_channels: string[];
      require_elevated_for_pii: boolean;
    } | null;
    team: { name: string; description: string | null; settings?: Record<string, unknown> } | null;
    scheduled_tasks: Array<{
      id: string;
      template_id: string;
      name: string;
      schedule_kind: string;
      schedule_expr: string | null;
      schedule_every: string | null;
      schedule_at: string | null;
      timezone: string;
      prompt: string;
      session_target: string;
      delivery_mode: string;
      // ENG-6124: /host/refresh returns this (ENG-6107 select); the type must
      // declare it so the producer map's cast isn't lying.
      delivery_policy?: string | null;
      delivery_channel: string;
      delivery_to: string | null;
      enabled: boolean;
    }> | null;
    plugin_skills?: Array<{
      plugin_slug: string;
      skill_id: string;
      skill_name: string;
      content: string;
    }>;
    plugin_install_hooks?: Array<{
      plugin_slug: string;
      script: string;
    }>;
    /** ENG-4421: toolkits required by installed plugins — manager resolves CLI installs from these. */
    plugin_toolkits?: Array<{
      plugin_slug: string;
      toolkits: string[];
    }>;
    /** ENG-4341: pre-resolved plugin contexts (defaults applied, agent-scope row merged in). */
    plugin_contexts?: Array<{
      plugin_id: string;
      plugin_slug: string;
      values: Record<string, unknown>;
      overrides: string;
    }>;
    /** Org + team knowledge items for skill generation. */
    knowledge?: Array<{ title: string; slug: string; content: string | null; scope: 'org' | 'team' }>;
    /** Team members for CLAUDE.md directory. */
    team_members?: Array<{ display_name: string; email?: string; role: string; title?: string; contact_channel?: string }>;
    /** People/contacts for CLAUDE.md directory. */
    people?: Array<{ display_name: string; email?: string; title?: string; department?: string; relationship?: string; contact_channel?: string }>;
    /**
     * Effective guardrails for this agent, pre-resolved server-side
     * (org → team → agent + overrides) and joined with their
     * definitions. Threaded straight into `provisionInput.guardrails`
     * for the framework adapter to render. Omitted by older API
     * builds; downstream code treats `undefined` and `[]` the same.
     */
    guardrails?: ProvisionInput['guardrails'];
    /**
     * ADR-0012 / ENG-6352: active dynamic workflows for this agent, resolved
     * server-side (team default ∪ agent override, agent grain wins) and gated
     * on the `workflows-down-sync` flag. Threaded straight into
     * `provisionInput.workflows` so the claude-code adapter writes each as
     * `.claude/workflows/<name>.js`. Omitted by older API builds / empty when
     * the flag is off; downstream treats `undefined` and `[]` the same.
     */
    workflows?: ProvisionInput['workflows'];
  };

  try {
    refreshData = await api.post<typeof refreshData>('/host/refresh', {
      agent_id: agent.agent_id,
    });
  } catch (err) {
    log(`Refresh failed for '${agent.code_name}': ${(err as Error).message}`);
    const existing = state.agents.find((a) => a.agentId === agent.agent_id);
    agentStates.push(existing ?? {
      agentId: agent.agent_id,
      codeName: agent.code_name,
      status: agent.status,
      charterVersion: '',
      toolsVersion: '',
      secretsHash: null,
      lastRefreshAt: null,
      lastProvisionAt: null,
      lastDriftCheckAt: null,
      lastSecretsProvisionAt: null,
      acpSessions: [],
    });
    return;
  }

  // Cache alert webhook from team settings (first agent wins)
  if (!getAlertSlackWebhook() && refreshData.team?.settings) {
    const webhook = refreshData.team.settings['alert_slack_webhook'];
    if (typeof webhook === 'string' && webhook.startsWith('https://')) {
      setAlertSlackWebhook(webhook);
    }
  }

  if (!refreshData.charter || !refreshData.tools) {
    log(`No charter/tools for '${agent.code_name}', skipping`);
    agentStates.push({
      agentId: agent.agent_id,
      codeName: agent.code_name,
      status: agent.status,
      charterVersion: '',
      toolsVersion: '',
      secretsHash: null,
      lastRefreshAt: now,
      lastProvisionAt: null,
      lastDriftCheckAt: null,
      lastSecretsProvisionAt: null,
      acpSessions: [],
    });
    return;
  }

  // Resolve framework adapter from agent data and update cache
  const frameworkId = (refreshData.agent.framework as string) ?? DEFAULT_FRAMEWORK;
  agentFrameworkCache.set(agent.code_name, frameworkId);
  const frameworkAdapter = getFramework(frameworkId);
  // ENG-4418: re-resolve agentDir now that /host/refresh has given us the
  // authoritative framework. If the cache was stale (agent switched
  // framework since last poll), the initial agentDir would have pointed at
  // the wrong tree — recalculate before any writes or adapter calls use it.
  agentDir = join(frameworkAdapter.getAgentDir(agent.code_name), 'provision');

  // ENG-4422 §5: cache the agent delivery metadata so the DM resolver has
  // what it needs without an extra API call at fire time.
  // The host-runtime payload carries reports_to_* + owner_team_name (see §8).
  cacheAgentDeliveryMetadata(agent.code_name, refreshData);

  // ENG-6695: record this agent's timezone inputs (its own override + the
  // flattened team timezone) so restartGateFor can compute off-peak in the
  // operator's zone. The org tier is supplied at gate time from the cached
  // maintenance window's resolved timezone (host → org → region → UTC).
  agentRestartTimezoneInputs.set(agent.code_name, {
    agentTimezone:
      typeof refreshData.agent.timezone === 'string' ? (refreshData.agent.timezone as string) : null,
    teamTimezone:
      typeof (refreshData.team as { timezone?: unknown } | null)?.timezone === 'string'
        ? ((refreshData.team as { timezone?: string }).timezone ?? null)
        : null,
  });

  // ENG-5901 PR 3: one-shot per manager run — hoist pre-Track-D literal
  // secrets out of the on-disk .mcp.json into .env.integrations and rewrite
  // templated. Without this, files carrying old literal entries can never
  // pass the armed lint (every incremental writer preserves the other
  // servers' entries verbatim → permanent write rejection; observed live
  // on agt-aws-1 2026-06-04). Idempotent and cheap for clean files.
  if (frameworkAdapter.migrateSecretStorage && !migratedSecretStorage.has(agent.code_name)) {
    try {
      frameworkAdapter.migrateSecretStorage(agent.code_name);
      migratedSecretStorage.add(agent.code_name);
    } catch (err) {
      // Leave un-marked so the next tick retries.
      log(`[mcp-migrate] failed for '${agent.code_name}': ${(err as Error).message}`);
    }
  }

  // Ensure per-agent profile config exists
  if (frameworkAdapter.seedProfileConfig) {
    frameworkAdapter.seedProfileConfig(agent.code_name);
  }

  const charterVersion = refreshData.charter.version;
  const toolsVersion = refreshData.tools.version;
  const known = agentState.knownVersions.get(agent.agent_id);

  let lastProvisionAt = state.agents.find((a) => a.agentId === agent.agent_id)?.lastProvisionAt ?? null;

  // CodeRabbit on PR #773 (round 4): diff against the *launchable* channel
  // set, not raw channel_configs membership. The Claude launch flags
  // (`--dangerously-load-development-channels`, `--allowedTools`) only
  // include channels whose entry has `status` in {active, pending} AND a
  // non-null config — the same gate writeChannelCredentials runs at line
  // 2571. A status-only flip like `slack: active → revoked` leaves the
  // channel id in `Object.keys(channel_configs)` and would have been
  // missed by a raw-key diff, leaving the running session with stale
  // launch flags. Inversely, a row that never becomes launchable would
  // have triggered an unnecessary restart. Compute the same launchable
  // filter here so add/remove + restart decisions agree with what the
  // session actually loads.
  //
  // Detect channel changes (launchable filter via the shared helper so
  // unit tests cover the same gate this code uses at runtime).
  // ENG-5932: drop locally-quarantined channels from the effective set at the
  // source, so they're excluded uniformly from the launch flags, the
  // writeChannelCredentials loop, the channel-set-change diff, and the cache.
  // In shadow/off mode this set is always empty (no markers are written), so
  // every line below is a no-op until enforcement is flipped on.
  const quarantinedChannels = channelQuarantineStore().getQuarantinedKeys(agent.code_name);
  const currentChannelIds = setWithout(
    launchableChannelIds(refreshData.channel_configs),
    quarantinedChannels,
  );
  const previousChannelIds = agentState.knownChannels.get(agent.agent_id);
  const channelsChanged = !previousChannelIds ||
    currentChannelIds.size !== previousChannelIds.size ||
    [...currentChannelIds].some((ch) => !previousChannelIds.has(ch)) ||
    [...previousChannelIds].some((ch) => !currentChannelIds.has(ch));

  // CodeRabbit on PR #773 (round 2): track whether the on-disk channel
  // credential state actually converges to `currentChannelIds` before we
  // (a) advance the diff cache (`agentState.knownChannels.set`) and (b) trigger an
  // ENG-4807 hot-reload restart. If a remove or write fails, leave the
  // diff live so the next refresh tick sees the change again and retries
  // the credential sync — and don't restart the session on broken
  // credential state.
  let channelConfigConverged = true;

  // Remove credentials for channels that were unbound
  if (previousChannelIds && channelsChanged && frameworkAdapter.removeChannelCredentials) {
    for (const ch of previousChannelIds) {
      if (!currentChannelIds.has(ch)) {
        try {
          frameworkAdapter.removeChannelCredentials(agent.code_name, ch);
          log(`Removed ${ch} credentials for '${agent.code_name}'`);
        } catch (err) {
          channelConfigConverged = false;
          log(`Failed to remove ${ch} credentials for '${agent.code_name}': ${(err as Error).message}`);
        }
      }
    }
  }

  // 3. Content-based re-provisioning: always generate artifacts, compare against
  //    what's on disk, and only write files that actually changed. This catches
  //    template code changes, DB data changes (role, description), and version bumps.
  try {
    const artifacts = generateArtifacts(agent, refreshData, frameworkAdapter);
    // Some artifacts (`.mcp.json`) need their on-disk merge computed *before*
    // the write loop, so track an optional override content alongside the
    // artifact. When set, it replaces artifact.content at write time.
    const changedFiles: { relativePath: string; content: string }[] = [];

    mkdirSync(agentDir, { recursive: true });
    for (const artifact of artifacts) {
      const filePath = join(agentDir, artifact.relativePath);

      // For CLAUDE.md: strip the skills index section before comparing,
      // since it's managed separately by refreshSkillsIndexInClaudeMd.
      // Without this, the provisioner re-writes CLAUDE.md every cycle
      // (it generates without the index, but on-disk has it).
      //
      // Both sides must be trimmed the same way — the stripped project copy
      // loses its trailing newline via trimEnd(), and the generated artifact
      // content preserves its trailing newline. Without trimming both,
      // hashes never match and the provisioner churns every poll.
      let existingHash: string | null;
      let newHash: string;
      let writeContent = artifact.content;
      if (artifact.relativePath === 'CLAUDE.md') {
        // Strip dynamically-patched sections from BOTH sides before hashing.
        // Two sections get added to project/CLAUDE.md by side-effect code paths
        // that the generator doesn't know about:
        //   1. `## Available Skills` + `## Updating Plugins` (index) — added by
        //      refreshSkillsIndexInClaudeMd, bracketed by AGT:SKILLS_INDEX markers.
        //   2. `## Integrations` — added by frameworkAdapter.writeIntegrations,
        //      bracketed by AGT:INTEGRATIONS markers (ENG-5794).
        //
        // ENG-5794: the Integrations strip used to be the broad
        //   `## Integrations[\s\S]*?(?=## Rules)`
        // which swallowed every section between `## Integrations` and
        // `## Rules` — capability prompt, knowledge, kanban work policy,
        // skill authoring, dashboards. Template changes to any of those
        // were invisible to the hash and never triggered a CLAUDE.md
        // rewrite, so improvements like ENG-5792's "What can you do for
        // me?" section could not reach existing agents without a manual
        // re-provision. The pattern is now pinned to the
        // AGT:INTEGRATIONS_START/_END sentinel pair so only the
        // Integrations block itself is excluded from the hash. A legacy
        // fallback regex remains so on-disk CLAUDE.md files written
        // before sentinels existed still hash cleanly until the next
        // render brings them up to date.
        const stripDynamicSections = (s: string): string => {
          let out = s.replace(new RegExp(`${SKILLS_INDEX_START}[\\s\\S]*?${SKILLS_INDEX_END}`), '');
          if (out.includes(INTEGRATIONS_SECTION_START)) {
            out = out.replace(
              new RegExp(`${escapeRegExp(INTEGRATIONS_SECTION_START)}[\\s\\S]*?${escapeRegExp(INTEGRATIONS_SECTION_END)}\\n*`),
              '',
            );
          } else {
            // Legacy on-disk shape — pre-ENG-5794. First render after the
            // manager upgrade replaces this with the sentinel-bracketed
            // form, after which this branch is dead.
            out = out.replace(/## Integrations[\s\S]*?(?=## Rules)/, '');
          }
          return out.trimEnd();
        };
        newHash = sha256(stripDynamicSections(artifact.content));
        try {
          const projectClaudeMd = join(config.configDir, agent.code_name, 'project', 'CLAUDE.md');
          const existing = readFileSync(projectClaudeMd, 'utf-8');
          existingHash = sha256(stripDynamicSections(existing));
        } catch {
          existingHash = null;
        }
      } else if (artifact.relativePath === '.mcp.json') {
        // The `.mcp.json` generator only owns a subset of mcpServers (augmented,
        // qmd, ...); channel servers (slack, direct-chat, …) and plugin-added
        // servers are merged in by other code paths. A naive overwrite would
        // wipe them every poll, which then makes step 5b re-add direct-chat,
        // step 5 re-add slack, etc. — the loop that produces:
        //   Updating 'agent': .mcp.json
        //   Channel credentials written for 'agent/direct-chat'
        // every cycle. Compare and write only the generator-owned keys,
        // preserving everything else.
        const parseMcp = (raw: string): Record<string, unknown> => {
          try {
            const parsed = JSON.parse(raw) as { mcpServers?: Record<string, unknown> };
            return parsed.mcpServers ?? {};
          } catch {
            return {};
          }
        };
        const generatorServers = parseMcp(artifact.content);
        const generatorKeys = Object.keys(generatorServers);
        let existingRaw = '';
        try { existingRaw = readFileSync(filePath, 'utf-8'); } catch { /* new file */ }
        const existingServers = parseMcp(existingRaw);
        const merged = { mcpServers: { ...existingServers, ...generatorServers } };
        writeContent = JSON.stringify(merged, null, 2);
        // Hash only the generator-owned slice on both sides. If the generator
        // produces the same shape as what's already there, skip the write.
        const sliceGeneratorKeys = (src: Record<string, unknown>): Record<string, unknown> => {
          const out: Record<string, unknown> = {};
          for (const k of generatorKeys) if (k in src) out[k] = src[k];
          return out;
        };
        newHash = sha256(JSON.stringify(generatorServers));
        existingHash = existingRaw
          ? sha256(JSON.stringify(sliceGeneratorKeys(existingServers)))
          : null;
      } else {
        newHash = sha256(artifact.content);
        existingHash = hashFile(filePath);
      }

      if (newHash !== existingHash) {
        changedFiles.push({ relativePath: artifact.relativePath, content: writeContent });
      }
    }

    if (changedFiles.length > 0) {
      const isFirst = !existsSync(join(agentDir, 'CHARTER.md'));
      const verb = isFirst ? 'Provisioning' : 'Updating';
      const fileNames = changedFiles.map((f) => f.relativePath).join(', ');
      log(`${verb} '${agent.code_name}': ${fileNames}`);

      for (const file of changedFiles) {
        const filePath = join(agentDir, file.relativePath);
        mkdirSync(dirname(filePath), { recursive: true });
        if (file.relativePath === '.mcp.json') {
          // ENG-5901 (CodeRabbit #1731): the secret-bearing artifact must
          // never exist world-readable, even briefly — write it via the
          // atomic temp-file + rename path with 0600 set on the temp file
          // before the swap (same writer safeWriteMcpJson uses, minus the
          // validation: the merged content was produced by the generator
          // merge above and parsed on the way in).
          safeWriteJsonAtomic(filePath, file.content, { mode: 0o600 });
        } else {
          writeFileSync(filePath, file.content);
        }
      }

      // Prune stale legacy knowledge-* skill dirs from provision dir
      // (replaced by single core-knowledge skill)
      try {
        const provSkillsDir = join(agentDir, '.claude', 'skills');
        if (existsSync(provSkillsDir)) {
          for (const folder of readdirSync(provSkillsDir)) {
            if (folder.startsWith('knowledge-')) {
              try { rmSync(join(provSkillsDir, folder), { recursive: true }); } catch { /* ignore */ }
            }
          }
        }
      } catch { /* non-fatal */ }

      lastProvisionAt = new Date().toISOString();

      agentState.knownVersions.set(agent.agent_id, { charterVersion, toolsVersion });

      // Update written hashes for drift detection
      const trackedFiles = frameworkAdapter.driftTrackedFiles();
      const hashes = new Map<string, string>();
      for (const file of trackedFiles) {
        const h = hashFile(join(agentDir, file));
        if (h) hashes.set(file, h);
      }
      agentState.writtenHashes.set(agent.agent_id, hashes);

      // Register in framework runtime if not already present
      const resolvedModelsForRegistration = resolveModelChain(refreshData);
      const primaryModel = resolvedModelsForRegistration.primary ?? (refreshData.agent.primary_model as string | null | undefined);
      const registeredAgents = await getOrCacheRegisteredAgents(frameworkAdapter, agent.code_name);
      if (!registeredAgents.has(agent.code_name)) {
        const registered = await frameworkAdapter.registerAgent(agent.code_name, agentDir, primaryModel);
        if (registered) {
          registeredAgents.add(agent.code_name);
          log(`Registered '${agent.code_name}' in ${frameworkAdapter.label}`);
        }
      }

      send({ type: 'provisioned', agentId: agent.agent_id, codeName: agent.code_name });
    }

    // ADR-0012 / ENG-6352: prune stale workflow files from the provision dir.
    // A workflow content change rides the per-artifact hash above, but a
    // *removal* (revoked / renamed / flag turned off) produces no changedFiles
    // entry — the artifact simply stops being generated — so the old
    // `.claude/workflows/<name>.js` would linger in the provision dir and the
    // deploy step (which mirrors provision → project) would keep copying it.
    // Reconcile the provision dir against the server-resolved set every cycle.
    // Only when `workflows` is a defined array: an older API that omits the
    // field entirely carries no information, so leave the dir untouched.
    if (Array.isArray(refreshData.workflows)) {
      try {
        const provWorkflowsDir = join(agentDir, '.claude', 'workflows');
        if (existsSync(provWorkflowsDir)) {
          const expected = new Set(refreshData.workflows.map((w) => `${w.name}.js`));
          for (const file of readdirSync(provWorkflowsDir)) {
            if (!file.endsWith('.js')) continue;
            if (expected.has(file)) continue;
            try { rmSync(join(provWorkflowsDir, file)); } catch { /* non-fatal */ }
          }
        }
      } catch { /* non-fatal */ }
    }

    // Always deploy artifacts to project dir — the provision dir is the source
    // of truth, and channel MCP servers are merged from a separate path.
    // This must run outside changedFiles so the merge always produces a
    // complete .mcp.json (base servers + channel servers).
    if (frameworkAdapter.deployArtifactsToProject) {
      frameworkAdapter.deployArtifactsToProject(agent.code_name, agentDir);
    }
  } catch (err) {
    log(`Provision failed for '${agent.code_name}': ${(err as Error).message}`);
  }

  // 3b. Ensure model is set correctly in per-agent profile (on first run and on change)
  const resolvedForModel = resolveModelChain(refreshData);
  const primaryModel = resolvedForModel.primary ?? (refreshData.agent.primary_model as string | null | undefined);
  if (primaryModel && frameworkAdapter.updateAgentModel) {
    const previousModel = agentState.knownModels.get(agent.agent_id);
    if (previousModel !== primaryModel) {
      try {
        const updated = await frameworkAdapter.updateAgentModel(agent.code_name, primaryModel);
        if (updated) {
          if (previousModel) {
            log(`Model updated for '${agent.code_name}': ${previousModel} → ${primaryModel}`);
          } else {
            log(`Model set for '${agent.code_name}': ${primaryModel}`);
          }
        }
      } catch (err) {
        log(`Failed to update model for '${agent.code_name}': ${(err as Error).message}`);
      }

      // ENG-5635: a model change only takes effect on the next spawn — the
      // launcher derives `--model <alias>` from primary_model at launch
      // (ENG-5631); the settings.json write above is inert for claude-code at
      // runtime. So for a live claude-code session, proactively respawn now
      // (via the coalescing scheduler, which preserves the presence-reaper
      // baseline since channels/MCP are unchanged) instead of leaving the
      // agent on its old model until the next natural restart. The gate skips
      // first-provision (no previousModel — the imminent first spawn already
      // carries the model) and the no-session case (next spawn picks it up).
      if (
        shouldRespawnForModelChange({
          previousModel,
          primaryModel,
          framework: frameworkId,
          sessionHealthy: isSessionHealthy(agent.code_name),
        })
      ) {
        const restartNotice =
          `Your model was changed to ${primaryModel}. Claude Code applies the model at launch, ` +
          `so your manager will restart your session shortly to switch.`;
        const delivered = await injectMessage(
          agent.code_name, 'system', restartNotice + BACK_ONLINE_GREETING_GUIDANCE, { task_name: 'model-update' }, log,
        ).catch(() => false);
        const delay = delivered ? 8_000 : 3_000;
        if (!delivered) {
          log(`[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`);
        }
        log(`[hot-reload] Model changed for '${agent.code_name}': ${previousModel} → ${primaryModel} — restarting session`);
        scheduleSessionRestart(agent.code_name, delay, 'model change', 'model-change');
      }
    }
    agentState.knownModels.set(agent.agent_id, primaryModel);
  }

  // 4. Drift check — hash local files and compare
  let lastDriftCheckAt = now;
  const written = agentState.writtenHashes.get(agent.agent_id);

  if (written && existsSync(agentDir)) {
    const driftedFiles: string[] = [];

    for (const [file, expectedHash] of written) {
      const localHash = hashFile(join(agentDir, file));
      if (localHash && localHash !== expectedHash) {
        driftedFiles.push(file);
      }
    }

    if (driftedFiles.length > 0) {
      log(`Drift detected for '${agent.code_name}': ${driftedFiles.join(', ')}`);
      send({ type: 'drift-detected', agentId: agent.agent_id, codeName: agent.code_name, files: driftedFiles });

      // Report drift to API
      try {
        const localHashes: Record<string, string | null> = {};
        for (const file of driftedFiles) {
          localHashes[file] = hashFile(join(agentDir, file));
        }
        await api.post('/host/drift', {
          agent_id: agent.agent_id,
          drifted_files: driftedFiles,
          local_hashes: localHashes,
        });
      } catch (err) {
        log(`Failed to report drift for '${agent.code_name}': ${(err as Error).message}`);
      }
    }
  }

  // Cache bot tokens from channel_configs for notification delivery
  if (refreshData.channel_configs) {
    const tokens: { slack?: string; telegram?: string; telegramAllowedChats?: string[] } = {};
    const slackCfg = refreshData.channel_configs['slack'];
    if (slackCfg?.config) {
      const bt = (slackCfg.config as Record<string, unknown>).bot_token;
      if (typeof bt === 'string' && bt) tokens.slack = bt;
    }
    const tgCfg = refreshData.channel_configs['telegram'];
    if (tgCfg?.config) {
      const tgConfig = tgCfg.config as Record<string, unknown>;
      const bt = tgConfig.bot_token;
      if (typeof bt === 'string' && bt) tokens.telegram = bt;
      const allowedChats = tgConfig.allowed_chat_ids;
      if (Array.isArray(allowedChats)) tokens.telegramAllowedChats = allowedChats as string[];
    }
    if (tokens.slack || tokens.telegram) {
      agentChannelTokens.set(agent.code_name, tokens);
    } else {
      agentChannelTokens.delete(agent.code_name);
    }
  }

  // 5. Write channel credentials to per-agent profile config (only if changed)
  if (refreshData.channel_configs && frameworkAdapter.writeChannelCredentials) {
    if (agent.status === 'active') {
      for (const [channelId, entry] of Object.entries(refreshData.channel_configs)) {
        // ENG-5932: a quarantined channel is dropped from the agent's set —
        // stop re-writing its credentials/.mcp.json entry, and actively delete
        // any entry a prior poll (or a manager restart, where the
        // removeChannelCredentials diff path below is skipped because
        // previousChannelIds is empty) left behind. removeChannelCredentials
        // deletes the channel from `.mcp.json mcpServers` + re-syncs the
        // project copy — the membership change the reaper keys off — so the
        // dead channel stops being seen declared-but-missing. Idempotent: a
        // no-op once the entry is already gone. Empty set in shadow/off mode.
        if (quarantinedChannels.has(channelId)) {
          try {
            frameworkAdapter.removeChannelCredentials?.(agent.code_name, channelId);
          } catch (err) {
            log(`[channel-quarantine] failed to drop quarantined channel '${agent.code_name}/${channelId}' from .mcp.json: ${(err as Error).message}`);
          }
          continue;
        }
        if ((entry.status === 'active' || entry.status === 'pending') && entry.config) {
          // Track that this channel is active for this agent's profile
          if (!activeChannels.has(channelId)) {
            activeChannels.set(channelId, new Set());
          }
          activeChannels.get(channelId)!.add(agent.code_name);

          // Hash the config to detect changes — skip credential write if
          // identical to last poll. ENG-4439: also verify the on-disk state
          // actually holds the entry before skipping. The cache is purely
          // "what I intended to write"; if an external process modified the
          // agent's runtime config between polls (manual edit, migration,
          // another tool), the cache would otherwise refuse to re-write
          // forever and the channel stays broken until the manager restarts.
          // Adapters without `hasChannelCredentials` are treated as "trust
          // the cache" so older framework adapters keep their existing
          // behavior.
          //
          // ENG-4468: hash the CANONICAL (recursively key-sorted) JSON,
          // not plain `JSON.stringify`. Postgres JSONB doesn't preserve
          // key order, so the same semantic config can serialise to
          // different strings across polls — producing different hashes
          // and forcing constant rewrites. The churn cascaded into
          // .mcp.json drift and MCP subprocess instability on augmenteds-mbp
          // (Phil / Pepper). Canonicalising fixes it regardless of which
          // field moved.
          // ENG-4912 / ENG-4940: fold the channel-agnostic peer kill
          // switch into the hash input so a team-admin flip invalidates
          // the cache and forces a credential rewrite (which propagates
          // the PEER_DISABLED env to the spawned MCP child). Without
          // this the per-channel hash on `entry.config` alone would skip
          // the rewrite — entry.config has nothing to do with team
          // settings — and the kill switch wouldn't take effect until
          // the channel's own config drifted.
          //
          // Reads `peer_disabled` (enum) first, falls back to the legacy
          // `telegram_peer_disabled` boolean (true → 'all') so any
          // pre-migration row still gates correctly while the deploy is
          // mid-flight.
          const teamSettings = (refreshData.team as { settings?: Record<string, unknown> } | undefined)?.settings;
          const peerDisabledMode: 'off' | 'cross_team_only' | 'all' = (() => {
            const next = teamSettings?.['peer_disabled'];
            if (next === 'off' || next === 'cross_team_only' || next === 'all') return next;
            return teamSettings?.['telegram_peer_disabled'] === true ? 'all' : 'off';
          })();
          // ENG-5363: team timezone propagates into the spawned channel
          // MCP child as the `TZ` env var so progress-message timestamps
          // ("last update: HH:MM:SS TZ") render in the agent's local
          // time instead of the host system zone (UTC on Lambda/EC2).
          // Folded into the hash so a team timezone change invalidates
          // the cache and triggers a credential rewrite.
          const agentTimezone = (() => {
            const tz = teamSettings?.['timezone'];
            return typeof tz === 'string' && tz.trim() !== '' ? tz.trim() : undefined;
          })();
          const teamSettingsForHash =
            channelId === 'telegram' || channelId === 'slack' || channelId === 'direct-chat'
              ? { peer_disabled: peerDisabledMode, timezone: agentTimezone ?? null }
              : null;
          // ENG-4923: also fold the resolved peer roster into the hash
          // input. entry.config carries peer_agent_mode + peer_group_ids
          // natively (TelegramChannelConfig fields), but the CHARTER-
          // derived peer list does not — so a CHARTER edit that changes
          // declared peers would otherwise leave the hash unchanged and
          // never propagate the new TELEGRAM_PEERS env to the child.
          const crossTeamData = refreshData as {
            organization?: { cross_team_peer_intra_org?: 'unrestricted' | 'consent_required' } | null;
            cross_team_grants?: {
              inbound?: ReadonlyArray<{
                grant_id: string;
                granted_agent_bot_id: number | null;
                /** ENG-4970 / ENG-4973: Slack `U…` user_id of granted_agent_id. */
                granted_agent_slack_user_id?: string | null;
                revoked_at: string | null;
                expires_at: string | null;
              }>;
            };
            team_peer_bot_ids?: ReadonlyArray<number>;
            /** ENG-4970 / ENG-4973: Slack `U…` user_ids of same-team agents. */
            team_peer_slack_user_ids?: ReadonlyArray<string>;
          };
          // CodeRabbit on PR #868: only build a gateContext when the
          // /host/refresh response actually carries the ENG-4935 fields.
          // Older API versions (and any partial payloads) leave the
          // discriminator field absent — passing a synthesised context
          // with `[]` defaults would silently mask cross-team peers as
          // `intra_org_unrestricted` and never reach the documented
          // backwards-compat path in extractCharterTelegramPeers.
          //
          // CodeRabbit on PR #934: pick the discriminator per-channel.
          // The previous all-Telegram discriminator would short-circuit
          // the Slack backwards-compat fallback on managers running
          // against a /host/refresh that has the Telegram fields but
          // not yet the Slack ones (mid-rollout). Without this, same-team
          // Slack peers would get downgraded to intra_org_unrestricted /
          // null because gateContext is "present but with empty Slack
          // arrays" — not the same as "absent entirely".
          const refreshHasCrossTeamFields =
            channelId === 'telegram'
              ? Array.isArray(crossTeamData.team_peer_bot_ids)
              : channelId === 'slack'
                ? Array.isArray(crossTeamData.team_peer_slack_user_ids)
                : false;
          const gateContext: CharterPeerGateContext | undefined =
            refreshHasCrossTeamFields
              ? {
                  teamPeerBotIds: crossTeamData.team_peer_bot_ids ?? [],
                  teamPeerSlackUserIds: crossTeamData.team_peer_slack_user_ids ?? [],
                  crossTeamGrants: crossTeamData.cross_team_grants?.inbound ?? [],
                  crossTeamPeerIntraOrg:
                    crossTeamData.organization?.cross_team_peer_intra_org ?? 'unrestricted',
                }
              : undefined;
          // Peer roster folded into the hash so a CHARTER edit / grant
          // flip invalidates the cache. Telegram + Slack each carry
          // their own peer arrays in CHARTER; whichever channel we're
          // rendering picks the matching one.
          const peersForHash =
            channelId === 'telegram'
              ? extractCharterTelegramPeers(refreshData.charter?.raw_content ?? '', gateContext)
              : channelId === 'slack'
                ? extractCharterSlackPeers(refreshData.charter?.raw_content ?? '', gateContext)
                : null;
          // Bump `writeVersion` whenever the env block emitted by
          // `writeChannelCredentials` changes shape (new env var added,
          // existing var renamed, etc.). Existing deployments cache the
          // hash on disk, so without this bump a fresh CLI binary will
          // skip the credentials rewrite — the agent never picks up
          // the new env vars until config / team / peers actually
          // change for some unrelated reason.
          //
          // History:
          //   v1 — initial channel hash (ENG-4439)
          //   v2 — PR #912 forwards AGT_HOST / AGT_API_KEY / AGT_AGENT_ID
          //        to the Telegram MCP child. Required for ENG-4986
          //        observed-chat, ENG-4937 audit emission, and ENG-4909
          //        durable peer rate limiting to function.
          //   v3 — ENG-4970 / ENG-4974: Slack MCP child gets the same
          //        AGT auth trio + SLACK_PEERS / SLACK_PEERS_GATE /
          //        SLACK_PEER_AGENT_MODE / SLACK_PEER_GROUP_IDS env so
          //        cross-team peer collab finally works on Slack at
          //        runtime (previously the classifier had an empty peer
          //        list).
          //   v5 — ENG-5363: fold `agentTimezone` (from
          //        team.settings.timezone) into the hash + emit `TZ` env
          //        var on telegram / slack MCP children so the
          //        in-place progress heartbeat's "last update: HH:MM:SS"
          //        footer renders in the agent's local timezone instead
          //        of UTC.
          //   v4 — ENG-5155: fold `session_mode` into the hash. The
          //        rendered slack/direct-chat blocks branch on
          //        `isPersistent` (`claudecode/index.ts:2149`), so a
          //        oneshot→persistent flip changes the output of
          //        `writeChannelCredentials` — but pre-v4 the hash
          //        didn't notice and the rewrite was skipped forever.
          //        Sherlock (2026-05-17) hit this: created as oneshot
          //        before #1108, then flipped to persistent, but his
          //        `.mcp.json` kept the broken `npx -y @augmented/
          //        claude-code-channel-slack` fallback until we cleared
          //        the cache by hand. The version bump itself forces
          //        one rewrite per agent on first poll after upgrade,
          //        which auto-repairs any other agents in the same
          //        stuck state.
          const sessionModeForHash =
            (refreshData.agent as Record<string, unknown>).session_mode as string | undefined;
          // ENG-5841: effective sender_policy lives at refreshData.sender_policy
          // (top-level field surfaced by /host/refresh). Fold into the hash so a
          // flip on either org or per-agent column triggers a credentials rewrite
          // — see v4 ENG-5155 precedent (Sherlock's stuck cache) for what happens
          // when the hash misses a shape change.
          const senderPolicyForHash =
            (refreshData as Record<string, unknown>).sender_policy ?? null;
          //   v6 — ENG-5841: SLACK_SENDER_POLICY / MSTEAMS_SENDER_POLICY env
          //        vars injected into slack-channel / teams-channel MCP env
          //        when sender_policy is set on org or agent. Bumps so deployed
          //        agents get a one-shot rewrite that picks up the new vars
          //        even though their on-disk entry would otherwise look "fresh".
          //   v7 — ENG-5842: manager_only mode adds
          //        SLACK_SENDER_POLICY_PRINCIPAL_ID / MSTEAMS_SENDER_POLICY_PRINCIPAL_ID
          //        when the resolved principal has a Slack user_id / Teams
          //        AAD id. The hash already covers refreshData.sender_policy
          //        (which now carries the principal sub-object), but the bump
          //        forces a rewrite for any agent that was already on v6
          //        when manager_only first hit their org.
          //   v8 — ENG-5843: internal_only adds SLACK_INTERNAL_ONLY +
          //        SLACK_HOME_TEAM_ID (and MSTEAMS_ equivalents) when the
          //        org sets internal_only:true. The hash covers
          //        refreshData.sender_policy.internal_only, but the bump
          //        forces a rewrite for agents on v7 the moment the org
          //        flips internal_only on.
          //   v9 — ENG-5871: team_only mode adds
          //        SLACK_SENDER_POLICY_TEAM_PRINCIPAL_IDS /
          //        MSTEAMS_SENDER_POLICY_TEAM_PRINCIPAL_IDS comma-separated
          //        lists when the resolved team has members with the
          //        relevant contact prefs. The hash already covers
          //        refreshData.sender_policy.team_principals, but the bump
          //        forces a rewrite for agents already on v8 the moment
          //        team_only first gets selected.
          //
          // ENG-6600: the writeVersion above is a MANUAL knob. Every entry
          // in the v4–v9 history is a code-only change to what the adapter
          // RENDERS into a channel entry (a new env var on slack/teams) that
          // the input-only hash couldn't see, so a human had to remember to
          // bump it. ENG-6563/6582 added AGT_TURN_INITIATOR_FILE to the
          // slack/telegram env and FORGOT the bump, so the per-turn initiator
          // marker write stayed dormant fleet-wide (the read path shipped,
          // the write path never reached existing agents). Folding the
          // build-time agt CLI version into the hash makes that class of bug
          // structurally impossible: any release that changes channel
          // rendering (every adapter change auto-publishes a new CLI, ENG-5795)
          // gets a new hash, so the entry is re-rendered ONCE per self-update
          // and the code-only change propagates without a writeVersion bump.
          //
          // This does NOT cause restart storms (the AC#3 concern):
          //   - The hash only moves on a CLI upgrade, never per poll, so the
          //     `knownChannelConfigHashes` skip below still suppresses the
          //     steady-state rewrite.
          //   - A re-render that produces identical bytes is a no-op for the
          //     `.mcp.json` drift watcher (it hashes content, not mtime).
          //   - A re-render that adds/changes an env value is a VALUE-ONLY
          //     drift: the drift watcher adopts the new baseline WITHOUT
          //     bouncing the live session (decideMcpRestartOnDrift,
          //     ENG-5285/ENG-5537). Only a server-membership change restarts.
          // In dev (tsx) agtCliVersion is the stable literal 'dev', so this
          // adds no churn there.
          //
          // writeVersion is kept as an explicit-intent override (e.g. to force
          // a one-shot rewrite without cutting a release); the CLI-version fold
          // now subsumes the need to bump it for ordinary code-only changes.
          // The hash itself lives in the pure, unit-tested
          // computeChannelConfigHash (channel-config-hash.ts, ENG-6600).
          const configHash = computeChannelConfigHash({
            cliVersion: agtCliVersion,
            config: entry.config,
            team: teamSettingsForHash,
            peers: peersForHash,
            sessionMode: sessionModeForHash,
            senderPolicy: senderPolicyForHash,
          });
          const cacheKey = `${agent.agent_id}:${channelId}`;
          let onDiskPresent = true;
          try {
            onDiskPresent =
              frameworkAdapter.hasChannelCredentials?.(agent.code_name, channelId) ?? true;
          } catch (err) {
            // A faulty adapter hook must not abort the provisioning loop.
            // Treat the failure as "on-disk entry missing" so we force a
            // rewrite rather than silently trusting a stale cache.
            log(`hasChannelCredentials failed for '${agent.code_name}/${channelId}': ${(err as Error).message} — forcing credential rewrite`);
            onDiskPresent = false;
          }
          if (agentState.knownChannelConfigHashes.get(cacheKey) === configHash && onDiskPresent) {
            continue;
          }
          // ENG-4468: capture prevHash BEFORE the on-disk-missing branch
          // can delete it, so the `reason` label below is accurate. Reading
          // after the delete would mislabel on-disk-missing as first-write.
          const prevHash = agentState.knownChannelConfigHashes.get(cacheKey);
          const reason = !prevHash
            ? 'first-write'
            : !onDiskPresent
              ? 'on-disk-missing'
              : 'content-changed';
          if (!onDiskPresent && prevHash) {
            log(`Cached hash for '${agent.code_name}/${channelId}' but on-disk entry missing — re-writing credentials`);
            agentState.knownChannelConfigHashes.delete(cacheKey);
          }
          try {
            const sessionMode = (refreshData.agent as Record<string, unknown>).session_mode as string | undefined;
            // ENG-4573: pass agent_id through so the slack-channel MCP can
            // wire AGT_AGENT_ID for slack.ask_user (block_kit_ask_user_enabled).
            // ENG-4912 / ENG-4940: thread the channel-agnostic peer kill
            // switch enum through. peerDisabled is the canonical signal
            // (PEER_DISABLED env on the child); telegramPeerDisabled is
            // a derived boolean kept for backwards compat — the MCP
            // child accepts both during the rollout.
            const peerDisabled =
              channelId === 'telegram' || channelId === 'slack'
                ? peerDisabledMode
                : undefined;
            const telegramPeerDisabled =
              channelId === 'telegram' ? peerDisabledMode === 'all' : undefined;
            // ENG-4923: assemble the per-agent peer roster from CHARTER
            // multi_agent.telegram_peers. Each entry is the CHARTER's
            // declared { code_name, bot_id }; agent_id is left empty
            // here because the manager doesn't yet have a cheap way to
            // resolve it from the team roster (would need either an
            // extension to /host/refresh or a separate /host/team-agents
            // call). The classifier doesn't gate on agent_id — only on
            // bot_id — so leaving it blank is safe today; ENG-4904's
            // runtime can fetch the agent_id when it needs to surface
            // the peer's identity in the prompt preamble.
            const telegramPeers =
              channelId === 'telegram'
                ? extractCharterTelegramPeers(refreshData.charter?.raw_content ?? '', gateContext)
                : undefined;
            // ENG-4970 / ENG-4974: Slack mirror. Parallel resolution
            // from CHARTER multi_agent.slack_peers — same gate-path
            // model, keyed on bot_user_id.
            const slackPeers =
              channelId === 'slack'
                ? extractCharterSlackPeers(refreshData.charter?.raw_content ?? '', gateContext)
                : undefined;
            // ENG-5841: forward sender_policy only on channels that actually
            // enforce it (slack-channel + teams-channel MCPs). Other channels
            // ignore the option, but threading it everywhere would mean
            // updating every adapter call site if the shape ever changes.
            const senderPolicyForCall =
              (channelId === 'slack' || channelId === 'msteams')
                ? ((refreshData as Record<string, unknown>).sender_policy as
                    | {
                        // ENG-5843: 'all' is now a valid mode value when
                        // paired with internal_only:true. Adapter handles
                        // both shapes.
                        mode: 'all' | 'agents_only' | 'team_agents_only' | 'manager_only';
                        team_id?: string;
                        // ENG-5842: principal IDs for manager_only mode.
                        principal?: {
                          slack_user_id?: string;
                          telegram_chat_id?: string;
                          teams_aad_object_id?: string;
                        };
                        // ENG-5843: org-boundary gate flag.
                        internal_only?: boolean;
                        source: 'agent' | 'org';
                      }
                    | null
                    | undefined) ?? undefined
                : undefined;
            // ENG-6155: forward the agent's avatar URL to the slack-channel
            // MCP only — it's the only consumer (sets it as the bot's Slack
            // profile photo on first connect). null/empty → option omitted,
            // so the MCP simply doesn't attempt a photo set.
            // ENG-6245: resolveAvatarEnvUrl drops a data-URI / oversized value
            // that would E2BIG the slack MCP spawn (posix_spawn arg limit) and
            // silently brick the channel. The adapter applies the same guard
            // structurally; here we also emit a one-line WARN so the skip is
            // visible in manager.log instead of only surfacing as an E2BIG
            // deep in mcp-logs-slack.
            let agentAvatarUrl: string | undefined;
            if (channelId === 'slack') {
              const rawAvatarUrl =
                ((refreshData.agent as Record<string, unknown>).avatar_url as
                  | string
                  | null
                  | undefined) ?? undefined;
              const avatarResolution = resolveAvatarEnvUrl(rawAvatarUrl);
              agentAvatarUrl = avatarResolution.url ?? undefined;
              if (
                avatarResolution.skipReason &&
                avatarResolution.skipReason !== 'empty'
              ) {
                log(
                  `WARN: skipped Slack bot avatar for '${agent.code_name}' ` +
                    `(reason=${avatarResolution.skipReason}` +
                    `${avatarResolution.bytes !== undefined ? `, bytes=${avatarResolution.bytes}` : ''}) ` +
                    `— not a hosted URL / exceeds ${MAX_AVATAR_ENV_URL_BYTES}B cap; ` +
                    `Slack runs without the custom photo. Upload the avatar to ` +
                    `storage and store the hosted URL to restore it (ENG-6245).`,
                );
              }
            }
            frameworkAdapter.writeChannelCredentials(
              agent.code_name,
              channelId,
              entry.config as Record<string, unknown>,
              {
                sessionMode,
                agentId: agent.agent_id,
                telegramPeerDisabled,
                peerDisabled,
                telegramPeers,
                slackPeers,
                agentTimezone,
                senderPolicy: senderPolicyForCall,
                agentAvatarUrl,
              },
            );
            agentState.knownChannelConfigHashes.set(cacheKey, configHash);
            saveChannelHashCache();
            log(`Channel credentials written for '${agent.code_name}/${channelId}' (reason=${reason}, hash=${configHash.slice(0, 8)}${prevHash ? `, prev=${prevHash.slice(0, 8)}` : ''})`);
          } catch (err) {
            channelConfigConverged = false;
            log(`Failed to write channel credentials for '${agent.code_name}/${channelId}': ${(err as Error).message}`);
          }
        }
      }

    } else if (agent.status === 'paused') {
      // Paused agents: disable channels in their profile
      if (frameworkAdapter.setChannelEnabled) {
        for (const channelId of Object.keys(refreshData.channel_configs)) {
          frameworkAdapter.setChannelEnabled(channelId, false, agent.code_name);
        }
      }
    }
  }

  // CodeRabbit on PR #773 (round 2): only advance the per-agent channel
  // diff cache when the on-disk credential state actually converged. If
  // a remove/write failed, leave `previousChannelIds` in place so the
  // next refresh tick re-sees the diff and retries the credential sync.
  // Otherwise we'd swallow the change forever the moment one transient
  // I/O failure occurred — and worse, fire a hot-reload restart on top
  // of broken credential state.
  if (channelConfigConverged) {
    agentState.knownChannels.set(agent.agent_id, currentChannelIds);
  } else {
    log(`[channels] Credential sync did not converge for '${agent.code_name}' — leaving diff live for next tick retry`);
  }

  // ENG-4807: when the channel set changes for a live persistent claude-code
  // session, restart the session so its launch flags reflect the new
  // channels. The flags baked at spawn time
  // (`--dangerously-load-development-channels` and `--allowedTools`) don't
  // pick up new channels without a respawn — the MCP loads but its
  // `notifications/claude/channel` get dropped, and tool calls to the new
  // channel's MCP (e.g. `telegram.reply`) get auto-denied. Pre-fix this
  // required an operator to manually run `tmux kill-session`. See ENG-4807
  // for the full diagnosis from Vigil's Telegram setup. The MCP-toolkit
  // hot-reload at line ~2926 already follows the same shape; this mirrors
  // it for channels.
  //
  // The decision logic lives in a pure helper so it's unit-testable
  // without standing up the rest of processAgent.
  //
  // CodeRabbit on PR #773 (round 2): suppress restart when credential
  // sync didn't converge — restarting on broken state would just reload
  // the session with stale flags that match the pre-failure state, then
  // the next tick's successful retry sees no diff (because previousChannelIds
  // is still the pre-change set) and never restarts again. Wait for
  // sync to land first.
  const restartDecision = channelConfigConverged
    ? decideChannelRestart({
        previousChannelIds,
        currentChannelIds,
        sessionMode: (refreshData.agent as Record<string, unknown>).session_mode as string | undefined,
        framework: agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK,
        sessionHealthy: isSessionHealthy(agent.code_name),
      })
    : { restart: false, added: [], removed: [] };
  // ENG-5932: 0-restart-on-removal. When the ONLY channel-set delta is the
  // removal of quarantined channel(s) — the transition poll where the
  // quarantine first drops a dead channel from `currentChannelIds` — do NOT
  // bounce the session. The dead channel never had a live MCP child, so
  // restarting to "drop" it achieves nothing and needlessly SIGINTs the
  // healthy sibling channels (the exact Kylie harm we're fixing). A real
  // add alongside the removal still restarts (the new channel needs it).
  const quarantineOnlyRemoval =
    restartDecision.restart &&
    restartDecision.added.length === 0 &&
    restartDecision.removed.length > 0 &&
    restartDecision.removed.every((c) => quarantinedChannels.has(c));
  // The post-suppression truth: did we ACTUALLY schedule a channel-set restart?
  // Downstream (decideSenderPolicyRestart) keys off this to avoid double-
  // restarting — so it must see the suppressed value, not raw
  // restartDecision.restart. Otherwise a same-tick sender_policy change would
  // be suppressed "because the channel-set restart will cover it" while that
  // restart was itself suppressed — leaving the live Slack/Teams MCP on a stale
  // policy with knownSenderPolicyHashes already advanced (CodeRabbit PR #1686).
  const channelSetRestartScheduled = restartDecision.restart && !quarantineOnlyRemoval;
  if (quarantineOnlyRemoval) {
    log(
      `[channel-quarantine] suppressing channel-set-change restart for '${agent.code_name}' — only delta is quarantined-channel removal [${restartDecision.removed.join(', ')}] (0-restart-on-removal, ENG-5932)`,
    );
  }
  if (channelSetRestartScheduled) {
    const reasonParts: string[] = [];
    if (restartDecision.added.length > 0) reasonParts.push(`added=${restartDecision.added.join(',')}`);
    if (restartDecision.removed.length > 0) reasonParts.push(`removed=${restartDecision.removed.join(',')}`);
    const reason = reasonParts.join(' ');
    log(`[hot-reload] Channel set changed for '${agent.code_name}' (${reason}) — restarting session`);

    const restartNotice = restartDecision.added.length > 0
      ? `New channels have been wired up (${restartDecision.added.join(', ')}). Note: channels require a session restart to attach their MCP servers as channel listeners. Your manager will restart your session shortly.`
      : `Channels were removed (${restartDecision.removed.join(', ')}). Your manager will restart your session shortly so the launch flags drop those channels.`;
    const delivered = await injectMessage(agent.code_name, 'system', restartNotice + BACK_ONLINE_GREETING_GUIDANCE, { task_name: 'channel-update' }, log).catch(() => false);
    const delay = delivered ? 8_000 : 3_000;
    if (!delivered) {
      log(`[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`);
    }
    // ENG-6491: tag an ADD restart 'channel-set-change' (not the default
    // 'hot-reload-mcp') so the maintenance-window gate can exempt it from the
    // off-peak WINDOW while still deferring-until-idle — a newly-added channel
    // must come online as soon as the agent is quiet, not hours later. The window
    // exemption is for ADDS only (a pure removal has no "comes online" urgency, so
    // it stays 'hot-reload-mcp' / fully window-gated). Integration churn likewise
    // stays 'hot-reload-mcp'.
    const isChannelAddRestart = restartDecision.added.length > 0;
    const addedChannels = [...restartDecision.added];
    // ENG-6491 (CodeRabbit): write the DM-notice marker at the moment the restart
    // ACTUALLY fires (beforeStop), not now — scheduleSessionRestart can idle-defer
    // and re-arm for minutes, and a marker stamped at schedule time could age past
    // the boot-freshness window and be skipped for the restart that just happened.
    // On a channel ADD only; drops a per-channel marker so the Slack/Telegram MCPs
    // notice DM users active in the last few minutes (shape must stay byte-
    // compatible with packages/mcp/src/dm-restart-notice.ts). Best-effort.
    const writeDmNoticeMarkers = isChannelAddRestart
      ? (): void => {
          try {
            const agentAugmentedDir = join(homedir(), '.augmented', agent.code_name);
            mkdirSync(agentAugmentedDir, { recursive: true });
            const markerJson = JSON.stringify({
              version: 1,
              at: new Date().toISOString(),
              added: addedChannels,
            });
            for (const file of ['slack-channel-add-restart.json', 'telegram-channel-add-restart.json']) {
              atomicWriteFileSync(join(agentAugmentedDir, file), markerJson);
            }
          } catch (err) {
            log(`[hot-reload] channel-add DM-notice marker write failed for '${agent.code_name}' (non-fatal): ${(err as Error).message}`);
          }
        }
      : undefined;
    scheduleSessionRestart(
      agent.code_name,
      delay,
      'new channel set',
      isChannelAddRestart ? 'channel-set-change' : 'hot-reload-mcp',
      writeDmNoticeMarkers,
    );
  }

  // ENG-5858: orthogonal restart trigger for sender_policy changes.
  // Why this exists despite the channel-cred hash already invalidating
  // on the same change: writeChannelCredentials rewrites .mcp.json on
  // disk, but the running slack-channel / teams-channel MCP child has
  // already read SLACK_SENDER_POLICY (and friends) from process.env at
  // boot. The two existing restart paths refuse to help — channel-set
  // restart only fires on membership changes, and the .mcp.json drift
  // watcher (ENG-5537) explicitly suppresses value-only restarts to
  // stop Composio URL rotation flapping the breaker. Keying this trigger
  // on the sender_policy hash alone means composio URL churn can't
  // accidentally fire it.
  if (channelConfigConverged) {
    // CR on PR #1622: gate on whether this agent actually has a channel
    // that *consumes* the SLACK_SENDER_POLICY / MSTEAMS_SENDER_POLICY env
    // vars. Only slack-channel and teams-channel read them at boot — a
    // telegram-only or direct-chat-only agent has no MCP child that
    // depends on the policy env, so a restart on an org-level policy
    // change would be wasted work (and a confusing user-visible blip).
    // Mirrors the same channel-id filter on the writeChannelCredentials
    // call site (~Lines 4433-4454) where the env vars are forwarded.
    const hasSenderPolicyChannel =
      currentChannelIds.has('slack') || currentChannelIds.has('msteams');
    const senderPolicyForRestartHash =
      (refreshData as Record<string, unknown>).sender_policy ?? null;
    const senderPolicyHash = createHash('sha256')
      .update(canonicalJson({ senderPolicy: senderPolicyForRestartHash }))
      .digest('hex');
    const prevSenderPolicyHash = agentState.knownSenderPolicyHashes.get(agent.agent_id);
    const senderPolicyDecision = hasSenderPolicyChannel
      ? decideSenderPolicyRestart({
          previousHash: prevSenderPolicyHash,
          currentHash: senderPolicyHash,
          sessionMode: (refreshData.agent as Record<string, unknown>).session_mode as string | undefined,
          framework: agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK,
          sessionHealthy: isSessionHealthy(agent.code_name),
          channelSetRestartAlreadyScheduled: channelSetRestartScheduled,
        })
      : { restart: false, firstPoll: prevSenderPolicyHash === undefined, changed: false };
    if (senderPolicyDecision.restart) {
      log(
        `[hot-reload] sender_policy changed for '${agent.code_name}' ` +
          `(${prevSenderPolicyHash?.slice(0, 8) ?? 'first'} → ${senderPolicyHash.slice(0, 8)}) — restarting session`,
      );
      const notice =
        'Your sender_policy has been updated. Restarting session shortly so the channel MCP servers pick up the new gate.';
      const delivered = await injectMessage(
        agent.code_name,
        'system',
        notice + BACK_ONLINE_GREETING_GUIDANCE,
        { task_name: 'sender-policy-update' },
        log,
      ).catch(() => false);
      const delay = delivered ? 8_000 : 3_000;
      if (!delivered) {
        log(
          `[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`,
        );
      }
      scheduleSessionRestart(
        agent.code_name,
        delay,
        'sender_policy change',
        'sender-policy-change',
      );
    }
    // Always advance the baseline once it converged, even on first poll
    // or when we suppressed the restart (other framework, oneshot, etc.).
    // The hash itself only depends on the policy, so storing it costs
    // nothing and prevents a restart from queueing on the *next* tick if
    // the agent's sessionHealthy state flips true later.
    agentState.knownSenderPolicyHashes.set(agent.agent_id, senderPolicyHash);

    // ENG-6024: orthogonal restart trigger for Teams behaviour-field changes
    // (thread_auto_follow / channel_response_mode / adaptive_cards_*) saved
    // via the webapp's Teams Advanced panel. Same last-hop gap as the
    // sender-policy trigger above: the teams-channel MCP child read those
    // env vars once at boot, the channel-set restart only fires on
    // membership change, and the .mcp.json drift watcher suppresses
    // value-only restarts (ENG-5537). Hashing only the behaviour subset
    // means token/credential churn can't fire it — the subset changes only
    // on an explicit operator save.
    // ENG-6035: tracks whether the msteams trigger scheduled a restart this
    // tick so the slack trigger below doesn't schedule a duplicate.
    let msteamsBehaviourRestartScheduled = false;
    if (currentChannelIds.has('msteams')) {
      const msteamsEntry = refreshData.channel_configs?.['msteams'];
      const behaviourSubset = extractMsTeamsBehaviourSubset(
        msteamsEntry?.config as Record<string, unknown> | null | undefined,
      );
      const behaviourHash = createHash('sha256')
        .update(canonicalJson(behaviourSubset))
        .digest('hex');
      const prevBehaviourHash = agentState.knownMsTeamsBehaviourHashes.get(agent.agent_id);
      const behaviourDecision = decideMsTeamsBehaviourRestart({
        previousHash: prevBehaviourHash,
        currentHash: behaviourHash,
        sessionMode: (refreshData.agent as Record<string, unknown>).session_mode as string | undefined,
        framework: agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK,
        sessionHealthy: isSessionHealthy(agent.code_name),
        // A channel-set or sender-policy restart scheduled this tick will
        // respawn the MCP children with the freshly-written env for free.
        channelSetRestartAlreadyScheduled:
          channelSetRestartScheduled || senderPolicyDecision.restart,
      });
      if (behaviourDecision.restart) {
        log(
          `[hot-reload] msteams behaviour settings changed for '${agent.code_name}' ` +
            `(${prevBehaviourHash?.slice(0, 8) ?? 'first'} → ${behaviourHash.slice(0, 8)}) — restarting session`,
        );
        const behaviourNotice =
          'Your Microsoft Teams channel behaviour settings were updated. Restarting session shortly so the teams-channel MCP picks up the new configuration.';
        const behaviourDelivered = await injectMessage(
          agent.code_name,
          'system',
          behaviourNotice + BACK_ONLINE_GREETING_GUIDANCE,
          { task_name: 'channel-update' },
          log,
        ).catch(() => false);
        const behaviourDelay = behaviourDelivered ? 8_000 : 3_000;
        if (!behaviourDelivered) {
          log(
            `[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`,
          );
        }
        scheduleSessionRestart(
          agent.code_name,
          behaviourDelay,
          'msteams behaviour change',
          'channel-behaviour-change',
        );
        msteamsBehaviourRestartScheduled = true;
      }
      // Always advance the baseline once converged — same rationale as the
      // sender-policy baseline above.
      agentState.knownMsTeamsBehaviourHashes.set(agent.agent_id, behaviourHash);
    } else {
      // Channel removed (or quarantined): drop the baseline so a later
      // re-add starts from first-poll (the channel-set restart covers the
      // respawn) instead of comparing against a stale pre-removal hash.
      agentState.knownMsTeamsBehaviourHashes.delete(agent.agent_id);
    }

    // ENG-6035: same trigger for Slack behaviour-field changes
    // (allowed_users / thread_auto_follow / channel_response_mode) saved via
    // the webapp's Slack panel. Closes the Slack half of the ENG-5537
    // value-only restart gap the same way ENG-6024 closed the Teams half:
    // slack-channel reads SLACK_ALLOWED_USERS (and friends) from process.env
    // once at boot, so the adapter's .mcp.json rewrite needs this dedicated
    // restart to reach the running child.
    if (currentChannelIds.has('slack')) {
      const slackEntry = refreshData.channel_configs?.['slack'];
      const slackBehaviourSubset = extractSlackBehaviourSubset(
        slackEntry?.config as Record<string, unknown> | null | undefined,
      );
      const slackBehaviourHash = createHash('sha256')
        .update(canonicalJson(slackBehaviourSubset))
        .digest('hex');
      const prevSlackBehaviourHash = agentState.knownSlackBehaviourHashes.get(agent.agent_id);
      const slackBehaviourDecision = decideSlackBehaviourRestart({
        previousHash: prevSlackBehaviourHash,
        currentHash: slackBehaviourHash,
        sessionMode: (refreshData.agent as Record<string, unknown>).session_mode as string | undefined,
        framework: agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK,
        sessionHealthy: isSessionHealthy(agent.code_name),
        // A channel-set, sender-policy, or msteams-behaviour restart
        // scheduled this tick respawns the MCP children with the
        // freshly-written env for free.
        channelSetRestartAlreadyScheduled:
          channelSetRestartScheduled ||
          senderPolicyDecision.restart ||
          msteamsBehaviourRestartScheduled,
      });
      if (slackBehaviourDecision.restart) {
        log(
          `[hot-reload] slack behaviour settings changed for '${agent.code_name}' ` +
            `(${prevSlackBehaviourHash?.slice(0, 8) ?? 'first'} → ${slackBehaviourHash.slice(0, 8)}) — restarting session`,
        );
        const slackBehaviourNotice =
          'Your Slack channel behaviour settings were updated. Restarting session shortly so the slack-channel MCP picks up the new configuration.';
        const slackBehaviourDelivered = await injectMessage(
          agent.code_name,
          'system',
          slackBehaviourNotice + BACK_ONLINE_GREETING_GUIDANCE,
          { task_name: 'channel-update' },
          log,
        ).catch(() => false);
        const slackBehaviourDelay = slackBehaviourDelivered ? 8_000 : 3_000;
        if (!slackBehaviourDelivered) {
          log(
            `[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`,
          );
        }
        scheduleSessionRestart(
          agent.code_name,
          slackBehaviourDelay,
          'slack behaviour change',
          'channel-behaviour-change',
        );
      }
      // Always advance the baseline once converged — same rationale as the
      // sender-policy baseline above.
      agentState.knownSlackBehaviourHashes.set(agent.agent_id, slackBehaviourHash);
    } else {
      // Channel removed (or quarantined): drop the baseline so a later
      // re-add starts from first-poll (the channel-set restart covers the
      // respawn) instead of comparing against a stale pre-removal hash.
      agentState.knownSlackBehaviourHashes.delete(agent.agent_id);
    }
  }

  // 5b. Always provision direct-chat channel for persistent sessions.
  // This runs as a standalone MCP channel server and pushes webapp direct
  // chat messages into the live session.
  //
  // Must live in project-scope .mcp.json (not a separate file). Claude Code's
  // channel name matcher (yG5 in 2.1.114) only resolves server:<name> against
  // MCP servers in enterprise/user/project/local scopes — --mcp-config loads
  // as session scope and is invisible to the lookup, which is why the separate
  // .mcp-channels.json approach always failed with "no MCP server configured".
  //
  // Write to the provision-dir .mcp.json (source of truth) then copy to the
  // project dir, so subsequent artifact syncs don't wipe the entry.
  const agentSessionMode = (refreshData.agent as Record<string, unknown>).session_mode as string | undefined;
  if (agentSessionMode === 'persistent' && (agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK) === 'claude-code') {
    try {
      // ENG-4418: provision dir now lives at ~/.augmented/<codeName>/provision/
      // (formerly split between <codeName>/provision/ and <codeName>/claudecode/provision/).
      // Reuse the refresh-resolved agentDir so this always matches the
      // authoritative framework (not the cached one from /host/agents).
      const agentProvisionDir = agentDir;
      const projectDir = join(homedir(), '.augmented', agent.code_name, 'project');
      mkdirSync(agentProvisionDir, { recursive: true });
      mkdirSync(projectDir, { recursive: true });
      const provisionMcpPath = join(agentProvisionDir, '.mcp.json');
      const projectMcpPath = join(projectDir, '.mcp.json');

      let mcpConfig: { mcpServers: Record<string, unknown> } = { mcpServers: {} };
      try {
        mcpConfig = JSON.parse(readFileSync(provisionMcpPath, 'utf-8'));
        if (!mcpConfig.mcpServers) mcpConfig.mcpServers = {};
      } catch { /* new file */ }

      const localDirectChatChannel = join(homedir(), '.augmented', '_mcp', 'direct-chat-channel.js');
      // ENG-5363: team timezone propagates as `TZ` so the direct-chat
      // progress-heartbeat's "last update" footer renders in the agent's
      // local time. Mirror of the Slack/Telegram fix in
      // claudecode/index.ts. The current block only writes on first
      // install (`!mcpConfig.mcpServers['direct-chat']`), so existing
      // agents will pick up TZ the next time their `.mcp.json` is reset
      // — acceptable for a hotfix; a follow-up could re-evaluate the
      // env block on each refresh.
      const directChatTeamSettings = (refreshData.team as { settings?: Record<string, unknown> } | undefined)?.settings;
      const directChatTz = (() => {
        const tz = directChatTeamSettings?.['timezone'];
        return typeof tz === 'string' && tz.trim() !== '' ? tz.trim() : undefined;
      })();
      if (existsSync(localDirectChatChannel)) {
        // ENG-5922: route through the framework adapter's writeMcpServer
        // so writeMcpJsonGuarded validation + syncMcpToProject + the
        // subagent render functions (renderChannelMessageHandlerForAgent
        // + renderAugmentedWorkerForAgent) all fire. The pre-fix path
        // was a direct writeFileSync to both provision and project
        // .mcp.json files, which DID get the server entry in place but
        // left the subagent .md tools allowlist stale.
        //
        // ENG-5924: gate REMOVED — call writeMcpServer every poll, NOT
        // only on first install. The "already present, skip" gate
        // (`!mcpConfig.mcpServers['direct-chat']`) prevented the
        // render chokepoint from firing for agents provisioned before
        // ENG-5922 landed: their .mcp.json had direct-chat from the
        // pre-fix direct write, so the gate was always false and the
        // render never re-fired, leaving .md allowlists stuck at
        // whatever stale snapshot the last write produced. Empirically
        // surfaced by `agt audit mcp-render` on agt-aws-1 2026-06-02:
        // 22 stale .md files across 8 agents. writeMcpServer +
        // syncMcpToProject + render are all idempotent on identical
        // content — calling them every poll heals existing drift and
        // costs only a per-poll .mcp.json mtime bump. DO NOT re-add
        // the gate without a different chokepoint for the render call.
        const directChatEnv: Record<string, string> = {
          AGT_HOST: requireHost(),
          // ENG-5901 Track D: templated — the manager exports the real
          // AGT_API_KEY to every spawn env (getApiKey()); Claude Code
          // substitutes at MCP-launch. No literal in .mcp.json.
          AGT_API_KEY: '${AGT_API_KEY}',
          AGT_AGENT_ID: agent.agent_id,
          // ENG-7262: the direct-chat MCP downloads inbound attachments into the
          // agent's code-name dir (~/.augmented/<code_name>/direct-chat-inbound/)
          // so the agent's isolation-hook-sandboxed file tools can read them.
          // Pass the code name explicitly (matches slack/telegram); the MCP also
          // falls back to dirname(AGT_TURN_INITIATOR_FILE) if this is ever unset.
          AGT_AGENT_CODE_NAME: agent.code_name,
          // ENG-6587 (D16): per-turn initiator marker path. The direct-chat MCP
          // (writer) and the broker MCPs (readers) share no other state-dir env,
          // so we inject the SAME absolute path buildMcpJson gives the brokers:
          // ~/.augmented/<codeName>/.current-turn-initiator.json. Note getAgentDir
          // returns the agent root (NOT the /provision subdir `agentDir` points at),
          // so it byte-matches the broker readers' path.
          AGT_TURN_INITIATOR_FILE: join(
            frameworkAdapter.getAgentDir(agent.code_name),
            '.current-turn-initiator.json',
          ),
        };
        if (directChatTz) directChatEnv['TZ'] = directChatTz;
        if (!frameworkAdapter.writeMcpServer) {
          // This branch is gated on `framework === 'claude-code'`
          // upstream — every Claude Code adapter MUST implement
          // writeMcpServer. If we reach here without it, that's a
          // framework-adapter contract violation, not a soft skip.
          // Fail loud so direct-chat doesn't silently stay
          // unprovisioned (CodeRabbit PR #1675 catch).
          throw new Error(
            `[direct-chat] Framework '${frameworkAdapter.id}' must implement writeMcpServer for direct-chat provisioning (agent=${agent.code_name}, cachedFramework=${agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK})`,
          );
        }
        frameworkAdapter.writeMcpServer(agent.code_name, 'direct-chat', {
          command: 'node',
          args: [localDirectChatChannel],
          env: directChatEnv,
        });
        // Only log on FIRST install — subsequent polls re-fire the
        // chokepoint as a no-op for the .mcp.json content and noise
        // would drown manager.log otherwise.
        if (!mcpConfig.mcpServers['direct-chat']) {
          log(`Channel credentials written for '${agent.code_name}/direct-chat'`);
        }
      }

      // Remove stale .mcp-channels.json from the pre-fix layout.
      const staleChannelsPath = join(projectDir, '.mcp-channels.json');
      if (existsSync(staleChannelsPath)) {
        try { rmSync(staleChannelsPath, { force: true }); } catch { /* non-fatal */ }
      }
    } catch (err) {
      log(`Failed to provision direct-chat channel for '${agent.code_name}': ${(err as Error).message}`);
    }
  }

  // 6. Fetch and write auth profiles (secrets)
  let lastSecretsProvisionAt = state.agents.find((a) => a.agentId === agent.agent_id)?.lastSecretsProvisionAt ?? null;
  let secretsHash = agentState.knownSecretsHashes.get(agent.agent_id) ?? null;

  try {
    const secretsData = await api.post<{
      profiles: Array<{
        provider: string;
        profile_name: string;
        auth_type: string;
        api_key?: string;
        metadata: Record<string, unknown>;
      }>;
      secrets_hash: string | null;
    }>('/host/secrets', { agent_id: agent.agent_id });

    const newHash = secretsData.secrets_hash;
    const hashChanged = newHash !== secretsHash;

    if (hashChanged && secretsData.profiles.length > 0) {
      frameworkAdapter.writeAuthProfiles(agent.code_name, secretsData.profiles);
      lastSecretsProvisionAt = new Date().toISOString();
      log(`Secrets updated for '${agent.code_name}'`);
    }

    if (newHash) {
      agentState.knownSecretsHashes.set(agent.agent_id, newHash);
      secretsHash = newHash;
    } else {
      agentState.knownSecretsHashes.delete(agent.agent_id);
      secretsHash = null;
    }
  } catch (err) {
    // Non-fatal — secrets are supplementary to provisioning
    log(`Secrets fetch failed for '${agent.code_name}': ${(err as Error).message}`);
  }

  // 6b. Integration provisioning — fetch integrations, resolve satisfied capabilities,
  //     install skills + CLI tools
  try {
    const integrationsData = await api.post<{
      integrations: Array<{
        id: string;
        definition_id: string;
        credentials: Record<string, unknown>;
        config: Record<string, unknown>;
        capabilities: Array<{ id: string; name: string; description: string; access: string }>;
        auth_type: string;
        display_name: string;
        scope: string;
        // ENG-5641: connectivity-probe inputs (enriched by /host/agent-integrations).
        source_type?: string | null;
        last_connectivity_check_at?: string | null;
        // ENG-7220 (Slice 2): last session-tool-bind probe time, so the runner's
        // rolling due-selection paces the probe instead of re-probing every poll.
        last_session_tool_bind_at?: string | null;
        // ENG-6242: the toolkit's connectivity_test override (managed/Composio).
        connectivity_test?: ConnectivityTestOverride | null;
        // ENG-6993 / ADR-0033 (Slice 2): the catalog remote-MCP descriptor
        // (integration_definitions.remote_mcp), forwarded by the API in
        // snake_case. ENG-7253: the manager MUST map this onto the camelCase
        // `remoteMcp` the adapter reads, or a pure-catalog remote-MCP
        // integration (e.g. monday.com - no INTEGRATION_REGISTRY fallback)
        // silently never renders its server into .mcp.json.
        remote_mcp?: RemoteMcpSpec | null;
      }>;
    }>('/host/agent-integrations', { agent_id: agent.agent_id });

    // ENG-7253: map the API's snake_case `remote_mcp` onto the camelCase
    // `remoteMcp` field every downstream consumer reads (buildRemoteMcpEntry in
    // writeIntegrations / buildMcpJson). Without this the forwarded catalog
    // descriptor is dropped at the manager seam and pure-catalog remote-MCP
    // integrations never bind. Hash-neutral: computeIntegrationsHash covers only
    // definition_id + credentials + config, so this addition can't perturb
    // change-detection.
    const integrations = (integrationsData.integrations ?? []).map(withResolvedRemoteMcp);

    // 6b-i. Refresh OAuth tokens nearing expiry (< 10 min remaining).
    //
    // ENG-6962: this host-side loop is the belt-and-braces fallback to the
    // central `oauth-token-refresh` cron (the primary path). It must call a
    // route the host JWT can actually authenticate. The old target,
    // `/integrations/oauth/:id/refresh`, is `authMiddleware + requireTeam`
    // (a webapp/team route). The host exchanges a host JWT and sends no
    // `X-Team-Slug`, so `requireTeam` 400'd every cycle ("Missing or invalid
    // X-Team-Slug header"), spamming manager.log every poll and never once
    // refreshing a token. We instead reuse the host-scoped
    // `/host/agent-integrations/:id/credential` route (hostAgentAuthMiddleware
    // + verifyHostAgentAccess, org from the host JWT claim), which already
    // refreshes via the shared `refreshIntegrationToken` helper when the token
    // is near expiry and returns the freshest access_token + expires_at. That
    // route's internal refresh threshold is ~5 min, so a token between 5 and
    // 10 min out comes back still-valid (no rotation) and the file handoff
    // below simply rewrites the current token (harmless); the actual rotation
    // lands on a later poll once it drops under the route's threshold.
    for (const integration of integrations) {
      if (integration.auth_type !== 'oauth2') continue;
      const expiresAt = integration.credentials?.token_expires_at as string | undefined;
      const refreshToken = integration.credentials?.refresh_token as string | undefined;
      if (!expiresAt || !refreshToken) continue;

      const msRemaining = new Date(expiresAt).getTime() - Date.now();
      if (msRemaining > 10 * 60 * 1000) continue; // Still valid for > 10 min

      try {
        const integrationId = integration.id;
        if (!integrationId) continue;
        const refreshResult = await api.post<{ access_token?: string | null; expires_at?: string | null }>(
          `/host/agent-integrations/${integrationId}/credential`,
          { agent_id: agent.agent_id },
        );
        if (refreshResult.access_token) {
          // The credential route always returns the current token, rotated only
          // when it was near expiry. Detect whether this poll actually rotated
          // it so we don't replace the old 400 spam with "refreshed" spam every
          // poll while the token sits between the manager's 10-min window and
          // the route's ~5-min threshold (CodeRabbit, ENG-6962).
          const rotated =
            refreshResult.access_token !== integration.credentials.access_token ||
            (refreshResult.expires_at != null &&
              refreshResult.expires_at !== integration.credentials.token_expires_at);

          // Update in-memory credentials so writeIntegrations() uses the
          // freshest token. The credential route always returns the current
          // (refreshed-if-needed) token, so a successful response means the
          // file handoff below should carry it.
          if (refreshResult.expires_at) {
            integration.credentials.token_expires_at = refreshResult.expires_at;
          }
          integration.credentials.access_token = refreshResult.access_token;
          if (rotated) {
            log(`OAuth token refreshed for '${agent.code_name}/${integration.definition_id}'`);
          }

          // Write live token file as fallback for agents not yet using the
          // token_refresh MCP tool. Agents with the plugin can call token_refresh
          // on demand instead of relying on this file-based handoff.
          if (frameworkAdapter.writeTokenFile) {
            frameworkAdapter.writeTokenFile(agent.code_name, integrations as Parameters<typeof frameworkAdapter.writeTokenFile>[1]);
          }
        }
      } catch (err) {
        log(`OAuth token refresh failed for '${agent.code_name}/${integration.definition_id}': ${(err as Error).message}`);
      }
    }

    // 6b-i.5 ENG-5641: rolling host-side connectivity probes — FLAG-GATED, default
    // off. Probes each integration ~hourly (rolling due-selection) using the
    // agent's real creds/MCP and reports to /host/integration-connectivity, where
    // the central evaluator escalates (also flag-off). Migrated to the
    // 'connectivity-probe' registry flag (ENG-7220, ADR-0022); the
    // AGT_CONNECTIVITY_PROBE_ENABLED env var stays the highest-precedence operator
    // override (env override > flag value > compiled default). Dormant until armed.
    if (hostFlagStore().getBoolean('connectivity-probe')) {
      try {
        const probeProjectDir = join(homedir(), '.augmented', agent.code_name, 'project');
        await runAgentConnectivityProbes(agent, integrations, probeProjectDir);
      } catch (err) {
        log(`Connectivity probe failed for '${agent.code_name}': ${(err as Error).message}`);
      }
    }

    // 6b-i.6 ENG-7053 / ENG-7220: rolling host-side session-tool-bind probe -
    // FLAG-GATED ('session-tool-probe', default off). Checks whether each
    // integration's MCP tools are actually bound in the running agent session
    // (stdio child alive / remote MCP answers tools/list) and reports a
    // bound|missing|unreachable|unknown verdict to /host/session-tool-bind. This
    // is the signal the Add Integration modal polls to confirm a newly-added
    // integration came online after the agent's next restart. AGT_SESSION_TOOL_PROBE_ENABLED
    // is the operator override. Best-effort: never fails the poll.
    if (hostFlagStore().getBoolean('session-tool-probe')) {
      try {
        const probeProjectDir = join(homedir(), '.augmented', agent.code_name, 'project');
        await runAgentSessionToolBindProbes(agent, integrations, probeProjectDir);
      } catch (err) {
        log(`Session-tool-bind probe failed for '${agent.code_name}': ${(err as Error).message}`);
      }
    }

    // 6b-ii. Integration credentials — env vars + CLAUDE.md snippets. Gated on
    // an integration hash that covers `definition_id + credentials + config`,
    // so config-only DB changes (e.g. switching Xero's `xero_tenant_id`
    // without rotating the access token) propagate to .env.integrations and
    // trigger the env-var-aware MCP child reap below. ENG-4911.
    if (integrations.length > 0) {
      const intHash = computeIntegrationsHash(integrations);
      const prevIntHash = agentState.knownIntegrationHashes.get(agent.agent_id);

      // ENG-6123: record the integration MEMBERSHIP (definition_ids, ignoring
      // credential/config churn) on EVERY poll so an oscillating set is detected
      // and a stabilised one recovers on the next quiet poll. Keyed on
      // membership, not intHash, so a Composio mcp_url re-sign / credential
      // rotation (monotonic, never revisits) never trips it — only a genuine
      // add↔remove↔add flap does.
      const intMembership = membershipSignature(integrations.map((i) => i.definition_id));
      const intFlap = mcpFlapDampener.record(agent.code_name, FLAP_CHANNEL_INTEGRATIONS, intMembership);
      if (intFlap.onset) {
        log(
          `[mcp-flap-dampener] integration set for '${agent.code_name}' is FLAPPING ` +
          `(${intFlap.tier} tier: ${intFlap.transitions} transitions / ${intFlap.distinctStates} states in window) — ` +
          `suppressing integration-update notices until it settles (ENG-6123, ENG-6512)`,
        );
        recordConfigChurnEvent(agent.agent_id, agent.code_name, FLAP_CHANNEL_INTEGRATIONS, intMembership);
      }

      if (intHash !== prevIntHash) {
        // ENG-4832 (CodeRabbit on PR #797 round 3): capture the
        // pre-write .env.integrations BEFORE writeIntegrations runs,
        // so we can diff against the post-write content and pass the
        // PRECISELY-rotated subset to findMcpServersUsingVars.
        // Without the diff, changedVars would be "every var currently
        // present in the file" and we'd over-reap MCP children whose
        // env didn't actually change. read returns undefined when the
        // file doesn't exist yet (first-ever integrations write for
        // this agent) — diffEnvIntegrations treats that as "every
        // post-write var is new", which is the correct first-write
        // behaviour: there's no prior state to preserve, so any new
        // vars warrant a reap.
        const projectDir = join(homedir(), '.augmented', agent.code_name, 'project');
        const envIntPath = join(projectDir, '.env.integrations');
        let preWriteEnv: string | undefined;
        try {
          preWriteEnv = readFileSync(envIntPath, 'utf-8');
        } catch {
          // No prior file — treat as empty so the diff shows all
          // post-write vars as added. fine.
          preWriteEnv = undefined;
        }

        if (frameworkAdapter.writeIntegrations) {
          // ENG-4920: thread agent.agent_id through so the claudecode
          // adapter can wire AGT_AGENT_ID + AGT_INTEGRATION_ID into the
          // Xero MCP server's spawn env.
          frameworkAdapter.writeIntegrations(agent.code_name, integrations as Parameters<typeof frameworkAdapter.writeIntegrations>[1], agent.agent_id);
        }
        log(`Integrations provisioned for '${agent.code_name}' (${integrations.length} integration(s))`);

        const fw = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;

        // Rotated env vars only reach MCP children that get RE-spawned.
        // The pre-existing children hold their spawn-time env, so the
        // access_token they're calling Xero/Gmail with is whatever was
        // in the env block when claude first launched them. Once that
        // token's 30-min TTL ticks past, every upstream call 401s and
        // the agent reports "refresh failed" (Stirling, 3-day blackout).
        // Identify which MCP server keys reference any of the
        // just-rotated env vars and SIGTERM their children so Claude
        // Code's MCP transport detects the dead child and respawns it
        // on next request — fresh env, fresh token, conversation
        // context preserved.
        //
        // CodeRabbit on PR #797 round 4: defer the
        // agentState.knownIntegrationHashes.set() update until the reap pipeline
        // completes. If the read/parse/reap path throws, we leave the
        // hash unchanged so the NEXT manager tick retries the rotation
        // end-to-end. Without this defer, a single transient failure
        // (file briefly missing, ps temporarily unavailable, JSON
        // parse error mid-write) would mark the rotation handled and
        // leave the stale MCP child to keep returning 401s
        // indefinitely — exactly the silent failure mode this whole
        // PR exists to eliminate.
        let rotationHandled = true;

        if (fw === 'claude-code' && isSessionHealthy(agent.code_name)) {
          try {
            const projectMcpPath = join(projectDir, '.mcp.json');
            const postWriteEnv = readFileSync(envIntPath, 'utf-8');
            const mcpContent = readFileSync(projectMcpPath, 'utf-8');
            // Diff pre-write vs post-write to get ONLY the rotated
            // var names. Without this we'd reap every MCP whose env
            // touches any var in the file — interrupting unrelated
            // tool traffic on every refresh tick.
            const changedVars = diffEnvIntegrations(preWriteEnv, postWriteEnv);
            const mcpJsonForReap = JSON.parse(mcpContent) as Parameters<typeof findMcpServersUsingVars>[0];
            const affectedServerKeys = findMcpServersUsingVars(mcpJsonForReap, changedVars);

            if (affectedServerKeys.length > 0) {
              reapStaleMcpChildren({
                log,
                codeName: agent.code_name,
                serverKeys: affectedServerKeys,
                mcpJson: mcpJsonForReap,
                // ENG-6670: enumerate + signal in the container PID namespace
                // for Docker-isolated agents (host ps can't see in-container
                // children; a host kill on a container pid would be wrong).
                isolated: isolationMode(agent.code_name) === 'docker',
              });
            }

            const names = integrations.map((i) => i.display_name || i.definition_id).join(', ');
            // Accurate now that the reaper above forces the affected
            // MCP children to respawn with fresh env. There's a brief
            // window where the next call to one of those tools lands
            // during the respawn; the MCP client's retry policy
            // handles that.
            const reapNote = affectedServerKeys.length > 0
              ? ` The MCP servers that depend on rotating credentials (${affectedServerKeys.join(', ')}) have been signalled to reconnect.`
              : '';
            // ENG-6123: suppress the opus-facing notice while the integration
            // set is flapping. The agent would otherwise write a full reply to a
            // notice that's already stale by the next ~60s poll — pure burn. The
            // env/MCP reap above still ran (cheap, no opus involvement); when the
            // set settles, the next changed poll injects one accurate notice.
            if (intFlap.flapping) {
              log(`[mcp-flap-dampener] suppressed integration-update notice for '${agent.code_name}' (set flapping, ENG-6123): ${names}`);
            } else {
              injectMessage(agent.code_name, 'system', `Your integrations have been refreshed. You have access to: ${names}.${reapNote}`, {
                task_name: 'integration-update',
              }, log).catch(() => {});
              log(`[hot-reload] Notified '${agent.code_name}' about integration update: ${names} (reaped ${affectedServerKeys.length} stale MCP server(s))`);
            }
          } catch (err) {
            // Don't mark handled — the next tick will reattempt the
            // full read/parse/reap pipeline. Idempotent
            // writeIntegrations means the second call is a no-op
            // write; the only practical cost is one extra
            // "Integrations provisioned" log line per retry, which is
            // the right operator signal that something's wrong.
            rotationHandled = false;
            log(`[hot-reload] Failed to compute / reap affected MCP servers for '${agent.code_name}': ${(err as Error).message} — will retry next tick`);
          }
        }

        if (rotationHandled) {
          agentState.knownIntegrationHashes.set(agent.agent_id, intHash);
        }
      }
    }

    // 6b-iii. Managed-toolkit MCP server convergence. Runs every poll (not
    // gated on integration credential changes) so the `.mcp.json` desired
    // state self-heals when an earlier write was skipped or lost — plugins
    // added to an agent now always propagate, not only on the exact poll
    // where credentials happened to change. ENG-4481.
    //
    // Idempotency: we hash the desired-state set (server_id → url + header
    // keys) and only re-write .mcp.json (and trigger the restart-session
    // notification) when that hash changes. `writeMcpServer` itself is
    // idempotent via canonical-json compare, so a redundant call with the
    // same config is a cheap no-op.
    const fwForMcp = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;
    if (frameworkAdapter.writeMcpServer) {
      try {
        const toolkitData = await api.post<{
          toolkits: Array<{ agent_id: string; toolkit_id: string; toolkit_name: string; proxy_url: string }>;
        }>('/host/managed-toolkits', { agent_ids: [agent.agent_id] });

        const agentToolkits = (toolkitData.toolkits ?? []).filter((tk) => tk.agent_id === agent.agent_id);
        const expectedServerIds = new Set<string>();
        const desiredEntries: Array<{ serverId: string; url: string; headers?: Record<string, string>; name: string }> = [];
        for (const tk of agentToolkits) {
          const serverId = tk.toolkit_id.replace(/[^a-z0-9]/gi, '_').toLowerCase();
          expectedServerIds.add(serverId);
          // ENG-5292: record reverse mapping so the reaper's give-up
          // callback (later in this file) can resolve a serverId back
          // to the toolkit_id when POSTing to /host/integration-health.
          managedToolkitIdByAgentAndServerId.set(
            managedToolkitMapKey(agent.agent_id, serverId),
            tk.toolkit_id,
          );
          const mcpUrl = (tk as Record<string, unknown>).mcp_url as string | undefined;
          const mcpHeaders = (tk as Record<string, unknown>).mcp_headers as Record<string, string> | undefined;
          const proxyUrl = tk.proxy_url.startsWith('/') ? `${requireHost()}${tk.proxy_url}` : tk.proxy_url;
          const url = mcpUrl ?? proxyUrl;
          desiredEntries.push({ serverId, url, headers: mcpHeaders, name: tk.toolkit_name });
        }

        // Desired-state hash — stable ordering (sort by serverId), url + full
        // headers (keys AND values). Header values must participate so that a
        // provider rotating a credential-bearing header triggers a rewrite;
        // hashing only keys would silently leave `.mcp.json` on stale auth
        // until an unrelated toolkit change bumped the hash.
        const hashBasis = desiredEntries
          .slice()
          .sort((a, b) => a.serverId.localeCompare(b.serverId))
          .map((e) => {
            const headersHash = createHash('sha256')
              .update(canonicalJson(e.headers ?? {}))
              .digest('hex')
              .slice(0, 16);
            return `${e.serverId}|${e.url}|${headersHash}`;
          })
          .join('\n');
        const mcpHash = createHash('sha256').update(hashBasis).digest('hex').slice(0, 16);
        const prevMcpHash = agentState.knownManagedMcpHashes.get(agent.agent_id);

        // ENG-5537: structure-only hash — serverId membership + header KEY
        // names, deliberately excluding the url and header VALUES. The write
        // below still keys off `mcpHash` (so .mcp.json converges to the live
        // signed url), but the session RESTART is gated on this hash so a
        // value-only rotation (composio re-signing `mcp_url` every poll)
        // refreshes the file without churning the running session. Only a
        // real structural change — toolkit added/removed, or a header key
        // appearing/disappearing — restarts the session.
        const structureHash = managedMcpStructureHash(desiredEntries);
        const prevStructureHash = agentState.knownManagedMcpStructure.get(agent.agent_id);

        // ENG-6123: record the managed-MCP MEMBERSHIP (serverIds) on EVERY poll
        // so an oscillating toolkit set is caught and a stabilised one recovers
        // on the next quiet poll. Keyed on membership (not mcpHash) so Composio
        // mcp_url re-signs — which move mcpHash but not the server set — never
        // trip it; only a real add↔remove↔add flap does.
        const mcpMembership = membershipSignature(expectedServerIds);
        const mcpFlap = mcpFlapDampener.record(agent.code_name, FLAP_CHANNEL_MANAGED_MCP, mcpMembership);
        if (mcpFlap.onset) {
          log(
            `[mcp-flap-dampener] managed-MCP set for '${agent.code_name}' is FLAPPING ` +
            `(${mcpFlap.tier} tier: ${mcpFlap.transitions} transitions / ${mcpFlap.distinctStates} states in window) — ` +
            `suppressing mcp-update notice + restart until it settles (ENG-6123, ENG-6512)`,
          );
          recordConfigChurnEvent(agent.agent_id, agent.code_name, FLAP_CHANNEL_MANAGED_MCP, mcpMembership);
        }

        if (mcpHash !== prevMcpHash) {
          for (const e of desiredEntries) {
            frameworkAdapter.writeMcpServer(agent.code_name, e.serverId, { url: e.url, headers: e.headers });
            // Hash the URL — `e.url` can be a provider-issued direct MCP URL
            // that carries signed query tokens. manager.log is persisted to
            // disk, so don't let raw creds land there.
            const urlHash = createHash('sha256').update(e.url).digest('hex').slice(0, 12);
            log(`[managed-toolkit] ${agent.code_name}: wrote '${e.name}' (serverId=${e.serverId}, url_hash=${urlHash})`);
          }

          // Prune stale managed MCP servers — skip entirely for adapters that
          // don't expose a local MCP file (e.g. cloud-hosted frameworks). The
          // framework-specific path comes from `getMcpPath`; reconstructing
          // `join(getAgentDir, 'provision', '.mcp.json')` here would hardcode
          // the claude-code layout.
          if (frameworkAdapter.removeMcpServer && frameworkAdapter.getMcpPath) {
            const mcpPath = frameworkAdapter.getMcpPath(agent.code_name);
            if (mcpPath) {
              try {
                const { readFileSync } = await import('node:fs');
                const mcpConfig = JSON.parse(readFileSync(mcpPath, 'utf-8')) as { mcpServers?: Record<string, unknown> };
                if (mcpConfig.mcpServers) {
                  for (const key of Object.keys(mcpConfig.mcpServers)) {
                    if (isManagedMcpServerKey(key) && !expectedServerIds.has(key)) {
                      frameworkAdapter.removeMcpServer(agent.code_name, key);
                      log(`[managed-toolkit] Removed stale MCP server '${key}' for '${agent.code_name}'`);
                    }
                  }
                }
              } catch {
                // MCP file doesn't exist yet — nothing to clean up
              }
            }
          }

          agentState.knownManagedMcpHashes.set(agent.agent_id, mcpHash);
          agentState.knownManagedMcpStructure.set(agent.agent_id, structureHash);

          // Only notify about MCP server changes after the first poll — on
          // initial convergence the session is either not yet started or will
          // be started with the fresh .mcp.json, so a restart notification
          // would be a noisy false alarm. Full-removal case is still a valid
          // change (we need to restart so the session drops the tools it had
          // loaded), so don't gate on `agentToolkits.length > 0`.
          //
          // ENG-5537: additionally gate on a *structural* change
          // (`structureHash`), not just any `mcpHash` change. A value-only
          // rotation (composio re-signing `mcp_url`) writes the fresh file
          // above but leaves `structureHash` identical — the running session's
          // established MCP connections are unaffected, so we skip the restart
          // and the inject-notice. `prevStructureHash === undefined` is the
          // first-convergence case (no running session to disturb).
          if (
            prevStructureHash !== undefined &&
            structureHash !== prevStructureHash &&
            fwForMcp === 'claude-code' &&
            isSessionHealthy(agent.code_name) &&
            // ENG-6123: hold the restart + inject-notice while the managed-MCP
            // membership is oscillating. The .mcp.json above was still written
            // (the file converges to the live set); we only withhold the
            // opus-facing notice + the session reload — the two per-flip burns.
            // When the set settles, the next structural change restarts once.
            !mcpFlap.flapping
          ) {
            const mcpNames = agentToolkits.map((tk) => tk.toolkit_name).join(', ') || 'none (all removed)';
            log(`[hot-reload] MCP servers changed for '${agent.code_name}': ${mcpNames} — restarting session`);

            const restartNotice = agentToolkits.length > 0
              ? `New MCP tool servers have been configured: ${mcpNames}. Note: MCP servers require a session restart to connect. Your manager will restart your session shortly.`
              : 'Managed MCP tool servers were removed. Your manager will restart your session shortly so the session drops those tools.';
            const delivered = await injectMessage(agent.code_name, 'system',
              restartNotice + BACK_ONLINE_GREETING_GUIDANCE,
              { task_name: 'mcp-update' }, log).catch(() => false);

            const delay = delivered ? 8_000 : 3_000;
            if (!delivered) {
              log(`[hot-reload] Inject notification unconfirmed for '${agent.code_name}' — proceeding with shorter delay`);
            }
            // ENG-4807 / CodeRabbit on PR #773: route through the
            // coalescing scheduler so a same-tick channel-set change
            // doesn't double-restart the agent.
            scheduleSessionRestart(agent.code_name, delay, 'new MCP servers');
          }
        }
      } catch (err) {
        log(`[managed-toolkit] Failed to provision for '${agent.code_name}': ${(err as Error).message}`);
      }
    }

    // ENG-4337: QMD CLI install + collection setup are now handled by the QMD
    // plugin (slug 'qmd') via its on_install hook. See refreshData.plugin_install_hooks
    // below — the manager runs the hook script `qmd collection add` after deploying
    // plugin skills. The QMD plugin is auto-installed for any agent that has the
    // QMD integration active (see migration 20260408000002_qmd_native_plugin.sql).
  } catch (err) {
    // Non-fatal — integration provisioning is supplementary
    log(`Integration provisioning failed for '${agent.code_name}': ${(err as Error).message}`);
  }

  // 7b. Auto-provision org/team default schedules
  let tasks = refreshData.scheduled_tasks ?? [];
  const existingTemplateIds = new Set(tasks.map((t) => t.template_id));

  // ENG-5662: the kanban-work retirement (hybrid is the SOLE path) applies only
  // to PERSISTENT claude-code agents — that's where the in-session `/loop` and
  // the `claude -p kanban-work` cron lived, and where the manager-gated hybrid
  // edge-trigger (step 7e, gated on persistent claude-code) replaces them.
  // Scoping to persistent is load-bearing: a claude-code *oneshot* agent has no
  // hybrid path, so it must keep firing kanban-work via syncAndCheckClaudeScheduler.
  const kanbanWorkRetired =
    (agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK) === 'claude-code' &&
    agentSessionMode === 'persistent';

  try {
    const defaultsData = await api.post<{
      schedules: Array<{
        template_id: string;
        name: string;
        schedule_expr: string;
        prompt: string;
        session_target?: string;
        enabled?: boolean;
      }>;
      team_id: string;
      timezone: string;
    }>('/host/resolve-default-schedules', { agent_id: agent.agent_id });

    // ENG-5662: for claude-code agents, never (re)provision a kanban-work
    // default schedule — the hybrid edge-trigger drives it, not a scheduled
    // task / claude -p cron.
    const allSchedules = (defaultsData.schedules ?? []).filter(
      (s) => !(kanbanWorkRetired && s.template_id === 'kanban-work'),
    );
    const missing = allSchedules.filter(
      (s) => !existingTemplateIds.has(s.template_id),
    );

    // Always call ensure-default-schedules to insert missing tasks and
    // fix delivery_mode on existing tasks that don't match the template default.
    if (allSchedules.length > 0) {
      await api.post('/host/ensure-default-schedules', {
        agent_id: agent.agent_id,
        team_id: defaultsData.team_id,
        timezone: defaultsData.timezone,
        schedules: allSchedules,
      });
      if (missing.length > 0) {
        log(`Auto-provisioned ${missing.length} default schedule(s) for '${agent.code_name}': ${missing.map((s) => s.template_id).join(', ')}`);
      }

      // Re-fetch tasks since schedules may have changed
      const freshData = await api.post<{ scheduled_tasks?: typeof tasks }>('/host/refresh', { agent_id: agent.agent_id });
      tasks = freshData.scheduled_tasks ?? tasks;
    }
  } catch (err) {
    log(`Default schedule provisioning failed for '${agent.code_name}': ${(err as Error).message}`);
  }

  // ENG-5662: for claude-code agents, kanban work is now driven solely by the
  // manager-gated hybrid edge-trigger injection (step 7e). Drop any residual
  // `kanban-work` rows (legacy data / stale team defaults) so neither the
  // persistent-session scheduler nor syncAndCheckClaudeScheduler ever fires it
  // as a claude -p oneshot.
  if (kanbanWorkRetired) {
    tasks = tasks.filter((t) => t.template_id !== 'kanban-work');
  }

  // 7c. Auto-provision the kanban skill for all active agents
  if (agent.status === 'active') {
    // Kanban skill files
    if (frameworkAdapter.installSkillFiles) {
      try {
        const skillContent = getBuiltInSkillContent('kanban');
        if (skillContent) {
          frameworkAdapter.installSkillFiles(agent.code_name, 'kanban', skillContent);
        }
      } catch (err) {
        log(`Kanban skill install failed for '${agent.code_name}': ${(err as Error).message}`);
      }
    }

    // Integration skills — deploy scope-filtered skills and clean up orphans
    if (frameworkAdapter.installSkillFiles) {
      const currentIntegrationSkillIds = new Set<string>();
      const installedIntegrationSkills: string[] = [];
      const { createHash } = await import('node:crypto');

      // ENG-4341: Index resolved integration contexts by slug for fast
      // lookup when rendering each skill. This map is small (one entry
      // per installed integration) and built once per refresh.
      // ENG-4629: prefer the new `integration_*` wire keys; fall back to
      // the legacy `plugin_*` shape so the CLI keeps working against
      // older API deploys mid-rollout. Each row also carries both
      // `integration_slug` and `plugin_slug` aliases.
      const refreshAny = refreshData as unknown as {
        integration_contexts?: Array<{ integration_slug?: string; plugin_slug?: string; values?: Record<string, unknown>; overrides?: string }>;
        plugin_contexts?: Array<{ plugin_slug?: string; integration_slug?: string; values?: Record<string, unknown>; overrides?: string }>;
        integration_skills?: unknown[];
        plugin_skills?: unknown[];
        integration_install_hooks?: Array<{ integration_slug?: string; plugin_slug?: string; script: string }>;
        plugin_install_hooks?: Array<{ plugin_slug?: string; integration_slug?: string; script: string }>;
        integration_toolkits?: Array<{ integration_slug?: string; plugin_slug?: string; toolkits: string[] }>;
        plugin_toolkits?: Array<{ plugin_slug?: string; integration_slug?: string; toolkits: string[] }>;
        // ADR-0023 / ENG-6349: published global skills delivered to every agent.
        global_skills?: Array<{ code_name: string; display_name?: string; content: string }>;
        // ENG-6815: published standalone agent/team/org skills, delivered via the
        // visibility chain (most-specific wins per code_name, resolved server-side).
        // Same shape + same delivery mechanism as global_skills.
        shared_skills?: Array<{ code_name: string; display_name?: string; content: string; scope?: string }>;
      };
      const contexts = refreshAny.integration_contexts ?? refreshAny.plugin_contexts ?? [];
      const contextBySlug = new Map<string, { values: Record<string, unknown>; overrides: string }>();
      for (const ctx of contexts) {
        const slug = ctx.integration_slug ?? ctx.plugin_slug;
        if (!slug) continue;
        contextBySlug.set(slug, { values: ctx.values ?? {}, overrides: (ctx.overrides ?? '').trim() });
      }

      // ENG-4502: group scopes by plugin and write a single plugin folder per
      // plugin. Claude Code's skill discovery does not recurse, so each
      // plugin's scopes go side-by-side under `plugin-<slug>/scopes/*.md`
      // while `plugin-<slug>/SKILL.md` is the umbrella index Claude actually
      // sees. Previous per-scope top-level folders (`plugin-<slug>-<id>/`)
      // are garbage-collected below alongside the orphan sweep.
      // ENG-4629: prefer integration_skills, fall back to plugin_skills.
      const integrationGroups = groupSkillsByIntegration(
        (refreshAny.integration_skills ?? refreshAny.plugin_skills ?? []) as IntegrationSkillInput[],
      );
      for (const [integrationSlug, scopes] of integrationGroups) {
        try {
          // ENG-4554: write under `integration-<slug>/` to align with the
          // post-cutover vocabulary. The orphan-sweep below recognises both
          // prefixes for one release so existing `plugin-<slug>/` folders
          // get cleaned up the first time the new manager runs.
          const integrationSkillId = `integration-${integrationSlug}`.replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
          currentIntegrationSkillIds.add(integrationSkillId);

          // ENG-4341: substitute {{context.<field>}} placeholders and
          // append the freeform overrides as "## Team Overrides".
          const ctx = contextBySlug.get(integrationSlug);
          const renderedScopes: IntegrationSkillInput[] = scopes.map((s) => ({
            plugin_slug: s.plugin_slug,
            skill_id: s.skill_id,
            skill_name: s.skill_name,
            content: renderIntegrationSkillContent(
              s.content,
              ctx?.values ?? {},
              ctx?.overrides ?? '',
              (warning) => log(`[plugin-context] ${s.plugin_slug}/${s.skill_id}: ${warning}`),
            ),
          }));

          const bundle = buildIntegrationBundle(renderedScopes);

          // Hash the whole bundle so any scope addition / removal / content
          // tweak triggers a re-install. Unchanged bundles skip disk I/O.
          const contentHash = createHash('sha256').update(bundleFingerprint(bundle.files)).digest('hex').slice(0, 12);
          const hashKey = `plugin-skill:${agent.agent_id}:${integrationSkillId}`;
          if (agentState.knownSkillHashes.get(hashKey) === contentHash) continue;

          frameworkAdapter.installSkillFiles(agent.code_name, integrationSkillId, bundle.files);
          agentState.knownSkillHashes.set(hashKey, contentHash);
          for (const s of scopes) installedIntegrationSkills.push(s.skill_name);
          log(`Installed integration skill bundle '${integrationSkillId}' for '${agent.code_name}' (${scopes.length} scope(s))`);
        } catch (err) {
          log(`Integration skill install failed for '${agent.code_name}' / '${integrationSlug}': ${(err as Error).message}`);
        }
      }

      // Clean up orphaned plugin skills (removed plugins) and sweep the
      // pre-ENG-4502 flat layout (`plugin-<slug>-<skill_id>/`). After the
      // switch to one folder per plugin, those per-scope folders become
      // stale duplicates — leaving them confuses Claude's skill list.
      try {
        const { readdirSync, rmSync } = await import('node:fs');
        const { homedir } = await import('node:os');

        // All on-disk directories that installSkillFiles() may have
        // written into. installSkillFiles for Claude Code writes to the
        // agent dir's `skills/` AND the project dir's `.claude/skills/`.
        // The provision/.claude/skills path isn't an install target but is
        // swept defensively in case older manager versions stashed copies
        // there.
        const frameworkId = frameworkAdapter.id;
        const candidateSkillDirs: string[] = [
          // Claude Code — framework runtime tree
          join(homedir(), '.augmented', agent.code_name, 'skills'),
          // Claude Code — project tree
          join(homedir(), '.augmented', agent.code_name, 'project', '.claude', 'skills'),
          // Defensive: legacy provision-side path, not currently an
          // install target but cheap to sweep.
          join(agentDir, '.claude', 'skills'),
        ];

        const existingDirs = candidateSkillDirs.filter((d) => existsSync(d));

        // Union of platform-managed entries across every existing skill
        // dir — gives a complete view of what might need cleaning even if
        // the agent has since switched framework or only some paths got
        // written during the transition.
        //
        // ENG-4554: accept BOTH the legacy `plugin-` and the current
        // `integration-` prefix. Old `plugin-<slug>/` folders aren't in
        // currentIntegrationSkillIds (which now holds `integration-<slug>`),
        // so they fall through to the orphan branch below and get removed
        // — that's the migration. The two-prefix logic stays for one
        // release; once everyone's on a manager that writes
        // `integration-`, the `plugin-` half can drop.
        const discoveredEntries = new Set<string>();
        for (const dir of existingDirs) {
          try {
            for (const entry of readdirSync(dir)) {
              if (entry.startsWith('plugin-') || entry.startsWith('integration-')) {
                discoveredEntries.add(entry);
              }
            }
          } catch { /* non-fatal per dir */ }
        }

        const removeSkillFolder = (entry: string, reason: string): void => {
          for (const dir of existingDirs) {
            const p = join(dir, entry);
            if (existsSync(p)) {
              rmSync(p, { recursive: true, force: true });
            }
          }
          log(`Removed ${reason} '${entry}' for '${agent.code_name}' (framework=${frameworkId})`);
        };

        for (const entry of discoveredEntries) {
          // An entry that isn't in currentIntegrationSkillIds is one of:
          //   - a removed integration (uninstalled)
          //   - a stale pre-ENG-4502 per-scope folder
          //     (`plugin-<slug>-<skill_id>`)
          //   - an ENG-4554 legacy `plugin-<slug>/` superseded by the new
          //     `integration-<slug>/` written this poll
          // All three are right to delete.
          if (!currentIntegrationSkillIds.has(entry)) {
            removeSkillFolder(entry, 'orphaned skill folder');
          }
        }
      } catch (err) {
        log(`Integration skill cleanup failed for '${agent.code_name}': ${(err as Error).message}`);
      }

      // ADR-0023 / ENG-6349: global skills — delivered to EVERY agent
      // install-free (no agent_skill_installs row). ENG-6815 folds in
      // `shared_skills` (published standalone agent/team/org skills resolved via
      // the visibility chain): same shape, same delivery, no code_name collision
      // with globals (precedence resolved server-side), so the union runs
      // through one plan. /host/refresh includes both; we write each as
      // `.claude/skills/<code_name>/SKILL.md` (bare code_name so the agent's
      // `/<code_name>` resolves), with the same content-hash skip as
      // integration skills. Runs BEFORE the skills-index refresh below so the
      // CLAUDE.md index picks them up. Best-effort — a failure here never
      // blocks the rest of the refresh.
      try {
        const plan = planGlobalSkillSync(
          [...(refreshAny.global_skills ?? []), ...(refreshAny.shared_skills ?? [])],
          agentState.knownGlobalSkillIds.get(agent.agent_id) ?? new Set<string>(),
          (content) => createHash('sha256').update(content).digest('hex').slice(0, 12),
          (skillId) => agentState.knownSkillHashes.get(`global-skill:${agent.agent_id}:${skillId}`),
        );

        for (const { skillId, content, hash } of plan.installs) {
          frameworkAdapter.installSkillFiles(agent.code_name, skillId, [{ relativePath: 'SKILL.md', content }]);
          agentState.knownSkillHashes.set(`global-skill:${agent.agent_id}:${skillId}`, hash);
          log(`Installed global skill '${skillId}' for '${agent.code_name}'`);
        }

        // Orphan sweep: remove the folder for any global skill we installed on
        // a previous refresh that's no longer published. Only ids we tracked as
        // global are touched — never the agent's own or integration skills.
        if (plan.removes.length) {
          const { rmSync } = await import('node:fs');
          const { homedir } = await import('node:os');
          const globalSkillDirs = [
            join(homedir(), '.augmented', agent.code_name, 'skills'),
            join(homedir(), '.augmented', agent.code_name, 'project', '.claude', 'skills'),
            join(agentDir, '.claude', 'skills'),
          ];
          for (const id of plan.removes) {
            for (const dir of globalSkillDirs) {
              const p = join(dir, id);
              if (existsSync(p)) rmSync(p, { recursive: true, force: true });
            }
            agentState.knownSkillHashes.delete(`global-skill:${agent.agent_id}:${id}`);
            log(`Removed unpublished global skill '${id}' for '${agent.code_name}'`);
          }
        }
        agentState.knownGlobalSkillIds.set(agent.agent_id, plan.currentIds);
      } catch (err) {
        log(`Global skill install failed for '${agent.code_name}': ${(err as Error).message}`);
      }

      // Refresh the "Available Skills" index in CLAUDE.md by scanning installed skills
      try {
        const agentFwForIndex = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;
        if (agentFwForIndex === 'claude-code') {
          await refreshSkillsIndexInClaudeMd(config.configDir, agent.code_name, log);
        }
      } catch (err) {
        log(`Skills index refresh failed for '${agent.code_name}': ${(err as Error).message}`);
      }

      // Run integration on_install hooks (idempotent — runs every refresh).
      // Hooks are deduplicated by content hash so we only re-run on script changes.
      // ENG-4629: prefer integration_install_hooks; fall back to plugin_install_hooks.
      const installHooks = refreshAny.integration_install_hooks ?? refreshAny.plugin_install_hooks ?? [];
      if (frameworkAdapter.executePluginHook && installHooks.length) {
        for (const hook of installHooks) {
          const slug = hook.integration_slug ?? hook.plugin_slug;
          if (!slug) continue;
          try {
            const scriptHash = createHash('sha256').update(hook.script).digest('hex').slice(0, 12);
            const hookKey = `${agent.agent_id}:${frameworkAdapter.id}:plugin-hook:${slug}:on_install`;
            if (agentState.knownSkillHashes.get(hookKey) === scriptHash) continue;

            const result = await frameworkAdapter.executePluginHook({
              codeName: agent.code_name,
              pluginSlug: slug,
              hookName: 'on_install',
              script: hook.script,
            });

            if (result.exitCode === 0) {
              agentState.knownSkillHashes.set(hookKey, scriptHash);
              log(`Integration hook on_install '${slug}' succeeded for '${agent.code_name}' (${result.durationMs}ms)`);
            } else if (result.timedOut) {
              log(`Integration hook on_install '${slug}' TIMED OUT for '${agent.code_name}' after ${result.durationMs}ms`);
            } else {
              // Don't log raw stderr — hooks inherit manager env which may include secrets.
              // Log a fingerprint instead so failures are identifiable without leaking content.
              const stderrHash = createHash('sha256').update(result.stderr).digest('hex').slice(0, 12);
              // ENG-4510: on exit 127, bash prints `bash: <cmd>: command not found`
              // before any user code runs. extractCommandNotFound parses + validates
              // that line, but we still log only a hash — the project guideline is
              // hash-only logging by default, and operators can correlate the hash
              // with `pnpm install <pkg>` failures or recompute it locally with a
              // suspected binary name when triaging.
              const missingCmd = result.exitCode === 127 ? extractCommandNotFound(result.stderr) : null;
              const missingCmdHash = missingCmd ? createHash('sha256').update(missingCmd).digest('hex').slice(0, 8) : null;
              log(
                `Integration hook on_install '${slug}' exited ${result.exitCode} for '${agent.code_name}' ` +
                (missingCmdHash ? `[missing_command_hash=${missingCmdHash}] ` : '') +
                `[stderr_hash=${stderrHash} stderr_len=${result.stderr.length}]`,
              );
            }
          } catch (err) {
            log(`Integration hook on_install failed for '${agent.code_name}' / '${slug}': ${(err as Error).message}`);
          }
        }
      }

      // ENG-4421: install CLIs for toolkits declared by the agent's plugins.
      // Union across plugins, dedup per process via toolkitCliEnsured. Runs
      // for claude-code hosts.
      const agentFwForToolkits = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;
      // ENG-4629: prefer integration_toolkits; fall back to plugin_toolkits.
      const toolkitsList = refreshAny.integration_toolkits ?? refreshAny.plugin_toolkits ?? [];
      if (agentFwForToolkits === 'claude-code' && toolkitsList.length) {
        const toolkitUnion = new Set<string>();
        for (const { toolkits } of toolkitsList) {
          for (const t of toolkits) toolkitUnion.add(t);
        }
        for (const toolkitSlug of toolkitUnion) {
          await ensureToolkitCli(toolkitSlug);
        }
      }

      // Hot-reload: notify running Claude Code session about new integration skills
      const agentFw = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;
      if (agentFw === 'claude-code' && installedIntegrationSkills.length > 0 && isSessionHealthy(agent.code_name)) {
        const names = installedIntegrationSkills.join(', ');
        injectMessage(agent.code_name, 'system',
          `New integration skills installed: ${names}. These are available immediately — Claude Code loads skills on demand from .claude/skills/.`,
          { task_name: 'plugin-skill-update' }, log).catch(() => {});
        log(`[hot-reload] Notified '${agent.code_name}' about new integration skills: ${names}`);
      }
    }
  }

  // 7d. Fetch kanban board for prompt injection into board-aware templates
  let boardItems: BoardItem[] = [];
  const hasBoardTemplates = tasks.some((t) => BOARD_INJECT_TEMPLATES.has(t.template_id));
  if (hasBoardTemplates) {
    try {
      const boardData = await api.post<{ items?: BoardItem[] }>('/host/my-kanban', { agent_id: agent.agent_id });
      boardItems = (boardData.items ?? []).map(sanitizeBoardItem);
      kanbanBoardCache.set(agent.code_name, boardItems);
    } catch {
      // Non-fatal — board may not exist yet
      boardItems = kanbanBoardCache.get(agent.code_name) ?? [];
    }
  }

  // Resolve framework once for sections 8-11
  const agentFw = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;

  // 8. Sync scheduled tasks
  const sessionMode = (refreshData.agent as Record<string, unknown>).session_mode as string ?? 'oneshot';

  // 7e. ENG-5435 hybrid kanban-work + ENG-5599/5600/5601 scheduled-task
  // reconcile & delivery. Both need a fresh board snapshot for a claude-code
  // persistent agent (oneshot agents have no live REPL), so they share one
  // fetch — but they're gated by SEPARATE flags and must not be coupled:
  //   - reconcile + delivery (ENG-5601) runs whenever scheduled-via-kanban is
  //     on. It's the PRIMARY delivery trigger; gating it behind the hybrid flag
  //     would silently drop every scheduled-task delivery when an operator
  //     enables AGT_SCHEDULED_VIA_KANBAN alone. (Harmless no-op when nothing is
  //     tracked in scheduledRunsByCode.)
  //   - the hybrid kanban-work inject (ENG-5435) runs only when the hybrid flag
  //     is on. Uses the tighter HYBRID_ACTIONABLE_STATUSES predicate
  //     (todo / in_progress — NOT backlog) per
  //     docs/research/eng-5435-hybrid-kanban-inject.md.
  const wantsHybridInject = isKanbanHybridEnabled();
  // ENG-6849: scheduled-task reconcile/delivery via the kanban route is now
  // unconditional - the AGT_SCHEDULED_VIA_KANBAN opt-out is retired with the
  // claude -p oneshot. Reconcile runs for every persistent claude-code agent;
  // the separate (always-on) hybrid-inject flag gates only the kanban-work nudge
  // below (the inner `if (wantsHybridInject)`), not scheduled-task delivery.
  if (agentFw === 'claude-code' && sessionMode === 'persistent') {
    // CR (CodeRabbit on PR #1293): the kanbanBoardCache is process-wide
    // and persists across poll ticks. The previous `!hybridBoard` short
    // circuit meant that once we populated the cache, we'd read stale
    // data forever for agents without a board-aware scheduled template
    // (step 7d only fetches when `hasBoardTemplates`). Reuse step 7d's
    // freshly-fetched board when it ran this tick; otherwise fetch our
    // own and update the cache. Falls back to whatever cached snapshot
    // exists on transport failure rather than treating the agent as
    // having an empty board.
    let hybridBoard = hasBoardTemplates ? boardItems : undefined;
    if (!hybridBoard) {
      try {
        const boardData = await api.post<{ items?: BoardItem[] }>(
          '/host/my-kanban',
          { agent_id: agent.agent_id },
        );
        hybridBoard = (boardData.items ?? []).map(sanitizeBoardItem);
        kanbanBoardCache.set(agent.code_name, hybridBoard);
      } catch {
        hybridBoard = kanbanBoardCache.get(agent.code_name) ?? [];
      }
    }
    // ENG-5600/5601: reconcile in-flight scheduled-task runs against this fresh
    // board snapshot — deliver the agent's result from the done card and close
    // the per-card run. Done here because we already have a current board and
    // the agent self-completing its card means the realtime onCompletion path
    // is suppressed by isSelfCompletion (so this poll is the primary trigger).
    // Fire-and-forget: it awaits delivery per card internally but the poll loop
    // doesn't block on it.
    void reconcileScheduledRuns(agent.code_name, agent.agent_id, hybridBoard);
    if (wantsHybridInject) {
      // ENG-6171: reset poison-card accounting against this fresh board — drop
      // any tracked card that has left in_progress or cooled down (AC: counter
      // resets), then suppress cards still flagged as suspected poison from the
      // nudge so a deterministically-wedging card stops driving the respawn loop
      // and falls to the 30-min stale-auto-fail instead.
      const poisonCfg = resolvePoisonCardConfig();
      const existingStates = wedgeRestartsByCard.get(agent.code_name);
      if (existingStates && existingStates.size > 0) {
        const liveInProgress = hybridBoard
          .filter((item) => item.status === 'in_progress' && isHybridActionable(item))
          .map((item) => item.id);
        const pruned = pruneCardStates(existingStates, liveInProgress, Date.now(), poisonCfg);
        if (pruned.size > 0) wedgeRestartsByCard.set(agent.code_name, pruned);
        else wedgeRestartsByCard.delete(agent.code_name);
      }
      const cardStates = wedgeRestartsByCard.get(agent.code_name);
      const actionable = hybridBoard.filter(isHybridActionable);
      const { allowed, suppressed } = cardStates
        ? partitionActionableByPoison(actionable, cardStates, poisonCfg)
        : { allowed: actionable, suppressed: [] as BoardItem[] };
      if (allowed.length > 0) {
        // Fire-and-forget per design-doc Q2 — the 10s waitForPromptReady
        // cap would otherwise serialise across N agents and starve the
        // poll cycle. Caller doesn't await. Count excludes suppressed poison
        // cards so the nudge advertises only the cards we want picked up.
        void maybeInjectKanbanCheck(agent.code_name, agent.agent_id, allowed.length);
      } else {
        // No nudgeable cards. Either the board drained (ENG-5565 — close any
        // open injected run so it doesn't absorb later non-kanban turns) or
        // every actionable card is a suspected poison task (ENG-6171 — same
        // run-close; the suppression itself was logged once at attribution
        // time, so no per-poll spam here).
        const reason =
          suppressed.length > 0
            ? 'kanban inject suppressed — suspected poison card(s) (ENG-6171)'
            : 'kanban board drained';
        closeInjectedRunIfOpen(agent.code_name, 'completed', reason);
      }
    }
  }

  if (agentFw === 'claude-code' && sessionMode === 'persistent') {
    // Claude Code persistent mode: manage long-running session with channels.
    //
    // ENG-5116: wrap the per-agent call in try/catch so an exception
    // affecting one agent's evaluation can't silently take that agent
    // down. Pre-fix, a throw inside ensurePersistentSession bypassed
    // every log statement and the operator only learned about it when
    // a user reported the agent unresponsive in Slack (Vigil, 2026-05-16).
    // Snapshot health *before* the call so an exception thrown after the
    // session is already healthy doesn't get misreported as down — that
    // would start a bogus stuck streak for an agent still serving traffic
    // (CodeRabbit PR #1070).
    const wasSessionHealthy = isSessionHealthy(agent.code_name);
    let psResult: PersistentSessionResult;
    try {
      psResult = await ensurePersistentSession(agent, tasks, boardItems, refreshData);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      log(`[persistent-session] EXCEPTION evaluating '${agent.code_name}': ${msg}`);
      // Construct a synthetic result so the decision-summary + stuck
      // tracker still see this tick. Carry forward the pre-call health
      // observation — the throw doesn't kill the tmux session, only our
      // evaluation of it.
      psResult = {
        decision: 'skipped-auth-resolve-failed',
        spawnAttempted: false,
        sessionHealthyAfter: wasSessionHealthy,
        detail: `exception: ${msg}`,
      };
    }

    // One structured decision-summary line per agent per tick. This is
    // the line that would have shown Vigil silently stuck — pre-fix only
    // the happy path logged. `detail` is JSON-stringified so embedded
    // quotes/newlines from exception messages can't break single-line
    // parsing downstream (CodeRabbit PR #1070).
    const normalizedDetail = psResult.detail?.replace(/\s+/g, ' ').trim();
    const detailSuffix = normalizedDetail
      ? ` detail=${JSON.stringify(normalizedDetail)}`
      : '';
    log(
      `[persistent-session-decision] agent=${agent.code_name} decision=${psResult.decision} ` +
        `spawn_attempted=${psResult.spawnAttempted} session_healthy_after=${psResult.sessionHealthyAfter}${detailSuffix}`,
    );

    const stuck = persistentSessionStuckTracker.record({
      codeName: agent.code_name,
      sessionHealthy: psResult.sessionHealthyAfter,
      spawnAttempted: psResult.spawnAttempted,
    });

    if (stuck.shouldWarn) {
      log(
        `[persistent-session-stuck] WARN: agent=${agent.code_name} unhealthy with no spawn attempt for ${stuck.consecutiveTicks} consecutive ticks ` +
          `(last_decision=${psResult.decision}) — manager-worker may need restart, or upstream guard is mis-firing (see ENG-5116)`,
      );
    } else if (stuck.recovered) {
      log(`[persistent-session-stuck] agent=${agent.code_name} recovered from stuck state`);
    }
    // ENG-4897: detect structural .mcp.json drift since the session was
    // launched and schedule a respawn. Runs only on persistent claude-code
    // agents — the only path that uses --strict-mcp-config and therefore
    // pins the MCP server set at startup. Other framework/mode combos
    // either re-read .mcp.json on every spawn or don't use it.
    try {
      checkMcpConfigDriftAndScheduleRestart(agent.code_name, psGetProjectDir(agent.code_name));
    } catch (err) {
      log(`[hot-reload] .mcp.json drift check failed for '${agent.code_name}': ${(err as Error).message}`);
    }
    // ENG-6062: detect a channel-token rotation since launch. The token now
    // lives only in `.env.integrations` (templated in `.mcp.json`), so the
    // `.mcp.json` drift check above is structurally blind to it — without this
    // a rotation silently no-ops (old, revoked token stays live) until a manual
    // session bounce. Same persistent-claude-code scope as the check above.
    //
    // CodeRabbit #1831: gate on channelConfigConverged (same guard the
    // channel-set restart path uses) — if a credential write/remove failed
    // this tick, `.env.integrations` is only partially written, so don't
    // respawn against it. The drift is sticky: a skipped tick re-detects it
    // once convergence is reached (baseline isn't advanced while skipped).
    if (channelConfigConverged) {
      try {
        checkChannelSecretDriftAndScheduleRestart(agent.code_name, psGetProjectDir(agent.code_name));
      } catch (err) {
        log(`[hot-reload] channel-secret drift check failed for '${agent.code_name}': ${(err as Error).message}`);
      }
    }

    // ENG-5053: presence check — flag declared MCP servers that have
    // no live children under this agent's claude (e.g. xero-mcp-server
    // crashed silently) and kill the session so the next ensure pass
    // respawns it. Skipped within the cold-start grace window so the
    // initial 20-30s npm-install of @composio/mcp + xero-mcp-server
    // doesn't trip a false-positive restart.
    try {
      const sess = getSessionState(agent.code_name);
      let mcpJsonParsed: import('./stale-mcp-reaper.js').McpConfig | null = null;
      try {
        const mcpPath = join(psGetProjectDir(agent.code_name), '.mcp.json');
        mcpJsonParsed = JSON.parse(readFileSync(mcpPath, 'utf-8'));
      } catch {
        // Missing/unreadable .mcp.json — nothing to check. The drift
        // check above already logs unparseable configs; we silently
        // skip presence so we don't double-log on a transient read.
      }
      reapMissingMcpSessions({
        log,
        codeName: agent.code_name,
        mcpJson: mcpJsonParsed,
        sessionStartedAt: sess?.startedAt ?? null,
        // ENG-6660: under Docker the MCP children live in the agent's container
        // PID namespace - probe inside it (docker exec ps) instead of the host,
        // or every declared MCP looks "missing" -> restart loop -> flapping.
        isolated: isolationMode(agent.code_name) === 'docker',
        // ENG-6486: a restart is already scheduled/deferred for this agent (e.g.
        // a freshly-added channel awaiting its convergence restart, deferred by
        // the maintenance window). The pending restart will spawn the declared-
        // but-not-yet-live MCP, so don't let the reaper re-fire + inflate the
        // McpReaperRestartsHourly metric into a false flapping alarm.
        restartPending: pendingSessionRestarts.has(agent.code_name),
        // ENG-5441 / ENG-5547: a mcp-presence-reaper restart counts against
        // the agent-wide breaker ONLY when several distinct MCP servers are
        // missing at once — a thrashing collection that rotates restarts of
        // the whole session, which the per-MCP cap can't see. A restart
        // driven by a SINGLE persistently-failing server is left to that
        // server's own ENG-5279 give-up cap (→ marked unhealthy via onGiveUp
        // → dropped from the next .mcp.json), so one bad integration is
        // isolated and the agent keeps running degraded instead of being
        // paused wholesale. reaperRestartBreakerReason() encodes that
        // single-vs-multi decision; undefined means "restart, don't count".
        //
        // ENG-6264: the breaker-count reason (above, undefined for a single dead
        // MCP) is decoupled from the GATE reason. Pre-6264 an undefined breaker
        // reason also made the restart non-gateable → 'bypass' → the session was
        // torn down mid-turn (the common single-MCP case interrupted busy
        // agents). Always pass 'mcp-presence-reaper' as the gate reason so the
        // restart defers-until-idle, while breakerReason still governs whether it
        // counts against the breaker.
        stopSession: (codeName, ctx) =>
          stopPersistentSessionAndForgetMcpBaseline(
            codeName,
            reaperRestartBreakerReason(ctx.activeKeys),
            'mcp-presence-reaper',
          ),
        // ENG-5292: when the reaper gives up on a managed MCP (cap from
        // ENG-5279 + state-preservation from ENG-5285 both said "this
        // MCP keeps failing after 3 restart cycles"), mark the matching
        // integration unhealthy server-side. The next /host/managed-
        // toolkits poll drops it from the response → next writeMcpServer
        // omits it → broken MCP is cleanly gone instead of declared-but-
        // dead.
        //
        // Async fire-and-forget — the reaper's correctness must not
        // depend on the API call succeeding. Errors are caught inside
        // the .then/.catch chain and logged.
        onGiveUp: (codeName, serverKey) => {
          const toolkitId = managedToolkitIdByAgentAndServerId.get(
            managedToolkitMapKey(agent.agent_id, serverKey),
          );
          if (!toolkitId) {
            log(
              `[mcp-presence-reaper] give-up on '${codeName}:${serverKey}' — not a managed toolkit (no toolkit_id mapping), skipping integration-health API call`,
            );
            return;
          }
          const agentId = agent.agent_id;
          api.post('/host/integration-health', {
            agent_id: agentId,
            toolkit_id: toolkitId,
            action: 'mark_unhealthy',
            reason: `mcp-presence-reaper cap (${3} consecutive failed restart cycles) tripped on '${serverKey}'`,
          }).then((res) => {
            log(
              `[mcp-presence-reaper] marked integration unhealthy for '${codeName}' toolkit '${toolkitId}' (ENG-5292): ${JSON.stringify(res).slice(0, 200)}`,
            );
          }).catch((err) => {
            log(
              `[mcp-presence-reaper] failed to mark integration unhealthy for '${codeName}' toolkit '${toolkitId}' (ENG-5292): ${(err as Error).message} — local give-up state still recorded; will retry on next agent provisioning cycle if .mcp.json changes`,
            );
          });
        },
        // ENG-5286: record the restart event with the API so the
        // agent-heartbeat-monitor cron can aggregate per-agent counts
        // into the McpReaperRestartsHourly CloudWatch metric. Fire-and-
        // forget — the reaper's correctness must not depend on this.
        onRestart: (codeName, { activeKeys, givenUpKeys }) => {
          api.post('/host/mcp-reaper-restart', {
            agent_id: agent.agent_id,
            code_name: codeName,
            server_keys: activeKeys,
            given_up_keys: givenUpKeys,
          }).catch((err) => {
            log(
              `[mcp-presence-reaper] failed to record restart event for '${codeName}' (ENG-5286): ${(err as Error).message} — local restart still proceeded; CloudWatch metric will under-count this event`,
            );
          });
          // ENG-6184: also open a paged `reaper_action` alert so a single
          // restart is visible without waiting for the flapping threshold.
          // Fire-and-forget: the reaper must not block on the report.
          void reportReaperEvent(
            agent.agent_id,
            'mcp-presence-reaper',
            'restart',
            givenUpKeys.length > 0
              ? `restarted session; MCP server(s) still down after restart budget: ${givenUpKeys.join(', ')}`
              : `restarted session to recover MCP server(s): ${activeKeys.join(', ')}`,
          );
        },
        // ENG-5932: classify each declared key for the quarantine path. Reads a
        // server-declared criticality field off the .mcp.json entry when
        // present (Slice 2 will stamp it); otherwise fails closed to ESSENTIAL
        // for everything except the known optional channel set. Tool servers
        // (composio_*/xero) classify ESSENTIAL here and stay on the existing
        // managed-toolkit onGiveUp path above.
        classifyKey: (serverKey) =>
          classifyChannelCriticality(
            serverKey,
            (mcpJsonParsed?.mcpServers as Record<string, unknown> | undefined)?.[serverKey],
          ),
        quarantineDwellMs: channelQuarantineDwellMs(),
        quarantineMode: channelQuarantineMode(),
        // ENG-5932: enforce-mode quarantine. Persist the local marker; the next
        // provisioning poll then omits the channel from .mcp.json + the launch
        // flags, and the channel-set-change / .mcp.json-drift restart paths are
        // taught to NOT bounce the session for a quarantine-driven removal.
        // Local-authoritative: no API call — with the control plane down, the
        // flap still stops.
        onQuarantine: (codeName, serverKey) => {
          const newly = channelQuarantineStore().quarantine(
            codeName,
            serverKey,
            `mcp-presence-reaper: optional channel dead past restart budget + dwell`,
            Date.now(),
          );
          if (newly) {
            log(
              `[channel-quarantine] persisted quarantine marker for '${codeName}:${serverKey}' — will be dropped from .mcp.json on next provisioning poll (ENG-5932)`,
            );
            // ENG-6184: a newly-quarantined channel is a critical reaper action
            // (the channel is pulled out of service) — surface it once.
            // Fire-and-forget: the reaper must not block on the report.
            void reportReaperEvent(
              agent.agent_id,
              'mcp-presence-reaper',
              'quarantine',
              `optional channel '${serverKey}' dead past restart budget + dwell — quarantined`,
            );
          }
        },
      });
    } catch (err) {
      log(`[mcp-presence-reaper] presence check failed for '${agent.code_name}': ${(err as Error).message}`);
    }
  } else if (agentFw === 'claude-code' && tasks.length > 0) {
    // Claude Code oneshot mode: fire-and-forget via claude -p
    await syncAndCheckClaudeScheduler(agent, tasks, boardItems, refreshData);
  }

  // 10. Immediate work trigger — if webapp signalled a new task, fire kanban-work now
  {
    const triggerAt = (refreshData.agent as Record<string, unknown>).work_trigger_at as string | null;
    if (triggerAt) {
      const triggerTs = new Date(triggerAt).getTime();
      const lastTrigger = lastWorkTriggerAt.get(agent.code_name) ?? 0;
      if (triggerTs > lastTrigger) {
        lastWorkTriggerAt.set(agent.code_name, triggerTs);

        if (agentFw === 'claude-code') {
          // Claude Code: for persistent sessions, inject via tmux; for oneshot, fire claude -p
          if (sessionMode === 'persistent') {
            // Persistent mode: inject into tmux session if healthy, skip if still booting
            if (isSessionHealthy(agent.code_name)) {
              // ENG-5405: include the top todo item's id in the inject so
              // the agent can kanban_move(id, "in_progress") without a
              // round-trip via kanban_list — saves ~one MCP call per
              // trigger. Falls back to a board-list prompt when there's
              // no obvious top item (empty board, all-blocked, etc).
              const todayItem = boardItems.find((b) => b.status === 'todo');
              // CodeRabbit PR #1275: titles are user-authored; JSON.stringify
              // so embedded quotes/newlines can't reshape the injected
              // prompt and mis-direct the agent to a different item.
              const triggerMessage = todayItem
                ? `New work triggered. Top item: id=${todayItem.id} title=${JSON.stringify(todayItem.title)} priority=${todayItem.priority}. Call kanban_move("${todayItem.id}", "in_progress") and start working — no need to kanban_list first.`
                : 'New work triggered. Check your kanban board with kanban_list and pick up the next item.';
              injectMessage(agent.code_name, 'task', triggerMessage, {
                task_name: 'kanban-work-trigger',
              }, log);
              log(`[persistent-session] Work trigger injected for '${agent.code_name}'`);
            } else {
              log(`[persistent-session] Work trigger skipped for '${agent.code_name}' — session not yet healthy`);
            }
          }
          // ENG-5662: non-persistent claude-code agents no longer have a
          // kanban-work claude -p fallback — kanban work is driven by the
          // hybrid edge-trigger on persistent sessions only.
        }
      }
    }
  }

  // 12. Detect newly-done kanban items and send notifications (runs every cycle)
  {
    const agentId = agentState.codeNameToAgentId.get(agent.code_name);
    if (agentId) {
      try {
        const boardData = await api.post<{ items?: BoardItem[] }>('/host/my-kanban', { agent_id: agentId });
        const freshBoard = (boardData.items ?? []).map(sanitizeBoardItem);
        const freshDoneIds = new Set(freshBoard.filter((b) => b.status === 'done' || b.status === 'failed').map((b) => b.id));

        const previousDoneIds = notifyBoardCache.get(agent.code_name);
        notifyBoardCache.set(agent.code_name, freshDoneIds);

        // Skip first cycle — no previous state to compare against
        if (!previousDoneIds) {
          log(`[${agent.code_name}] Board diff: initial cache (${freshDoneIds.size} done items)`);
        } else {
          const newlyDone = freshBoard.filter(
            (b) => (b.status === 'done' || b.status === 'failed') && !previousDoneIds.has(b.id),
          );

          log(`[${agent.code_name}] Board diff: ${previousDoneIds.size} prev done → ${freshDoneIds.size} now, ${newlyDone.length} newly done`);

        if (newlyDone.length > 0) {
          const displayName = agentState.agentDisplayNames.get(agent.code_name) ?? agent.code_name;
          for (const item of newlyDone) {
            log(`Newly done: '${item.title}' notify_channel=${item.notify_channel ?? 'none'} notify_to=${item.notify_to ?? 'none'}`);
            if (item.notify_channel && item.notify_to) {
              const message = await buildDoneCardNotification(agentId, agent.code_name, displayName, item);
              sendTaskNotification(agent.code_name, item.notify_channel, item.notify_to, message).catch((err) => {
                log(`sendTaskNotification failed for '${agent.code_name}': ${(err as Error).message}`);
              });
            }
          }
        }
        }
        // Check for stale in_progress items
        const staleItems = freshBoard.filter((b) => {
          if (b.status !== 'in_progress' || !b.updated_at) return false;
          const age = Date.now() - new Date(b.updated_at).getTime();
          return age > STALE_TASK_THRESHOLD_MS && !alertedStaleItems.has(`${agent.code_name}:${b.id}`);
        });

        if (staleItems.length > 0) {
          const displayName = agentState.agentDisplayNames.get(agent.code_name) ?? agent.code_name;
          for (const item of staleItems) {
            const age = Math.round((Date.now() - new Date(item.updated_at!).getTime()) / 60_000);
            // ENG-5408: structured stuck-task watchdog line. Lands BEFORE
            // the existing auto-fail so CloudWatch metric filters can
            // count occurrences even when the auto-fail succeeds. Same
            // shape as `[persistent-session-stuck]` (ENG-5116) so the
            // alerting/monitoring pipeline can pattern-match without a
            // new parser. One line per row per occurrence — `stuckKanbanLogged`
            // dedupes within the session.
            const stuckKey = `${agent.code_name}:${item.id}`;
            if (!stuckKanbanLogged.has(stuckKey)) {
              log(
                `[persistent-session-stuck-kanban] agent=${agent.code_name} ` +
                  `item_id=${item.id} title=${JSON.stringify(item.title)} ` +
                  `status=in_progress age_minutes=${age} ` +
                  `threshold_minutes=${Math.round(STALE_TASK_THRESHOLD_MS / 60_000)}`,
              );
              stuckKanbanLogged.add(stuckKey);
            }
            log(`Stale task: '${item.title}' (id=${item.id}) in_progress for ${age}m — auto-failing for '${agent.code_name}'`);
            alertedStaleItems.add(`${agent.code_name}:${item.id}`);

            // Auto-fail the task via API (try ID first, fallback to title)
            try {
              const failResult = await api.post<{ ok?: boolean; updated?: number; error?: string }>('/host/kanban', {
                agent_id: agentId,
                update: [{ id: item.id, title: item.title, status: 'failed', result: `Timed out — in progress for ${age} minutes with no update` }],
              });
              log(`Auto-fail result for '${item.title}': updated=${failResult.updated}`);
              // Mark as done only when mutation actually succeeded,
              // so we don't suppress legitimate later notifications.
              if ((failResult.updated ?? 0) > 0 || failResult.ok === true) {
                freshDoneIds.add(item.id);
              }
            } catch (err) {
              log(`Auto-fail API error for '${item.title}': ${(err as Error).message}`);
            }

            // Notify
            const message = `⏳ Task Timed Out — ${displayName}\n${item.title}\nIn progress for ${age} minutes with no update — auto-failed`;
            if (item.notify_channel && item.notify_to) {
              sendTaskNotification(agent.code_name, item.notify_channel, item.notify_to, message).catch(() => {});
            }
            sendSlackWebhookMessage(`⏳ *Task Timed Out* — *${displayName}*\n*${item.title}*\nIn progress for ${age} minutes — auto-failed`).catch(() => {});
          }
        }

        // Clear stale alerts for items no longer in_progress (scoped to this agent)
        const prefix = `${agent.code_name}:`;
        for (const key of alertedStaleItems) {
          if (key.startsWith(prefix)) {
            const itemId = key.slice(prefix.length);
            if (!freshBoard.some((b) => b.id === itemId && b.status === 'in_progress')) {
              alertedStaleItems.delete(key);
            }
          }
        }
        // ENG-5408: clear the structured-log dedupe set in lockstep, so an
        // item that gets cancelled / fixed / re-spawned can re-trigger the
        // watchdog if it goes stuck again later in this session.
        for (const key of stuckKanbanLogged) {
          if (key.startsWith(prefix)) {
            const itemId = key.slice(prefix.length);
            if (!freshBoard.some((b) => b.id === itemId && b.status === 'in_progress')) {
              stuckKanbanLogged.delete(key);
            }
          }
        }
      } catch (err) {
        log(`Board diff failed for '${agent.code_name}': ${(err as Error).message}`);
      }
    }
  }

  // 13. Memory sync — upload local memory files to DB, download DB memories to local
  if (frameworkId === 'claude-code' && agent.status === 'active') {
    try {
      await syncMemories(agent, config.configDir, log);
    } catch (err) {
      log(`Memory sync failed for '${agent.code_name}': ${(err as Error).message}`);
    }
  }

  // ENG-4473: Re-capture agentState.writtenHashes AFTER all tracked-file writers have
  // finished this poll — channel credentials (step 5), the direct-chat
  // provisioner (step 5b), managed-provider proxies (step 7), etc. The
  // provisioner write loop at step 3 records `agentState.writtenHashes` before those
  // later writers run, so the drift check at the TOP of the next poll
  // sees the final file state vs the step-3 hash and mis-reports every
  // poll as drifted (even when the state is perfectly stable). Re-hashing
  // here converges agentState.writtenHashes to what's actually on disk after this
  // agent's full processing, so:
  //   - genuine external edits between polls are still detected (drift
  //     check runs BEFORE our writers in the next poll)
  //   - our own multi-writer sequence stops reading as drift
  const trackedFiles = frameworkAdapter.driftTrackedFiles();
  if (trackedFiles.length > 0 && existsSync(agentDir)) {
    // Rebuild from scratch so files no longer tracked (or missing on disk)
    // don't leave stale hashes that would mis-report drift next poll.
    const hashes = new Map<string, string>();
    for (const file of trackedFiles) {
      const h = hashFile(join(agentDir, file));
      if (h) hashes.set(file, h);
    }
    agentState.writtenHashes.set(agent.agent_id, hashes);
  } else {
    agentState.writtenHashes.delete(agent.agent_id);
  }

  // ENG-6533 (folds ENG-6527): drive self-onboarding. If this claude-code
  // persistent agent's onboarding is mid-flow (on a per-area step), inject a
  // one-shot "run onboarding_get and proceed" directive into its live session.
  // Idempotent via a per-agent marker that self-heals a stale area; pending/
  // ready clear it so a later /onboard (resets → the first area) re-drives. Wrapped
  // best-effort: an onboarding-drive failure must never break the refresh loop.
  try {
    const obFramework = agentFrameworkCache.get(agent.code_name) ?? DEFAULT_FRAMEWORK;
    const obSessionMode = (refreshData.agent as Record<string, unknown>).session_mode as string | undefined;
    if (obFramework === 'claude-code' && obSessionMode === 'persistent') {
      const obState = coerceOnboardingState(
        (refreshData.agent as Record<string, unknown>).onboarding_state,
      );
      const obStep = obState.step;
      const markerPath = join(homedir(), '.augmented', agent.code_name, 'onboarding-drive.json');
      const marker = readOnboardingDriveMarker(markerPath);
      // ENG-6601: pass the live reset generation so a same-area RESET (/onboard)
      // re-injects even though the step didn't change.
      const decision = decideOnboardingDrive(obStep, marker, Date.now(), obState.generation ?? 0);
      if (decision.clearMarker) {
        clearOnboardingDriveMarker(markerPath);
      } else if (
        decision.inject &&
        isOnboardingArea(obStep) &&
        isSessionHealthy(agent.code_name)
      ) {
        const injectResult = await injectMessageWithStatus(
          agent.code_name,
          'system',
          // ENG-6583: anchor the ask to the channel onboarding was triggered
          // from, and phrase a staleness re-fire as a gentle reminder. ENG-6626:
          // the final bounded nudge flips to the stand-down message, which names
          // the agent's /onboard-<code> resume command. ENG-6636: a /onboard
          // restart (generation change) forces the "post the question afresh
          // this turn, don't stand down" variant.
          buildOnboardingDirective(obStep, obState.completed, {
            channel: obState.channel,
            reminder: decision.reminder,
            standDown: decision.standDown,
            freshRestart: decision.freshRestart,
            code: agent.code_name,
          }),
          { task_name: 'onboarding' },
          log,
        ).catch(() => ({ delivered: false, fallbackUsed: false }));
        // Persist the marker once the nudge REACHED the session (ENG-6576). With
        // acpx removed (ENG-6345) tmux send-keys is structurally delivered:false
        // even when it lands, so gating on `delivered` alone never wrote the
        // marker and re-injected every poll forever. `onboardingNudgeReachedSession`
        // treats a landed keystroke (fallbackUsed) as reached; a genuine miss
        // (neither flag) leaves the marker unwritten so the 5-min staleness
        // self-heal retries next poll instead of looping every cycle.
        if (onboardingNudgeReachedSession(injectResult) && decision.nextMarker) {
          writeOnboardingDriveMarker(markerPath, decision.nextMarker);
          // ENG-6626: surface the bounded-nudge progression (which nudge, and
          // whether this was the final stand-down) for fleet diagnosis.
          const tag = decision.standDown
            ? `stand-down (nudge ${decision.nextMarker.nudgeCount})`
            : `nudge ${decision.nextMarker.nudgeCount}`;
          log(`[onboarding] ${tag} '${agent.code_name}' into '${obStep}'`);
        }
      }
    }
  } catch (e) {
    log(`[onboarding] drive failed for '${agent.code_name}': ${(e as Error).message}`);
  }

  agentStates.push({
    agentId: agent.agent_id,
    codeName: agent.code_name,
    status: agent.status,
    charterVersion,
    toolsVersion,
    secretsHash,
    lastRefreshAt: now,
    lastProvisionAt,
    lastDriftCheckAt,
    lastSecretsProvisionAt,
    acpSessions: [],
  });
}

// ---------------------------------------------------------------------------
// Claude Code in-process scheduler
// ---------------------------------------------------------------------------

import {
  syncTasksToScheduler,
  getReadyTasks,
  markTaskFired,
  loadSchedulerState,
  getProjectDir as ccGetProjectDir,
  type SchedulerTaskInput,
  type SchedulerTaskState,
} from './claude-scheduler.js';



// ---------------------------------------------------------------------------
// Persistent session management (session_mode = 'persistent')
// ---------------------------------------------------------------------------

import {
  startPersistentSession,
  isolationMode,
  buildEgressAllowlist,
  writeEgressAllowlist,
  reloadEgressSidecar,
  restartEgressSidecar,
  egressMode,
  type EgressToolsFrontmatter,
  stopPersistentSession,
  injectMessage,
  injectMessageWithStatus,
  isSessionHealthy,
  getSessionState,
  readDirectChatSessionState,
  resetRestartCount,
  stopAllSessions,
  stopAllSessionsAndWait,
  getProjectDir as psGetProjectDir,
  resolveClaudeBinary,
  getLastFailureContext,
  prepareForRespawn,
  takeZombieDetection,
  rotateSessionForWedge,
  sendToAgent,
  isAgentPromptReady,
  readPaneLogTail,
  paneLogPath,
} from './persistent-session.js';
import {
  resolveWedgeConfig,
  decideWedgeRestart,
  isWedgeCandidateCycle,
  wedgeExemptionReason,
} from './wedge-detection.js';
import {
  resolvePoisonCardConfig,
  recordWedgeForCards,
  pruneCardStates,
  partitionActionableByPoison,
  type CardWedgeState,
} from './wedge-poison-card.js';
import {
  isAgentIdle,
  isStaleForToday,
  peekCurrentSession,
  subagentActivityAgeSeconds,
  transcriptActivityAgeSeconds,
} from './daily-session.js';
import { processRestartFlags } from './restart-handler.js';
import { PersistentSessionStuckTracker } from './persistent-session-stuck-tracker.js';

// ADR-0011 PR-A: track which agents have persistent sessions started.
// Moved into `agentState.persistentSessionAgents` so the future kanban-inject
// gate (PR-C) doesn't have to import from the lifecycle module.

// ENG-5459: per-agent dedupe for the pane.log MCP-failure-banner
// observation. Manager only POSTs when the count differs from the last
// reported value, so a stable healthy agent (banner-not-present →
// count 0) emits at most one POST per fresh worker.
const lastMcpFailedBannerCount = new Map<string, number>();

/**
 * ENG-5116 — discriminator for the *one decision* a persistent-session
 * tick made for a given agent. Every code path inside
 * `ensurePersistentSession` records exactly one of these so the caller
 * can emit a single structured `[persistent-session-decision]` log line
 * even when the path took an early return. Before this, only the happy
 * path logged ("Starting tmux session ...") and silent skips were
 * effectively invisible — see the Vigil incident (2026-05-16).
 */
type PersistentSessionDecision =
  | 'spawn'                       // startPersistentSession() invoked this tick
  | 'healthy'                     // session is up; tasks may have synced
  | 'restart-pending'             // stopped this tick (auth tuple changed); next tick respawns
  | 'day-rollover-restart'        // stopped this tick for date rollover; next tick respawns
  | 'day-rollover-deferred'       // stale but agent still active mid-task; will retry
  | 'circuit-tripped'             // ENG-5441: breaker tripped — no spawn until operator resumes
  | 'skipped-no-api-key'
  | 'skipped-auth-resolve-failed'
  | 'skipped-not-authenticated'
  | 'skipped-api-key-mode-no-key'
  | 'skipped-openrouter-no-key';   // ENG-7152: host in openrouter mode, agent has no key/model

interface PersistentSessionResult {
  decision: PersistentSessionDecision;
  /** True when `startPersistentSession` was called this tick. */
  spawnAttempted: boolean;
  /** Session health observed AFTER the tick's work — what the next tick will see. */
  sessionHealthyAfter: boolean;
  /** Optional human-readable suffix for the decision log line. */
  detail?: string;
}

/**
 * ENG-5116 — once an agent has been unhealthy without a respawn for
 * `threshold` consecutive ticks, emit a loud WARN exactly once until
 * the streak breaks. See `persistent-session-stuck-tracker.ts`.
 */
const persistentSessionStuckTracker = new PersistentSessionStuckTracker();

/**
 * Per-agent fingerprint of the Claude Code auth config used to spawn the
 * current session: `"<mode>:<fingerprint ?? 'none'>"`. When this changes
 * between polls (operator toggled subscription→api_key, or rotated the key),
 * the session is stopped so the next cycle respawns it with fresh auth.
 */
const claudeAuthTupleBySession = new Map<string, string>();

/**
 * ENG-6579: last-applied egress allowlist (sorted domains) per session. Lets a
 * healthy tick detect when an agent's trusted allowlist changed (operator edited
 * TOOLS.md allowlist_domains) and apply it live - SIGHUP for a purely additive
 * change (zero disruption), or a full sidecar restart when a domain was removed
 * (SIGHUP would leave an in-flight tunnel to the removed domain alive).
 */
const egressAllowlistBySession = new Map<string, string[]>();

/** ENG-6579: derive the trusted egress allowlist from control-plane tools content. */
function deriveEgressAllowlist(toolsRaw: string | undefined): string[] {
  return buildEgressAllowlist(
    toolsRaw ? (extractFrontmatter(toolsRaw).frontmatter as EgressToolsFrontmatter) : null,
  );
}

/** Both inputs are sorted (buildEgressAllowlist sorts), so an index compare suffices. */
const egressAllowlistEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((d, i) => d === b[i]);

async function ensurePersistentSession(
  agent: { agent_id: string; code_name: string; display_name: string },
  tasks: Array<Record<string, unknown>>,
  // ENG-5769: source coords flow through for the kanban-work nudge.
  boardItems: Array<{ id: string; title: string; status: string; priority: number; estimated_minutes?: number; deliverable?: string; result?: string; source_integration?: string | null; source_external_id?: string | null; source_url?: string | null }>,
  refreshData: Record<string, unknown>,
): Promise<PersistentSessionResult> {
  const codeName = agent.code_name;
  const projectDir = psGetProjectDir(codeName);
  const mcpConfigPath = join(projectDir, '.mcp.json');
  const claudeMdPath = join(projectDir, 'CLAUDE.md');

  // ENG-5441: circuit breaker — if this agent's session restart count
  // crossed the threshold inside the sliding window, refuse to spawn
  // until an operator clears the trip (currently: webapp resume / agt
  // agent resume flips the agent's status from paused → active, which
  // the breaker observes on the next poll via clearBreakerOnResume).
  if (restartBreaker.isTripped(codeName)) {
    const trip = restartBreaker.getTrip(codeName)!;
    return {
      decision: 'circuit-tripped',
      spawnAttempted: false,
      sessionHealthyAfter: isSessionHealthy(codeName),
      detail: trip.statusMessage,
    };
  }

  // ENG-5371 / ENG-6695: resolve the agent's IANA timezone used to scope the
  // daily-session day rollover so a fresh conversation starts at the agent's
  // local midnight, not host UTC. Precedence matches the restart gate:
  // agent.timezone → team (teams.settings.timezone) → org-effective (the cached
  // maintenance-window tz, host → org → region). `undefined` keeps the original
  // ENG-4642 host-local behaviour, preserved here for the case where NO tier is
  // configured so teams/agents that never set a timezone are unchanged.
  const teamSettingsForTz = (
    refreshData.team as { settings?: Record<string, unknown> } | undefined
  )?.settings;
  const agentTimezone = (() => {
    const ownTzRaw = (refreshData.agent as { timezone?: unknown } | undefined)?.timezone;
    const ownTz = typeof ownTzRaw === 'string' && ownTzRaw.trim() !== '' ? ownTzRaw.trim() : null;
    const tz = teamSettingsForTz?.['timezone'];
    const teamTz = typeof tz === 'string' && tz.trim() !== '' ? tz.trim() : null;
    const orgTierTz = cachedMaintenanceWindow?.timezone ?? null;
    // Nothing configured at any tier → undefined (host-local), exactly as before.
    if (isUnsetTimezone(ownTz) && isUnsetTimezone(teamTz) && isUnsetTimezone(orgTierTz)) {
      return undefined;
    }
    return resolveAgentTimezone(ownTz, teamTz, orgTierTz);
  })();

  // Resolve channel plugins from agent's channel configs. ENG-6022: the
  // server-id gating lives in resolveChannelLaunchFlags (pure, unit-tested)
  // — the msteams channel shipped with this list hand-maintained inline and
  // missing the `server:msteams` case, so sessions never subscribed to the
  // channel pipeline even though the MCP was running.
  const channelConfigs = refreshData.channel_configs as Record<string, { config: unknown; status: string }> | null;
  const { devChannels, pluginChannels } = resolveChannelLaunchFlags(
    channelConfigs,
    channelQuarantineStore().getQuarantinedKeys(codeName),
  );
  const channels: string[] = [...pluginChannels];

  // Always add direct-chat channel for persistent sessions.
  // This is provisioned in step 5b of processAgent() into .mcp-channels.json.
  devChannels.push('server:direct-chat');

  // ENG-7152: OpenRouter BYO-model block from /host/refresh (null unless the
  // openrouter-mode flag is on for this org/host AND the agent has a stored
  // OpenRouter key). When present it SUPERSEDES the host's subscription/api_key
  // auth for THIS agent — the launcher points Claude Code at OpenRouter's native
  // Anthropic endpoint instead of the operator subscription. An older API that
  // doesn't send the field → undefined → normal auth (graceful degradation).
  const openRouterRaw = (refreshData as {
    openrouter?: {
      auth_token?: string;
      model?: string;
      small_fast_model?: string | null;
      fingerprint?: string;
    } | null;
  }).openrouter ?? null;
  const openRouterForAgent =
    openRouterRaw && openRouterRaw.auth_token && openRouterRaw.model
      ? {
          authToken: openRouterRaw.auth_token,
          model: openRouterRaw.model,
          smallFastModel: openRouterRaw.small_fast_model ?? null,
          fingerprint: openRouterRaw.fingerprint ?? null,
        }
      : null;

  // Resolve auth mode + key from the exchange BEFORE any OAuth precheck —
  // otherwise api_key hosts get skipped for never having run `claude /login`
  // even though they don't need OAuth at all. Force-refresh bypasses the
  // ~50m JWT cache so mode/key changes on the webapp are picked up on the
  // next poll (ENG-4417 rotation acceptance criterion).
  //
  // Fail-closed on error: silently falling back to 'subscription' is exactly
  // the confused-deputy path ENG-4417 removes. If /host/exchange rejects with
  // anthropic_key_decrypt_failed, the host is misconfigured — refuse to
  // spawn (or stop a running session) so the operator sees the problem
  // instead of a session quietly booting under the wrong auth.
  let claudeAuthMode: 'subscription' | 'api_key' | 'openrouter';
  let anthropicApiKey: string | null;
  let anthropicApiKeyFingerprint: string | null;
  try {
    const apiKey = getApiKey();
    if (!apiKey) {
      log(`[persistent-session] Skipping '${codeName}' — AGT_API_KEY not set`);
      return {
        decision: 'skipped-no-api-key',
        spawnAttempted: false,
        sessionHealthyAfter: isSessionHealthy(codeName),
      };
    }
    const exchange = await exchangeApiKey(apiKey, false, { forceRefresh: true });
    claudeAuthMode = exchange.claudeAuthMode;
    anthropicApiKey = exchange.anthropicApiKey;
    anthropicApiKeyFingerprint = exchange.anthropicApiKeyFingerprint;
  } catch (err) {
    const msg = (err as Error).message;
    log(`[persistent-session] Failed to resolve auth for '${codeName}': ${msg} — refusing to spawn`);
    // Stop any running session: the auth config is unknown, so continuing is
    // a silent-fallback bug waiting to happen. Next successful poll respawns.
    if (isSessionHealthy(codeName)) {
      stopPersistentSessionAndForgetMcpBaseline(codeName);
      agentState.persistentSessionAgents.delete(codeName);
      claudeAuthTupleBySession.delete(codeName);
    }
    return {
      decision: 'skipped-auth-resolve-failed',
      spawnAttempted: false,
      sessionHealthyAfter: isSessionHealthy(codeName),
      detail: msg,
    };
  }

  // OAuth precheck only applies in subscription mode. api_key hosts have
  // their credentials in the exchange response above — no `claude /login`
  // required — so don't block them on a missing OAuth token. Hosts in
  // api_key mode with no key would already have failed the exchange
  // (503 anthropic_key_decrypt_failed or host_metadata_unavailable).
  // ENG-7152: OpenRouter mode is host-level — when the host is in openrouter mode
  // EVERY agent runs against OpenRouter, so there's no subscription/api_key to
  // check. The per-agent OpenRouter key (delivered as openRouterForAgent) IS the
  // credential. Fail LOUD if the host is in openrouter mode but this agent has no
  // key or no resolvable model — never silently fall back to a subscription the
  // host doesn't have (it would auth-fail in tmux with no clear cause).
  if (claudeAuthMode === 'openrouter' || openRouterForAgent) {
    if (!openRouterForAgent) {
      log(
        `[persistent-session] Skipping '${codeName}' — host is in OpenRouter mode but this agent has no OpenRouter key or resolvable model. Provision one (Agent → OpenRouter key) and set an openrouter/ model.`,
      );
      return {
        decision: 'skipped-openrouter-no-key',
        spawnAttempted: false,
        sessionHealthyAfter: isSessionHealthy(codeName),
      };
    }
  } else if (claudeAuthMode === 'subscription') {
    if (!agentRuntimeAuthenticated) {
      // Re-check in case user logged in since last check
      agentRuntimeAuthenticated = await checkClaudeAuth();
      if (!agentRuntimeAuthenticated) {
        log(`[persistent-session] Skipping '${codeName}' — Claude Code not authenticated (subscription mode)`);
        return {
          decision: 'skipped-not-authenticated',
          spawnAttempted: false,
          sessionHealthyAfter: isSessionHealthy(codeName),
        };
      }
    }
  } else if (!anthropicApiKey) {
    // Belt-and-braces: exchange should have returned a key when mode=api_key.
    // If it didn't, don't spawn — the session would just fail auth in tmux.
    log(`[persistent-session] Skipping '${codeName}' — api_key mode but no key returned from exchange`);
    return {
      decision: 'skipped-api-key-mode-no-key',
      spawnAttempted: false,
      sessionHealthyAfter: isSessionHealthy(codeName),
    };
  }

  // ENG-5116: track which reason (if any) triggered a session stop on this
  // tick. Used at the end of the function to distinguish "we stopped and
  // also respawned" (decision=spawn) from the silent-bug case "we stopped
  // but didn't respawn this tick" (decision=restart-pending /
  // day-rollover-restart, with sessionHealthyAfter=true exposing the
  // false-positive that took Vigil down).
  let restartTrigger: 'auth-tuple' | 'day-rollover' | null = null;
  let dayRolloverDeferred = false;

  // Detect auth rotation: if the (mode, fingerprint) tuple differs from what
  // we last launched with, stop the session so it respawns with fresh auth
  // on the next poll. Fingerprint captures key rotation without exposing the
  // raw value. First launch (no recorded tuple) stamps the baseline only.
  // ENG-7152: in OpenRouter mode the live credential is the OpenRouter token +
  // model, not the host auth — key it on the non-secret fingerprint (falling back
  // to the model id) so a key rotation, a model change, or toggling the flag
  // on/off changes the tuple and restarts the session on the next poll.
  const currentAuthTuple = openRouterForAgent
    ? `openrouter:${openRouterForAgent.fingerprint ?? openRouterForAgent.model}`
    : `${claudeAuthMode}:${anthropicApiKeyFingerprint ?? 'none'}`;
  const recordedAuthTuple = claudeAuthTupleBySession.get(codeName);
  if (recordedAuthTuple && recordedAuthTuple !== currentAuthTuple && isSessionHealthy(codeName)) {
    log(`[persistent-session] Auth config changed for '${codeName}' (${recordedAuthTuple} → ${currentAuthTuple}) — restarting session`);
    stopPersistentSessionAndForgetMcpBaseline(codeName, 'auth-tuple-change');
    agentState.persistentSessionAgents.delete(codeName);
    restartTrigger = 'auth-tuple';
  }

  // ENG-4642: scheduled day rollover. When the daily-session.json's
  // current.date no longer matches today's local date, the running
  // session is on yesterday's UUID. Kill it so the next pass mints a
  // fresh UUID via getOrCreateDailySession — but only when the agent
  // is between turns (JSONL mtime older than IDLE_THRESHOLD_S). If the
  // agent is mid-task, defer to the next tick rather than interrupt.
  // First-spawn agents (no current entry yet) and unhealthy sessions
  // (no tmux to kill) are no-ops.
  if (isStaleForToday(codeName, new Date(), agentTimezone) && isSessionHealthy(codeName)) {
    const current = peekCurrentSession(codeName);
    if (current) {
      const idle = isAgentIdle(projectDir, current.sessionId);
      if (idle) {
        log(
          `[persistent-session] Day rollover for '${codeName}' (yesterday=${current.date}) — agent idle, restarting to mint fresh session`,
        );
        stopPersistentSessionAndForgetMcpBaseline(codeName, 'day-rollover');
        agentState.persistentSessionAgents.delete(codeName);
        restartTrigger = 'day-rollover';
      } else {
        log(
          `[persistent-session] Day rollover for '${codeName}' deferred — agent still active on session ${current.sessionId} (will retry next tick)`,
        );
        dayRolloverDeferred = true;
      }
    }
  }

  // ENG-5441 + CodeRabbit on PR #1302: re-check the breaker after the
  // auth-tuple-change and day-rollover blocks. Either of those can call
  // `recordRestartForBreaker` for the (max+1)th time *inside this tick*,
  // and without a re-gate the spawn block below would still respawn.
  // The initial gate at the top of this function catches *already-tripped*
  // breakers; this second check catches the case where this tick is the
  // one that trips it.
  if (restartBreaker.isTripped(codeName)) {
    const trip = restartBreaker.getTrip(codeName)!;
    return {
      decision: 'circuit-tripped',
      spawnAttempted: false,
      sessionHealthyAfter: isSessionHealthy(codeName),
      detail: trip.statusMessage,
    };
  }

  // Start or maintain the persistent session
  if (!isSessionHealthy(codeName)) {
    if (agentState.persistentSessionAgents.has(codeName)) {
      // ENG-4659: include the actual claude error from the captured
      // pane log + a restart-cycle counter so silent restart loops are
      // visible at a glance. Run the recovery hook BEFORE the next
      // spawn so e.g. "Session ID already in use" rotates the UUID
      // instead of looping forever.
      //
      // CodeRabbit (PR #618): pane output is untrusted — it can echo
      // user prompts, tool output, secrets, etc. Per CLAUDE.md
      // ("Default logging to hash-only or redacted in prod; never log
      // raw secrets") we only embed the raw tail when the signature
      // is in a strict allowlist of known-safe Claude error messages.
      // For everything else we log only a sha256 prefix of the tail —
      // operators can correlate with the full file at
      // ~/.augmented/<codeName>/pane.log when they need it.
      const ctx = getLastFailureContext(codeName);
      const recovery = prepareForRespawn(codeName);
      const tailSummary = !ctx.tail
        ? ''
        : KNOWN_SAFE_TAIL_SIGNATURES.has(ctx.signature)
          ? `; last pane output (${PANE_TAIL_PREVIEW_LINES} of ~20 lines):\n${truncateForLog(ctx.tail)}`
          : `; pane_tail_hash=sha256:${createHash('sha256').update(ctx.tail).digest('hex').slice(0, 12)} (raw at ~/.augmented/${codeName}/pane.log)`;
      const sigSummary = ctx.signature !== 'unknown' ? `; signature=${ctx.signature}` : '';
      const recoverySummary = recovery ? `; recovery=${recovery}` : '';
      log(
        `[persistent-session] Session for '${codeName}' is unhealthy ` +
          `(restart #${ctx.restartCount}${sigSummary}${recoverySummary}), will restart${tailSummary}`,
      );
    }

    // ENG-5391: if this unhealthy detection was the dead-claude-in-live-tmux
    // case, ship an audit event so we can graph occurrences. Fire-and-forget —
    // a failed POST must not block the respawn that follows.
    const zombie = takeZombieDetection(codeName);
    if (zombie) {
      log(
        `[persistent-session] ZOMBIE DETECTED for '${codeName}' — tmux session ` +
          `${zombie.tmuxSession} alive but no claude process inside; killed shell, will respawn`,
      );
      getHostId()
        .then((hostId) => {
          if (!hostId) return;
          // CodeRabbit: pane output is untrusted (echoed user prompts,
          // tool output, secrets). Same redaction policy as the manager
          // log — ship a sha256 prefix the operator can correlate with
          // ~/.augmented/<codeName>/pane.log instead of the raw text.
          const paneTailHash = zombie.paneTail
            ? `sha256:${createHash('sha256').update(zombie.paneTail).digest('hex').slice(0, 12)}`
            : null;
          return api.post('/host/events', {
            host_id: hostId,
            agent_code_name: codeName,
            event_type: 'manager.agent_zombie_detected',
            payload: {
              agent_code_name: codeName,
              agent_id: agent.agent_id,
              tmux_session: zombie.tmuxSession,
              detected_at: new Date(zombie.detectedAt).toISOString(),
              pane_tail_hash: paneTailHash,
              pane_tail_redacted: zombie.paneTail !== null,
            },
            timestamp: new Date(zombie.detectedAt).toISOString(),
          });
        })
        .catch((err) => {
          log(`[persistent-session] Failed to forward zombie audit event for '${codeName}': ${(err as Error).message}`);
        });
    }

    // Provision Stop hook for result capture (idempotent)
    try {
      provisionStopHook(codeName);
    } catch (err) {
      log(`[persistent-session] Failed to provision Stop hook for '${codeName}': ${(err as Error).message}`);
    }

    // Provision isolation hook to prevent cross-agent file access (idempotent)
    try {
      provisionIsolationHook(codeName);
    } catch (err) {
      log(`[persistent-session] Failed to provision isolation hook for '${codeName}': ${(err as Error).message}`);
    }

    // ENG-5397: provision SessionStart orient hook (idempotent). Replaces
    // --resume's transcript continuity with a fresh-session + injected
    // orientation context model.
    try {
      provisionOrientHook(codeName);
    } catch (err) {
      log(`[persistent-session] Failed to provision orient hook for '${codeName}': ${(err as Error).message}`);
    }

    // ENG-6241: provision the PostToolUse auto-progress hook (idempotent).
    // ENG-6179 shipped this hook only inside claudecode-plugin-augmented, which
    // the managed fleet never installs, so kanban cards stayed frozen on
    // "Waiting for the agent's first update…". Installing it here — alongside
    // the Stop / isolation / orient hooks — is the actual delivery path.
    try {
      provisionAutoKanbanProgressHook(codeName);
    } catch (err) {
      log(`[persistent-session] Failed to provision auto-progress hook for '${codeName}': ${(err as Error).message}`);
    }

    // ENG-6567 Phase 2: provision the PostToolUse channel-progress heartbeat hook
    // (idempotent). Must run AFTER provisionAutoKanbanProgressHook — it appends
    // its command into the PostToolUse group the kanban hook owns. Writes a
    // local heartbeat the channel MCP reads to drive the live in-thread progress
    // indicator (flag-gated, ships dark).
    try {
      provisionChannelProgressHook(codeName);
    } catch (err) {
      log(`[persistent-session] Failed to provision channel-progress hook for '${codeName}': ${(err as Error).message}`);
    }

    // ENG-6233 / ENG-6268: provision the SessionStart session-state recorder
    // (idempotent). ENG-6233 shipped this hook only inside
    // claudecode-plugin-augmented, which the managed fleet never installs, so
    // `/status` replied "session state not recorded yet" on every agent.
    // Installing it here — alongside the Stop / isolation / orient / kanban
    // hooks — is the actual fleet delivery path.
    try {
      provisionSessionStateHook(codeName);
    } catch (err) {
      log(`[persistent-session] Failed to provision session-state hook for '${codeName}': ${(err as Error).message}`);
    }

    // ENG-5051: mint a run UUID for the persistent session so Claude
    // Code's `${AGT_RUN_ID}` substitution in .mcp.json resolves to a
    // real value. Without this the cloud-broker MCP boots with the
    // literal `${AGT_RUN_ID}` placeholder, fails its startup guard,
    // and every aws_request_access 400s. Best-effort: if startRun
    // fails, we fall through to the legacy behaviour (placeholder
    // leaks; agent has to mint manually). Tied to the SESSION boot,
    // not per-tool-call — one run UUID covers everything inside this
    // long-running session, which matches the cloud-broker's
    // single-startup-guard semantics.
    const sessionRunResult = await startRun({
      agent_id: agent.agent_id,
      source_type: 'system',
      metadata: { type: 'persistent_session_boot' },
    });

    // ENG-5631: resolve the agent's model (agent override → org → platform
    // default) and hand it to the launcher so it can pass `--model <alias>`.
    // Same resolution as provisioning/registration above (resolveModelChain).
    const spawnPrimaryModel =
      resolveModelChain(refreshData).primary ??
      ((refreshData.agent as Record<string, unknown> | undefined)?.['primary_model'] as
        | string
        | null
        | undefined) ??
      null;

    // ENG-6579: derive the egress allowlist from the TRUSTED control-plane tools
    // content. Must NOT read the agent's on-disk TOOLS.md - that lives in the
    // agent's read-write mount, so sourcing the containment allowlist from it
    // would let a compromised agent widen its own egress (CodeRabbit, PR #1528).
    const egressAllowlist = deriveEgressAllowlist(
      (refreshData.tools as { raw_content?: string } | undefined)?.raw_content,
    );
    // Seed the live-refresh baseline so the first healthy tick doesn't redundantly
    // reload the sidecar for an allowlist that just got written by this spawn.
    egressAllowlistBySession.set(codeName, egressAllowlist);

    startPersistentSession({
      codeName,
      agentId: agent.agent_id,
      projectDir,
      mcpConfigPath,
      claudeMdPath,
      channels,
      devChannels,
      apiHost: requireHost(),
      claudeAuthMode,
      anthropicApiKey,
      primaryModel: spawnPrimaryModel,
      // ENG-7152: when set, the launcher points Claude Code at OpenRouter's
      // native Anthropic endpoint for this agent (supersedes claudeAuthMode).
      openRouter: openRouterForAgent
        ? {
            authToken: openRouterForAgent.authToken,
            model: openRouterForAgent.model,
            smallFastModel: openRouterForAgent.smallFastModel,
          }
        : null,
      runId: sessionRunResult.run_id,
      agentTimezone,
      egressAllowlist,
      log,
    });
    agentState.persistentSessionAgents.add(codeName);
    // Record the tuple we launched with so future polls can detect rotation.
    claudeAuthTupleBySession.set(codeName, currentAuthTuple);
    return {
      decision: 'spawn',
      spawnAttempted: true,
      sessionHealthyAfter: isSessionHealthy(codeName),
      detail: restartTrigger ? `trigger=${restartTrigger}` : undefined,
    };
  }

  // Session is healthy — reset restart counter.
  resetRestartCount(codeName);

  // ENG-6579: live egress refresh. When egress is enabled for this agent,
  // recompute the TRUSTED allowlist; if it changed since the last spawn/refresh
  // (operator edited TOOLS.md allowlist_domains), rewrite the host-only file and
  // SIGHUP the squid sidecar so the running session applies the new policy
  // without a respawn. No-op (and zero cost) when egress is off.
  if (egressMode(codeName) === 'allowlist') {
    const allowlist = deriveEgressAllowlist(
      (refreshData.tools as { raw_content?: string } | undefined)?.raw_content,
    );
    const prev = egressAllowlistBySession.get(codeName);
    if (!prev || !egressAllowlistEqual(prev, allowlist)) {
      try {
        writeEgressAllowlist(codeName, allowlist);
        // If any domain was REMOVED, restart the sidecar so in-flight CONNECT
        // tunnels to the now-removed domain are severed (a SIGHUP reload would
        // leave them alive, defeating an incident-response tightening). A purely
        // additive change can SIGHUP - zero disruption to live tunnels.
        // Unknown previous baseline (e.g. manager restarted while the tmux
        // session survived) is treated as a narrowing: we can't prove nothing
        // was removed, so restart to sever any stale tunnel (deny-by-default).
        const removed = prev ? prev.some((d) => !allowlist.includes(d)) : true;
        const applied = removed ? restartEgressSidecar(codeName) : reloadEgressSidecar(codeName);
        if (applied) {
          // Advance the baseline ONLY when the policy actually reached a running
          // sidecar. If the sidecar isn't up yet (applied=false), the file is
          // written and the (re)spawn path will apply it - leave the baseline so
          // we retry next tick. try/catch keeps an egress hiccup from aborting
          // the rest of the tick (graceful degradation).
          egressAllowlistBySession.set(codeName, allowlist);
          log(
            `[persistent-session] egress allowlist for '${codeName}' changed ` +
              `(${allowlist.length} domains), ${removed ? 'restarted' : 'reloaded'} squid sidecar`,
          );
        } else {
          log(
            `[persistent-session] egress allowlist for '${codeName}' written ` +
              `(${allowlist.length} domains) but sidecar not running; will apply on next tick/spawn`,
          );
        }
      } catch (err) {
        log(
          `[persistent-session] egress allowlist refresh for '${codeName}' failed ` +
            `(will retry next tick): ${(err as Error).message}`,
        );
      }
    }
  }

  // Stamp the baseline tuple on first healthy observation (covers the
  // manager-restart case where the tmux session survived but our Map didn't).
  if (!claudeAuthTupleBySession.has(codeName)) {
    claudeAuthTupleBySession.set(codeName, currentAuthTuple);
  }

  // Sync scheduler state from API before checking for ready tasks.
  // This ensures deleted/disabled tasks don't fire and new tasks are picked up.
  // The scheduleChanged guard in syncTasksToScheduler preserves nextFireAt.
  const stableTasksHash = createHash('sha256')
    .update(JSON.stringify(tasks))
    .digest('hex')
    .slice(0, 16);
  const prevHash = agentState.knownTasksHashes.get(agent.agent_id);
  if (stableTasksHash !== prevHash) {
    const taskInputs: SchedulerTaskInput[] = tasks.map((t) => buildSchedulerTaskInput(t));
    const schedulerState = syncTasksToScheduler(codeName, agent.agent_id, taskInputs);
    claudeSchedulerStates.set(codeName, schedulerState);
    agentState.knownTasksHashes.set(agent.agent_id, stableTasksHash);
    log(`[persistent-session] Tasks synced for '${codeName}' (${taskInputs.length} task(s))`);
  } else if (!claudeSchedulerStates.has(codeName)) {
    claudeSchedulerStates.set(codeName, loadSchedulerState(codeName));
  }

  // Inject scheduled tasks that are due
  const state = claudeSchedulerStates.get(codeName);
  if (state) {
    // ENG-4675: pass inFlightClaudeTasks so getReadyTasks filters out
    // tasks whose subprocess is still running. Without this, every
    // supervisor tick logged "N ready task(s)" + "Firing task ..." for
    // the same trigger, even though the in-flight guard below correctly
    // skipped the duplicate spawn.
    const ready = getReadyTasks(state, inFlightClaudeTasks);
    if (ready.length > 0) {
      log(`[persistent-session] ${ready.length} ready task(s) for '${codeName}': ${ready.map(t => `${t.name}(next=${t.nextFireAt ? new Date(t.nextFireAt).toISOString() : 'null'})`).join(', ')}`);
    }
    for (const task of ready) {

      // ENG-5662: kanban-work is never fired by the scheduler anymore — the
      // manager-gated hybrid edge-trigger injection (step 7e) owns it. The
      // task list is already filtered to drop kanban-work rows; this is a
      // defensive guard for any that slip through (e.g. a same-tick race).
      if (KANBAN_WORK_TEMPLATES.has(task.templateId)) {
        const updated = markTaskFired(codeName, task.taskId, 'ok');
        claudeSchedulerStates.set(codeName, updated);
        continue;
      }

      // Enrich prompt with board context
      let prompt = task.prompt;
      if (BOARD_INJECT_TEMPLATES.has(task.templateId) && boardItems.length > 0) {
        const template = PLAN_TEMPLATES.has(task.templateId) ? 'morning-plan' : 'follow-up';
        const boardPrefix = formatBoardForPrompt(boardItems, template);
        prompt = boardPrefix + prompt;
      }

      // Guard against duplicate launches (same pattern as oneshot path).
      // ENG-4675: also defends against a stale in-flight set if getReadyTasks'
      // filter is ever bypassed.
      if (inFlightClaudeTasks.has(task.taskId)) {
        continue;
      }
      if ((claudeTaskConcurrency.get(codeName) ?? 0) >= MAX_CLAUDE_CONCURRENCY) {
        break;
      }
      inFlightClaudeTasks.add(task.taskId);
      claudeTaskConcurrency.set(codeName, (claudeTaskConcurrency.get(codeName) ?? 0) + 1);

      // ENG-6849: the kanban todo-card + in-session nudge is the SOLE path for
      // scheduled tasks on a healthy persistent session - the legacy `claude -p`
      // oneshot is retired (Anthropic headless billing deprecation under Max).
      // kanban-work is the only excluded family and is skipped above; every
      // remaining template (plain / standup / task-update / plan) routes here,
      // through the ENG-6839-gated deliverScheduledCardResult so a quiet
      // `conditional` run is suppressed by default. Terminal: no oneshot
      // fallback. The helper advances the schedule and disables a one-off.
      // We're already inside the isSessionHealthy branch, so no extra health
      // check is needed.
      await fireScheduledTaskViaKanban(codeName, agent.agent_id, task, prompt);
      // No long-running subprocess on this path - release the guard.
      inFlightClaudeTasks.delete(task.taskId);
      claudeTaskConcurrency.set(codeName, Math.max(0, (claudeTaskConcurrency.get(codeName) ?? 1) - 1));
      // One task per cycle in persistent mode.
      break;
    }
  }

  // ENG-5116: we reach here only when the spawn branch was NOT entered,
  // i.e. `isSessionHealthy(codeName)` returned true at line ~5719.
  // - restartTrigger set ⇒ we stopped the session earlier on this tick
  //   but somehow it still looks healthy. That's the Vigil silent-bug
  //   signature: surface it explicitly so the caller can warn after N
  //   consecutive ticks instead of waiting hours for a customer to
  //   notice. (Pre-ENG-5116 this collapsed into the same "tasks synced"
  //   log as a normal healthy tick.)
  // - dayRolloverDeferred set ⇒ stale-for-today but agent mid-turn;
  //   benign, will retry next tick.
  // - otherwise ⇒ ordinary healthy steady state.
  const finalDecision: PersistentSessionDecision =
    restartTrigger === 'auth-tuple'
      ? 'restart-pending'
      : restartTrigger === 'day-rollover'
        ? 'day-rollover-restart'
        : dayRolloverDeferred
          ? 'day-rollover-deferred'
          : 'healthy';
  return {
    decision: finalDecision,
    spawnAttempted: false,
    sessionHealthyAfter: isSessionHealthy(codeName),
  };
}

// ---------------------------------------------------------------------------
// Supabase Realtime for direct chat (replaces 2s polling)
// ---------------------------------------------------------------------------

import {
  startRealtimeChat,
  startRealtimeDrift,
  startRealtimeAssignments,
  startRealtimeConfig,
  startRealtimeKanban,
  startRealtimeIntegrationContext,
  stopRealtimeIntegrationContext,
  stopRealtimeChat,
  isRealtimeConnected,
  isRealtimeSocketConnected,
  shouldForcePollFallback,
  updateRealtimeToken,
} from './realtime-chat.js';

let realtimeStarted = false;
let realtimeDriftStarted = false;
let realtimeKanbanStarted = false;
// Snapshot of the agent_ids currently subscribed to the realtime
// `plugin_context` channel. Empty set ⇒ not yet subscribed. Compared
// against the current active-agent set each tick so an agent
// activated after manager startup also gets realtime context updates
// (ENG-4725 — previously this used a one-shot boolean and silently
// missed any agents that came online later).
let subscribedIntegrationContextAgentIds: Set<string> = new Set();
let realtimeAssignStarted = false;
let realtimeConfigStarted = false;
let realtimeSubscribedAgentIds = new Set<string>();

/**
 * ENG-5614: reconcile the realtime channel-subscription flag against the
 * actual phoenix socket state. A half-open websocket can leave
 * `isRealtimeConnected()` reporting `true` while the socket is dead and the
 * channel's CLOSED/CHANNEL_ERROR callback never fired — inbound direct-chat
 * delivery silently stops, yet the manager keeps skipping the polling
 * fallback (it only polls `if (!isRealtimeConnected())`). The manager
 * heartbeat stays fresh the whole time (this same poll cycle posts it), so
 * nothing else catches it.
 *
 * On divergence we tear the realtime client down (which clears the flag and
 * nulls the client) and reset the subscription flags so `ensureRealtime
 * Started()` rebuilds a fresh client+subscription next tick. Because the flag
 * is now false, the polling fallback runs THIS cycle — inbound delivery is
 * restored immediately rather than waiting on the ~125-min synthetic-probe
 * backstop. Returns true when a heal was performed (for heartbeat reporting).
 */
function healRealtimeSocketDivergence(): boolean {
  if (!shouldForcePollFallback(isRealtimeConnected(), isRealtimeSocketConnected())) return false;
  log('[realtime] Socket divergence — channel flag connected but socket dead; forcing poll fallback + reconnect');
  stopRealtimeChat();
  realtimeStarted = false;
  realtimeDriftStarted = false;
  realtimeAssignStarted = false;
  realtimeConfigStarted = false;
  realtimeKanbanStarted = false;
  subscribedIntegrationContextAgentIds = new Set();
  return true;
}

function ensureRealtimeStarted(agentStates: AgentState[]): void {
  // Check if new agents have appeared since last subscription
  const currentActiveIds = new Set(
    agentStates.filter((a) => a.status === 'active').map((a) => a.agentId),
  );
  if (realtimeStarted) {
    // Detect any change: additions or removals
    const sameSize = currentActiveIds.size === realtimeSubscribedAgentIds.size;
    const sameMembers = sameSize && [...currentActiveIds].every((id) => realtimeSubscribedAgentIds.has(id));
    if (sameMembers) return;
    // Agent set changed — tear down and reconnect with fresh set
    log('[realtime] Agent set changed — reconnecting subscriptions');
    stopRealtimeChat();
    realtimeStarted = false;
    realtimeDriftStarted = false;
    realtimeAssignStarted = false;
    realtimeConfigStarted = false;
    realtimeKanbanStarted = false;
    subscribedIntegrationContextAgentIds = new Set();
  }

  const activeAgentIds = agentStates
    .filter((a) => a.status === 'active')
    .map((a) => a.agentId);

  if (activeAgentIds.length === 0) return;

  const apiKey = process.env['AGT_API_KEY'];
  if (!apiKey) return;

  // Get Supabase config + token from the cached exchange result
  void exchangeApiKey(apiKey).then((exchange) => {
    if (!exchange.supabaseUrl || !exchange.supabaseAnonKey) {
      log('[realtime-chat] No Supabase URL/key from exchange — staying on polling');
      return;
    }

    startRealtimeChat({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      agentIds: activeAgentIds,
      onMessage: (msg) => {
        const agent = agentStates.find((a) => a.agentId === msg.agent_id);
        if (!agent) return;

        if (directChatInFlight.has(msg.id)) return;
        directChatInFlight.add(msg.id);

        processDirectChatMessage(agent, {
          id: msg.id,
          session_id: msg.session_id,
          content: msg.content,
        }).finally(() => {
          directChatInFlight.delete(msg.id);
        });
      },
      onError: (err) => {
        log(`[realtime-chat] Error: ${err.message}`);
      },
      onStatusChange: (status) => {
        if (status === 'disconnected' || status === 'error') {
          log('[realtime] Disconnected — falling back to polling, will reconnect next cycle');
          // Reset all subscription flags so they reconnect with a fresh client
          realtimeStarted = false;
          realtimeDriftStarted = false;
          realtimeAssignStarted = false;
          realtimeConfigStarted = false;
          realtimeKanbanStarted = false;
          subscribedIntegrationContextAgentIds = new Set();
        }
      },
      log,
    });

    realtimeStarted = true;
    realtimeSubscribedAgentIds = new Set(activeAgentIds);
    log(`[realtime-chat] Started for ${activeAgentIds.length} agent(s)`);
  }).catch((err) => {
    log(`[realtime-chat] Failed to start: ${(err as Error).message}`);
  });
}

function ensureRealtimeDriftStarted(agentStates: AgentState[]): void {
  if (realtimeDriftStarted) return;

  const activeAgentIds = agentStates.filter((a) => a.status === 'active').map((a) => a.agentId);
  if (activeAgentIds.length === 0) return;

  const apiKey = process.env['AGT_API_KEY'];
  if (!apiKey) return;

  void exchangeApiKey(apiKey).then((exchange) => {
    if (!exchange.supabaseUrl || !exchange.supabaseAnonKey) return;

    startRealtimeDrift({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      agentIds: activeAgentIds,
      onDrift: (doc) => {
        // Invalidate the known version so next poll cycle re-provisions.
        // ADR-0011 PR-A: local renamed `agentState` → `agent` to disambiguate
        // from the imported `agentState` module namespace.
        const agent = agentStates.find((a) => a.agentId === doc.agent_id);
        if (agent) {
          agentState.knownVersions.delete(doc.agent_id);
          log(`[realtime] Drift invalidated for '${agent.codeName}' — will re-provision next cycle`);
        }
      },
      log,
    });

    realtimeDriftStarted = true;
    log(`[realtime] Drift subscription started for ${activeAgentIds.length} agent(s)`);
  }).catch((err) => {
    log(`[realtime] Drift subscription failed: ${(err as Error).message}`);
  });
}

// ENG-7335: agents currently being serviced by the fast-restart lane. Both the
// lane below and the poll's restart-servicing loop consult this so the two paths
// can't double-kill/double-respawn the same agent. This is the narrow per-agent
// lock the doorbell design uses IN PLACE OF a whole-cycle re-entrancy guard: the
// lane only ever touches ONE agent, so mutual exclusion is per-agent, not global.
const restartInFlight = new Set<string>();

/**
 * ENG-7335: fast agent-restart lane. Invoked by the host_agents restart doorbell
 * (a Realtime UPDATE on restart_requested_at) to service a dashboard-issued
 * restart within a few seconds, instead of waiting up to 50-110s for the next
 * full poll cycle to notice it.
 *
 * It reuses the SAME spawn path the poll uses - ensurePersistentSession over the
 * agent's already-provisioned on-disk artifacts plus a fresh single-agent
 * /host/refresh (for live channel/timezone/auth config). A restart re-launches an
 * already-provisioned agent, so the heavier provisioning side effects (.mcp.json,
 * settings, secrets) are already on disk from the last poll; the next full poll
 * reconciles any drift. There is deliberately NO second spawn code path to drift.
 *
 * Advisory + backstopped by design: gated on the restart-doorbell flag; on ANY
 * failure it does NOT ack, so restart_requested_at stays set and
 * lastRestartProcessedAt stays un-advanced, and the slow poll re-services it.
 * Correctness never depends on the doorbell firing - it only collapses latency.
 */
async function handleRestartDoorbell(agentId: string, requestedAt: string): Promise<void> {
  // Flag gate: env override (AGT_RESTART_DOORBELL_ENABLED) > heartbeat flag >
  // compiled default (off). Re-read per invocation so a central flip takes effect
  // on the next restart without re-subscribing.
  if (!hostFlagStore().getBoolean('restart-doorbell')) return;

  // Per-agent lock - never run two lane passes for the same agent concurrently.
  if (restartInFlight.has(agentId)) return;

  const prev = state.agents.find((a) => a.agentId === agentId);
  const codeName = prev?.codeName;
  // Unknown agent (not assigned to this manager yet, or assigned mid-flight):
  // let the next full poll provision + service it. Never ack what we didn't do.
  if (!codeName) return;

  // Durable dedup - identical semantics to the poll's alreadyServiced guard. If
  // we (or a prior manager process, via persisted state) already serviced this
  // exact request, do nothing.
  const lastProcessed = prev?.lastRestartProcessedAt ?? null;
  if (lastProcessed != null && Date.parse(lastProcessed) >= Date.parse(requestedAt)) return;

  restartInFlight.add(agentId);
  try {
    log(`[restart-lane] Fast restart for '${codeName}' at ${requestedAt}`);
    const hostId = await getHostId();

    // One-agent refresh for the live spawn config (channels/timezone/auth). The
    // provisioning side effects it would normally drive are already on disk.
    let refreshData: Record<string, unknown>;
    try {
      refreshData = await api.post<Record<string, unknown>>('/host/refresh', { agent_id: agentId });
    } catch (err) {
      log(`[restart-lane] refresh failed for '${codeName}', leaving for slow poll: ${(err as Error).message}`);
      return;
    }
    const displayName =
      (refreshData.agent as { display_name?: string } | undefined)?.display_name ?? codeName;

    // Kill the current session so ensurePersistentSession cold-starts a fresh one
    // (same mechanism the poll's servicing loop uses).
    try {
      const { execSync: es } = await import('node:child_process');
      es(`tmux kill-session -t agt-${codeName} 2>/dev/null`, { stdio: 'ignore' });
    } catch { /* no session to kill - the respawn starts fresh */ }
    stopPersistentSessionAndForgetMcpBaseline(codeName);

    // Respawn via the shared spawn path (tasks/boardItems empty - the kanban
    // nudge is a post-spawn concern the next poll handles).
    const result = await ensurePersistentSession(
      { agent_id: agentId, code_name: codeName, display_name: displayName },
      [],
      [],
      refreshData,
    );

    if (result.decision !== 'spawn' || !result.sessionHealthyAfter) {
      // Respawn didn't confirm (circuit-breaker trip, auth, spawn failure). Do
      // NOT ack - leave restart_requested_at set so the slow poll retries and, if
      // it's a persistent condition, surfaces it through the normal path.
      log(`[restart-lane] respawn not confirmed for '${codeName}' (decision=${result.decision}) - leaving for slow poll`);
      return;
    }

    // Confirmed respawn. Ack (clears restart_requested_at) AFTER the new session
    // exists, so the dashboard's startedAt>requested signal can't resolve against
    // a not-yet-respawned session. Best-effort; race-safe server-side (only nulls
    // when the stored value still equals requestedAt).
    void api
      .post('/host/restart-ack', { host_id: hostId, agent_id: agentId, restart_requested_at: requestedAt })
      .catch((err) => log(`[restart-lane] ack failed for '${codeName}': ${(err as Error).message}`));

    // ENG-7335: flush a diagnostics-only heartbeat now so the respawned session's
    // fresh startedAt lands within seconds (mirrors the slow path's ENG-7328
    // flush). Without it the dashboard's startedAt>restart_requested_at signal
    // would wait for the next regular heartbeat before confirming "back online".
    void (async () => {
      try {
        const { collectDiagnostics } = await import('./persistent-session.js');
        await api.post('/host/heartbeat', {
          host_id: hostId,
          agent_diagnostics: collectDiagnostics([codeName]),
        });
      } catch (err) {
        log(`[restart-lane] post-respawn diagnostics flush failed for '${codeName}': ${(err as Error).message}`);
      }
    })();

    // Stamp the durable dedup so neither the slow poll nor a manager restart
    // re-fires this request. Persist immediately (crash-replay guard, mirroring
    // the poll's applyRestartAcks immediate write).
    prev.lastRestartProcessedAt = requestedAt;
    try {
      atomicWriteFileSync(getStateFile(), JSON.stringify(state, null, 2));
    } catch (err) {
      log(`[restart-lane] failed to persist ack for '${codeName}': ${(err as Error).message}`);
    }
    log(`[restart-lane] '${codeName}' respawned via fast lane`);
  } finally {
    restartInFlight.delete(agentId);
  }
}

function ensureRealtimeAssignStarted(agentStates: AgentState[]): void {
  if (realtimeAssignStarted) return;

  const apiKey = process.env['AGT_API_KEY'];
  if (!apiKey) return;

  void exchangeApiKey(apiKey).then((exchange) => {
    if (!exchange.supabaseUrl || !exchange.supabaseAnonKey || !exchange.hostId) return;

    startRealtimeAssignments({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      hostId: exchange.hostId,
      // ENG-7335: fast-restart doorbell. Fires the narrow lane; the lane itself is
      // flag-gated and backstopped by the slow poll, so wiring it unconditionally
      // is safe (a no-op when the flag is off).
      onRestartRequested: (payload) => {
        void handleRestartDoorbell(payload.agent_id, payload.restart_requested_at).catch((err) =>
          // Never let a fast-lane failure become an unhandled rejection - the slow
          // poll backstop re-services the (still-set) restart_requested_at.
          log(`[restart-lane] doorbell handler failed for ${payload.agent_id}: ${(err as Error).message}`),
        );
      },
      onAssign: (payload) => {
        log(`[realtime] Agent ${payload.agent_id} assigned — will pick up next cycle`);
        // ENG-4643: stamp the agent for a fresh memory pull on its next
        // sync tick. Without this, a long-running manager that hosted
        // this agent before its current migration could still have
        // memoryFileHashes / lastDownloadHash entries from the prior
        // assignment and skip the catch-up download (the local-list-
        // hash short-circuit in syncMemories sees "nothing changed"
        // and returns). Clearing the caches + flagging for a forced
        // download-first pass keeps the new host in sync with the DB.
        //
        // Deliberately not invoking syncMemories from here. The
        // realtime payload only carries agent_id; syncMemories needs
        // {agent_id, code_name} plus the supervisor's configDir, none
        // of which are reachable from this callback's closure. The
        // next supervisor tick (interval default 10s) iterates the
        // freshly-loaded agent list and consumes the pending flag,
        // so the latency cost of waiting one tick is bounded and
        // the alternative (extra API call to resolve code_name from
        // here, plus duplicating the sync entrypoint plumbing) is
        // not worth the saved seconds.
        markAgentForFreshMemorySync(payload.agent_id);
      },
      onUnassign: (payload) => {
        log(`[realtime] Agent ${payload.agent_id} unassigned`);
        clearAgentCaches(payload.agent_id, '');
      },
      log,
    });

    realtimeAssignStarted = true;
    log(`[realtime] Assignment subscription started for host ${exchange.hostId}`);
  }).catch((err) => {
    log(`[realtime] Assignment subscription failed: ${(err as Error).message}`);
  });
}

function ensureRealtimeConfigStarted(agentStates: AgentState[]): void {
  if (realtimeConfigStarted) return;

  const activeAgentIds = agentStates.filter((a) => a.status === 'active').map((a) => a.agentId);
  if (activeAgentIds.length === 0) return;

  const apiKey = process.env['AGT_API_KEY'];
  if (!apiKey) return;

  void exchangeApiKey(apiKey).then((exchange) => {
    if (!exchange.supabaseUrl || !exchange.supabaseAnonKey) return;

    startRealtimeConfig({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      agentIds: activeAgentIds,
      onConfigChange: (agent) => {
        // Only invalidate status cache — version/provision caches are handled by drift subscription.
        // Don't invalidate agentState.knownVersions here as that triggers re-provision which updates
        // updated_at, creating a feedback loop with this Realtime subscription.
        agentState.knownStatuses.delete(agent.agent_id);
      },
      log,
    });

    realtimeConfigStarted = true;
    log(`[realtime] Config subscription started for ${activeAgentIds.length} agent(s)`);
  }).catch((err) => {
    log(`[realtime] Config subscription failed: ${(err as Error).message}`);
  });
}

function ensureRealtimeKanbanStarted(agentStates: AgentState[]): void {
  if (realtimeKanbanStarted) return;

  const activeAgentIds = agentStates.filter((a) => a.status === 'active').map((a) => a.agentId);
  if (activeAgentIds.length === 0) return;

  const apiKey = process.env['AGT_API_KEY'];
  if (!apiKey) return;

  void exchangeApiKey(apiKey).then((exchange) => {
    if (!exchange.supabaseUrl || !exchange.supabaseAnonKey) return;

    startRealtimeKanban({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      agentIds: activeAgentIds,
      onTodayItem: (item) => {
        // ENG-5599: scheduled-task cards are already nudged by
        // routeScheduledTaskViaKanban with their own per-card run marker. The
        // generic kanban-work trigger here would double-nudge them (and the
        // second nudge carries no marker, so any turns it provokes are
        // unattributed) — skip them.
        if (item.source_type === 'scheduled_task') return;

        // Trigger kanban-work immediately for this agent
        const agent = agentStates.find((a) => a.agentId === item.agent_id);
        if (!agent) return;

        const agentFw = agentFrameworkCache.get(agent.codeName) ?? DEFAULT_FRAMEWORK;

        if (agentFw === 'claude-code') {
          if (isSessionHealthy(agent.codeName)) {
            // ENG-5405: include the item's id in the inject so the agent
            // can call kanban_move(id, "in_progress") directly instead of
            // having to kanban_list first to look up its UUID.
            // CodeRabbit PR #1275: JSON.stringify the title — see the
            // ensurePersistentSession poll-driven trigger above for the
            // same hardening rationale (user-authored content can break
            // the injected prompt shape).
            injectMessage(agent.codeName, 'task', `New task added to your board: id=${item.id} title=${JSON.stringify(item.title)} priority=${item.priority}. Call kanban_move("${item.id}", "in_progress") and start working.`, {
              task_name: 'kanban-work-trigger',
            }, log);
            log(`[realtime] Injected kanban-work trigger for '${agent.codeName}': "${item.title}"`);
          } else {
            // ENG-5662: session still booting — skip. The manager-gated hybrid
            // edge-trigger (step 7e) will pick this item up on the next poll
            // once the session is healthy; there's no claude -p fallback now.
            log(`[realtime] Work trigger skipped for '${agent.codeName}' — session not yet healthy; hybrid will pick up "${item.title}"`);
          }
        }
      },
      // ENG-4507: kanban completion notification — surfaces user-driven
      // closures to the daemon log + telemetry surface. The agent-runtime
      // ingestion path is the kanban_list `closed_by` annotation (no live
      // injection in this slice — the agent reads its board between turns
      // and sees the new state naturally).
      onCompletion: (event) => {
        const agent = agentStates.find((a) => a.agentId === event.agent_id);
        const codeName = agent?.codeName ?? event.agent_id;
        log(
          `[realtime] Kanban completion forwarded for '${codeName}': ` +
          `item=${event.item_id} status=${event.status} actor=${event.last_actor_id ?? 'unknown'}`,
        );
        // ENG-5601: secondary delivery path, for a HUMAN completing the agent's
        // scheduled-task card from the console. Two gates, both load-bearing:
        //   1. `user:` actor only. Agent self-completion (the in-session happy
        //      path) is suppressed upstream by isSelfCompletion and handled by
        //      the poll path.
        //   2. Manager-ROUTED cards only (isScheduledCardTracked). Restricting to
        //      tracked cards matches the poll path, which only ever iterates
        //      tracked cards.
        // deliverScheduledCardResult shares the idempotency claim with the poll
        // path, so a human closing a card the poll already delivered is a no-op.
        // ENG-6849: the AGT_SCHEDULED_VIA_KANBAN gate is retired (the claude -p
        // oneshot it guarded is gone); manager-side delivery is the only model.
        if (
          event.status === 'done' &&
          classifyActor(event.last_actor_id, event.agent_id) === 'user' &&
          isScheduledCardTracked(event.item_id)
        ) {
          // ENG-6546: explicitly 'user' — a human closed this card from the
          // console, so deliverScheduledCardResult must NOT parse their free text
          // as a standup / task-update side effect into /host/agent-status. Only
          // the self-completion poll path (completedBy='self') runs side effects.
          void deliverScheduledCardResult(codeName, event.agent_id, event.item_id, 'user');
        }
      },
      log,
    });

    realtimeKanbanStarted = true;
    log(`[realtime] Kanban subscription started for ${activeAgentIds.length} agent(s)`);
  }).catch((err) => {
    log(`[realtime] Kanban subscription failed: ${(err as Error).message}`);
  });
}

// ---------------------------------------------------------------------------
// Integration context realtime (ENG-4342)
//
// Subscribes to plugin_context table changes for the manager's agents and
// triggers an early poll cycle whenever a context row is inserted or
// updated. The early poll is what actually re-renders SKILL.md — this
// realtime channel just collapses the latency from "next poll cycle"
// (~60s) to "next event loop tick after the change" (~100ms + RTT).
// ---------------------------------------------------------------------------

function activeAgentIdSetsEqual(current: string[], previous: Set<string>): boolean {
  if (current.length !== previous.size) return false;
  for (const id of current) if (!previous.has(id)) return false;
  return true;
}

function ensureRealtimeIntegrationContextStarted(agentStates: AgentState[]): void {
  const activeAgentIds = agentStates.filter((a) => a.status === 'active').map((a) => a.agentId);

  // No active agents → tear down any existing subscription so we don't
  // hold a channel filter for agents we no longer manage.
  if (activeAgentIds.length === 0) {
    if (subscribedIntegrationContextAgentIds.size > 0) {
      stopRealtimeIntegrationContext();
      subscribedIntegrationContextAgentIds = new Set();
      log('[realtime] Integration context subscription torn down (no active agents)');
    }
    return;
  }

  // Already subscribed to exactly this set → nothing to do.
  if (activeAgentIdSetsEqual(activeAgentIds, subscribedIntegrationContextAgentIds)) return;

  const apiKey = process.env['AGT_API_KEY'];
  if (!apiKey) return;

  // Active set changed (agent activated, deactivated, reassigned, etc.).
  // Tear down the existing channel so we can re-subscribe with the
  // updated `agent_id=in.(...)` filter — Supabase realtime filters are
  // immutable per channel, so a re-bind is the only path.
  const isResubscribe = subscribedIntegrationContextAgentIds.size > 0;
  if (isResubscribe) {
    stopRealtimeIntegrationContext();
  }

  // Snapshot the set we're about to subscribe to before the async work
  // so a concurrent tick sees the in-flight subscription and skips.
  // exchangeApiKey resolution happens off-tick; if it fails, we reset
  // below so the next tick retries.
  const targetSet = new Set(activeAgentIds);
  subscribedIntegrationContextAgentIds = targetSet;

  void exchangeApiKey(apiKey).then((exchange) => {
    // A newer tick may have replaced the snapshot (e.g. agent set changed
    // again, or the broader chat client disconnected and reset all flags).
    // Bail without mutating state or starting a now-stale subscription —
    // identity comparison is enough because we only ever assign fresh
    // `Set` instances to the module-level snapshot.
    if (subscribedIntegrationContextAgentIds !== targetSet) return;

    if (!exchange.supabaseUrl || !exchange.supabaseAnonKey) {
      subscribedIntegrationContextAgentIds = new Set();
      return;
    }

    startRealtimeIntegrationContext({
      supabaseUrl: exchange.supabaseUrl,
      supabaseAnonKey: exchange.supabaseAnonKey,
      token: exchange.token,
      agentIds: [...targetSet],
      onContextChange: (payload) => {
        // Invalidate the affected agent's cached plugin-skill hashes so the
        // next poll definitely re-renders SKILL.md (the hash check would
        // otherwise be a no-op if the LLM-rendered output happens to be
        // identical, which is rare but possible). Then trigger an early poll.
        const agent = agentStates.find((a) => a.agentId === payload.agent_id);
        if (agent) {
          // Match the cache key shape used when hashes are written in the
          // refresh loop: `plugin-skill:${agent.agent_id}:...`. Using
          // codeName here (as the original version did) silently failed to
          // invalidate anything, so plugin-context changes could be skipped
          // until some unrelated bundle change happened or the manager
          // restarted.
          for (const key of agentState.knownSkillHashes.keys()) {
            if (key.startsWith(`plugin-skill:${agent.agentId}:`)) {
              agentState.knownSkillHashes.delete(key);
            }
          }
        }
        triggerEarlyPoll(`integration context changed for agent ${payload.agent_id}`);
      },
      log,
    });

    log(
      `[realtime] Integration context subscription ${isResubscribe ? 're-bound' : 'started'} for ${targetSet.size} agent(s)`,
    );
  }).catch((err) => {
    // Reset so the next tick retries — but only if we're still the
    // owning snapshot. Otherwise a newer tick has already taken over
    // and clearing here would silently undo its in-flight work.
    if (subscribedIntegrationContextAgentIds === targetSet) {
      subscribedIntegrationContextAgentIds = new Set();
    }
    log(`[realtime] Integration context subscription failed: ${(err as Error).message}`);
  });
}

/**
 * Cancel the scheduled poll and run a new poll cycle on the next event-loop
 * tick. Used by realtime callbacks (plugin context, etc.) to collapse the
 * latency between an external change and the manager observing it.
 *
 * Idempotent — multiple triggers within the same tick coalesce into one
 * poll because pollCycle() is awaited and scheduleNext() only fires after
 * it completes.
 */
function triggerEarlyPoll(reason: string): void {
  if (!running) return;
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  log(`[realtime] Triggering early poll: ${reason}`);
  // Schedule on the next tick so we don't run reentrantly inside a Realtime
  // callback. scheduleNext() will fire on completion.
  pollTimer = setTimeout(() => {
    void pollCycle().then(() => {
      // scheduleNext() is the normal post-poll re-arm. Forward declared at
      // the bottom of this file; safe to reference here because the function
      // declaration is hoisted.
      scheduleNext();
    });
  }, 0);
}

// ---------------------------------------------------------------------------
// Direct chat — poll for pending messages (fallback when Realtime is down)
// ---------------------------------------------------------------------------

// Track in-flight direct chat messages to avoid double-processing
const directChatInFlight = new Set<string>();

async function pollDirectChatMessages(agentStates: AgentState[]): Promise<void> {
  for (const agent of agentStates) {
    if (agent.status !== 'active') continue;

    try {
      const data = await api.post<{
        messages: Array<{
          id: string;
          session_id: string;
          content: string;
          created_at: string;
        }>;
      }>('/host/direct-chat/poll', { agent_id: agent.agentId });

      for (const msg of data.messages) {
        if (directChatInFlight.has(msg.id)) continue;
        directChatInFlight.add(msg.id);

        // Process async so we don't block the poll cycle
        processDirectChatMessage(agent, msg).finally(() => {
          directChatInFlight.delete(msg.id);
        });
      }
    } catch (err) {
      log(`Direct chat poll failed for '${agent.codeName}': ${(err as Error).message}`);
    }
  }
}

async function processDirectChatMessage(
  agent: AgentState,
  // `created_at` is present on the poll/backlog path (the replay path) and
  // absent on the Realtime path (those messages are by definition fresh). It
  // drives the backlog-staleness expiry below.
  msg: { id: string; session_id: string; content: string; created_at?: string },
): Promise<void> {
  // ENG-5963: record inbound activity for the defer-until-idle restart gate —
  // stamped before the expiry/delivery branches so even a drained backlog
  // message counts as "the agent was just addressed".
  noteInbound(agent.codeName);
  const fw = agentFrameworkCache.get(agent.codeName) ?? DEFAULT_FRAMEWORK;
  log(`[direct-chat] Processing message for '${agent.codeName}' (fw=${fw}): id=${msg.id} len=${msg.content.length}`);

  // Backlog-staleness expiry (checked BEFORE any delivery attempt). A backlog
  // message that replayed long after the user gave up isn't worth delivering at
  // all. Drain it with a terminal notice (visible degradation, not silent loss)
  // so it leaves the pending set and never replays again. Realtime messages
  // carry no `created_at` and so never expire here; expiry is disabled entirely
  // when the window is 0.
  if (isDirectChatMessageExpired(msg.created_at, Date.now(), directChatMaxAgeMs())) {
    log(`[direct-chat] Expired backlog message for '${agent.codeName}' (msg=${msg.id}, created_at=${msg.created_at}) — draining without delivery`);
    try {
      await api.post('/host/direct-chat/reply', {
        agent_id: agent.agentId,
        session_id: msg.session_id,
        content: '[Expired] This message timed out before it could be processed. Please resend it.',
      });
    } catch {
      // Best-effort drain — if the reply post fails the poll will retry the
      // message on the next cycle.
    }
    return;
  }

  // ENG-4778: when a persistent session is running for this agent, deliver the
  // direct-chat message into it (doorbell ring, or send-keys inject as a
  // fallback) so the agent answers with full conversation history. Inject as a
  // `<channel>` tag so the agent's existing system-prompt instructions for
  // direct-chat (call `direct_chat.reply` with the session_id) still apply.
  //
  // ENG-6850: there is no `claude -p` one-shot fallback anymore. A claude-code
  // agent with no healthy session (or an unverified inject) leaves the message
  // PENDING on the durable queue (ENG-5927) so it redelivers on the next poll /
  // session recovery — see the early-return at the end of this function.
  if (isSessionHealthy(agent.codeName)) {
    // ENG-5927 (PR-2): doorbell + pull-cursor rail (ADR-0020 §Decision 4),
    // gated default-OFF behind AGT_DIRECT_CHAT_DOORBELL_ENABLED. When enabled we
    // ring a *content-free doorbell* — touch <agentDir>/direct-chat-doorbell —
    // instead of send-keys injecting the content. The agent's in-session
    // direct-chat MCP watches that file, PULLS via the capped
    // /host/direct-chat/poll claim, and pushes the message as a native
    // notifications/claude/channel (no keystrokes). The durable queue is the
    // cursor: the row advances pending→processing on the MCP's claim and
    // →delivered when the agent replies. A dropped ring costs only latency (the
    // MCP's safety-net poll drains it), so the ring needs no ack. On a ring
    // failure we fall through to the existing send-keys path — silent loss is
    // worse than a possible duplicate.
    // ENG-6818: isolated agents MUST use the doorbell rail. Under Docker isolation
    // the agent runs as `docker run -it` inside the pane, so `tmux send-keys`
    // lands in the host pane and never reaches the in-container claude — yet
    // defaultArmSender reports success, so the message is silently lost (3 dots,
    // no reply). The doorbell+pull rail is container-safe (HTTP poll + a
    // bind-mounted doorbell file), and the in-container MCP has it force-enabled
    // via AGT_DIRECT_CHAT_DOORBELL_ENABLED (buildDockerRunCommand). A failed ring
    // here is not fatal: the MCP's 30s safety-net poll still drains the pending row.
    const useDoorbell =
      hostFlagStore().getBoolean('direct-chat-doorbell') ||
      isolationMode(agent.codeName) === 'docker';
    if (useDoorbell) {
      try {
        const doorbell = directChatDoorbellPath(agent.agentId, homedir());
        mkdirSync(dirname(doorbell), { recursive: true });
        writeFileSync(doorbell, String(Date.now()));
        log(`[direct-chat] Doorbell rung for '${agent.codeName}' (msg=${msg.id}) — in-session MCP will pull via the cursor`);
        return;
      } catch (err) {
        // For isolated agents send-keys can't reach the container, so don't fall
        // through to it — the row stays pending and the MCP safety-net poll drains
        // it. For host agents, fall through to send-keys as before.
        if (isolationMode(agent.codeName) === 'docker') {
          log(`[direct-chat] Doorbell ring failed for isolated '${agent.codeName}' (msg=${msg.id}): ${(err as Error).message} — leaving pending for the in-session safety-net poll`);
          return;
        }
        log(`[direct-chat] Doorbell ring failed for '${agent.codeName}' (msg=${msg.id}): ${(err as Error).message} — falling back to send-keys inject`);
        // fall through to the send-keys path below
      }
    }
    // CodeRabbit (PR #748): escape session_id + content before splicing
    // into the XML-shaped envelope so a payload containing quotes,
    // angle brackets, or a literal `</channel>` can't break the
    // envelope structure (and possibly steer the agent's downstream
    // routing). Apply to both attribute values and body for safety —
    // escaped < / > in the body still render legibly to the agent.
    const escapeXml = (value: string): string =>
      value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&apos;');
    // ENG-6407 / ADR-0024 Slice 1: stamp provenance + reply-obligation on the
    // tag at the source. A manager-injected direct-chat message is conversational
    // and reply-expected. The fragment is a closed enum / boolean, so it needs no
    // escaping. Additive only - no consumer gates on these yet (Slice 2).
    const channelEnvelope =
      `<channel source="direct-chat" session_id="${escapeXml(msg.session_id)}" user="webapp" ${laneTagFragment('conversational', true)}>\n` +
      `${escapeXml(msg.content)}\n` +
      `</channel>`;
    const delivered = await injectMessage(
      agent.codeName,
      'chat',
      channelEnvelope,
      { task_name: 'direct-chat' },
      log,
    );
    if (delivered) {
      log(`[direct-chat] Injected into persistent session for '${agent.codeName}' (msg=${msg.id}); agent will reply via direct_chat.reply tool`);
      return;
    }
    // ENG-6850: injectMessage returned false — in-session delivery is
    // `tmux send-keys`, which doesn't guarantee submission. There is no
    // `claude -p` one-shot fallback anymore, so don't strand or duplicate:
    // leave the message PENDING (the claude-code early-return below) and let
    // ENG-5927 durable replay redeliver it on the next poll / session recovery.
    // The backlog-staleness expiry above is the terminal backstop.
    log(`[direct-chat] Inject reported unverified for '${agent.codeName}' (msg=${msg.id}) — leaving pending for durable replay`);
  }

  // ENG-6850: claude-code agents have no one-shot `claude -p` fallback. Reaching
  // here means the message wasn't delivered in-session (no healthy session, a
  // doorbell ring that failed on a host agent, or an unverified send-keys
  // inject) — leave it PENDING so ENG-5927 durable replay redelivers on the next
  // poll / session recovery. A message that goes stale is drained by the expiry
  // check above with a terminal notice (visible degradation, never silent loss).
  log(`[direct-chat] No in-session delivery for '${agent.codeName}' (msg=${msg.id}) — leaving pending for ENG-5927 durable replay`);
}

// ---------------------------------------------------------------------------
// Cron result harvesting — polls cron run history and updates agent status
// ---------------------------------------------------------------------------

// Template-id classification, the hybrid-actionable predicate, and the kanban
// LLM-output parsers now live in ./manager/kanban/parsers.js (ADR-0011 leaf
// extraction); the stateful inject/harvest code below imports from there.

// Module-level debounce state. See design-doc Q3 for the
// "no manager-state.json persistence" rationale.
const lastKanbanInjectAt = new Map<string, number>();

// ENG-5565: the currently-open injected `runs` row per agent (codeName →
// run_id). Each manager-injected kanban nudge opens a run; the next inject
// (the agent is idle again, so the prior work is done) closes it. The run_id
// is also written into the injected prompt as a marker (see formatRunMarker)
// so the per-turn transcript parser can attribute that run's token usage.
// At most one open injected run per agent; the last one stays open until the
// next inject (or session end) — fine, since attribution is marker-bounded.
const openInjectedRunByCode = new Map<string, string>();

// ENG-5565: close an agent's open injected run if one exists. Fire-and-forget
// (the map is drained synchronously; finishRun POSTs in the background). Called
// when the board drains (kanban work done), the next inject supersedes it, or
// the session is torn down — so a run never hangs open past its real
// prompt-ready boundary, which would mis-attribute later turns. A manager
// restart that loses this in-memory map can't close in-process; a server-side
// stale-run reaper is the backstop there (tracked for B3/hardening).
function closeInjectedRunIfOpen(
  codeName: string,
  outcome: 'completed' | 'failed' | 'cancelled' | 'timeout',
  outcomeMessage: string,
): void {
  const runId = openInjectedRunByCode.get(codeName);
  if (!runId) return;
  openInjectedRunByCode.delete(codeName);
  void finishRun(runId, outcome, { outcomeMessage });
}

// Floor at 10s — anything tighter risks pummelling the REPL when the
// agent's first response itself adds new actionable items.
// CR (CodeRabbit on PR #1293): clamp sub-10 overrides to 10s rather
// than discarding them and falling back to the 60s default. An operator
// who sets `=5` is asking for "as fast as you can safely go", not "give
// me a slow default."
const KANBAN_HYBRID_DEBOUNCE_MS = (() => {
  const raw = process.env['AGT_KANBAN_HYBRID_DEBOUNCE_SECONDS'];
  const parsed = raw ? parseInt(raw, 10) : NaN;
  if (Number.isFinite(parsed) && parsed > 0) return Math.max(parsed, 10) * 1000;
  return 60_000;
})();

/**
 * ENG-6785: deliver the kanban check over the direct-chat durable rail. Opens
 * an attribution run (same model as the send-keys path — the run marker rides
 * the notice content so the transcript parser still attributes the work), then
 * enqueues a kind:'notice' direct_chat_messages row via /host/kanban/notify and
 * rings the existing direct-chat doorbell so the in-session MCP pulls promptly.
 * Returns true when the rail accepted the nudge; false signals the caller to
 * fall back to send-keys. Never throws.
 */
async function injectKanbanCheckViaDirectChat(
  codeName: string,
  agentId: string,
  actionableCount: number,
): Promise<boolean> {
  if (isKanbanHybridDryRun()) {
    log(
      `[manager-worker] kanban inject DRY-RUN (direct-chat) — would enqueue for '${codeName}' (${actionableCount} actionable items)`,
    );
    // Roll back the stamp so the next tick can DRY-RUN-log again immediately.
    lastKanbanInjectAt.delete(codeName);
    return true;
  }

  // The agent is being nudged again, so any previously-injected run is finished
  // — close it before opening the next (marker-bounded attribution).
  closeInjectedRunIfOpen(codeName, 'completed', 'kanban inject superseded by next inject');

  const { run_id } = await startRun({
    agent_id: agentId,
    source_type: 'kanban',
    metadata: { injected: 'kanban-check', actionable_count: actionableCount, delivery: 'direct-chat' },
  });

  const content = run_id
    ? `${KANBAN_CHECK_COMMAND}\n${formatRunMarker(run_id)}`
    : KANBAN_CHECK_COMMAND;

  const ok = await enqueueKanbanNotice({ agentId, content, runId: run_id, actionableCount });

  if (!ok) {
    // Enqueue failed — don't leave a phantom open run for a nudge that never
    // landed. Close it and let the caller fall back to send-keys.
    if (run_id) void finishRun(run_id, 'failed', { outcomeMessage: 'kanban notice enqueue failed' });
    return false;
  }

  if (run_id) openInjectedRunByCode.set(codeName, run_id);

  // Ring the existing direct-chat doorbell so the in-session MCP pulls the notice
  // promptly rather than waiting for its safety-net poll. Best-effort: a missed
  // ring only costs latency (the notice is durable in the queue).
  try {
    const doorbell = directChatDoorbellPath(agentId, homedir());
    mkdirSync(dirname(doorbell), { recursive: true });
    writeFileSync(doorbell, String(Date.now()));
  } catch (err) {
    log(`[kanban] doorbell ring failed for '${codeName}': ${(err as Error).message} (notice still queued)`);
  }

  log(
    `[manager-worker] Enqueued kanban check via direct-chat for '${codeName}' (${actionableCount} actionable items)${run_id ? ` run_id=${run_id}` : ''}`,
  );
  return true;
}

/**
 * Per-agent inject decision: debounce-then-idle-then-send. Stamps the
 * debounce BEFORE the idle check (design-doc Q3 mitigation) so two
 * back-to-back ticks don't both proceed into a double send. Idle skip
 * is a soft-fail: the agent gets another shot on the next tick.
 *
 * Fire-and-forget — caller MUST NOT await the outer promise, or a
 * busy agent's 10s `waitForPromptReady` cap serialises every later
 * agent in the same poll cycle.
 */
export async function maybeInjectKanbanCheck(
  codeName: string,
  agentId: string,
  actionableCount: number,
): Promise<void> {
  const lastAt = lastKanbanInjectAt.get(codeName) ?? 0;
  if (Date.now() - lastAt < KANBAN_HYBRID_DEBOUNCE_MS) {
    // Quiet log — every 5s poll tick on a chatty board would otherwise
    // emit a debounce line per agent. Only log every ~10th skip via a
    // counter would be nicer; for v1 we just log nothing on debounce-
    // skip and let the next successful inject mark the cadence.
    return;
  }
  // Stamp NOW so a concurrent tick can't race past the debounce. A
  // failed send (idle skip, tmux gone) leaves the stamp in place for
  // one debounce window — acceptable: the next inject just waits one
  // window before retrying.
  lastKanbanInjectAt.set(codeName, Date.now());

  // ENG-6785: durable kanban delivery. When the flag is on, enqueue the nudge
  // onto the verified direct-chat notice rail instead of unverified tmux
  // send-keys. The board state is already durable (ADR-0020 T1), so we don't
  // need the prompt-ready gate the send-keys path relies on — the in-session MCP
  // pulls and consumes the notice when the agent is free; a missed poll just
  // re-nudges next debounce window. On a rail failure we fall through to the
  // legacy send-keys path below (a possibly-unverified send beats no delivery).
  if (hostFlagStore().getBoolean('kanban-doorbell')) {
    const ok = await injectKanbanCheckViaDirectChat(codeName, agentId, actionableCount);
    if (ok) return;
    log(`[manager-worker] kanban direct-chat enqueue failed for '${codeName}' — falling back to send-keys`);
  }

  const tmuxSession = `agt-${codeName}`;
  const ready = await isAgentPromptReady(tmuxSession);
  if (!ready) {
    log(`[manager-worker] kanban inject skipped — agent busy ('${codeName}')`);
    return;
  }

  if (isKanbanHybridDryRun()) {
    log(
      `[manager-worker] kanban inject DRY-RUN — would inject for '${codeName}' (${actionableCount} actionable items)`,
    );
    // Roll back the stamp so the next tick can DRY-RUN-log again
    // immediately. Without this, dry-run output would only appear once
    // per debounce window per agent and be hard to use as a verifier.
    lastKanbanInjectAt.delete(codeName);
    return;
  }

  // ENG-5565: the agent is idle (ready), so any previously-injected run is
  // finished — close it before opening the next. Marker-bounded attribution
  // means the prior run's turns are everything up to this new marker.
  closeInjectedRunIfOpen(codeName, 'completed', 'kanban inject superseded by next inject');

  // Open a run for this injection so its token usage is attributable. A
  // start failure (run_id null) is non-fatal: we still nudge, just without
  // a marker this cycle (the work counts toward the agent total, unattributed).
  const { run_id } = await startRun({
    agent_id: agentId,
    source_type: 'kanban',
    metadata: { injected: 'kanban-check', actionable_count: actionableCount },
  });

  // Deliver the nudge. Plain text → tmux send-keys with the run marker
  // appended as an inert trailing line (collapsed onto one line by the
  // injector so bracketed-paste doesn't swallow the submit). A slash command
  // goes via sendToAgent directly and carries no marker.
  let delivered: boolean;
  if (isSlashCommand(KANBAN_CHECK_COMMAND)) {
    delivered = sendToAgent(tmuxSession, KANBAN_CHECK_COMMAND);
  } else {
    const content = run_id
      ? `${KANBAN_CHECK_COMMAND}\n${formatRunMarker(run_id)}`
      : KANBAN_CHECK_COMMAND;
    delivered = await injectMessage(codeName, 'system', content, undefined, log);
  }

  if (delivered) {
    if (run_id) openInjectedRunByCode.set(codeName, run_id);
    log(
      `[manager-worker] Injected kanban check for '${codeName}' (${actionableCount} actionable items)${run_id ? ` run_id=${run_id}` : ''}`,
    );
  } else {
    // Delivery failed — don't leave a phantom open run for a nudge that
    // never landed. Close it so it isn't billed turns it didn't cause.
    if (run_id) {
      void finishRun(run_id, 'failed', { outcomeMessage: 'kanban inject delivery failed' });
    }
    log(`[manager-worker] kanban inject failed for '${codeName}'`);
  }
}

// Throttle harvest — no need to check cron results every 30s cycle
const lastHarvestAt = new Map<string, number>();
const HARVEST_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes

// Cache kanban board per agent per poll cycle. BoardItem is defined in
// ./manager/kanban/parsers.js (ADR-0011) and imported above.
const kanbanBoardCache = new Map<string, BoardItem[]>();
// Separate cache for notification board diff — not pre-populated by step 7d
const notifyBoardCache = new Map<string, Set<string>>();

// ADR-0011 PR-4: the low-level channel senders (telegramApiCall,
// sendSlackWebhookMessage, sendSlackChannelMessage, postSlackChannelMessage,
// maybePostSlackThreadHint, maybeSendTelegramFollowUpHint) moved into
// ./manager/channels/{slack,telegram}.ts and are imported above.


/** ENG-4581: dispatch any pending Claude Code OAuth pairing sessions
 *  across this host's agents. Runs once per refresh cycle.
 *
 *  Two states drive work:
 *    - 'initiating'     → run startClaudePair, report URL or error
 *    - 'code_submitted' → run submitClaudePairCode, report success/failure
 *
 *  Best-effort: a single bad session shouldn't block the others, and a
 *  transient API failure just means we retry on the next refresh.
 */
interface PairableAgent { agentId: string; codeName: string }

// Pair_ids the manager has spawned a tmux session for. Used to ask the
// server "of these, which are now terminal?" so we can kill orphan
// `agt-pair-<id>` tmux sessions left over from operator cancels or
// connection drops.
const spawnedPairIds = new Set<string>();

async function processClaudePairSessions(agents: PairableAgent[]): Promise<void> {
  // Keep polling whenever we have orphan pair-tmux sessions to clean
  // up — even if the host temporarily has zero assigned agents.
  // Otherwise stale agt-pair-* sessions could linger until the manager
  // restarts.
  if (agents.length === 0 && spawnedPairIds.size === 0) return;
  const agentIds = agents.map((a) => a.agentId);
  const codeNameByAgentId = new Map(agents.map((a) => [a.agentId, a.codeName]));

  const pendingResp = await api.post<{
    pending: Array<{
      pair_id: string;
      agent_id: string;
      status: 'initiating' | 'code_submitted';
      code: string | null;
      created_at: string;
      updated_at: string;
    }>;
    cancelled_pair_ids?: string[];
  }>('/host/claude-pair/pending', {
    agent_ids: agentIds,
    spawned_pair_ids: Array.from(spawnedPairIds),
  });

  const {
    startClaudePair,
    submitClaudePairCode,
    spawnPairSession,
    killPairSession,
    pairTmuxSession,
    finalizeClaudePairOnboarding,
  } = await import('./claude-pair-runtime.js');

  // Sweep orphans the server flagged as terminal but still in our
  // spawned set (operator cancel, status reported via a different code
  // path, etc.). Kill the tmux session and forget about the id.
  for (const pairId of pendingResp.cancelled_pair_ids ?? []) {
    log(`[claude-pair] sweeping orphan tmux session for pair ${pairId.slice(0, 8)}`);
    const killed = await killPairSession(pairTmuxSession(pairId));
    if (killed) {
      spawnedPairIds.delete(pairId);
    } else {
      // Kill failed for a non-missing-session reason — leave the id in
      // spawnedPairIds so the next poll retries.
      log(`[claude-pair] kill-session failed for pair ${pairId.slice(0, 8)} — will retry on next poll`);
    }
  }

  if (!pendingResp.pending || pendingResp.pending.length === 0) return;

  // Terminal states cleared from the manager's perspective — once we
  // report any of these, the pair-scoped tmux session is no longer needed
  // and should be torn down.
  const TERMINAL: ReadonlySet<string> = new Set([
    'success',
    'failure',
    'session_missing',
    'timeout',
  ]);

  async function reportAndCleanup(
    pairId: string,
    body: Record<string, unknown>,
  ): Promise<void> {
    await api.post('/host/claude-pair/result', { pair_id: pairId, ...body });
    if (typeof body.status === 'string' && TERMINAL.has(body.status)) {
      // On success, give claude a beat to persist credentials to
      // ~/.claude/.credentials.json before we kill the tmux session.
      // Killing during the write window can leave the host with an
      // empty ~/.claude (claude shows the theme picker on next launch
      // as if no auth had ever happened).
      if (body.status === 'success') {
        await new Promise<void>((r) => setTimeout(r, 3_000));
      }
      const killed = await killPairSession(pairTmuxSession(pairId));
      if (killed) {
        spawnedPairIds.delete(pairId);
      }
      // If kill failed, the next poll's cancelled_pair_ids sweep will
      // retry — the row is now terminal so it'll be returned again.
    }
  }

  for (const session of pendingResp.pending) {
    // codeName is informational only — handy for log lines. The pair
    // flow drives a throwaway tmux session, NOT the agent's persistent
    // session, so we no longer require an entry in codeNameByAgentId.
    const codeName = codeNameByAgentId.get(session.agent_id) ?? '<unassigned>';
    const pairSession = pairTmuxSession(session.pair_id);

    try {
      if (session.status === 'initiating') {
        log(`[claude-pair] spawning pair session ${pairSession} for '${codeName}'`);
        const spawn = await spawnPairSession(pairSession);
        if (!spawn.ok) {
          await reportAndCleanup(session.pair_id, {
            status: 'failure',
            error_code: spawn.error.kind,
            error_message:
              spawn.error.kind === 'unknown' ? spawn.error.message : undefined,
          });
          continue;
        }
        spawnedPairIds.add(session.pair_id);

        log(`[claude-pair] dispatching /login (pair ${session.pair_id.slice(0, 8)})`);
        const result = await startClaudePair({ session: pairSession });
        if (result.kind === 'url') {
          await api.post('/host/claude-pair/result', {
            pair_id: session.pair_id,
            status: 'awaiting_code',
            url: result.url,
          });
        } else if (result.kind === 'timeout') {
          await reportAndCleanup(session.pair_id, {
            status: 'timeout',
            error_code: 'url_prompt_not_seen',
            error_message: 'Claude Code did not print the URL prompt within the deadline',
          });
        } else {
          const errKind = result.error.kind;
          // Forward the message for any kind that carries one. `oauth-retry-stuck`
          // ships actionable remediation text ("clear ~/.claude/.credentials.json...")
          // that operators need; the previous unknown-only filter dropped that
          // guidance and reduced the failure to a bare error_code.
          const errMessage =
            'message' in result.error ? result.error.message : undefined;
          await reportAndCleanup(session.pair_id, {
            status: errKind === 'no-session' ? 'session_missing' : 'failure',
            error_code: errKind,
            error_message: errMessage,
          });
        }
      } else if (session.status === 'code_submitted' && session.code) {
        log(`[claude-pair] submitting code (pair ${session.pair_id.slice(0, 8)})`);
        const result = await submitClaudePairCode({
          session: pairSession,
          code: session.code,
        });
        if (result.kind === 'success') {
          // ENG-4633: drive Claude Code through its post-OAuth dialogs
          // so it persists ~/.claude.json before we kill the pair tmux
          // session. Without this step claude only wrote
          // ~/.claude/.credentials.json — the next agent launch hit
          // the login picker again. The result of this finalize is
          // logged but never gates the success report; even an
          // un-finalized pair still has working OAuth tokens, the
          // worst-case fallout is a one-time login picker on the
          // next agent launch (which ENG-4634 now surfaces).
          const finalize = await finalizeClaudePairOnboarding(pairSession, log);
          if (!finalize.finalized) {
            log(`[claude-pair] WARN: ~/.claude.json was not updated during onboarding (pair ${session.pair_id.slice(0, 8)}) — first agent launch may show the login picker`);
          }
          await reportAndCleanup(session.pair_id, { status: 'success' });
        } else if (result.kind === 'failure') {
          await reportAndCleanup(session.pair_id, {
            status: 'failure',
            error_code: 'invalid_code',
            error_message: result.rawMatch,
          });
        } else if (result.kind === 'timeout') {
          await reportAndCleanup(session.pair_id, {
            status: 'timeout',
            error_code: 'outcome_not_seen',
            error_message: 'Claude Code did not show a success/failure marker after submitting the code',
          });
        } else {
          const errKind = result.error.kind;
          // Forward the message for any kind that carries one. `oauth-retry-stuck`
          // ships actionable remediation text ("clear ~/.claude/.credentials.json...")
          // that operators need; the previous unknown-only filter dropped that
          // guidance and reduced the failure to a bare error_code.
          const errMessage =
            'message' in result.error ? result.error.message : undefined;
          await reportAndCleanup(session.pair_id, {
            status: errKind === 'no-session' ? 'session_missing' : 'failure',
            error_code: errKind,
            error_message: errMessage,
          });
        }
      }
    } catch (err) {
      // Don't poison the loop. Log and let the next refresh retry —
      // pending rows stay in 'initiating' / 'code_submitted' until we
      // succeed or the operator gives up.
      log(`[claude-pair] dispatch failed for pair ${session.pair_id.slice(0, 8)}: ${(err as Error).message}`);
    }
  }
}


/**
 * Generate provision artifacts without writing to disk.
 * Returns the list of artifacts (relativePath + content) for comparison.
 */
function generateArtifacts(
  agent: { agent_id: string; code_name: string; display_name: string; status: string; environment: string },
  refreshData: {
    agent: Record<string, unknown>;
    charter: { raw_content: string; version: string } | null;
    tools: { raw_content: string; version: string } | null;
    channel_configs: Record<string, { config: unknown; status: string }> | null;
    team_channel_policy: {
      team_id: string;
      allowed_channels: string[];
      denied_channels: string[];
      require_elevated_for_pii: boolean;
    } | null;
    team: { name: string; description: string | null; settings?: Record<string, unknown> } | null;
    scheduled_tasks?: unknown;
    knowledge?: Array<{ title: string; slug: string; content: string | null; scope: 'org' | 'team' }>;
    team_members?: Array<{ display_name: string; email?: string; role: string; title?: string; contact_channel?: string }>;
    people?: Array<{ display_name: string; email?: string; title?: string; department?: string; relationship?: string; contact_channel?: string }>;
    guardrails?: ProvisionInput['guardrails'];
    // ADR-0012 / ENG-6352: down-synced dynamic workflows (flag-gated, resolved
    // server-side). Threaded into provisionInput.workflows for the adapter.
    workflows?: ProvisionInput['workflows'];
    // ENG-5380: active kanban tasks payload from /host/refresh. The API
    // caps at 10 and filters to todo/in_progress; here we just thread it
    // through to the adapter behind the feature flag.
    active_tasks?: ProvisionInput['activeTasks'];
  },
  adapter: FrameworkAdapter,
): { relativePath: string; content: string }[] {
  if (!refreshData.charter || !refreshData.tools) {
    throw new Error('No charter/tools available');
  }

  const charterContent = refreshData.charter.raw_content;
  const toolsContent = refreshData.tools.raw_content;

  const charterParsed = extractFrontmatter(charterContent);
  if (!charterParsed.frontmatter) {
    throw new Error(`Failed to parse CHARTER.md frontmatter: ${charterParsed.error ?? 'unknown'}`);
  }

  const toolsParsed = extractFrontmatter(toolsContent);
  if (!toolsParsed.frontmatter) {
    throw new Error(`Failed to parse TOOLS.md frontmatter: ${toolsParsed.error ?? 'unknown'}`);
  }

  const charterFrontmatter = charterParsed.frontmatter as unknown as CharterFrontmatter;
  const toolsFrontmatter = toolsParsed.frontmatter as unknown as ToolsFrontmatter;

  const configuredChannels = Object.keys(refreshData.channel_configs ?? {}) as ChannelId[];

  const agentChannelPolicy: ChannelPolicy = {
    policy: 'allowlist',
    allowed: configuredChannels,
    denied: [],
    require_approval_to_change: true,
  };

  let orgChannelPolicy: OrgChannelPolicy | undefined;
  const policyData = refreshData.team_channel_policy;

  if (policyData) {
    orgChannelPolicy = {
      organization_id: policyData.team_id,
      allowed_channels: (policyData.allowed_channels ?? []) as ChannelId[],
      denied_channels: (policyData.denied_channels ?? []) as ChannelId[],
      require_elevated_for_pii: policyData.require_elevated_for_pii ?? false,
    };
  }

  const resolvedChannels = resolveChannels(agentChannelPolicy, orgChannelPolicy);
  const effectiveChannels = agent.status === 'paused' ? [] : resolvedChannels;

  // Resolve timezone deterministically — use unanimous task timezone, then team, then UTC
  const tasks = (refreshData as Record<string, unknown>).scheduled_tasks as Array<{ timezone?: string | null }> | null | undefined;
  const taskTimezones = [...new Set(
    (tasks ?? [])
      .map((t) => (typeof t.timezone === 'string' ? t.timezone.trim() : ''))
      .filter((tz): tz is string => tz.length > 0),
  )];
  const teamTimezoneRaw = (refreshData.team as { timezone?: unknown } | null | undefined)?.timezone;
  const teamTimezone = typeof teamTimezoneRaw === 'string' && teamTimezoneRaw.trim().length > 0 ? teamTimezoneRaw.trim() : undefined;
  const agentTimezone = (taskTimezones.length === 1 ? taskTimezones[0] : undefined) ?? teamTimezone ?? 'UTC';

  // Resolve personality seed: agent-level overrides org-level
  const agentPersonalitySeed = (refreshData.agent as Record<string, unknown>).personality_seed as string | undefined;
  const orgDefaults = (refreshData as Record<string, unknown>).model_defaults as { org?: { settings?: Record<string, unknown> } } | undefined;
  const personalitySeed = agentPersonalitySeed || orgDefaults?.org?.settings?.personality_seed as string | undefined;

  // Resolve reports_to reference (person or agent name)
  let reportsTo: ProvisionInput['reportsTo'];
  const agentData = refreshData.agent as Record<string, unknown>;
  const reportsToId = agentData.reports_to as string | null | undefined;
  const reportsToType = (agentData.reports_to_type as string | null | undefined) ?? 'agent';

  if (reportsToId) {
    const reportsToName = agentData.reports_to_name as string | undefined;
    const reportsToTitle = agentData.reports_to_title as string | undefined;
    const reportsToDesc = agentData.reports_to_description as string | undefined;
    if (reportsToName) {
      reportsTo = {
        name: reportsToName,
        type: reportsToType as 'agent' | 'person',
        title: reportsToTitle,
        description: reportsToDesc,
      };
    }
  }

  // ENG-5769: session-start active-tasks injection is now always on. The
  // ENG-5380 flag (`AGT_ACTIVE_TASKS_INJECT`) was default-off, which meant
  // Slack-thread continuity silently broke on every kanban resume — the
  // agent picked the card back up via the kanban-work nudge but had no
  // thread coordinates in its context (the nudge's `formatBoardForPrompt`
  // didn't carry them either, fixed in this same commit). The Active Tasks
  // section is hard-capped at ~200 tokens (`ACTIVE_TASKS_MAX_CHARS`) so
  // the always-on default is bounded and safe.
  //
  // Logged unconditionally so we can monitor the steady-state cost in
  // prod and catch any regression in the cap.
  const activeTasksFromRefresh = refreshData.active_tasks ?? [];
  const activeTasksTokens = estimateActiveTasksTokens(activeTasksFromRefresh);
  log(
    `[provision/active-tasks] agent=${agent.code_name} count=${activeTasksFromRefresh.length} estimated_tokens=${activeTasksTokens}`,
  );

  const provisionInput: ProvisionInput = {
    agent: refreshData.agent as any,
    charterFrontmatter,
    charterContent,
    toolsFrontmatter,
    toolsContent,
    resolvedChannels: effectiveChannels,
    deploymentTarget: 'local_docker' as DeploymentTarget,
    team: refreshData.team ?? undefined,
    activeTasks: activeTasksFromRefresh,
    // ENG-5009: org name for the identity-preamble. Only present when
    // /host/refresh ships the field — falls through cleanly for older
    // API payloads that don't carry it.
    organization: (() => {
      // CodeRabbit on PR #941: trim before the length check so a
      // whitespace-only value (`'   '`) doesn't render a broken
      // "in the X team at **   **" scope in CLAUDE.md.
      const org = (refreshData as unknown as { organization?: { name?: unknown } | null })
        .organization;
      if (typeof org?.name !== 'string') return undefined;
      const name = org.name.trim();
      return name.length > 0 ? { name } : undefined;
    })(),
    timezone: agentTimezone,
    reportsTo,
    personalitySeed,
    knowledge: (refreshData.knowledge ?? []).filter((k): k is { title: string; slug: string; content: string; scope: 'org' | 'team' } => !!k.content),
    knowledgeDelivery: ((refreshData.agent as Record<string, unknown>).knowledge_delivery as 'search' | 'files' | 'both' | undefined) ?? 'both',
    teamMembers: refreshData.team_members ?? undefined,
    people: refreshData.people ?? undefined,
    guardrails: refreshData.guardrails,
    // ADR-0012 / ENG-6352: down-synced dynamic workflows (flag-gated, resolved
    // server-side). The claude-code adapter renders each as
    // `.claude/workflows/<name>.js`; undefined/empty no-ops.
    workflows: refreshData.workflows,
  };

  const provisionOutput = provision(provisionInput, adapter.id);
  return provisionOutput.artifacts;
}

// ---------------------------------------------------------------------------
// Memory sync — bidirectional sync between local files and agent_memories DB
// ---------------------------------------------------------------------------

/** Per-file content hashes to detect individual memory changes */
const memoryFileHashes = new Map<string, Map<string, string>>(); // agentId → (fileName → hash)
/** Hash of DB memories response to skip redundant downloads */
const lastDownloadHash = new Map<string, string>(); // agentId → hash
/** Hash of local file listing to skip redundant download checks */
const lastLocalFileHash = new Map<string, string>(); // agentId → hash

/**
 * ENG-4643: agents that have just been assigned to this host (or have
 * otherwise been flagged as needing a clean re-pull from the API).
 * Set populated by the onAssign realtime callback; consumed at the
 * top of the next syncMemories run for that agent. The pending tick
 * skips the local-list-hash short-circuit, downloads first (so DB is
 * authoritative on the migration boundary, never overwritten by stale
 * local files), and clears the per-agent in-memory caches so a long-
 * running manager that previously hosted this agent doesn't carry
 * stale hashes that would suppress fresh writes.
 */
const pendingFreshMemorySync = new Set<string>(); // agent_id

export function markAgentForFreshMemorySync(agentId: string): void {
  pendingFreshMemorySync.add(agentId);
  memoryFileHashes.delete(agentId);
  lastDownloadHash.delete(agentId);
  lastLocalFileHash.delete(agentId);
}

function parseMemoryFile(raw: string, fallbackName: string): { name: string; content: string; type: string } | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const fmMatch = trimmed.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);

  if (fmMatch) {
    // Has YAML frontmatter — parse structured fields
    const frontmatter = fmMatch[1] ?? '';
    const body = (fmMatch[2] ?? '').trim();
    if (!body) return null;

    const nameMatch = frontmatter.match(/^name:\s*(.+)$/m);
    const typeMatch = frontmatter.match(/^type:\s*(.+)$/m);
    const name = nameMatch?.[1]?.trim().replace(/^["']|["']$/g, '') ?? fallbackName;
    const rawType = typeMatch?.[1]?.trim().replace(/^["']|["']$/g, '') ?? 'user';

    const typeMap: Record<string, string> = { user: 'user', feedback: 'feedback', project: 'project', reference: 'reference', daily: 'daily' };
    const type = typeMap[rawType] ?? 'user';

    return { name, content: body, type };
  }

  // No frontmatter — treat the entire file as content.
  // Derive type from filename pattern: date-like names (YYYY-MM-DD) → daily, else project.
  const isDaily = /^\d{4}-\d{2}-\d{2}$/.test(fallbackName);
  return {
    name: fallbackName,
    content: trimmed,
    type: isDaily ? 'daily' : 'project',
  };
}

async function syncMemories(
  agent: { agent_id: string; code_name: string },
  configDir: string,
  log: (msg: string) => void,
): Promise<void> {
  const projectDir = join(configDir, agent.code_name, 'project');
  const memoryDir = join(projectDir, 'memory');

  // ENG-4643: on a fresh-sync tick (post-migration / post-assignment) we
  // download first and let the DB be authoritative. Any stale local
  // file from a prior provision gets overwritten when its DB twin has
  // different content. Subsequent ticks fall through to the regular
  // upload-then-download bidirectional flow.
  //
  // Skip the upload path entirely when the download fails — without
  // a confirmed catch-up pull, any local .md files we'd upload risk
  // overwriting fresher DB content with stale local copies (the
  // exact scenario we're guarding against). Leave the pending flag
  // set so the next supervisor tick retries the download.
  const isFreshSync = pendingFreshMemorySync.has(agent.agent_id);
  if (isFreshSync) {
    log(`[memory-sync] Fresh-sync requested for '${agent.code_name}' — pulling DB first`);
    const ok = await downloadMemories(agent, memoryDir, log, { force: true });
    if (!ok) {
      log(`[memory-sync] Fresh-sync download failed for '${agent.code_name}' — skipping upload, will retry next tick`);
      return;
    }
    pendingFreshMemorySync.delete(agent.agent_id);
  }

  // --- Upload: local → DB (only changed files) ---
  if (existsSync(memoryDir)) {
    const prevHashes = memoryFileHashes.get(agent.agent_id) ?? new Map<string, string>();
    const currentHashes = new Map<string, string>();
    const changedMemories: Array<{ name: string; content: string; type: string; expires_at?: string }> = [];

    for (const file of readdirSync(memoryDir)) {
      if (!file.endsWith('.md')) continue;
      try {
        const raw = readFileSync(join(memoryDir, file), 'utf-8');
        const fileHash = createHash('sha256').update(raw).digest('hex').slice(0, 16);
        currentHashes.set(file, fileHash);

        // Only parse + upload if file content changed since last cycle
        if (prevHashes.get(file) === fileHash) continue;

        const parsed = parseMemoryFile(raw, file.replace(/\.md$/, ''));
        if (parsed) {
          // Daily memories require expires_at (DB constraint)
          if (parsed.type === 'daily') {
            (parsed as { expires_at?: string }).expires_at = new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString();
          }
          changedMemories.push(parsed);
        }
      } catch { /* skip unreadable */ }
    }

    memoryFileHashes.set(agent.agent_id, currentHashes);

    if (changedMemories.length > 0) {
      try {
        await api.post('/host/sync-memories', {
          agent_id: agent.agent_id,
          memories: changedMemories,
        });
        log(`Synced ${changedMemories.length} changed memories to DB for '${agent.code_name}'`);
      } catch (err) {
        // Reset hashes for changed files so they retry next cycle
        for (const mem of changedMemories) {
          for (const [file] of currentHashes) {
            const parsed = parseMemoryFile(readFileSync(join(memoryDir, file), 'utf-8'), file.replace(/\.md$/, ''));
            if (parsed?.name === mem.name) currentHashes.delete(file);
          }
        }
        log(`Memory upload failed for '${agent.code_name}': ${(err as Error).message}`);
      }
    }
  }

  // --- Download: DB → local (skip if local files unchanged) ---
  // ENG-4643: skipped when isFreshSync ran above (we already pulled).
  if (!isFreshSync) {
    await downloadMemories(agent, memoryDir, log, { force: false });
  }
}

/**
 * Pull DB memories and write them to the local memory dir, reconciling
 * against existing files by CONTENT hash instead of slug presence.
 *
 * ENG-4643:
 *   - Old behaviour: skip the DB version whenever a same-slug local
 *     file existed, regardless of whether the local content matched.
 *     Stale provision leftovers permanently shadowed DB updates.
 *   - New behaviour: derive slug + render the canonical file body for
 *     each DB memory; if the existing file's bytes match exactly,
 *     skip; otherwise overwrite. Files with no DB twin are left alone
 *     (the upload path handles those on the next pass).
 *   - When `force` is true, the local-listing-hash short-circuit is
 *     bypassed so the migration / fresh-assignment first sync always
 *     reaches the network call.
 */
async function downloadMemories(
  agent: { agent_id: string; code_name: string },
  memoryDir: string,
  log: (msg: string) => void,
  { force }: { force: boolean },
): Promise<boolean> {
  const localFiles = existsSync(memoryDir)
    ? readdirSync(memoryDir).filter((f) => f.endsWith('.md')).sort()
    : [];
  const localListHash = createHash('sha256').update(localFiles.join(',')).digest('hex').slice(0, 16);
  const prevLocalHash = lastLocalFileHash.get(agent.agent_id);
  const prevDownload = lastDownloadHash.get(agent.agent_id);

  try {
    const dbMemories = await api.post<{ memories: Array<{ name: string; type: string; content: string }> }>('/host/memories', {
      agent_id: agent.agent_id,
    });

    // Hash the response to detect changes server-side
    const responseHash = createHash('sha256')
      .update(JSON.stringify(dbMemories.memories ?? []))
      .digest('hex').slice(0, 16);

    // Skip processing if nothing changed (local files same + DB response same).
    // `force=true` callers (post-migration first sync) ignore this guard.
    if (
      !force &&
      prevDownload &&
      prevLocalHash === localListHash &&
      lastDownloadHash.get(agent.agent_id) === responseHash
    ) {
      return true;
    }

    lastDownloadHash.set(agent.agent_id, responseHash);
    lastLocalFileHash.set(agent.agent_id, localListHash);

    if (dbMemories.memories?.length) {
      mkdirSync(memoryDir, { recursive: true });

      let written = 0;
      let overwritten = 0;
      for (let i = 0; i < dbMemories.memories.length; i++) {
        const mem = dbMemories.memories[i]!;
        const rawSlug = mem.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 60);
        const slug = rawSlug || `memory-${i}`;
        const filePath = join(memoryDir, `${slug}.md`);
        const desired = `---\nname: ${JSON.stringify(mem.name)}\ntype: ${mem.type}\ndescription: ${JSON.stringify(mem.content.slice(0, 200))}\n---\n\n${mem.content}\n`;

        if (existsSync(filePath)) {
          // Content compare — DB wins on mismatch (the local file is
          // either a stale provision leftover or out-of-band edited
          // copy that the upload path didn't catch).
          let existing = '';
          try { existing = readFileSync(filePath, 'utf-8'); } catch { /* unreadable → overwrite */ }
          if (existing === desired) continue;
          writeFileSync(filePath, desired);
          overwritten++;
        } else {
          writeFileSync(filePath, desired);
          written++;
        }
      }

      if (written > 0 || overwritten > 0) {
        // Update local file hash since we just wrote new/changed files
        const updatedFiles = readdirSync(memoryDir).filter((f) => f.endsWith('.md')).sort();
        lastLocalFileHash.set(agent.agent_id, createHash('sha256').update(updatedFiles.join(',')).digest('hex').slice(0, 16));
        log(`Memory download for '${agent.code_name}': wrote ${written} new, overwrote ${overwritten} stale`);
      }
    }
    return true;
  } catch (err) {
    log(`Memory download failed for '${agent.code_name}': ${(err as Error).message}`);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Cleanup helper (for revoked agents)
// ---------------------------------------------------------------------------

async function cleanupAgentFiles(codeName: string, agentDir: string): Promise<void> {
  // Remove the provision directory
  if (existsSync(agentDir)) {
    try {
      rmSync(agentDir, { recursive: true, force: true });
      log(`Removed provision directory for '${codeName}'`);
    } catch (err) {
      log(`Failed to remove provision dir for '${codeName}': ${(err as Error).message}`);
    }
  }

  // Deregister from the framework runtime
  try {
    const adapter = resolveAgentFramework(codeName);
    const registered = await getOrCacheRegisteredAgents(adapter, codeName);
    if (registered.has(codeName)) {
      await adapter.deregisterAgent(codeName);
      registered.delete(codeName);
      log(`Deregistered '${codeName}' from ${adapter.label}`);
    }
  } catch (err) {
    log(`Failed to deregister '${codeName}': ${(err as Error).message}`);
  }
}

// ---------------------------------------------------------------------------
// IPC + lifecycle
// ---------------------------------------------------------------------------

// Caffeinate subprocess — prevents macOS sleep while manager is running
let caffeinateProc: import('node:child_process').ChildProcess | null = null;

async function startCaffeinate(): Promise<void> {
  if (process.platform !== 'darwin') return;
  try {
    const { spawn } = await import('node:child_process');
    caffeinateProc = spawn('caffeinate', ['-dims'], {
      stdio: 'ignore',
      detached: false,
    });
    caffeinateProc.on('error', (err) => {
      log(`caffeinate failed: ${err.message} — Mac may sleep during operation`);
      caffeinateProc = null;
    });
    caffeinateProc.on('exit', () => { caffeinateProc = null; });
    if (caffeinateProc.pid) {
      log(`caffeinate started (PID ${caffeinateProc.pid}) — Mac sleep prevented while manager is running`);
    }
  } catch (err) {
    log(`caffeinate not available: ${(err as Error).message} — Mac may sleep during operation`);
  }
}

function stopCaffeinate(): void {
  if (caffeinateProc) {
    caffeinateProc.kill();
    caffeinateProc = null;
    log('caffeinate stopped');
  }
}

// ENG-6257 (Augmented Live Phase 2D): one streaming scanner per live agent,
// driven once per poll cycle (no dedicated timer — reuses the poll cadence).
// Self-gating: a no-op for agents with no artefacts dir; best-effort and
// isolated so a streaming hiccup never affects the poll loop.
const artifactScanners = new Map<string, ArtifactStreamScanner>();
let artifactStreamingInFlight = false;

async function driveArtifactStreaming(): Promise<void> {
  // The two call sites fire-and-forget after each pollCycle; if a scan outlasts
  // the poll interval, a second run would race the shared scanner map. Skip an
  // overlapping run rather than mutate the map concurrently.
  if (artifactStreamingInFlight) return;
  artifactStreamingInFlight = true;
  try {
    await driveArtifactStreamingInner();
  } finally {
    artifactStreamingInFlight = false;
  }
}

async function driveArtifactStreamingInner(): Promise<void> {
  const liveIds = new Set<string>();
  for (const agent of state.agents) {
    if (!agent.agentId || !agent.codeName || agent.status !== 'active') continue;
    liveIds.add(agent.agentId);
    let scanner = artifactScanners.get(agent.agentId);
    if (!scanner) {
      scanner = new ArtifactStreamScanner(
        agent.agentId,
        artifactsDirFor(agent.codeName),
        nodeArtifactFs,
        { api, log },
      );
      artifactScanners.set(agent.agentId, scanner);
    }
    try {
      await scanner.scanOnce();
    } catch (err) {
      log(`[artifact-stream] scan failed for ${agent.codeName}: ${(err as Error).message}`);
    }
  }
  // Drop scanners for agents no longer active so their encoder/draft state is released.
  for (const id of artifactScanners.keys()) {
    if (!liveIds.has(id)) artifactScanners.delete(id);
  }
}

/**
 * ENG-7273: the fast, independent liveness heartbeat. The main poll cycle stamps
 * hosts.last_seen_at + agents.last_heartbeat_at only once per iteration (section
 * 1c), but a busy host's cycle wall-clock - per-agent provisioning and
 * connectivity probes, and the multi-minute re-provision after a restart - can
 * exceed the webapp's 2-minute offline threshold, flipping every agent to
 * "offline" (and blocking Direct Chat) while they are alive and replying. This
 * posts a MINIMAL /host/heartbeat on its own ~30s interval so the liveness
 * signal can never be starved by cycle duration.
 *
 * The body is intentionally minimal: the route requires only host_id and writes
 * every other column conditionally, so this bumps last_seen_at (and, for the
 * host's assigned agents, last_heartbeat_at) without touching
 * framework_version/host_security/diagnostics - those stay on the slow cycle.
 * Best-effort and non-throwing; the reentrancy guard stops a slow POST stacking.
 */
async function postLivenessHeartbeat(): Promise<void> {
  if (!running || !config || restartAfterUpgrade) return;
  if (livenessInFlight) return;
  livenessInFlight = true;
  try {
    const hostId = await getHostId();
    // host id is null until the first key exchange resolves; an early tick
    // before the first poll cycle would otherwise POST host_id:null -> 400.
    // Skip and let the next tick (post-exchange) refresh liveness.
    if (!hostId) return;
    await api.post('/host/heartbeat', { host_id: hostId, agt_version: agtCliVersion });
  } catch (err) {
    // A transient API/exchange failure must never crash the manager; the next
    // tick (and the slow cycle's own heartbeat) retry.
    log(`[liveness] fast heartbeat failed: ${(err as Error).message}`);
  } finally {
    livenessInFlight = false;
  }
}

function startPolling(): void {
  if (!config || running) return;
  running = true;

  // ENG-7273: arm the fast liveness heartbeat immediately, before the first
  // (and every subsequent) heavy poll cycle, so last_seen_at stays fresh even
  // while a long cycle or a post-restart re-provision is in flight. unref() so
  // it never holds the process open on shutdown.
  livenessTimer = setInterval(() => {
    void postLivenessHeartbeat();
  }, LIVENESS_HEARTBEAT_INTERVAL_MS);
  livenessTimer.unref();

  void startCaffeinate();
  // ENG-4712: rehydrate the channel-config hash cache from disk so a
  // fresh manager process recognises credentials it (or a sibling
  // manager) already wrote on a previous run, and skips the noisy
  // `reason=first-write` rewrite for unchanged configs.
  loadChannelHashCache();
  // ENG-6251: hydrate the host flag store from last-known-good cache and log
  // its age, so a stale flags-cache.json is visible at boot. Cached values are
  // used only until the first heartbeat refreshes the map this same poll cycle.
  hostFlagStore().init();
  log(`Starting poll loop (interval=${config.intervalMs}ms, configDir=${config.configDir})`);

  // Kill any orphaned agt-* tmux sessions from a previous manager run,
  // then start the poll loop. Must await to avoid race with session spawn.
  void killAllAgtTmuxSessions().catch(() => {}).then(() => {
    return pollCycle();
  }).then(() => {
    void driveArtifactStreaming();
    scheduleNext();
  });
}

/**
 * ENG-6399: report a self-update-induced restart to audit_log for each agent
 * with a live persistent session, via POST /host/restart-event (source
 * 'self-update'). Called on the upgrade-restart path right before the manager
 * exits. Bounded by a short deadline so a slow/down API can't stall the restart
 * — any POSTs still in flight when the deadline wins are dropped (best-effort,
 * the same contract as the other restart-event reports).
 */
async function reportSelfUpdateRestarts(): Promise<void> {
  const liveAgents = [...agentState.persistentSessionAgents];
  if (liveAgents.length === 0) return;
  const reason = `CLI self-update${pendingUpgradeVersion ? ` to ${pendingUpgradeVersion}` : ''}`;
  const posts = Promise.allSettled(
    liveAgents.map((codeName) => {
      const agentId = agentState.codeNameToAgentId.get(codeName);
      if (!agentId) return Promise.resolve();
      return api.post('/host/restart-event', {
        agent_id: agentId,
        source: 'self-update',
        reason,
      });
    }),
  ).then(() => undefined);
  const deadline = new Promise<void>((resolve) => setTimeout(resolve, 3_000));
  await Promise.race([posts, deadline]);
}

function scheduleNext(): void {
  if (!running || !config) return;

  // ENG-4488: if the self-update flipped this during the poll we just
  // finished, exit cleanly instead of scheduling another cycle. Also
  // re-check inside the timer callback — `checkAndUpdateCli()` is kicked
  // off without await, so the flag can flip between scheduleNext() and
  // the next fire. Without the second check we'd run one more pollCycle
  // on the old Cellar path, exactly the failure this is meant to avoid.
  // forcedExitCode: 0 so the supervisor treats this as a clean respawn
  // even if graceful cleanup hits its 15s backstop.
  const restartNow = (): void => {
    log(`[self-update] Restarting manager to load upgraded binary (${pendingUpgradeVersion ?? 'unknown version'})`);
    // ENG-6399: audit the self-update-induced restart per running agent BEFORE
    // we exit — this is the one point where we still know both the agents we're
    // about to bounce and the version delta. The new manager cold-starts those
    // sessions on its first poll but can't cleanly attribute the cold-start to a
    // self-update, so the honest signal lives here. Bounded + best-effort: it
    // must never delay or block the upgrade restart.
    void reportSelfUpdateRestarts()
      .catch((err) => log(`[self-update] restart-event audit failed: ${(err as Error).message}`))
      .finally(() => {
        // Exit with the dedicated restart code so the supervisor distinguishes
        // "please respawn me" from "the operator asked me to stop" — overloading
        // exit code 0 would make `agt manager stop` unable to actually stop
        // a supervised manager.
        void stopPolling({ forcedExitCode: SUPERVISOR_RESTART_EXIT_CODE }).then(() => {
          process.exit(SUPERVISOR_RESTART_EXIT_CODE);
        });
      });
  };

  if (restartAfterUpgrade) {
    restartNow();
    return;
  }
  // ENG-5041: extend the inter-poll delay when the last cycle failed, so
  // a sustained API outage doesn't hammer the cloud every few seconds.
  // Returns 0 when consecutivePollFailures is 0, so healthy hosts use
  // the original interval verbatim.
  const backoffMs = currentPollBackoffMs(consecutivePollFailures);
  const delayMs = Math.max(config.intervalMs, backoffMs);
  pollTimer = setTimeout(() => {
    if (restartAfterUpgrade) {
      restartNow();
      return;
    }
    void pollCycle().then(() => {
      void driveArtifactStreaming();
      scheduleNext();
    });
  }, delayMs);
}

async function killAllAgtTmuxSessions(): Promise<void> {
  try {
    const { stdout: output } = await execFilePromiseLong(
      'tmux',
      ['list-sessions', '-F', '#{session_name}'],
      { timeout: 2_000, stdin: 'ignore' },
    );
    const sessions = output.trim().split('\n').filter((s) => s.startsWith('agt-'));
    for (const session of sessions) {
      try {
        await execFilePromiseLong('tmux', ['kill-session', '-t', session], {
          timeout: 2_000,
          stdin: 'ignore',
        });
        log(`Killed tmux session '${session}'`);
      } catch { /* session may have already exited */ }
    }
    if (sessions.length > 0) {
      log(`Cleaned up ${sessions.length} agt-* tmux session(s)`);
    }
  } catch {
    // tmux not installed or no server running — nothing to clean up
  }
}

async function stopPolling(opts: { forcedExitCode?: number } = {}): Promise<void> {
  running = false;
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  // ENG-7273: stop the fast liveness heartbeat alongside the poll loop.
  if (livenessTimer) {
    clearInterval(livenessTimer);
    livenessTimer = null;
  }

  // Hard backstop: force exit after 15s if graceful cleanup hangs. Default
  // exit code is 1 (crash-signal) so a SIGTERM handler that can't drain
  // surfaces as a failure to any supervisor. The self-update restart path
  // passes forcedExitCode: 0 so a stuck cleanup after `brew upgrade`
  // doesn't poison the respawn — the supervisor still sees clean exit and
  // re-spawns onto the new binary.
  const shutdownTimer = setTimeout(() => {
    log('Shutdown timeout exceeded (15s), forcing exit');
    process.exit(opts.forcedExitCode ?? 1);
  }, 15000);
  shutdownTimer.unref();

  // ENG-5865 — drain phase. Structured `[drain] ...` lines on every step
  // give operators a single greppable signal for the shutdown outcome.
  // sigtermClaudePids is the single load-bearing new behaviour: SIGTERM every
  // tracked claude -p child (the conv-eval backend) BEFORE we tear down tmux.
  // Without this they get re-parented to init and keep burning tokens; with
  // it, the boot-side reaper on the next manager start (startManager →
  // reapOrphanedClaudePids) SIGKILLs anything that didn't honour the SIGTERM.
  const drainStartedAt = Date.now();
  const livePids = [...inFlightClaudePids.keys()];
  if (livePids.length > 0) {
    for (const pid of livePids) {
      try { process.kill(pid, 'SIGTERM'); } catch { /* already exited */ }
    }
    log(formatDrainLine({ step: 'sigterm-claude-pids', elapsedMs: Date.now() - drainStartedAt, killedPids: livePids }));
  } else {
    log(formatDrainLine({ step: 'sigterm-claude-pids', elapsedMs: 0, note: 'no in-flight claude -p children' }));
  }

  const subsysStartedAt = Date.now();
  stopCaffeinate();
  stopRealtimeChat();
  log(formatDrainLine({ step: 'stop-subsystems', elapsedMs: Date.now() - subsysStartedAt }));

  // ENG-5599: close any in-session scheduled-task runs still tracked in memory.
  // The per-agent stop paths (stopPersistentSessionAndForgetMcpBaseline) cover
  // pause/revoke/restart, but a full manager shutdown/restart can exit without
  // hitting those — without this drain those runs would be left open forever
  // once scheduledRunsByCode is lost. Best-effort (finishRun POSTs in the
  // background under the 15s shutdown budget); the server-side reaper (S2) is
  // the durable backstop.
  for (const codeName of [...scheduledRunsByCode.keys()]) {
    closeScheduledRunsForCode(codeName, 'cancelled', 'manager shutdown');
  }

  // Kill all agt-* tmux sessions FIRST — this is the most critical cleanup
  // Do it before graceful session stop to ensure sessions are killed even if
  // the graceful path hangs or times out.
  log('Killing tmux sessions...');
  await killAllAgtTmuxSessions();

  // Then gracefully stop persistent sessions (no-op if already killed)
  log('Stopping persistent sessions...');
  await stopAllSessionsAndWait(log, { timeoutMs: 4_000 });

  clearTimeout(shutdownTimer);
}

// ---------------------------------------------------------------------------
// Public API — called directly by the CLI command (no fork/IPC)
// ---------------------------------------------------------------------------

/**
 * Start the manager poll loop. Call this directly from the CLI command.
 * The process stays alive running the poll loop until stopManager() is called
 * or a signal is received.
 */
export function startManager(opts: { intervalMs: number; configDir: string }): void {
  config = opts;
  // ENG-4947: rehydrate AgentState[] from disk so dashboard-issued restart
  // acks (lastRestartProcessedAt) survive a manager process restart. We
  // only carry forward `agents`; pid/startedAt/pollCount stay fresh.
  // Errors are logged and ignored — first-run with no state file is the
  // normal case and a corrupt file shouldn't block startup.
  try {
    const stateFile = getStateFile();
    if (existsSync(stateFile)) {
      const raw = readFileSync(stateFile, 'utf-8');
      const parsed = JSON.parse(raw) as Partial<ManagerState>;
      if (Array.isArray(parsed.agents)) {
        state.agents = parsed.agents as AgentState[];
        log(`[startup] rehydrated ${state.agents.length} agent state(s) from ${stateFile}`);
      }
      // ENG-5441: rehydrate circuit-breaker trips so a manager restart
      // can't accidentally clear a tripped breaker. An operator who
      // intentionally stopped the loop must be the one to clear it.
      if (parsed.circuitBreakerTrips && typeof parsed.circuitBreakerTrips === 'object') {
        restartBreaker.hydrate(parsed.circuitBreakerTrips);
        const n = Object.keys(parsed.circuitBreakerTrips).length;
        if (n > 0) log(`[startup] rehydrated ${n} circuit-breaker trip(s) — agents will remain paused until manually resumed`);
      }
      // ENG-6088: rehydrate spent auto-resume credits so a manager restart
      // can't re-arm a re-tripped agent's auto-resume.
      if (parsed.circuitBreakerAutoResumes && typeof parsed.circuitBreakerAutoResumes === 'object') {
        autoResumeMarkers = hydrateAutoResumeMarkers(parsed.circuitBreakerAutoResumes);
        const n = autoResumeMarkers.size;
        if (n > 0) log(`[startup] rehydrated ${n} auto-resume marker(s) (ENG-6088)`);
      }
      // ENG-6692: rehydrate the forced-update dedup marker. The self-update
      // RESTARTS the manager, so this marker only does its job (fire once per
      // distinct "Update CLI now" timestamp) if it survives that restart — and
      // the heartbeat ack that clears hosts.update_requested_at goes out only on
      // the NEXT heartbeat, so the DB still carries the timestamp on the first
      // post-restart cycle. Without this, the fresh worker would re-consume it.
      if (
        typeof parsed.lastUpdateRequestProcessedAt === 'string' ||
        parsed.lastUpdateRequestProcessedAt === null
      ) {
        state.lastUpdateRequestProcessedAt = parsed.lastUpdateRequestProcessedAt;
        if (state.lastUpdateRequestProcessedAt) {
          log(`[startup] rehydrated update-request ack at ${state.lastUpdateRequestProcessedAt} (ENG-6692)`);
        }
      }
    }
  } catch (err) {
    log(`[startup] state rehydration failed (continuing with empty state): ${(err as Error).message}`);
  }
  // ENG-4658: First log line on every fresh worker. Two purposes:
  //   1. Proves the on-disk log destination is writable from this worker
  //      (the bug was a silent write to a stdio fd that pointed nowhere).
  //   2. Gives operators a "manager process boundary" marker so a long
  //      manager.log can be split into per-worker segments by grepping
  //      for `[startup] worker pid=`.
  // If this line doesn't appear in manager.log shortly after a fresh
  // spawn, the worker can't write to the file and the operator should
  // check ~/.augmented permissions.
  log(
    `[startup] worker pid=${process.pid} ppid=${process.ppid} ` +
      `node=${process.version} log=${join(homedir(), '.augmented', 'manager.log')}`,
  );
  deployMcpAssets();
  // ENG-4808: reap any channel-MCP processes that were left orphaned by
  // a prior manager crash or unclean exit. The per-restart reap inside
  // stopPersistentSession handles the in-process case; this catches the
  // pre-existing-orphan case where the manager process itself died with
  // children still attached. Idempotent: a clean restart with no orphans
  // is a no-op.
  reapOrphanChannelMcps({ log });
  // ENG-5865 — boot-side reaper for claude -p processes orphaned by a
  // previous manager death. Must complete BEFORE startPolling() so
  // processAgent can't observe (and re-pick) the same kanban card the
  // reaper is about to reset to backlog. CodeRabbit (PR #1631) caught
  // that the previous `void reapOrphanedClaudePids()` was fire-and-forget
  // and racing the very window it was meant to close. Sequenced now via
  // an IIFE: reaper → ensureHostFrameworkBinaries → startPolling. A
  // reaper failure logs and continues; the poll loop still must start
  // regardless (orphaned billing is better than a host that never polls).
  void (async (): Promise<void> => {
    try { await reapOrphanedClaudePids(); }
    catch (err) { log(`[drain] boot reaper failed: ${(err as Error).message}`); }
    // Fetch the host's own framework from /host/exchange and ensure the
    // framework's binaries are installed. Runs async so startup isn't blocked
    // on a brew install — the binary is required only when agents are launched,
    // not for the polling loop itself. Failures are logged, never fatal.
    void ensureHostFrameworkBinaries();
    startPolling();
  })();
}

/**
 * ENG-5865 — boot-side reaper. On manager startup, read the host-wide
 * claude-pid file written by the previous instance and SIGKILL any
 * survivors (orphaned conversation-eval `claude -p` children, the sole
 * tracked spawn since ENG-6347/ENG-6851). The pid file is truncated at the
 * end regardless of success — leaving stale records would cause the next
 * reaper run to try to SIGKILL random pids.
 */
async function reapOrphanedClaudePids(): Promise<void> {
  const path = claudePidFilePath();
  const file = readClaudePidFile(path);
  if (file.spawns.length === 0) return;

  // CodeRabbit (PR #1631) — PID-reuse defense. Linux `/proc/<pid>/comm` is
  // the kernel's view of the process's binary name (15 bytes max); if a
  // recorded PID has been recycled to an unrelated process between our
  // SIGTERM and this probe, that line will not contain 'claude'. On
  // non-Linux (macOS dev hosts), no /proc → degrade to "trust the PID",
  // matching the pre-fix behaviour; macOS PID recycling is sufficiently
  // slower in practice that the SIGTERM→boot-reaper race is much tighter.
  const looksLikeClaude = (pid: number): boolean => {
    if (process.platform !== 'linux') return true;
    try {
      const comm = readFileSync(`/proc/${pid}/comm`, 'utf-8').trim().toLowerCase();
      return comm.includes('claude');
    } catch {
      // No /proc entry typically means the process is already gone — but
      // isAlive caught that case. A read failure here is unusual; safer
      // to skip the kill than to gamble on identity.
      return false;
    }
  };

  const decision = decideClaudeReaperActions(
    file,
    (pid) => {
      try { process.kill(pid, 0); return true; }
      catch { return false; }
    },
    looksLikeClaude,
  );

  for (const spawn of decision.toKill) {
    try { process.kill(spawn.pid, 'SIGKILL'); }
    catch (err) { log(`[drain] reaper SIGKILL pid=${spawn.pid} failed: ${(err as Error).message}`); }
  }

  // Log the skipped-PID-reuse cases so operators have a paper trail — the
  // SIGKILL was skipped intentionally because the live PID no longer looks
  // like a claude process (it was reused by something unrelated).
  for (const spawn of decision.pidReusedSkipped) {
    log(
      `[drain] reaper skipped pid=${spawn.pid} — PID reuse suspected (identity probe rejected)`,
    );
  }

  log(formatReaperLine({
    totalRecorded: file.spawns.length,
    killed: decision.toKill.length,
    alreadyDead: decision.alreadyDead.length,
    pidReusedSkipped: decision.pidReusedSkipped.length,
  }));

  // Truncate the file — anything left from the previous instance is now
  // either reaped or already dead. Live spawns from THIS instance will
  // re-register themselves via the onSpawn hook on the next execFile call.
  try { writeClaudePidFile(path, { version: 1, spawns: [] }); }
  catch (err) { log(`[drain] reaper file truncate failed: ${(err as Error).message}`); }
}

async function ensureHostFrameworkBinaries(): Promise<void> {
  const apiKey = getApiKey();
  if (!apiKey) {
    log('[framework-install] AGT_API_KEY not set — skipping host framework binary check');
    return;
  }
  try {
    const exchange = await exchangeApiKey(apiKey);
    if (!exchange.framework) {
      log('[framework-install] host has no framework set — skipping');
      return;
    }
    log(`[framework-install] host framework = ${exchange.framework}`);
    await ensureFrameworkBinary(exchange.framework);
  } catch (err) {
    log(`[framework-install] failed: ${(err as Error).message}`);
  }
}

/**
 * Deploy bundled MCP server files to ~/.augmented/_mcp/ if they don't exist
 * or are outdated. This ensures prod hosts have the Slack channel server
 * and augmented MCP server without manual file copying.
 */
/**
 * SIGTERM every running instance of the given channel MCP basenames so they
 * respawn with the newly-deployed bundle on next message.
 *
 * Channel MCPs are stdio children of each agent's Claude Code tmux session —
 * when they exit, Claude Code auto-respawns them via the entry in .mcp.json.
 * That respawn reads the freshly-written bundle from ~/.augmented/_mcp/, so
 * all we need is a death signal.
 *
 * Matches by command line containing the basename — `ps` output on macOS +
 * Linux both include the full path, so `/path/to/slack-channel.js` grep-hits
 * reliably. Not SIGKILL: the channel servers already handle SIGTERM cleanly
 * (ENG-4452) to drain in-flight Socket Mode events.
 */
function restartRunningChannelMcps(basenames: string[]): void {
  try {
    const psOutput = syncExecFile(
      'ps',
      ['-eo', 'pid=,command='],
      { encoding: 'utf-8', timeout: 5_000 },
    );
    const lines = psOutput.split('\n').filter((l) => l.trim());
    const selfPid = process.pid;
    let signalled = 0;
    for (const line of lines) {
      const match = line.match(/^\s*(\d+)\s+(.*)$/);
      if (!match) continue;
      const pid = Number(match[1]);
      const command = match[2]!;
      if (pid === selfPid) continue;
      // Only match JS basenames in the command path; env-var substrings like
      // SOMEPATH=/fake/slack-channel.js don't count because the basename
      // token is followed by whitespace or end-of-line when it's the actual
      // script being executed.
      const hit = basenames.find((b) => new RegExp(`/${b}\\.js(\\s|$)`).test(command));
      if (!hit) continue;
      try {
        process.kill(pid, 'SIGTERM');
        signalled++;
        log(`[manager]   sent SIGTERM to pid=${pid} (${hit}.js) — will respawn on next message`);
      } catch (err) {
        // ESRCH = already dead; others are "not our process" (EPERM). Ignore.
        const code = (err as NodeJS.ErrnoException).code;
        if (code && code !== 'ESRCH') {
          log(`[manager]   failed to signal pid=${pid}: ${code}`);
        }
      }
    }
    if (signalled === 0) {
      log(`[manager]   no running instances to restart (deploy still applies to future spawns)`);
    }
  } catch (err) {
    log(`[manager] restartRunningChannelMcps failed: ${(err as Error).message}`);
  }
}

function deployMcpAssets(): void {
  const targetDir = join(homedir(), '.augmented', '_mcp');
  mkdirSync(targetDir, { recursive: true });

  // Resolve MCP files from the CLI package's dist/mcp/ directory (ENG-5410).
  // The tarball is restricted to dist/ only, so the bundled MCP assets are
  // copied under dist/mcp/ by the build:mcp-assets script.
  const moduleDir = dirname(fileURLToPath(import.meta.url));
  // Dev: src/lib → ../../dist/mcp, Built: dist/lib → ../dist/mcp, npm global: dist/lib → ../dist/mcp
  let mcpSourceDir = '';
  let dir = moduleDir;
  for (let i = 0; i < 6; i++) {
    const candidate = join(dir, 'dist', 'mcp');
    if (existsSync(join(candidate, 'index.js'))) {
      mcpSourceDir = candidate;
      break;
    }
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }

  if (!mcpSourceDir) {
    log('[manager] MCP assets not found in CLI package — skipping deployment');
    return;
  }

  // Track which channel basenames got a new bundle so we can signal running
  // instances to restart. Without this, already-spawned channel MCP children
  // (parented to each agent's Claude Code tmux session) keep running the
  // previous bundle from memory — `deployMcpAssets` only updates the file
  // on disk. That's exactly the scenario where a merged branch like ENG-4503
  // (Slack image auto-download) or ENG-4504 (Telegram image auto-download)
  // silently "doesn't work" until the operator manually restarts every agent.
  const changedBasenames: string[] = [];
  const fileHash = (p: string): string | null => {
    try {
      if (!existsSync(p)) return null;
      return createHash('sha256').update(readFileSync(p)).digest('hex');
    } catch {
      return null;
    }
  };

  // Only these basenames are eligible for deploy-triggered SIGTERM. `index.js`
  // is deployed alongside the channels but must NEVER trigger the restart path
  // because countless unrelated Node processes on the host have an `index.js`
  // entrypoint — matching on `/index.js` would SIGTERM any of them. The list
  // below is the closed set of channel bundles whose process commands reliably
  // end in `/<basename>.js` with no collisions.
  const RESTARTABLE_CHANNEL_FILES = new Set([
    'slack-channel.js',
    'direct-chat-channel.js',
    'telegram-channel.js',
    // ENG-6019: teams-channel was provisioned into .mcp.json but never shipped
    // in the bundle — the file below has never existed on hosts until now.
    'teams-channel.js',
    'whatsapp-channel.js', // ENG-6812
  ]);

  for (const file of [
    'index.js',
    'slack-channel.js',
    'direct-chat-channel.js',
    'telegram-channel.js',
    'teams-channel.js', // ENG-6019
    'whatsapp-channel.js', // ENG-6812
    // ENG-6827: the one-time WhatsApp/Baileys QR-link tool. Deployed so an
    // operator can run it on the host, but NOT in RESTARTABLE_CHANNEL_FILES —
    // it's an interactive tool, not a per-session channel server.
    'whatsapp-link.js',
    // ENG-6195: the staff-only admin-debug broker, bundled with the CLI (never
    // published to npm). NOT in RESTARTABLE_CHANNEL_FILES — it's not a channel,
    // so it doesn't drive the channel-restart path; the mcp-presence-reaper
    // (re)starts it with the session, and a bundle update applies on the next
    // natural session restart.
    'augmented-admin.js',
    // ENG-7023: the per-org self-troubleshoot broker for the system_support
    // concierge agent, bundled with the CLI (never published to npm). Same
    // treatment as augmented-admin - NOT a channel, so not in
    // RESTARTABLE_CHANNEL_FILES; the reaper (re)starts it with the session and a
    // bundle update applies on the next natural session restart.
    'augmented-support.js',
    // ENG-6859: the remote OAuth MCP proxy (brand-ninja, granola). NOT in
    // RESTARTABLE_CHANNEL_FILES - it's not a channel; a bundle update applies on
    // the next natural session restart. It re-reads the token from the secrets
    // file per request, so it never needs restarting just to pick up a rotation.
    'remote-oauth-proxy.js',
  ]) {
    const src = join(mcpSourceDir, file);
    const dst = join(targetDir, file);
    if (!existsSync(src)) continue;
    const before = fileHash(dst);
    try {
      // Always overwrite to ensure latest version
      copyFileSync(src, dst);
      const after = fileHash(dst);
      if (before !== after && RESTARTABLE_CHANNEL_FILES.has(file)) {
        // file is 'slack-channel.js' → basename 'slack-channel' (matches the
        // ChannelType enum in channel-sweep.ts)
        changedBasenames.push(file.replace(/\.js$/, ''));
      }
    } catch (err) {
      log(`[manager] Failed to deploy ${file}: ${(err as Error).message}`);
    }
  }
  log(`[manager] MCP assets deployed to ${targetDir}`);

  if (changedBasenames.length > 0) {
    log(`[manager] Bundle(s) updated: ${changedBasenames.join(', ')} — signalling running instances to restart`);
    restartRunningChannelMcps(changedBasenames);
  }

  // Patch any agent .mcp.json files that still reference the npx fallback
  // (which was never published). Replace with the local node path.
  const localMcpPath = join(targetDir, 'index.js');
  try {
    const agentsDir = join(homedir(), '.augmented', 'agents');
    if (existsSync(agentsDir)) {
      for (const entry of readdirSync(agentsDir, { withFileTypes: true })) {
        if (!entry.isDirectory()) continue;
        // Check both provision and project dirs
        for (const subdir of ['provision', 'project']) {
          const mcpJsonPath = join(agentsDir, entry.name, subdir, '.mcp.json');
          try {
            const raw = readFileSync(mcpJsonPath, 'utf-8');
            if (!raw.includes('@integrity-labs/augmented-mcp')) continue;
            const mcpConfig = JSON.parse(raw) as { mcpServers?: Record<string, Record<string, unknown>> };
            const augServer = mcpConfig.mcpServers?.['augmented'];
            if (!augServer) continue;
            augServer.command = 'node';
            augServer.args = [localMcpPath];
            writeFileSync(mcpJsonPath, JSON.stringify(mcpConfig, null, 2));
            log(`[manager] Patched ${entry.name}/${subdir}/.mcp.json: npx → node`);
          } catch {
            // File doesn't exist or isn't valid JSON — skip
          }
        }
      }
    }
  } catch (err) {
    log(`[manager] Failed to patch agent .mcp.json files: ${(err as Error).message}`);
  }
}

/**
 * Stop the manager poll loop gracefully.
 */
export async function stopManager(): Promise<void> {
  await stopPolling();
}

// Graceful shutdown on signals
let shuttingDown = false;
for (const sig of ['SIGTERM', 'SIGINT'] as const) {
  process.on(sig, () => {
    if (shuttingDown) {
      log(`Received ${sig} again during shutdown — ignoring (cleanup in progress)`);
      return;
    }
    shuttingDown = true;
    log(`Received ${sig}, shutting down`);
    void stopPolling().then(() => {
      process.exit(0);
    });
  });
}

// If parent disconnects, exit
process.on('disconnect', () => {
  log('Parent disconnected, exiting');
  void stopPolling().then(() => {
    process.exit(0);
  });
});
